import { lazy, Suspense, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import {
  Wallet, Zap, HardDrive, TrendingUp, Users, Settings, Bell,
  ArrowUpRight, ArrowDownLeft, Plus, Minus, X, Send, Check,
  Upload, Download, Shield, Star, Folder, FileText,
  Server, DollarSign, ChevronRight, ChevronDown,
  Activity, Layers, Trash2, MoreHorizontal, Copy, LogOut,
  RefreshCcw, LoaderCircle, Camera, Clock,
  Eye, EyeOff, TerminalSquare,
} from "lucide-react"
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts"
import { useInstanceBranding } from "@/components/instance/InstanceBrandingProvider"
import { instanceBackgroundValue } from "@/lib/instance-branding"
import { WalletEntryPrompts } from "@/components/ai-wallet/WalletEntryPrompts"
import { apiRequest, buildAPIURL, readUISettings } from "@/lib/api"
import { AUTH_USER_STORAGE_KEY, readStoredAuthSession } from "@/lib/auth-storage"
import {
  extractWalletBackgroundImage,
  readWalletAppearance,
  readWalletBackgroundFile,
  walletBackgroundCSSImage,
  writeWalletAppearance,
  writeWalletNotificationSettings,
} from "@/lib/wallet-ui-persistence"
import { timeAgo, useNotifications, type Notification as WalletNotification } from "@/lib/notifications-context"
import {
  buildSnapshotContinuationPrompt,
  shouldUsePromptContextSnapshotContinuation,
} from "@/lib/snapshot-continuation"
import {
  WALLET_GATEWAY_MODEL_OPTIONS,
  WALLET_VM_MODEL_OPTIONS,
  walletGatewayModelIDFromTask,
} from "@/lib/wallet-model-routing"
import {
  assignHardwareCostBands,
  hardwareCostBandLabel,
  type HardwareCostBand,
} from "@/lib/hardware-cost-band"
import {
  walletHardwareOptionLaunchable,
  walletProviderHourlyPriceSortValue,
  walletPinProAccessProvider,
  walletProHardwareSupportsProvider,
  walletProProviderChoicesForAccess,
  walletProProviderHardwareRequestPath,
  walletProviderHardwareOptionsFromPayload,
  walletProviderLaunchHardwareLabel,
  walletProviderSlugFromLabel,
  uniqueWalletProviderSlugs,
  walletProviderLabel,
  type WalletProviderSlug,
} from "@/lib/wallet-provider-routing"
import { walletChartRangeLabel, walletSpendDisplayValue } from "@/lib/wallet-spend-chart"
import { walletPanelFromSearch } from "@/lib/wallet-panel-deeplink"
import {
  WALLET_MARKET_PROVIDER_OPTIONS,
  type ProProviderAuthInput,
} from "@/lib/pro"
import {
  PROVIDER_CREDENTIAL_JSON_ACCEPT,
  readProviderCredentialJSONFile,
} from "@/lib/provider-credential-file"
import {
  providerAccountScopeErrors,
  providerClearSecretInputValues,
  providerCredentialFieldsForMode,
  providerDescriptorForSlug,
  providerInputDisplayValue,
  providerInputInitialValues,
  providerInputUsesMultilineEditor,
  providerInputUsesMultilineSecret,
  providerInputValue,
  providerInvalidJSONInputs,
  providerLiteralAllRegionInputs,
  providerMissingRequiredInputs,
  providerProductAvailable,
  providerSupportsLegacyAPIKeySetup,
  providerSupportsProNetworkVisibility,
  providerTypedProSetupPayload,
  readProviderRegistry,
  type ProviderInputValue,
  type ProviderInputValues,
  type ProviderRegistryDescriptor,
  type ProviderRegistryInputField,
} from "@/lib/provider-registry"
import { ProviderCreditStatusCard } from "@/components/provider-credit/ProviderCreditStatusCard"
import {
  isGoogleProviderSlug,
  type ProviderCreditStatus,
} from "@/lib/provider-credit"
import { WalletSubscriptionChooser } from "@/components/billing/WalletSubscriptionChooser"

declare global {
  interface Window {
    __GENRL_WALLET_PRELOAD_BACKGROUND__?: string
  }
}

const LazyWebSSHTerminal = lazy(() =>
  import("@/components/jobs/WebSSHTerminal").then(module=>({default:module.WebSSHTerminal})),
)

// ─── Types ───────────────────────────────────────────────────────────────────
type Panel =
  | "send" | "add" | "receive" | "more" | "settings" | "subscriptions"
  | "run" | "storage" | "market" | "workspace"
  | null

const LEDGER_PAGE_SIZE = 10
const PROCESSING_RESPONSE_TEXT = "Working on it..."
const TEXT_RESPONSE_STREAM_WORDS = 3
const TEXT_RESPONSE_STREAM_MS = 55
const streamedTextMessageKeys = new Set<string>()
const WALLET_NOTIFICATION_ICONS: Record<WalletNotification["kind"], typeof Bell> = {
  job: Zap,
  wallet: Wallet,
  bid: TrendingUp,
  system: Activity,
  connect: Server,
}

export type DirectWalletUser = {
  name: string
  handle: string
  id: string
  initials: string
  plan: string
  email?: string
}

export type DirectWalletChartPoint = { d: string; v: number }
export type DirectWalletTaskStageStatus = "pending" | "active" | "done" | "failed"
export type DirectWalletTaskStage = {
  id: string
  label: string
  status: DirectWalletTaskStageStatus
  detail?: string
}
export type DirectWalletTaskMessage = {
  id: string
  role: "user" | "assistant" | "system"
  text: string
  status?: string
  time?: string
}
export type DirectWalletGatewayMessage = {
  role: "user" | "assistant" | "system"
  text: string
}
export type DirectWalletTaskAccess = {
  mode?: string
  status?: string
  provider?: string
  primary_label?: string
  primary_value?: string
  copy_text?: string
  resource?: Record<string, unknown>
  ssh?: Record<string, unknown>
  web?: Record<string, unknown>
  api?: Record<string, unknown>
  container?: Record<string, unknown>
  [key: string]: unknown
}
export type DirectWalletTaskDetail = {
  id: string
  title: string
  input?: string
  output?: string
  prompt?: string
  model?: string
  provider?: string
  providerStatus?: string
  status?: string
  stageLabel?: string
  gpu?: string
  gpuVerified?: boolean
  publicIp?: string
  runtimeUrl?: string
  remoteId?: string
  region?: string
  image?: string
  verificationStatus?: string
  verificationSummary?: string
  attestationStatus?: string
  attestationHash?: string
  attestationError?: string
  lastError?: string
  leaseStatus?: string
  providerJobId?: string
  clientLaunchId?: string
  runtimeSessionId?: string
  persistent?: boolean
  gateway?: boolean
  sshTerminal?: boolean
  webTerminalSupported?: boolean
  accessMode?: string
  snapshotEnabled?: boolean
  snapshotId?: string
  gatewayMessages?: DirectWalletGatewayMessage[]
  access?: DirectWalletTaskAccess
  stages: DirectWalletTaskStage[]
}
export type DirectWalletLedgerRow = {
  id: string
  displayId?: string
  kind: "run" | "send" | "topup" | "store" | string
  label: string
  meta: string
  amt: number
  time: string
  task?: DirectWalletTaskDetail
}
export type DirectWalletSession = {
  id: string
  model: string
  status: string
  turns: number
  started: string
  rate?: string
  costBand?: HardwareCostBand
  provider: string
  hw: string
  providerJobId?: string
  lastActive?: string
  lastOutput?: string
  billingStatus?: string
  lastError?: string
  expires?: string
}
export type DirectWalletAccountSession = {
  id: string
  current: boolean
  status: "active" | "revoked" | "expired" | "invalid" | string
  userAgent?: string
  ipAddress?: string
  createdAt?: string
  lastSeenAt?: string
  expiresAt?: string
  revokedAt?: string
}
export type DirectWalletRuntimeSessionDetail = {
  session: DirectWalletSession
  messages: DirectWalletTaskMessage[]
  lastError?: string
}
export type DirectWalletRuntimeSessionTurnInput = {
  text: string
  files?: File[]
  restoreContext?: Record<string, unknown>
}
export type DirectWalletRuntimeSessionTurnStreamEvent = {
  turnId?: string
  stage?: string
  message?: string
  delta?: string
  detail?: DirectWalletRuntimeSessionDetail
}
export type DirectWalletRuntimeSessionTurnStreamHandlers = {
  onPhase?: (event: DirectWalletRuntimeSessionTurnStreamEvent) => void
  onToken?: (event: DirectWalletRuntimeSessionTurnStreamEvent) => void
  onDetail?: (detail: DirectWalletRuntimeSessionDetail) => void
}
export type DirectWalletJob = {
  id: string
  model: string
  provider: string
  hw: string
  status: string
  cost: number
  dur: string
  time: string
  clientLaunchId?: string
  webTerminalSupported?: boolean
}
export type DirectWalletSnapshot = {
  id: string
  model: string
  created: string
  size: string
  jobId: string
  status?: string
  outputDownloadURL?: string
  manifestDownloadURL?: string
}
export type DirectWalletVolume = {
  id: string
  name: string
  size: string
  files: number
  mod: string
  provider: string
  providerSlug?: string
  sourceSlug?: string
  routeID?: string
  region: string
  status?: string
  usedPct?: number
}
export type DirectWalletStorageFile = {
  id: string
  filename: string
  provider?: string
  providerPath?: string
  providerStatus?: string
  providerError?: string
  sizeBytes?: number
  contentType?: string
  createdAt?: string
  updatedAt?: string
  downloadURL?: string
}
export type DirectWalletHFImportInput = {
  repoID: string
  filename: string
  hfToken?: string
}
export type DirectWalletStorageEnvironment = {
  name: string
  region?: string
}
export type DirectWalletStorageVolumeType = {
  id: string
  label: string
  volumeType: string
  pricePerGBHour?: number
}
export type DirectWalletStorageRoute = {
  id: string
  routeID: string
  sourceSlug: string
  providerSlug: string
  label: string
  detail: string
  environments: DirectWalletStorageEnvironment[]
  volumeTypes: DirectWalletStorageVolumeType[]
}
export type DirectWalletStorageVolumeInput = {
  name: string
  sizeGB: number
  volumeType: string
  environmentName: string
  routeID?: string
  sourceSlug?: string
  providerSlug?: string
}
export type DirectWalletMember = {
  id?: string
  email?: string
  name: string
  role: string
  roleKey?: DirectWalletWorkspaceRole
  status?: string
  canBudget?: boolean
  spend: number
  active: boolean
  initials: string
  tasks: number
  budget: number
  budgetFunded?: number
  budgetRemaining?: number
  budgetStatus?: string
}
export type DirectWalletWorkspaceActivity = {
  u: string
  name: string
  act: string
  cost: number | null
  jobId: string | null
  t: string
}
export type DirectWalletWorkspaceRole = "viewer" | "admin" | "owner"
export type DirectWalletWorkspaceInvite = {
  id: string
  workspaceId: string
  workspaceName: string
  companyName: string
  email: string
  role: DirectWalletWorkspaceRole
  invitedAt?: string
}
export type DirectWalletWorkspaceInfo = {
  id: string
  name: string
  workspaceType: "personal" | "organization"
  billingEmail: string
  currentRole: DirectWalletWorkspaceRole
  canManage: boolean
  canManageBilling: boolean
  canInvite: boolean
  canBudget: boolean
  budgetLimit: number
  budgetFunded: number
  budgetRemaining: number
}
export type DirectWalletMarketOffer = {
  id: string
  name: string
  region: string
  gpu: string
  vram: string
  rate: number
  up: boolean
  uptime: string
}
export type DirectWalletHardwareOption = {
  id: string
  hardwareId: string
  hardwareLabel: string
  gpuCount: number
  stockAvailable: number
  launchable?: boolean
  capacityStatus?: string
  bestPricePerHour: number
  bestBuyerMicros: number
  originalBuyerMicros: number
  discountPercent: number
  fromProAccount: boolean
  providerCount: number
  providers: string[]
  regions: string[]
  runtimeClasses: string[]
  proAccessModes?: DirectWalletProAccessMode[]
  proAccessOptions?: DirectWalletProAccessOption[]
  updatedAt?: string
}
export type DirectWalletProProvider = {
  id: string
  slug: string
  n: string
  g: string
  m: string
  e: string
  active: boolean
  status?: string
  credentialLast4?: string
  hasCredential?: boolean
  authMode?: string
  principalMask?: string
  providerName?: string
  visibility?: string
  broadcastMode?: string
  pricingMode?: string
  providerAdjustmentBps?: number
  fixedUnitPriceMicros?: number
  minUnitPriceMicros?: number
  maxUnitPriceMicros?: number
  reserveBalanceThresholdMicros?: number
  broadcastStockLimit?: number
  allowedRegions?: string[]
  allowedHardware?: string[]
  providerBalanceMicros?: number
  providerBalanceCurrency?: string
  providerBalanceLastCheckedAt?: string
  providerBalanceKind?: string
  providerBalanceStatus?: string
  creditStatus?: ProviderCreditStatus | null
  earnedCreditMicros?: number
  pendingPayoutMicros?: number
  settledJobs?: number
  lastValidatedAt?: string
  lastSettlementAt?: string
  lastError?: string
  updatedAt?: string
}
export type DirectWalletProProviderLedgerRow = {
  id: string
  routeType: "internal" | "external" | string
  hardware: string
  region?: string
  status: string
  earnedMicros: number
  time: string
}
export type DirectWalletProStats = {
  proEnabled?: boolean
  proCredits: string
  proCreditsHint: string
  earnings: string
  earningsHint: string
  activeNodes: string
  activeNodesHint: string
  uptime: string
  uptimeHint: string
  providers: DirectWalletProProvider[]
  payoutDestination: string
}
export type DirectWalletProviderInput = {
  providerSlug: string
  providerName?: string
  displayName: string
  apiKey?: string
  auth?: ProProviderAuthInput
  account?: Record<string, unknown>
  visibility: "private" | "network"
}
export type DirectWalletProviderCreateResult = {
  providerId: string
  providerSlug: string
}

function directWalletCanRotateLegacyKey(provider: DirectWalletProProvider) {
  const mode = String(provider.authMode || "").trim().toLowerCase()
  return !mode || mode === "api_key" || mode === "static_api_key"
}
type DirectWalletProBroadcastMode = "INTERNAL_ONLY" | "BROADCAST_ONLY" | "BOTH"
type DirectWalletProPricingMode = "PASSTHROUGH" | "PREMIUM_OR_DISCOUNT" | "FIXED" | "FLOOR_ADJUSTED"
type DirectWalletProVisibility = "private" | "network"
export type DirectWalletRunTab = "Gateway" | "VM/GPU" | "Direct"
type DirectWalletRunPanelTab = DirectWalletRunTab | "SSH Terminal"
export type DirectWalletProAccessMode = "vm" | "container" | "notebook" | "api" | "cluster"
export type DirectWalletClusterType = "genrl-managed" | "kubernetes" | "slurm" | "ray"
export type DirectWalletProAccessOption = {
  mode: DirectWalletProAccessMode
  supportMode?: string
  providers?: string[]
  regions?: string[]
  clusterTypes?: DirectWalletClusterType[]
  requiredParameters?: string[]
  resolvedParameters?: Record<string,string>
}
export type DirectWalletExecutionMode = "gateway" | "vm_gpu"
export type DirectWalletSnapshotContinuation = {
  taskId?: string
  snapshotId?: string
  providerJobId?: string
  clientLaunchId?: string
  runtimeSessionId?: string
  title?: string
  promptContextOnly?: boolean
}
export type DirectWalletCapacityUpdate = {
  display_name?: string
  visibility?: DirectWalletProVisibility | "marketplace"
  status?: string
  broadcast_mode?: DirectWalletProBroadcastMode
  pricing_mode?: DirectWalletProPricingMode
  provider_adjustment_bps?: number
  fixed_unit_price_micros?: number
  min_unit_price_micros?: number
  max_unit_price_micros?: number
  reserve_balance_threshold_micros?: number
  broadcast_stock_limit?: number
  allowed_regions?: string[]
  allowed_hardware?: string[]
}
export type DirectWalletLaunchInput = {
  executionMode: DirectWalletExecutionMode
  runTab: DirectWalletRunTab
  sshTerminal?: boolean
  serviceName?: string
  proAccessMode?: DirectWalletProAccessMode
  proAccessOption?: DirectWalletProAccessOption
  providerSlug?: WalletProviderSlug
  clusterType?: DirectWalletClusterType
  clusterNodeCount?: number
  model: string
  hardware: string
  hardwareLabel?: string
  hourlyPriceCredits?: number
  maxPriceCredits?: number
  estimatedMinutes?: number
  gpuCount?: number
  prompt: string
  files: File[]
  persistent: boolean
  idleTimeoutSeconds?: number
  snapshots: boolean
  continueFromSnapshot?: DirectWalletSnapshotContinuation
  gatewayMessages?: DirectWalletGatewayMessage[]
  gatewayParentProviderJobId?: string
}
export type DirectWalletLaunchResult = {
  taskId?: string
  providerJobId?: string
  clientLaunchId?: string
  runtimeSessionId?: string
}
type DirectWalletTaskView = "progress" | "task"
type DirectWalletLaunchFocusOptions = {
  view?: DirectWalletTaskView
}
type DirectWalletPendingLaunch = {
  result: DirectWalletLaunchResult
  view: DirectWalletTaskView
}
export type DirectWalletDashboardProps = {
  user?: Partial<DirectWalletUser>
  balance?: number
  reserved?: number
  chartData?: DirectWalletChartPoint[]
  ledger?: DirectWalletLedgerRow[]
  sessions?: DirectWalletSession[]
  jobs?: DirectWalletJob[]
  snapshots?: DirectWalletSnapshot[]
  volumes?: DirectWalletVolume[]
  storageRoutes?: DirectWalletStorageRoute[]
  members?: DirectWalletMember[]
  workspaceInvites?: DirectWalletWorkspaceInvite[]
  workspaceActivity?: DirectWalletWorkspaceActivity[]
  marketOffers?: DirectWalletMarketOffer[]
  hardwareOptions?: DirectWalletHardwareOption[]
  proStats?: DirectWalletProStats
  workspaceInfo?: Partial<DirectWalletWorkspaceInfo>
  workspaceName?: string
  storageUsagePct?: number
  loading?: boolean
  refreshing?: boolean
  launchBusy?: boolean
  creditActionsDisabled?: boolean
  accountSessions?: DirectWalletAccountSession[]
  accountSessionsLoading?: boolean
  accountSessionsError?: string
  revokingAccountSessionId?: string
  onRefresh?: () => void | Promise<void>
  onAddCredits?: (amountCredits: number) => void | Promise<void>
  onSendCredits?: (input: { recipient: string; amountCredits: number; note: string }) => void | Promise<void>
  onLaunchWorkload?: (input: DirectWalletLaunchInput) => DirectWalletLaunchResult | void | Promise<DirectWalletLaunchResult | void>
  onCreateProProvider?: (
    input: DirectWalletProviderInput,
  ) =>
    | DirectWalletProviderCreateResult
    | void
    | Promise<DirectWalletProviderCreateResult | void>
  onUpdateProProvider?: (providerId: string, input: DirectWalletCapacityUpdate) => void | Promise<void>
  onValidateProProvider?: (providerId: string) => void | Promise<void>
  onRotateProProviderKey?: (providerId: string, apiKey: string) => void | Promise<void>
  onDeleteProProvider?: (providerId: string) => void | Promise<void>
  onLoadProProviderLedger?: (providerId: string) => Promise<DirectWalletProProviderLedgerRow[]>
  onCreateStorageVolume?: (input: DirectWalletStorageVolumeInput) => void | Promise<void>
  onDeleteStorageVolume?: (volume: DirectWalletVolume) => void | Promise<void>
  onDeleteRunSnapshot?: (snapshot: DirectWalletSnapshot) => void | Promise<void>
  onLoadStorageVolumeFiles?: (volume: DirectWalletVolume) => Promise<DirectWalletStorageFile[]>
  onUploadStorageVolumeFiles?: (volume: DirectWalletVolume, files: File[]) => void | DirectWalletStorageFile[] | Promise<void | DirectWalletStorageFile[]>
  onImportStorageVolumeHF?: (volume: DirectWalletVolume, input: DirectWalletHFImportInput) => void | DirectWalletStorageFile[] | Promise<void | DirectWalletStorageFile[]>
  onUpdateUser?: (input: { name: string; handle: string; email: string }) => void | Promise<void>
  onUpdateWorkspace?: (input: { companyName: string; billingEmail: string }) => void | Promise<void>
  onInviteWorkspaceMember?: (input: { name: string; email: string; role: DirectWalletWorkspaceRole }) => void | Promise<void>
  onAcceptWorkspaceInvite?: (inviteId: string) => void | Promise<void>
  onDeclineWorkspaceInvite?: (inviteId: string) => void | Promise<void>
  onSetWorkspaceBudget?: (input: { amountCredits: number }) => void | Promise<void>
  onSetMemberBudget?: (input: { memberId: string; amountCredits: number }) => void | Promise<void>
  onLoadRuntimeSession?: (sessionId: string) => Promise<DirectWalletRuntimeSessionDetail>
  onSendRuntimeSessionTurn?: (sessionId: string, input: DirectWalletRuntimeSessionTurnInput, stream?: DirectWalletRuntimeSessionTurnStreamHandlers) => Promise<DirectWalletRuntimeSessionDetail>
  onCloseRuntimeSession?: (sessionId: string) => void | DirectWalletRuntimeSessionDetail | Promise<void | DirectWalletRuntimeSessionDetail>
  onTerminateProviderJob?: (providerJobId: string) => void | Promise<void>
  onRefreshAccountSessions?: () => void | Promise<void>
  onRevokeAccountSession?: (sessionId: string) => void | Promise<void>
  onRevokeOtherAccountSessions?: () => void | Promise<void>
  onExportLedger?: () => void
  onSignOut?: () => void
}

type DirectWalletResolvedData = Required<
  Pick<
    DirectWalletDashboardProps,
    | "balance"
    | "reserved"
    | "chartData"
    | "ledger"
    | "sessions"
    | "jobs"
    | "snapshots"
    | "volumes"
    | "storageRoutes"
    | "members"
    | "workspaceInvites"
    | "workspaceActivity"
    | "marketOffers"
    | "hardwareOptions"
    | "workspaceName"
    | "storageUsagePct"
    | "loading"
    | "refreshing"
    | "launchBusy"
  >
> & {
  user: DirectWalletUser
  proStats: DirectWalletProStats
  workspaceInfo: DirectWalletWorkspaceInfo
}

// ─── Constants ────────────────────────────────────────────────────────────────
const EASE = [0.32, 0.72, 0, 1] as const

const PAGE_BG = [
  "radial-gradient(ellipse at 18% -8%, rgba(224,185,120,0.55) 0%, transparent 48%)",
  "radial-gradient(ellipse at 88% 108%, rgba(200,160,100,0.42) 0%, transparent 52%)",
  "radial-gradient(ellipse at 55% 45%, rgba(255,245,225,0.45) 0%, transparent 60%)",
  "#EDE8DC",
].join(", ")

function walletPreloadBackground() {
  if (typeof window === "undefined") return ""
  return String(window.__GENRL_WALLET_PRELOAD_BACKGROUND__ || "").trim()
}

function walletAppearanceFallback(current?: string) {
  return walletPreloadBackground() || current || "transparent"
}

const bgPhoto = (fileName: string) => `url("/wallet-backgrounds/${fileName}")`

const BG_PRESETS = [
  { id:"warm",   label:"Warm",  value: PAGE_BG, labelColor:"rgba(0,0,0,0.55)", labelShadow:"none" },
  { id:"slate",  label:"Slate", value: bgPhoto("slate-lou-batier.jpg"), labelColor:"rgba(255,255,255,0.84)", labelShadow:"0 1px 8px rgba(0,0,0,0.58)" },
  { id:"rose",   label:"Rose",  value: bgPhoto("rose-bailey-zindel.jpg"), labelColor:"rgba(255,255,255,0.86)", labelShadow:"0 1px 8px rgba(0,0,0,0.45)" },
  { id:"sage",   label:"Sage",  value: bgPhoto("sage-ishan-seefromthesky.jpg"), labelColor:"rgba(0,0,0,0.62)", labelShadow:"0 1px 8px rgba(255,255,255,0.36)" },
  { id:"sand",   label:"Sand",  value: ["radial-gradient(ellipse at 20% 10%, rgba(253,230,138,0.45) 0%, transparent 50%)","radial-gradient(ellipse at 80% 90%, rgba(251,191,36,0.25) 0%, transparent 50%)","#fdf9ee"].join(", "), labelColor:"rgba(0,0,0,0.55)", labelShadow:"none" },
  { id:"night",  label:"Night", value: bgPhoto("night-daniel-leone.jpg"), labelColor:"rgba(255,255,255,0.86)", labelShadow:"0 1px 8px rgba(0,0,0,0.62)" },
]

const G = {
  nav:    { background:"rgba(255,255,255,0.28)", backdropFilter:"blur(40px)", WebkitBackdropFilter:"blur(40px)", borderBottom:"1px solid rgba(255,255,255,0.55)" },
  card:   { background:"rgba(255,255,255,0.22)", backdropFilter:"blur(40px)", WebkitBackdropFilter:"blur(40px)", border:"1px solid rgba(255,255,255,0.68)", boxShadow:"0 4px 24px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)" },
  subtle: { background:"rgba(255,255,255,0.18)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", border:"1px solid rgba(255,255,255,0.55)" },
  dark:   { background:"rgba(14,12,8,0.88)",     backdropFilter:"blur(36px)", WebkitBackdropFilter:"blur(36px)", border:"1px solid rgba(255,255,255,0.07)", boxShadow:"0 20px 60px rgba(0,0,0,0.35)" },
  panel:  { background:"rgba(255,255,255,0.26)", backdropFilter:"blur(52px)", WebkitBackdropFilter:"blur(52px)", border:"1px solid rgba(255,255,255,0.72)", boxShadow:"0 8px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)" },
  field:  { background:"rgba(0,0,0,0.04)", border:"1px solid rgba(0,0,0,0.07)" },
  inset:  { background:"rgba(0,0,0,0.035)" },
} as const

// ─── Data ─────────────────────────────────────────────────────────────────────
const USER = { name:"Elara Voss", handle:"@elara", id:"G-00421", initials:"EV", plan:"Personal Pro" }
const BALANCE = 4_250.00
const RESERVED = 50.00

const CHART_DATA = [
  {d:"Jun 8",v:42},{d:"Jun 11",v:95},{d:"Jun 14",v:110},{d:"Jun 17",v:43},
  {d:"Jun 20",v:38},{d:"Jun 23",v:118},{d:"Jun 26",v:62},{d:"Jun 29",v:142},
  {d:"Jul 2",v:96},{d:"Jul 5",v:138},{d:"Jul 7",v:55},
]

const LEDGER = [
  {id:"job_c2e1f3", kind:"run",   label:"deepseek-r1-distill-qwen-7b", meta:"RunPod · A100 80 GB", amt:-18.40, time:"4m ago"   },
  {id:"job_d3f209", kind:"run",   label:"qwen2.5-7b-instruct",         meta:"Hyperstack · H100 SXM",amt:-5.20,  time:"1h ago"   },
  {id:"txn_e4a01",  kind:"send",  label:"Transfer to marcus",   meta:"Workspace member",          amt:-100,   time:"3h ago"   },
  {id:"chk_f5b92",  kind:"topup", label:"Credit purchase",      meta:"Stripe · Visa ···· 4821",   amt:+500,   time:"Yesterday"},
  {id:"job_g6c83",  kind:"run",   label:"mistral-7b-instruct",  meta:"Lambda · A10G 24 GB",       amt:-9.80,  time:"Yesterday"},
  {id:"vol_h7d74",  kind:"store", label:"training-data volume", meta:"Hyperstack object storage", amt:-1.60,  time:"2d ago"   },
]

const SESSIONS = [
  {id:"sess_a7c923", model:"deepseek-r1-distill-qwen-7b", status:"running", turns:4, started:"12m ago", costBand:"High cost" as HardwareCostBand, provider:"RunPod", hw:"A100 80 GB"},
  {id:"sess_b1d041", model:"qwen2.5-7b-instruct", status:"idle", turns:2, started:"1h ago", costBand:"Low cost" as HardwareCostBand, provider:"Lambda", hw:"A10G 24 GB"},
]

const JOBS = [
  {id:"job_c2e1f3", model:"deepseek-r1-distill-qwen-7b", provider:"RunPod",    hw:"A100 80 GB", status:"completed", cost:18.40, dur:"2m 14s", time:"4m ago"   },
  {id:"job_d3f209", model:"qwen2.5-7b-instruct",         provider:"Hyperstack",hw:"H100 SXM",  status:"completed", cost:5.20,  dur:"47s",    time:"1h ago"   },
  {id:"job_g6c83",  model:"mistral-7b-instruct",          provider:"Lambda",    hw:"A10G 24 GB",status:"failed",    cost:0,     dur:"—",      time:"3h ago"   },
  {id:"job_i8e53",  model:"qwen2.5-7b-instruct",          provider:"RunPod",    hw:"A100 80 GB",status:"completed", cost:7.10,  dur:"1m 22s", time:"Yesterday"},
]

const SNAPSHOTS = [
  {id:"snap_f5b821", model:"deepseek-r1-distill-qwen-7b", created:"1h ago",    size:"128 MB", jobId:"job_c2e1f3"},
  {id:"snap_g6c933", model:"mistral-7b-instruct",          created:"Yesterday", size:"84 MB",  jobId:"job_g6c83" },
  {id:"snap_h7d044", model:"qwen2.5-7b-instruct",          created:"2d ago",    size:"96 MB",  jobId:"job_d3f209"},
]

const VOLUMES = [
  {id:"vol_v1", name:"training-data",  size:"24.3 GB",  files:1420, mod:"2h ago",    provider:"Hyperstack", region:"us-east-1"},
  {id:"vol_v2", name:"model-outputs",  size:"8.7 GB",   files:312,  mod:"Yesterday", provider:"RunPod",     region:"us-east-1"},
  {id:"vol_v3", name:"raw-datasets",   size:"102.1 GB", files:8903, mod:"3d ago",    provider:"Lambda",     region:"eu-west-1"},
]

const WEIGHT_UPLOADS = [
  {id:"wu_i8e153", name:"mistral-7b-finetune", size:"14.3 GB", status:"ready",     progress:100, created:"2d ago"},
  {id:"wu_j9f264", name:"qwen-custom-v2",      size:"28.7 GB", status:"uploading", progress:67,  created:"1h ago"},
]

const MODELS = WALLET_VM_MODEL_OPTIONS
const GATEWAY_MODELS = WALLET_GATEWAY_MODEL_OPTIONS

const HW = [
  {id:"h100-80gb",  label:"H100 80 GB",  Icon:Star,       spec:"1 GPU · 80 GB"},
  {id:"a100-80gb", label:"A100 80 GB",  Icon:Zap,        spec:"1 GPU · 80 GB"},
  {id:"a10g-24gb", label:"A10G 24 GB",  Icon:DollarSign, spec:"1 GPU · 24 GB"},
]

type WalletRunHardwareChoice = {
  id: string
  label: string
  Icon: typeof DollarSign
  costBand: HardwareCostBand
  spec: string
  available: boolean
  hardwareLabel: string
  hourlyPriceCredits: number
  originalHourlyPriceCredits: number
  discountPercent: number
  fromProAccount: boolean
  maxPriceCredits: number
  estimatedMinutes: number
  gpuCount: number
  providerCount: number
  providers: string[]
}

const PRO_ACCESS_MODES: Array<{
  id: DirectWalletProAccessMode
  label: string
  title: string
  detail: string
  credential: string
  route: string
  action: string
  Icon: typeof Server
}> = [
  {id:"vm",label:"VM",title:"SSH VM",detail:"Virtual GPU instance",credential:"SSH command",route:"Runtime agent",action:"Create SSH Access",Icon:Server},
  {id:"container",label:"Containers",title:"Container",detail:"Image-based GPU runtime",credential:"Service URL",route:"Logs and exec",action:"Create Container Access",Icon:Layers},
  {id:"notebook",label:"Notebook",title:"Notebook",detail:"Interactive GPU workspace",credential:"Notebook URL",route:"Jupyter or VS Code",action:"Create Notebook Access",Icon:FileText},
  {id:"api",label:"API",title:"API Endpoint",detail:"GPU-backed API route",credential:"API key",route:"Base URL",action:"Create API Access",Icon:Zap},
  {id:"cluster",label:"Clusters",title:"Cluster",detail:"Provider cluster capacity",credential:"Cluster SSH",route:"Provider cluster",action:"Create Cluster Access",Icon:Layers},
]

const PRO_HARDWARE_CATALOG: Array<{
  id: string
  hardwareLabel: string
  detail: string
  gpuCount: number
  match: RegExp
}> = [
  {id:"h100",hardwareLabel:"H100",detail:"80 GB HBM3 class",gpuCount:1,match:/\bh100\b/},
  {id:"h200",hardwareLabel:"H200",detail:"141 GB HBM3e class",gpuCount:1,match:/\bh200\b/},
  {id:"a100-80g",hardwareLabel:"A100 80G",detail:"80 GB HBM2e class",gpuCount:1,match:/\ba100\b.*\b80\b|\ba100\s*80g\b/},
  {id:"a100-40g",hardwareLabel:"A100 40G",detail:"40 GB HBM2 class",gpuCount:1,match:/\ba100\b.*\b40\b|\ba100\s*40g\b/},
  {id:"l40s",hardwareLabel:"L40S",detail:"48 GB Ada class",gpuCount:1,match:/\bl40s\b/},
  {id:"l40",hardwareLabel:"L40",detail:"48 GB Ada class",gpuCount:1,match:/\bl40\b/},
  {id:"rtx-a6000",hardwareLabel:"RTX A6000",detail:"48 GB workstation class",gpuCount:1,match:/\brtx\b.*\ba6000\b|\ba6000\b/},
  {id:"a10",hardwareLabel:"A10",detail:"24 GB Ampere class",gpuCount:1,match:/\ba10g?\b|\ba10\b/},
]

const MEMBERS = [
  {name:"Elara Voss",   role:"Owner",  spend:842.10, active:true,  initials:"EV", tasks:94, budget:2000},
  {name:"Marcus Webb",  role:"Admin",  spend:312.50, active:true,  initials:"MW", tasks:47, budget:1000},
  {name:"Priya Sharma", role:"Member", spend:189.20, active:false, initials:"PS", tasks:28, budget:500 },
  {name:"James Liu",    role:"Viewer", spend:12.40,  active:false, initials:"JL", tasks:4,  budget:200 },
]

const WORKSPACE_ACTIVITY = [
  {u:"EV",name:"Elara Voss",   act:"ran deepseek-r1-distill-qwen-7b", cost:18.40, jobId:"job_c2e1f3", t:"4m ago"   },
  {u:"MW",name:"Marcus Webb",  act:"uploaded to training-data",cost:null,  jobId:null,          t:"18m ago"  },
  {u:"EV",name:"Elara Voss",   act:"ran qwen2.5-7b-instruct",  cost:5.20,  jobId:"job_d3f209",  t:"1h ago"   },
  {u:"PS",name:"Priya Sharma", act:"ran mistral-7b-instruct (batch)", cost:24.80, jobId:"job_g6c83", t:"3h ago" },
  {u:"JL",name:"James Liu",    act:"viewed model-outputs",     cost:null,  jobId:null,          t:"Yesterday"},
]

const MARKET_OFFERS = [
  {id:"p1",name:"RunPod",    region:"US East",   gpu:"A100 SXM", vram:"80 GB",rate:2.40,up:true, uptime:"99.7%"},
  {id:"p2",name:"Hyperstack",region:"US West",   gpu:"H100 SXM", vram:"80 GB",rate:3.90,up:true, uptime:"99.9%"},
  {id:"p3",name:"Lambda",    region:"EU West",   gpu:"A10G",     vram:"24 GB",rate:0.88,up:true, uptime:"98.4%"},
  {id:"p4",name:"GENRL Network", region:"US Central",gpu:"RTX 4090", vram:"24 GB",rate:1.20,up:false,uptime:"93.1%"},
]

const DEFAULT_PRO_STATS: DirectWalletProStats = {
  proEnabled: true,
  proCredits: "42.80 UC",
  proCreditsHint: "available at provider",
  earnings: "342.80 UC",
  earningsHint: "+24% this month",
  activeNodes: "2",
  activeNodesHint: "1 GPU each",
  uptime: "99.2%",
  uptimeHint: "30-day avg",
  providers: [
    { id:"node-01", slug:"hyperstack", n:"Hyperstack", g:"RTX 4090 · 24 GB", m:"Network", e:"1.20 UC/hr", active:true, providerBalanceMicros:32_100_000 },
    { id:"node-02", slug:"runpod", n:"RunPod", g:"RTX 3090 · 24 GB", m:"Private", e:"—", active:true, providerBalanceMicros:10_700_000 },
  ],
  payoutDestination: "Stripe · Visa ···· 4821",
}

function resolveDashboardData(props: DirectWalletDashboardProps): DirectWalletResolvedData {
  return {
    user: { ...USER, ...(props.user || {}) },
    balance: props.balance ?? BALANCE,
    reserved: props.reserved ?? RESERVED,
    chartData: Array.isArray(props.chartData) ? props.chartData : CHART_DATA,
    ledger: props.ledger ?? LEDGER,
    sessions: props.sessions ?? SESSIONS,
    jobs: props.jobs ?? JOBS,
    snapshots: props.snapshots ?? SNAPSHOTS,
    volumes: props.volumes ?? VOLUMES,
    storageRoutes: props.storageRoutes ?? [],
    members: props.members ?? MEMBERS,
    workspaceInvites: props.workspaceInvites ?? [],
    workspaceActivity: props.workspaceActivity ?? WORKSPACE_ACTIVITY,
    marketOffers: props.marketOffers ?? MARKET_OFFERS,
    hardwareOptions: props.hardwareOptions ?? [],
    proStats: props.proStats || DEFAULT_PRO_STATS,
    workspaceName: props.workspaceName || "Æther Labs",
    workspaceInfo: {
      id: props.workspaceInfo?.id || "",
      name: props.workspaceInfo?.name || props.workspaceName || "Æther Labs",
      workspaceType: props.workspaceInfo?.workspaceType || "personal",
      billingEmail: props.workspaceInfo?.billingEmail || "",
      currentRole: props.workspaceInfo?.currentRole || "owner",
      canManage: props.workspaceInfo?.canManage ?? true,
      canManageBilling: props.workspaceInfo?.canManageBilling ?? false,
      canInvite: props.workspaceInfo?.canInvite ?? true,
      canBudget: props.workspaceInfo?.canBudget ?? true,
      budgetLimit: props.workspaceInfo?.budgetLimit ?? 0,
      budgetFunded: props.workspaceInfo?.budgetFunded ?? 0,
      budgetRemaining: props.workspaceInfo?.budgetRemaining ?? 0,
    },
    storageUsagePct: props.storageUsagePct ?? 65,
    loading: props.loading ?? false,
    refreshing: props.refreshing ?? false,
    launchBusy: props.launchBusy ?? false,
  }
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function Label({ children, color = "rgba(0,0,0,0.38)" }: { children: ReactNode; color?: string }) {
  return <span className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color }}>{children}</span>
}

function StatusBadge({ status }: { status: string }) {
  const c = ({ completed:{bg:"rgba(34,197,94,0.1)",color:"#15803d"}, failed:{bg:"rgba(220,38,38,0.1)",color:"#b91c1c"}, running:{bg:"rgba(245,158,11,0.1)",color:"#b45309"}, idle:{bg:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.45)"}, ready:{bg:"rgba(34,197,94,0.1)",color:"#15803d"}, uploading:{bg:"rgba(59,130,246,0.1)",color:"#1d4ed8"} } as Record<string,{bg:string;color:string}>)[status] ?? {bg:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.45)"}
  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{background:c.bg,color:c.color}}>{status}</span>
}

function Avatar({ initials, active, size=32 }: { initials:string; active:boolean; size?:number }) {
  return (
    <div className="relative flex-shrink-0" style={{width:size,height:size}}>
      <div className="rounded-full flex items-center justify-center text-white font-semibold" style={{width:size,height:size,background:"rgba(14,12,8,0.85)",fontSize:size*0.35}}>{initials}</div>
      {active && <span className="absolute bottom-0 right-0 rounded-full border-2 border-white" style={{width:9,height:9,background:"#22c55e"}} />}
    </div>
  )
}

function Toggle({ value, onChange, label }: { value:boolean; onChange:(v:boolean)=>void; label?: string }) {
  return (
    <motion.button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      onClick={()=>onChange(!value)}
      className="relative rounded-full flex-shrink-0 overflow-hidden"
      style={{width:40,height:22,background:value?"rgba(14,12,8,0.85)":"rgba(0,0,0,0.12)"}}
      whileTap={{scale:0.95}}>
      <motion.span className="absolute rounded-full bg-white" style={{left:3,top:3,width:16,height:16}} animate={{x:value?18:0}} transition={{type:"spring",stiffness:500,damping:30}} />
    </motion.button>
  )
}

function SegCtrl({ options, value, onChange, disabled, disabledLabels, layoutId = "seg" }: { options:string[]; value:string; onChange:(v:string)=>void; disabled?: Record<string,string>; disabledLabels?: Record<string,string>; layoutId?: string }) {
  return (
    <div className="flex rounded-xl p-1 gap-0.5" style={G.inset}>
      {options.map(o=>{
        const disabledHint = disabled?.[o]
        const disabledLabel = disabledLabels?.[o]
        const isDisabled = Boolean(disabledHint)
        return (
          <button type="button" key={o} disabled={isDisabled} onClick={()=>!isDisabled&&onChange(o)} aria-disabled={isDisabled} aria-label={disabledLabel ? `${o} — ${disabledLabel}` : undefined} title={disabledHint || undefined}
            className="relative px-4 py-1.5 rounded-lg text-[13px] font-medium"
            style={{color:isDisabled?"rgba(0,0,0,0.24)":value===o?"rgba(0,0,0,0.85)":"rgba(0,0,0,0.42)",cursor:isDisabled?"not-allowed":"pointer"}}>
            {value===o && !isDisabled && <motion.div layoutId={layoutId} className="absolute inset-0 rounded-lg bg-white" style={{boxShadow:"0 1px 4px rgba(0,0,0,0.08)"}} transition={{type:"spring",stiffness:500,damping:35}} />}
            <span className="relative z-10">
              {o}
              {disabledLabel ? <span className="ml-1.5 rounded-full px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.08em]" style={{background:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.32)"}}>{disabledLabel}</span> : null}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ─── Panel wrapper ────────────────────────────────────────────────────────────

function Panel({ onClose, children, wide=false }: { onClose:()=>void; children:ReactNode; wide?:boolean }) {
  return (
    <>
      {/* Blurred backdrop */}
      <motion.div
        className="fixed inset-0 z-40"
        style={{ background:"rgba(20,16,8,0.28)", backdropFilter:"blur(20px)", WebkitBackdropFilter:"blur(20px)" }}
        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        transition={{ duration:0.22 }}
        onClick={onClose}
      />
      {/* Container: flex column so panel floats with gap at bottom */}
      <div className="fixed inset-x-0 bottom-0 z-[80] flex justify-center px-2 pb-[calc(env(safe-area-inset-bottom)+88px)] sm:px-5 sm:pb-5"
        onClick={onClose}>
        <motion.div
          className={`flex h-[calc(100dvh-104px)] max-h-[calc(100dvh-104px)] w-full flex-col overflow-hidden rounded-[28px] sm:h-[86vh] sm:max-h-[86vh] sm:rounded-3xl ${wide ? "max-w-[820px]" : "max-w-[500px]"}`}
          style={G.panel}
          initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }}
          transition={{ type:"spring", damping:38, stiffness:380 }}
          onClick={e=>e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex-shrink-0 pt-3 pb-0">
            <div className="w-9 h-1 rounded-full mx-auto" style={{ background:"rgba(0,0,0,0.13)" }} />
          </div>
          {children}
        </motion.div>
      </div>
    </>
  )
}

function PanelHead({ title, onClose }: { title:string; onClose:()=>void }) {
  return (
    <div className="flex-shrink-0 flex items-center justify-between px-4 py-4 border-b sm:px-6 sm:py-5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
      <h2 className="text-[17px] font-semibold tracking-tight">{title}</h2>
      <motion.button whileTap={{scale:0.88}} onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset}>
        <X size={15} style={{color:"rgba(0,0,0,0.5)"}} />
      </motion.button>
    </div>
  )
}

// ─── Send panel ───────────────────────────────────────────────────────────────

function scannerErrorText(error: unknown) {
  const name = error && typeof error === "object" && "name" in error ? String((error as { name?: unknown }).name) : ""
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "Camera permission was blocked."
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "No camera was found."
  return "Could not start the camera."
}

const WALLET_CODE_SIZE = 15
const WALLET_CODE_INNER = WALLET_CODE_SIZE - 2
const WALLET_CODE_HEX_VERSION = [1,0,1,1]
const WALLET_CODE_TEXT_VERSION = [1,1,0,1]
const WALLET_CODE_TEXT_CHARS = "@abcdefghijklmnopqrstuvwxyz0123456789._-+|:"
const WALLET_CODE_TEXT_LENGTH_BITS = 5
const WALLET_CODE_TEXT_CHAR_BITS = 6
const WALLET_CODE_TEXT_MAX_CHARS = 25
const WALLET_CODE_TEXT_CHECKSUM_BITS = 10

function walletUserIDHex(userID: string) {
  return /^usr_[0-9a-f]{32}$/i.test(userID.trim()) ? userID.trim().slice(4).toLowerCase() : ""
}

function walletNumberToBits(value: number, bitCount: number) {
  return Array.from({length:bitCount},(_,i)=>(value >> (bitCount - 1 - i)) & 1)
}

function walletBitsToNumber(bits: number[]) {
  return bits.reduce((value, bit)=>(value << 1) | (bit ? 1 : 0), 0)
}

function walletHexToBits(hex: string) {
  return hex.split("").flatMap(char => {
    const value = parseInt(char, 16)
    return [3,2,1,0].map(bit => (value >> bit) & 1)
  })
}

function walletBitsToHex(bits: number[]) {
  let hex = ""
  for (let i = 0; i < bits.length; i += 4) {
    const value = ((bits[i] || 0) << 3) | ((bits[i + 1] || 0) << 2) | ((bits[i + 2] || 0) << 1) | (bits[i + 3] || 0)
    hex += value.toString(16)
  }
  return hex
}

function walletChecksumBits(userID: string) {
  const value = walletMarkHash(`genrl-wallet-code:${userID}`) & 0xffff
  return Array.from({length:16},(_,i)=>(value >> (15 - i)) & 1)
}

function walletTextChecksumBits(recipient: string) {
  const value = walletMarkHash(`genrl-wallet-text-code:${recipient}`) & ((1 << WALLET_CODE_TEXT_CHECKSUM_BITS) - 1)
  return walletNumberToBits(value, WALLET_CODE_TEXT_CHECKSUM_BITS)
}

function walletNormalizeRecipientText(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, "")
}

function walletCanEncodeRecipientText(value: string) {
  const text = walletNormalizeRecipientText(value)
  return text.length > 0
    && text.length <= WALLET_CODE_TEXT_MAX_CHARS
    && Array.from(text).every(char => WALLET_CODE_TEXT_CHARS.includes(char))
}

function walletRecipientText(user: DirectWalletUser) {
  for (const candidate of [user.handle, user.email || "", user.id]) {
    const text = walletNormalizeRecipientText(candidate)
    if (walletCanEncodeRecipientText(text)) return text
  }
  return ""
}

function walletRecipientTextBits(recipient: string) {
  const bits = [...WALLET_CODE_TEXT_VERSION, ...walletNumberToBits(recipient.length, WALLET_CODE_TEXT_LENGTH_BITS)]
  for (let i = 0; i < WALLET_CODE_TEXT_MAX_CHARS; i += 1) {
    const charValue = i < recipient.length ? WALLET_CODE_TEXT_CHARS.indexOf(recipient[i]) : 0
    bits.push(...walletNumberToBits(Math.max(0, charValue), WALLET_CODE_TEXT_CHAR_BITS))
  }
  bits.push(...walletTextChecksumBits(recipient))
  return bits
}

function walletFrameCell(row: number, col: number) {
  return row === 0 || col === 0 || row === WALLET_CODE_SIZE - 1 || col === WALLET_CODE_SIZE - 1
}

function walletAddressCodeCells(user: DirectWalletUser) {
  const hex = walletUserIDHex(user.id)
  const recipient = walletRecipientText(user)
  const payload = recipient
    ? walletRecipientTextBits(recipient)
    : hex
    ? [...WALLET_CODE_HEX_VERSION, ...walletHexToBits(hex), ...walletChecksumBits(user.id)]
    : walletHexToBits(walletMarkHash(`${user.id}:${user.handle}`).toString(16).padStart(8, "0").repeat(4))
  return Array.from({length:WALLET_CODE_SIZE},(_,row)=>
    Array.from({length:WALLET_CODE_SIZE},(_,col)=>{
      if (walletFrameCell(row, col)) return true
      const index = (row - 1) * WALLET_CODE_INNER + (col - 1)
      const bit = payload[index]
      if (bit === 0 || bit === 1) return bit === 1
      return walletMarkHash(`${user.id}:${row}:${col}`) % 3 === 0
    }),
  )
}

function walletDecodeCodeCells(cells: boolean[][]) {
  const payload: number[] = []
  for (let row = 1; row < WALLET_CODE_SIZE - 1; row += 1) {
    for (let col = 1; col < WALLET_CODE_SIZE - 1; col += 1) {
      payload.push(cells[row]?.[col] ? 1 : 0)
    }
  }
  if (WALLET_CODE_TEXT_VERSION.every((bit, index) => payload[index] === bit)) {
    const lengthStart = WALLET_CODE_TEXT_VERSION.length
    const charStart = lengthStart + WALLET_CODE_TEXT_LENGTH_BITS
    const recipientLength = walletBitsToNumber(payload.slice(lengthStart, charStart))
    if (recipientLength <= 0 || recipientLength > WALLET_CODE_TEXT_MAX_CHARS) return ""
    let recipient = ""
    for (let i = 0; i < recipientLength; i += 1) {
      const value = walletBitsToNumber(payload.slice(charStart + i * WALLET_CODE_TEXT_CHAR_BITS, charStart + (i + 1) * WALLET_CODE_TEXT_CHAR_BITS))
      const char = WALLET_CODE_TEXT_CHARS[value]
      if (!char) return ""
      recipient += char
    }
    const checksumStart = charStart + WALLET_CODE_TEXT_MAX_CHARS * WALLET_CODE_TEXT_CHAR_BITS
    const checksum = payload.slice(checksumStart, checksumStart + WALLET_CODE_TEXT_CHECKSUM_BITS)
    if (!walletTextChecksumBits(recipient).every((bit, index) => checksum[index] === bit)) return ""
    return recipient
  }
  if (WALLET_CODE_HEX_VERSION.every((bit, index) => payload[index] === bit)) {
    const hex = walletBitsToHex(payload.slice(WALLET_CODE_HEX_VERSION.length, WALLET_CODE_HEX_VERSION.length + 128))
    const userID = `usr_${hex}`
    const checksumStart = WALLET_CODE_HEX_VERSION.length + 128
    const checksum = payload.slice(checksumStart, checksumStart + 16)
    if (!walletChecksumBits(userID).every((bit, index) => checksum[index] === bit)) return ""
    return userID
  }
  return ""
}

function walletPixelLuma(data: Uint8ClampedArray, index: number) {
  return data[index] * 0.299 + data[index + 1] * 0.587 + data[index + 2] * 0.114
}

function walletSampleCodeGrid(image: ImageData, canvasSize: number, left: number, top: number, right: number, bottom: number, threshold: number, sampleRadius: number) {
  const grid: boolean[][] = []
  for (let row = 0; row < WALLET_CODE_SIZE; row += 1) {
    const line: boolean[] = []
    for (let col = 0; col < WALLET_CODE_SIZE; col += 1) {
      const cx = Math.round(left + (col + 0.5) * (right - left) / WALLET_CODE_SIZE)
      const cy = Math.round(top + (row + 0.5) * (bottom - top) / WALLET_CODE_SIZE)
      let total = 0
      let samples = 0
      for (let oy = -sampleRadius; oy <= sampleRadius; oy += 1) {
        for (let ox = -sampleRadius; ox <= sampleRadius; ox += 1) {
          const px = Math.max(0, Math.min(canvasSize - 1, cx + ox))
          const py = Math.max(0, Math.min(canvasSize - 1, cy + oy))
          total += walletPixelLuma(image.data, (py * canvasSize + px) * 4)
          samples += 1
        }
      }
      line.push(total / samples < threshold)
    }
    grid.push(line)
  }
  return walletDecodeCodeCells(grid)
}

function walletReadCodeFromVideo(video: HTMLVideoElement, canvas: HTMLCanvasElement, deepScan = false) {
  const width = video.videoWidth
  const height = video.videoHeight
  if (!width || !height) return ""
  const ctx = canvas.getContext("2d", { willReadFrequently: true })
  if (!ctx) return ""
  const canvasSize = deepScan ? 260 : 220
  canvas.width = canvasSize
  canvas.height = canvasSize
  const sampleRadius = deepScan ? 2 : 1
  const scales = deepScan ? [0.72, 0.84, 0.62, 0.52, 0.94] : [0.76, 0.88]
  const thresholdRatios = deepScan ? [0.46, 0.38, 0.54, 0.62] : [0.48, 0.56]
  const codeSizeRatios = deepScan ? [0.64, 0.73, 0.57, 0.8, 0.5] : [0.68, 0.76, 0.6]
  const offsets = deepScan ? [0, -0.08 * canvasSize, 0.08 * canvasSize] : [0]
  for (const scale of scales) {
    const sourceSize = Math.min(width, height) * scale
    const sx = (width - sourceSize) / 2
    const sy = (height - sourceSize) / 2
    ctx.drawImage(video, sx, sy, sourceSize, sourceSize, 0, 0, canvasSize, canvasSize)
    const image = ctx.getImageData(0, 0, canvasSize, canvasSize)
    let min = 255
    let max = 0
    for (let i = 0; i < image.data.length; i += 16) {
      const luma = walletPixelLuma(image.data, i)
      min = Math.min(min, luma)
      max = Math.max(max, luma)
    }
    if (max - min < 32) continue
    for (const thresholdRatio of thresholdRatios) {
      const threshold = min + (max - min) * thresholdRatio
      if (deepScan || thresholdRatio > 0.47) {
        for (const codeSizeRatio of codeSizeRatios) {
          const codeSize = canvasSize * codeSizeRatio
          for (const offsetY of offsets) {
            for (const offsetX of offsets) {
              const left = canvasSize / 2 - codeSize / 2 + offsetX
              const top = canvasSize / 2 - codeSize / 2 + offsetY
              const decoded = walletSampleCodeGrid(image, canvasSize, left, top, left + codeSize, top + codeSize, threshold, sampleRadius)
              if (decoded) return decoded
            }
          }
        }
      }
      if (!deepScan) continue

      let left = canvasSize, top = canvasSize, right = 0, bottom = 0, count = 0
      for (let y = 0; y < canvasSize; y += 2) {
        for (let x = 0; x < canvasSize; x += 2) {
          const index = (y * canvasSize + x) * 4
          if (walletPixelLuma(image.data, index) < threshold) {
            left = Math.min(left, x)
            top = Math.min(top, y)
            right = Math.max(right, x)
            bottom = Math.max(bottom, y)
            count += 1
          }
        }
      }
      if (count < 240 || right - left < 120 || bottom - top < 120) continue

      const boxSize = Math.max(right - left, bottom - top)
      const centerX = (left + right) / 2
      const centerY = (top + bottom) / 2
      left = Math.max(0, centerX - boxSize / 2)
      right = Math.min(canvasSize - 1, centerX + boxSize / 2)
      top = Math.max(0, centerY - boxSize / 2)
      bottom = Math.min(canvasSize - 1, centerY + boxSize / 2)

      const decoded = walletSampleCodeGrid(image, canvasSize, left, top, right, bottom, threshold, sampleRadius)
      if (decoded) return decoded
    }
  }
  return ""
}

function SendPanel({
  onClose,
  balance,
  onSendCredits,
}: {
  onClose:()=>void
  balance: number
  onSendCredits?: DirectWalletDashboardProps["onSendCredits"]
}) {
  const [step,setStep] = useState<"form"|"confirm"|"done">("form")
  const [to,setTo] = useState(""), [amt,setAmt] = useState(""), [note,setNote] = useState("")
  const [busy,setBusy] = useState(false)
  const [scannerOpen,setScannerOpen] = useState(false)
  const [scannerBusy,setScannerBusy] = useState(false)
  const [scannerError,setScannerError] = useState("")
  const videoRef = useRef<HTMLVideoElement|null>(null)
  const streamRef = useRef<MediaStream|null>(null)
  const scanTimerRef = useRef<number|null>(null)
  const scanCanvasRef = useRef<HTMLCanvasElement|null>(null)
  const scanAttemptRef = useRef(0)
  const ready = to.trim().length>0 && parseFloat(amt)>0
  const scannerStatus = scannerError || (scannerBusy ? "Opening camera" : "Scanning live")

  function stopScanner() {
    if (scanTimerRef.current !== null) {
      window.clearTimeout(scanTimerRef.current)
      scanTimerRef.current = null
    }
    streamRef.current?.getTracks().forEach(track=>track.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
    scanAttemptRef.current = 0
    setScannerBusy(false)
    setScannerOpen(false)
  }

  function scanCurrentFrame() {
    const video = videoRef.current
    if (!video) return
    const canvas = scanCanvasRef.current || document.createElement("canvas")
    scanCanvasRef.current = canvas
    const deepScan = scanAttemptRef.current % 4 === 0
    scanAttemptRef.current += 1
    const recipient = walletReadCodeFromVideo(video, canvas, deepScan)
    if (recipient) {
      setTo(recipient)
      setScannerError("")
      stopScanner()
      return
    }
    scanTimerRef.current = window.setTimeout(scanCurrentFrame, deepScan ? 90 : 55)
  }

  async function startScanner() {
    if (scannerBusy || streamRef.current) {
      stopScanner()
      return
    }
    setScannerError("")
    setScannerOpen(true)
    setScannerBusy(true)
    scanAttemptRef.current = 0
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("No camera was found.")
      await new Promise<void>((resolve)=>window.requestAnimationFrame(()=>resolve()))
      const video = videoRef.current
      if (!video) {
        setScannerError("Camera view is not ready.")
        setScannerBusy(false)
        return
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 960 },
        },
      })
      streamRef.current = stream
      video.srcObject = stream
      await video.play()
      setScannerBusy(false)
      scanCurrentFrame()
    } catch (error) {
      setScannerError(scannerErrorText(error))
      stopScanner()
      setScannerOpen(true)
    }
  }

  function closePanel() {
    stopScanner()
    onClose()
  }

  useEffect(() => () => {
    if (scanTimerRef.current !== null) window.clearTimeout(scanTimerRef.current)
    streamRef.current?.getTracks().forEach(track=>track.stop())
    streamRef.current = null
  }, [])

  async function confirmSend() {
    if (!ready || busy) return
    setBusy(true)
    try {
      if (onSendCredits) {
        await onSendCredits({ recipient: to.trim(), amountCredits: parseFloat(amt), note: note.trim() })
      }
      setStep("done")
    } catch {
      // The route-level callback owns the user-facing toast.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel onClose={closePanel}>
      <PanelHead title="Send Credits" onClose={closePanel} />
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <AnimatePresence mode="wait">
          {step==="form" && (
            <motion.div key="form" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="flex flex-col gap-4">
              <div>
                <Label>Recipient</Label>
                <div className="relative mt-2">
                  <input className="w-full px-4 py-3 pr-12 rounded-xl text-[14px] outline-none" style={G.field} placeholder="@handle or member email" value={to} onChange={e=>setTo(e.target.value)} />
                  <motion.button
                    type="button"
                    aria-label="Scan recipient code"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center"
                    style={{...G.subtle, boxShadow:scannerOpen ? "0 0 0 3px rgba(255,255,255,0.18)" : undefined}}
                    animate={scannerOpen ? {scale:[1,1.05,1]} : {scale:1}}
                    transition={scannerOpen ? {duration:1.35,repeat:Infinity,ease:"easeInOut"} : {duration:0.16}}
                    whileHover={{scale:1.05}}
                    whileTap={{scale:0.92}}
                    onClick={startScanner}>
                    {scannerBusy ? <LoaderCircle size={16} className="animate-spin" style={{color:"rgba(0,0,0,0.58)"}} /> : <Camera size={16} style={{color:"rgba(0,0,0,0.58)"}} />}
                  </motion.button>
                </div>
                <AnimatePresence>
                  {scannerOpen && (
                    <motion.div initial={{opacity:0,height:0}} animate={{opacity:1,height:"auto"}} exit={{opacity:0,height:0}} className="overflow-hidden">
                      <div className="mt-3 overflow-hidden rounded-2xl p-3" style={{...G.inset,border:"1px solid rgba(255,255,255,0.42)"}}>
                        <div className="relative overflow-hidden rounded-xl aspect-[4/3]" style={{background:"linear-gradient(135deg, rgba(8,10,12,0.92), rgba(25,30,32,0.82))"}}>
                          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
                          <div className="pointer-events-none absolute inset-0" style={{background:"radial-gradient(circle at 50% 42%, transparent 0%, transparent 45%, rgba(0,0,0,0.30) 100%)"}} />
                          <motion.div
                            className="pointer-events-none absolute inset-[15%] rounded-[22px]"
                            style={{border:"1px solid rgba(255,255,255,0.36)", boxShadow:"0 0 0 999px rgba(0,0,0,0.18)"}}
                            animate={{boxShadow:["0 0 0 999px rgba(0,0,0,0.16), 0 0 18px rgba(255,255,255,0.12)","0 0 0 999px rgba(0,0,0,0.24), 0 0 34px rgba(255,255,255,0.28)","0 0 0 999px rgba(0,0,0,0.16), 0 0 18px rgba(255,255,255,0.12)"]}}
                            transition={{duration:1.8,repeat:Infinity,ease:"easeInOut"}}
                          />
                          {[
                            {key:"tl",style:{left:18,top:18,borderTop:"2px solid rgba(255,255,255,0.88)",borderLeft:"2px solid rgba(255,255,255,0.88)",borderTopLeftRadius:18}},
                            {key:"tr",style:{right:18,top:18,borderTop:"2px solid rgba(255,255,255,0.88)",borderRight:"2px solid rgba(255,255,255,0.88)",borderTopRightRadius:18}},
                            {key:"bl",style:{left:18,bottom:18,borderBottom:"2px solid rgba(255,255,255,0.88)",borderLeft:"2px solid rgba(255,255,255,0.88)",borderBottomLeftRadius:18}},
                            {key:"br",style:{right:18,bottom:18,borderBottom:"2px solid rgba(255,255,255,0.88)",borderRight:"2px solid rgba(255,255,255,0.88)",borderBottomRightRadius:18}},
                          ].map(corner=>(
                            <span key={corner.key} className="pointer-events-none absolute h-12 w-12" style={corner.style} />
                          ))}
                          {!scannerError && (
                            <motion.div
                              className="pointer-events-none absolute left-[15%] right-[15%] h-[2px] rounded-full"
                              style={{background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.96), transparent)", boxShadow:"0 0 18px rgba(255,255,255,0.78), 0 0 36px rgba(45,212,191,0.26)"}}
                              animate={{top:["18%","82%","18%"]}}
                              transition={{duration:2.1,repeat:Infinity,ease:"easeInOut"}}
                            />
                          )}
                          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                            <div className="min-w-0 flex items-center gap-2 rounded-full px-3 py-1.5" style={{background:"rgba(255,255,255,0.18)",backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",border:"1px solid rgba(255,255,255,0.26)"}}>
                              {scannerBusy ? (
                                <LoaderCircle size={13} className="animate-spin flex-shrink-0" style={{color:"rgba(255,255,255,0.86)"}} />
                              ) : scannerError ? (
                                <X size={13} className="flex-shrink-0" style={{color:"rgba(254,202,202,0.95)"}} />
                              ) : (
                                <motion.span className="h-2 w-2 flex-shrink-0 rounded-full" style={{background:"#86efac",boxShadow:"0 0 12px rgba(134,239,172,0.85)"}} animate={{opacity:[0.45,1,0.45],scale:[0.9,1.2,0.9]}} transition={{duration:1.05,repeat:Infinity,ease:"easeInOut"}} />
                              )}
                              <span className="truncate text-[11px] font-semibold" style={{color:scannerError?"rgba(254,202,202,0.96)":"rgba(255,255,255,0.86)"}}>{scannerStatus}</span>
                              {!scannerBusy && !scannerError && (
                                <span className="flex items-end gap-0.5">
                                  {[0,1,2].map(i=>(
                                    <motion.span
                                      key={i}
                                      className="w-0.5 rounded-full"
                                      style={{background:"rgba(255,255,255,0.72)"}}
                                      animate={{height:[5,11,7,13,5],opacity:[0.45,0.92,0.62,1,0.45]}}
                                      transition={{duration:1.05,repeat:Infinity,ease:"easeInOut",delay:i*0.14}}
                                    />
                                  ))}
                                </span>
                              )}
                            </div>
                            <motion.button type="button" className="rounded-full px-3 py-1.5 text-[11px] font-semibold" style={{background:"rgba(255,255,255,0.18)",color:"rgba(255,255,255,0.86)",border:"1px solid rgba(255,255,255,0.24)",backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)"}} whileTap={{scale:0.94}} onClick={stopScanner}>Close</motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {scannerError && !scannerOpen && <p className="text-[11px] mt-2" style={{color:"#b91c1c"}}>{scannerError}</p>}
              </div>
              <div>
                <Label>Amount (UC)</Label>
                <input className="w-full mt-2 px-4 py-4 rounded-xl text-[36px] outline-none" style={{...G.field,fontWeight:300,fontStyle:"italic"}} placeholder="0" value={amt} onChange={e=>setAmt(e.target.value)} />
                <p className="text-[12px] mt-1.5" style={{color:"rgba(0,0,0,0.38)"}}>Available: {balance.toLocaleString("en-US",{minimumFractionDigits:2})} UC</p>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <input className="w-full mt-2 px-4 py-3 rounded-xl text-[14px] outline-none" style={G.field} placeholder="What's it for?" value={note} onChange={e=>setNote(e.target.value)} />
              </div>
              <motion.button className="w-full py-3.5 rounded-2xl text-[14px] font-semibold text-white mt-2" style={{background:ready?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.25)"}} whileTap={{scale:0.97}} onClick={()=>ready&&setStep("confirm")} disabled={!ready}>
                Review Transfer
              </motion.button>
            </motion.div>
          )}
          {step==="confirm" && (
            <motion.div key="confirm" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="flex flex-col gap-4">
              <div className="rounded-2xl p-5 flex flex-col gap-3 text-center" style={G.inset}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em]" style={{color:"rgba(0,0,0,0.38)"}}>Sending</p>
                <p className="text-[48px] leading-none" style={{fontWeight:300,letterSpacing:"-0.02em"}}>{amt} UC</p>
                <p className="text-[14px]" style={{color:"rgba(0,0,0,0.5)"}}>to <span className="font-semibold text-foreground">{to}</span></p>
                {note && <p className="text-[12px] italic" style={{color:"rgba(0,0,0,0.38)"}}>{note}</p>}
              </div>
              <p className="text-[12px] text-center" style={{color:"rgba(0,0,0,0.38)"}}>Balance after: {(balance-parseFloat(amt)).toLocaleString()} UC</p>
              <div className="flex gap-3">
                <motion.button className="flex-1 py-3 rounded-2xl text-[14px] font-medium" style={G.subtle} whileTap={{scale:0.97}} onClick={()=>setStep("form")}>Back</motion.button>
                <motion.button className="flex-1 py-3 rounded-2xl text-[14px] font-semibold text-white" style={{background:busy?"rgba(14,12,8,0.45)":"rgba(14,12,8,0.9)"}} whileTap={{scale:0.97}} onClick={confirmSend} disabled={busy}>{busy ? "Sending..." : "Confirm Send"}</motion.button>
              </div>
            </motion.div>
          )}
          {step==="done" && (
            <motion.div key="done" initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} exit={{opacity:0}} className="flex flex-col items-center gap-4 py-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{background:"rgba(34,197,94,0.12)"}}><Check size={24} color="#15803d" /></div>
              <div className="text-center"><p className="text-[16px] font-semibold">Transfer sent</p><p className="text-[13px] mt-1" style={{color:"rgba(0,0,0,0.5)"}}>{amt} UC → {to}</p></div>
              <motion.button className="w-full py-3 rounded-2xl text-[14px] font-semibold text-white" style={{background:"rgba(14,12,8,0.9)"}} whileTap={{scale:0.97}} onClick={onClose}>Done</motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Panel>
  )
}

// ─── Add panel ────────────────────────────────────────────────────────────────

function AddPanel({
  onClose,
  balance,
  onAddCredits,
}: {
  onClose:()=>void
  balance: number
  onAddCredits?: DirectWalletDashboardProps["onAddCredits"]
}) {
  const PRESETS=[25,50,100,250,500]
  const [sel,setSel]=useState(100)
  const [other,setOther]=useState(false)
  const [customAmount,setCustomAmount]=useState("")
  const [done,setDone]=useState(false)
  const [busy,setBusy]=useState(false)
  const rawAmount = other ? Number(customAmount) : sel
  const amount = Number.isFinite(rawAmount) ? Math.max(0, rawAmount) : 0
  const amountText = amount.toLocaleString("en-US", { maximumFractionDigits: 2 })
  const total = amount
  const hasAmount = amount > 0
  const canAdd = hasAmount && !busy
  const customInputWidth = `${Math.min(7.5, Math.max(0.72, (customAmount.length || 1) * 0.58))}em`

  function updateCustomAmount(value: string) {
    const cleaned = value.replace(/[^\d.]/g, "")
    const [whole, ...decimal] = cleaned.split(".")
    setCustomAmount(decimal.length ? `${whole}.${decimal.join("").slice(0, 2)}` : whole)
  }

  async function addCredits() {
    if (!canAdd) return
    setBusy(true)
    try {
      if (onAddCredits) {
        await onAddCredits(amount)
      } else {
        setDone(true)
      }
    } catch {
      // The route-level callback owns the user-facing toast.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel onClose={onClose}>
      <PanelHead title="Add Credits" onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {done ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{background:"rgba(34,197,94,0.12)"}}><Check size={24} color="#15803d" /></div>
            <div className="text-center"><p className="text-[16px] font-semibold">Credits added</p><p className="text-[13px] mt-1" style={{color:"rgba(0,0,0,0.5)"}}>{amountText} UC · new balance {(balance+amount).toLocaleString(undefined,{maximumFractionDigits:2})} UC</p></div>
            <motion.button className="w-full py-3 rounded-2xl text-[14px] font-semibold text-white" style={{background:"rgba(14,12,8,0.9)"}} whileTap={{scale:0.97}} onClick={onClose}>Done</motion.button>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="text-center py-2">
              <div className="h-[72px] flex items-center justify-center">
                {other ? (
                  <label className="h-full flex items-center justify-center leading-none cursor-text" style={{fontSize:64,fontWeight:300,letterSpacing:"-0.03em"}}>
                    <span>$</span>
                  <input
                    autoFocus
                    aria-label="Dollar amount"
                    type="text"
                    inputMode="decimal"
                    className="h-full bg-transparent border-0 p-0 outline-none text-left tabular-nums"
                    style={{width:customInputWidth,fontSize:"inherit",fontWeight:"inherit",letterSpacing:"inherit",lineHeight:1}}
                    placeholder="0"
                    value={customAmount}
                    onChange={e=>updateCustomAmount(e.target.value)}
                  />
                  </label>
                ) : (
                  <p className="h-full flex items-center justify-center text-[64px] leading-none" style={{fontWeight:300,letterSpacing:"-0.03em"}}>${amountText}</p>
                )}
              </div>
              <p className="text-[13px] mt-2" style={{color:"rgba(0,0,0,0.4)"}}>= {amountText} Universal Credits</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {PRESETS.map(p=>(
                <motion.button key={p} onClick={()=>{setSel(p);setOther(false)}} className="px-4 py-2 rounded-xl text-[13px] font-medium" style={!other&&sel===p?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{...G.subtle,color:"rgba(0,0,0,0.7)"}} whileTap={{scale:0.96}}>${p.toLocaleString()}</motion.button>
              ))}
              <motion.button onClick={()=>setOther(true)} className="px-4 py-2 rounded-xl text-[13px] font-medium" style={other?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{...G.subtle,color:"rgba(0,0,0,0.7)"}} whileTap={{scale:0.96}}>Other</motion.button>
            </div>
            <div className="rounded-2xl p-4 flex flex-col gap-2.5" style={G.inset}>
              {[["Credits",`${amountText} UC`],["Processing fee","Included"]].map(([l,v])=>(
                <div key={l} className="flex justify-between text-[13px]"><span style={{color:"rgba(0,0,0,0.5)"}}>{l}</span><span>{v}</span></div>
              ))}
              <div className="h-px" style={{background:"rgba(0,0,0,0.07)"}} />
              <div className="flex justify-between text-[13px] font-semibold"><span>Total</span><span>${total.toFixed(2)}</span></div>
            </div>
            <motion.button className="w-full py-3.5 rounded-2xl text-[14px] font-semibold text-white flex items-center justify-center gap-2" style={{background:hasAmount?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.45)"}} whileTap={{scale:0.97}} onClick={addCredits} disabled={!canAdd} aria-busy={busy}>
              {busy ? <><LoaderCircle size={16} className="animate-spin" /> Processing</> : `Add $${amountText} Credits`}
            </motion.button>
            <p className="text-center text-[11px]" style={{color:"rgba(0,0,0,0.35)"}}><Shield size={11} className="inline mr-1" />Secured by Stripe · No stored card data</p>
          </div>
        )}
      </div>
    </Panel>
  )
}

// ─── Receive panel ────────────────────────────────────────────────────────────

function walletMarkHash(seed: string) {
  let hash = 2166136261
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function WalletAddressMark({ user }: { user: DirectWalletUser }) {
  const cells = useMemo(() => walletAddressCodeCells(user), [user.id, user.handle])
  const cell = 11
  const gap = 4
  const margin = 12
  const size = margin * 2 + WALLET_CODE_SIZE * cell + (WALLET_CODE_SIZE - 1) * gap
  return (
    <div className="relative w-full max-w-[280px] rounded-[28px] p-4 sm:rounded-[34px] sm:p-7" style={{background:"rgba(255,252,244,0.24)"}}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={252}
        height={252}
        role="img"
        aria-label="GENRL receive barcode"
        className="block h-auto w-full max-w-[252px]"
        shapeRendering="geometricPrecision">
        {cells.map((row, rowIndex)=>
          row.map((dark, colIndex)=>{
            const x = margin + colIndex * (cell + gap)
            const y = margin + rowIndex * (cell + gap)
            return (
              <rect
                key={`${rowIndex}-${colIndex}`}
                x={x}
                y={y}
                width={cell}
                height={cell}
                rx={3.2}
                fill={dark ? "rgba(31,29,24,0.94)" : "rgba(0,0,0,0.06)"}
              />
            )
          }),
        )}
      </svg>
    </div>
  )
}

function ReceivePanel({ onClose, user }: { onClose:()=>void; user: DirectWalletUser }) {
  const [copied,setCopied]=useState(false)
  const addr=`${user.handle} · ${user.id}`
  return (
    <Panel onClose={onClose}>
      <PanelHead title="Receive Credits" onClose={onClose} />
      <div className="px-6 py-6 flex flex-col gap-5">
        <div className="rounded-2xl p-4 flex items-center justify-center sm:p-8" style={G.inset}>
          <WalletAddressMark user={user} />
        </div>
        <div className="flex flex-col items-stretch justify-between gap-3 rounded-2xl px-5 py-4 sm:flex-row sm:items-center" style={G.inset}>
          <div className="min-w-0">
            <Label>Your address</Label>
            <p className="text-[14px] font-medium mt-1 break-all">{addr}</p>
          </div>
          <motion.button whileTap={{scale:0.9}} onClick={()=>{navigator.clipboard.writeText(addr).catch(()=>{}); setCopied(true); setTimeout(()=>setCopied(false),2000)}}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all sm:py-1.5"
            style={copied?{background:"rgba(14,12,8,0.85)",color:"#fff"}:{...G.subtle,color:"rgba(0,0,0,0.6)"}}>
            {copied?<><Check size={12}/> Copied</>:<><Copy size={12}/> Copy</>}
          </motion.button>
        </div>
        <p className="text-[12px] text-center" style={{color:"rgba(0,0,0,0.38)"}}>Share your address to receive Universal Credits from any GENRL user.</p>
      </div>
    </Panel>
  )
}

// ─── More panel ───────────────────────────────────────────────────────────────

function MorePanel({
  onClose,
  onNavigate,
  user,
  onExportLedger,
  onSignOut,
}: {
  onClose:()=>void
  onNavigate:(p:Panel)=>void
  user: DirectWalletUser
  onExportLedger?: () => void
  onSignOut?: () => void
}) {
  const items=[
    { I:Settings, l:"Settings",       s:"Account · appearance · sessions",  action:()=>onNavigate("settings") },
    { I:Star,     l:"Subscriptions",  s:"Individual · Pro",                 action:()=>onNavigate("subscriptions") },
    { I:Layers,   l:"Reservations",   s:"Pending charges · open reserves",  action:()=>onNavigate("run") },
    { I:Activity, l:"Usage report",   s:"Export ledger as CSV",             action:()=>onExportLedger?.() },
    { I:Shield,   l:"Security events",s:"Auth log · active sessions",       action:()=>onNavigate("workspace") },
    { I:LogOut,   l:"Sign out",       s:user.handle, danger:true,           action:()=>onSignOut?.() },
  ]
  return (
    <Panel onClose={onClose}>
      <div className="px-6 pt-5 pb-3 flex items-center gap-3 border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
        <Avatar initials={user.initials} active={true} size={36} />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold">{user.name}</p>
          <p className="text-[11px]" style={{color:"rgba(0,0,0,0.4)"}}>{user.handle} · {user.id} · {user.plan}</p>
        </div>
        <motion.button whileTap={{scale:0.88}} onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset}>
          <X size={15} style={{color:"rgba(0,0,0,0.5)"}} />
        </motion.button>
      </div>
      <div className="px-3 py-3">
        {items.map(({I,l,s,danger,action})=>(
          <motion.button key={l} whileTap={{scale:0.98}} onClick={action}
            className="w-full flex items-center gap-3.5 px-3 py-3.5 rounded-2xl text-left hover:bg-black/[0.03] transition-colors">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{background:danger?"rgba(220,38,38,0.08)":"rgba(0,0,0,0.05)"}}>
              <I size={15} style={{color:danger?"#dc2626":"rgba(0,0,0,0.45)"}} />
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-semibold" style={{color:danger?"#dc2626":undefined}}>{l}</p>
              <p className="text-[11px]" style={{color:"rgba(0,0,0,0.38)"}}>{s}</p>
            </div>
            {!danger && <ChevronRight size={14} style={{color:"rgba(0,0,0,0.2)"}} />}
          </motion.button>
        ))}
      </div>
    </Panel>
  )
}

function SubscriptionsPanel({
  onClose,
  workspaceInfo,
}: {
  onClose:()=>void
  workspaceInfo: DirectWalletWorkspaceInfo
}) {
  return (
    <Panel onClose={onClose} wide>
      <PanelHead title="Subscriptions" onClose={onClose} />
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <WalletSubscriptionChooser
          workspaceId={workspaceInfo.id}
          workspaceName={workspaceInfo.name}
          workspaceType={workspaceInfo.workspaceType}
          canManageBilling={workspaceInfo.canManageBilling}
        />
      </div>
    </Panel>
  )
}

function formatWalletAccountSessionAgent(value?: string) {
  const raw = String(value || "").trim()
  if (!raw) return "Unknown browser"

  const browser =
    /Edg\//.test(raw) ? "Edge" :
    /Chrome\//.test(raw) && /Safari\//.test(raw) ? "Chrome" :
    /Firefox\//.test(raw) ? "Firefox" :
    /Version\/.*Safari\//.test(raw) ? "Safari" :
    ""
  const os =
    /iPhone/.test(raw) ? "iPhone" :
    /iPad/.test(raw) ? "iPad" :
    /Android/.test(raw) ? "Android" :
    /Mac OS X|Macintosh/.test(raw) ? "macOS" :
    /Windows NT/.test(raw) ? "Windows" :
    /Linux/.test(raw) ? "Linux" :
    ""

  if (browser || os) return [browser || "Browser", os].filter(Boolean).join(" · ")
  return raw.length > 56 ? `${raw.slice(0, 53)}...` : raw
}

function formatWalletAccountSessionLastSeen(session: DirectWalletAccountSession) {
  if (session.current) return "Active now"
  const raw = String(session.lastSeenAt || session.createdAt || "").trim()
  const timestamp = raw ? Date.parse(raw) : Number.NaN
  if (Number.isNaN(timestamp)) return "last seen unknown"
  return `last seen ${timeAgo(timestamp)}`
}

function formatWalletAccountSessionMeta(session: DirectWalletAccountSession) {
  return `${session.ipAddress || "Unknown IP"} · ${formatWalletAccountSessionLastSeen(session)}`
}

// ─── Settings panel ───────────────────────────────────────────────────────────

function SettingsPanel({ onClose, bg, setBg, lightText, setLightText, user, onUpdateUser, accountSessions = [], accountSessionsLoading = false, accountSessionsError = "", revokingAccountSessionId = "", onRefreshAccountSessions, onRevokeAccountSession, onRevokeOtherAccountSessions }: {
  onClose: ()=>void
  bg: string
  setBg: (v:string)=>void
  lightText: boolean
  setLightText: (v:boolean)=>void
  user: DirectWalletUser
  onUpdateUser?: DirectWalletDashboardProps["onUpdateUser"]
  accountSessions?: DirectWalletAccountSession[]
  accountSessionsLoading?: boolean
  accountSessionsError?: string
  revokingAccountSessionId?: string
  onRefreshAccountSessions?: DirectWalletDashboardProps["onRefreshAccountSessions"]
  onRevokeAccountSession?: DirectWalletDashboardProps["onRevokeAccountSession"]
  onRevokeOtherAccountSessions?: DirectWalletDashboardProps["onRevokeOtherAccountSessions"]
}) {
  const initialSettings = readWalletAppearance(bg, user.id)
  const [name,    setName]    = useState(user.name)
  const [handle,  setHandle]  = useState(user.handle)
  const [email,   setEmail]   = useState(user.email || "")
  const [saved,   setSaved]   = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [notifWorkload, setNotifWorkload] = useState(initialSettings.notifications.workload)
  const [notifBilling,  setNotifBilling]  = useState(initialSettings.notifications.billing)
  const [notifTeam,     setNotifTeam]     = useState(initialSettings.notifications.team)
  const [bgImage, setBgImage] = useState<string|null>(() => extractWalletBackgroundImage(bg) || null)
  const [appearanceError, setAppearanceError] = useState("")

  useEffect(() => {
    setBgImage(extractWalletBackgroundImage(bg) || null)
  }, [bg])

  useEffect(() => {
    const next = readWalletAppearance(bg, user.id).notifications
    setNotifWorkload(next.workload)
    setNotifBilling(next.billing)
    setNotifTeam(next.team)
  }, [bg, user.id])

  const activeOtherAccountSessions = accountSessions.filter(session => !session.current && session.status === "active")

  async function handleSave() {
    setSaving(true)
    try {
      await onUpdateUser?.({ name: name.trim(), handle: handle.trim(), email: email.trim() })
      setSaved(true)
      setTimeout(()=>setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  async function handleImageUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setAppearanceError("")
    try {
      const image = await readWalletBackgroundFile(file)
      setBgImage(image)
      setBg(walletBackgroundCSSImage(image))
    } catch (error) {
      setAppearanceError(error instanceof Error ? error.message : "Could not save that image.")
    } finally {
      e.target.value = ""
    }
  }

  function setNotification(key: "workload"|"billing"|"team", value: boolean) {
    const next = {
      workload: notifWorkload,
      billing: notifBilling,
      team: notifTeam,
      [key]: value,
    }
    setNotifWorkload(next.workload)
    setNotifBilling(next.billing)
    setNotifTeam(next.team)
    writeWalletNotificationSettings(next, user.id)
  }

  function SectionTitle({ children }: { children: ReactNode }) {
    return <p className="text-[11px] font-semibold uppercase tracking-[0.15em] px-1 mb-2" style={{color:"rgba(0,0,0,0.38)"}}>{children}</p>
  }

  return (
    <Panel onClose={onClose}>
      <PanelHead title="Settings" onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7" style={{scrollbarWidth:"none"}}>

        {/* ── Profile ── */}
        <section>
          <SectionTitle>Profile</SectionTitle>
          <div className="rounded-2xl overflow-hidden space-y-px" style={G.subtle}>
            {/* Avatar row */}
            <div className="flex items-center gap-4 px-4 py-4">
              <Avatar initials={user.initials} active={true} size={48} />
              <div>
                <p className="text-[14px] font-semibold">{name || user.name}</p>
                <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.4)"}}>{handle || user.handle} · {user.id}</p>
              </div>
              <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{background:"rgba(14,12,8,0.85)",color:"white"}}>{user.plan}</span>
            </div>

            <div className="h-px mx-4" style={{background:"rgba(0,0,0,0.06)"}} />

            {/* Editable fields */}
            {[
              {label:"Display name", value:name,   set:setName,   placeholder:"Your name",   mono:false},
              {label:"Handle",       value:handle, set:setHandle, placeholder:"@handle",      mono:true },
              {label:"Email",        value:email,  set:setEmail,  placeholder:"you@email.com",mono:false},
            ].map(({label,value,set,placeholder,mono})=>(
              <div key={label} className="flex items-center gap-4 px-4 py-3.5 border-t" style={{borderColor:"rgba(0,0,0,0.05)"}}>
                <p className="text-[12px] font-medium w-28 flex-shrink-0" style={{color:"rgba(0,0,0,0.45)"}}>{label}</p>
                <input value={value} onChange={e=>set(e.target.value)} placeholder={placeholder}
                  className={`flex-1 bg-transparent text-[14px] outline-none ${mono?"font-mono":""}`}
                  style={{color:"rgba(14,12,8,0.85)"}} />
              </div>
            ))}
          </div>
          <motion.button whileTap={{scale:0.97}} onClick={()=>void handleSave()} disabled={saving}
            className="mt-3 w-full py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2"
            style={{background:"rgba(14,12,8,0.88)",color:"white",opacity:saving?0.72:1}}>
            {saving ? <><LoaderCircle size={14} className="animate-spin"/> Saving</> : saved ? <><Check size={14}/> Saved</> : "Save changes"}
          </motion.button>
        </section>

        {/* ── Appearance ── */}
        <section>
          <SectionTitle>Background</SectionTitle>
          <div className="grid grid-cols-3 gap-2.5 mb-3">
            {BG_PRESETS.map(p=>(
              <motion.button key={p.id} whileTap={{scale:0.96}} onClick={()=>{ setAppearanceError(""); setBg(p.value); setBgImage(null) }}
                className="relative h-16 rounded-2xl overflow-hidden border-2 transition-all"
                style={{
                  background: p.value,
                  backgroundSize:"cover",
                  backgroundPosition:"center",
                  borderColor: bg===p.value && !bgImage ? "rgba(14,12,8,0.7)" : "rgba(255,255,255,0.5)"
                }}>
                <span className="absolute bottom-1.5 left-0 right-0 text-center text-[10px] font-semibold"
                  style={{color:p.labelColor,textShadow:p.labelShadow}}>{p.label}</span>
                {bg===p.value && !bgImage && (
                  <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                    <Check size={10} style={{color:"rgba(14,12,8,0.8)"}} />
                  </div>
                )}
              </motion.button>
            ))}
          </div>

          {/* Upload custom image */}
          <label className="block cursor-pointer">
            <div className={`flex items-center justify-center gap-2.5 py-3.5 rounded-2xl text-[13px] font-medium transition-all border-2 border-dashed`}
              style={{
                borderColor: bgImage ? "rgba(14,12,8,0.4)" : "rgba(0,0,0,0.15)",
                background: bgImage ? "rgba(14,12,8,0.05)" : "transparent",
                color:"rgba(0,0,0,0.55)"
              }}>
              <Upload size={15} />
              {bgImage ? "Custom image applied — click to change" : "Upload custom background image"}
            </div>
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
          {appearanceError && (
            <p className="mt-2 px-1 text-[11px]" style={{color:"#b42318"}}>{appearanceError}</p>
          )}

          {bgImage && (
            <motion.button whileTap={{scale:0.96}} initial={{opacity:0}} animate={{opacity:1}}
              onClick={()=>{ setAppearanceError(""); setBgImage(null); setBg(PAGE_BG) }}
              className="mt-2 w-full py-2 rounded-xl text-[12px] font-medium text-center"
              style={{color:"rgba(0,0,0,0.45)"}}>
              Remove custom image
            </motion.button>
          )}

          {/* Light text toggle */}
          <div className="flex items-center justify-between rounded-2xl px-4 py-3.5 mt-3" style={G.subtle}>
            <div>
              <p className="text-[14px] font-medium">Light text on background</p>
              <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.42)"}}>
                Switch the home view to white text — useful for dark or photo backgrounds
              </p>
            </div>
            <Toggle value={lightText} onChange={setLightText} />
          </div>
        </section>

        {/* ── Notifications ── */}
        <section>
          <SectionTitle>Notifications</SectionTitle>
          <div className="rounded-2xl overflow-hidden" style={G.subtle}>
            {[
              {label:"Workload completed",  sub:"Alert when a run finishes",           v:notifWorkload, key:"workload" as const},
              {label:"Billing alerts",       sub:"Low balance and reservation warnings",v:notifBilling,  key:"billing" as const },
              {label:"Team activity",        sub:"When workspace members run jobs",     v:notifTeam,     key:"team" as const    },
            ].map(({label,sub,v,key},i)=>(
              <div key={label} className={`flex items-center justify-between px-4 py-4 ${i>0?"border-t":""}`}
                style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div>
                  <p className="text-[14px] font-medium">{label}</p>
                  <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.4)"}}>{sub}</p>
                </div>
                <Toggle value={v} onChange={next=>setNotification(key,next)} />
              </div>
            ))}
          </div>
        </section>

        {/* ── Active sessions ── */}
        <section>
          <div className="mb-2 flex items-center justify-between gap-3 px-1">
            <SectionTitle>Active sessions</SectionTitle>
            <motion.button
              type="button"
              whileTap={{scale:0.94}}
              onClick={()=>void onRefreshAccountSessions?.()}
              disabled={!onRefreshAccountSessions || accountSessionsLoading}
              className="flex h-7 w-7 items-center justify-center rounded-full disabled:opacity-45"
              style={G.inset}
              aria-label="Refresh active sessions">
              <RefreshCcw size={12} className={accountSessionsLoading ? "animate-spin" : ""} style={{color:"rgba(0,0,0,0.42)"}} />
            </motion.button>
          </div>
          <div className="rounded-2xl overflow-hidden" style={G.subtle}>
            {accountSessionsLoading && accountSessions.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12px]" style={{color:"rgba(0,0,0,0.4)"}}>Loading sessions...</div>
            ) : accountSessionsError && accountSessions.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12px]" style={{color:"#b42318"}}>{accountSessionsError}</div>
            ) : accountSessions.length === 0 ? (
              <div className="px-4 py-6 text-center text-[12px]" style={{color:"rgba(0,0,0,0.4)"}}>No active sessions found.</div>
            ) : accountSessions.map((s,i)=>(
              <div key={s.id} className={`flex items-center gap-3 px-4 py-3.5 ${i>0?"border-t":""}`}
                style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-[13px] font-medium">{formatWalletAccountSessionAgent(s.userAgent)}</p>
                    {s.current && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{background:"rgba(34,197,94,0.12)",color:"#15803d"}}>current</span>
                    )}
                    {!s.current && s.status !== "active" && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={{background:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.45)"}}>{s.status}</span>
                    )}
                  </div>
                  <p className="truncate text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.4)"}}>{formatWalletAccountSessionMeta(s)}</p>
                </div>
                {!s.current && (
                  <motion.button
                    type="button"
                    whileTap={{scale:0.94}}
                    onClick={()=>void onRevokeAccountSession?.(s.id)}
                    disabled={!onRevokeAccountSession || s.status !== "active" || revokingAccountSessionId === s.id || revokingAccountSessionId === "all"}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-lg disabled:opacity-45"
                    style={{background:"rgba(220,38,38,0.08)",color:"#dc2626"}}>
                    {revokingAccountSessionId === s.id ? "Revoking" : "Revoke"}
                  </motion.button>
                )}
              </div>
            ))}
            {accountSessionsError && accountSessions.length > 0 && (
              <div className="border-t px-4 py-2 text-[11px]" style={{borderColor:"rgba(0,0,0,0.06)",color:"#b42318"}}>{accountSessionsError}</div>
            )}
          </div>
          <motion.button
            type="button"
            whileTap={{scale:0.97}}
            onClick={()=>void onRevokeOtherAccountSessions?.()}
            disabled={!onRevokeOtherAccountSessions || activeOtherAccountSessions.length === 0 || revokingAccountSessionId === "all"}
            className="mt-2 w-full py-2.5 rounded-xl text-[13px] font-semibold disabled:opacity-45"
            style={{background:"rgba(220,38,38,0.08)",color:"#dc2626"}}>
            {revokingAccountSessionId === "all" ? "Revoking sessions" : "Revoke all other sessions"}
          </motion.button>
        </section>

        {/* ── Danger zone ── */}
        <section className="pb-4">
          <SectionTitle>Account</SectionTitle>
          <div className="rounded-2xl overflow-hidden" style={G.subtle}>
            <motion.button whileTap={{scale:0.98}}
              className="w-full flex items-center gap-3.5 px-4 py-4 text-left hover:bg-black/[0.02] transition-colors">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:"rgba(220,38,38,0.08)"}}>
                <LogOut size={15} style={{color:"#dc2626"}} />
              </div>
              <div>
                <p className="text-[14px] font-semibold" style={{color:"#dc2626"}}>Sign out</p>
                <p className="text-[11px]" style={{color:"rgba(0,0,0,0.38)"}}>{user.handle}</p>
              </div>
            </motion.button>
          </div>
        </section>
      </div>
    </Panel>
  )
}

// ─── Run panel ────────────────────────────────────────────────────────────────

function walletRunFileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`
}

const WALLET_RUN_SUPPORTED_FILE_EXTENSIONS = new Set([".csv", ".tsv", ".json", ".jsonl", ".xls", ".xlsx", ".py", ".ipynb"])
const WALLET_RUN_SUPPORTED_FILE_LABEL = "CSV, TSV, JSON, JSONL, XLS, XLSX, or Python"
const WALLET_RUN_SUPPORTED_FILE_TYPES = new Set([
  "text/csv",
  "text/tab-separated-values",
  "application/json",
  "application/jsonl",
  "application/x-ndjson",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/x-python",
  "application/x-python-code",
  "application/x-ipynb+json",
  "application/vnd.jupyter",
])
const WALLET_RUN_FILE_ACCEPT = [
  ".csv",
  ".tsv",
  ".json",
  ".jsonl",
  ".xls",
  ".xlsx",
  ".py",
  ".ipynb",
  ...Array.from(WALLET_RUN_SUPPORTED_FILE_TYPES),
].join(",")

function walletRunFileExtension(file: File) {
  const match = String(file.name || "").toLowerCase().match(/\.[^.]+$/)
  return match ? match[0] : ""
}

function walletRunFileSupported(file: File) {
  const extension = walletRunFileExtension(file)
  if (WALLET_RUN_SUPPORTED_FILE_EXTENSIONS.has(extension)) return true
  const contentType = String(file.type || "").toLowerCase()
  return WALLET_RUN_SUPPORTED_FILE_TYPES.has(contentType) || contentType.includes("python") || contentType.includes("ipynb")
}

function walletRunUnsupportedFilesMessage(files: File[]) {
  const names = files.slice(0, 3).map(file=>file.name || "unnamed file")
  const suffix = files.length > 3 ? ` and ${files.length - 3} more` : ""
  const subject = files.length === 1 ? names[0] : `${names.join(", ")}${suffix}`
  return `${subject} ${files.length === 1 ? "is" : "are"} not supported. Upload a ${WALLET_RUN_SUPPORTED_FILE_LABEL} file.`
}

function appendUniqueWalletRunFiles(current: File[], incoming: FileList | File[]) {
  const next = [...current]
  const seen = new Set(next.map(walletRunFileKey))
  Array.from(incoming).forEach((file) => {
    const key = walletRunFileKey(file)
    if (seen.has(key)) return
    seen.add(key)
    next.push(file)
  })
  return next
}

function formatWalletRunFileSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB", "TB"]
  let value = size
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${unit === 0 ? value.toFixed(0) : value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`
}

const DEFAULT_RUNTIME_IDLE_MINUTES = 30
const MIN_RUNTIME_IDLE_MINUTES = 5
const MAX_RUNTIME_IDLE_MINUTES = 24 * 60

function normalizeRuntimeIdleMinutes(value: string | number) {
  const parsed = Math.floor(Number(value))
  if (!Number.isFinite(parsed)) return DEFAULT_RUNTIME_IDLE_MINUTES
  return Math.min(MAX_RUNTIME_IDLE_MINUTES, Math.max(MIN_RUNTIME_IDLE_MINUTES, parsed))
}

const WALLET_CREDIT_MICROS = 1_000_000
const WALLET_RUN_PRICE_BUFFER = 1.2
// Mirrors the backend's 10-minute completion window plus 2 minutes for teardown.
const WALLET_RUN_BILLING_OVERHEAD_MINUTES = 12

function normalizeWalletRunHardwareText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g," ")
}

function walletRunHardwareLaunchLabelFromText(value: string, fallback = "") {
  const text = normalizeWalletRunHardwareText(value)
  const has = (pattern: RegExp) => pattern.test(text)
  if (has(/\bh200\b/)) return "H200 141 GB"
  if (has(/\bh100\b/)) return "H100 80 GB"
  if (has(/\ba100\b/) && has(/\b40\b|40gb|40g/)) return "A100 40 GB"
  if (has(/\ba100\b/)) return "A100 80 GB"
  if (has(/\bl40s\b/)) return "L40S 48 GB"
  if (has(/\bl40\b/)) return "L40 48 GB"
  if (has(/\brtx\b.*\ba6000\b|\ba6000\b/)) return "RTX A6000 48 GB"
  if (has(/\ba10g?\b|\ba10\b/)) return "A10G 24 GB"
  if (has(/\bl4\b/)) return "L4 24 GB"
  if (has(/\bt4\b/)) return "T4 16 GB"
  return fallback
}

function walletRunBaseHardwareLaunchLabel(id: string, fallback = "") {
  if (id === "h100" || id === "h100-80gb") return "H100 80 GB"
  if (id === "h200") return "H200 141 GB"
  if (id === "a100-80g" || id === "a100-80gb") return "A100 80 GB"
  if (id === "a100-40g" || id === "a100-40gb") return "A100 40 GB"
  if (id === "a10" || id === "a10g-24gb") return "A10G 24 GB"
  if (id === "l40s") return "L40S 48 GB"
  if (id === "l40") return "L40 48 GB"
  if (id === "rtx-a6000") return "RTX A6000 48 GB"
  return fallback
}

function walletRunHardwareLaunchLabel(base: typeof HW[number], option: DirectWalletHardwareOption) {
  return walletRunHardwareLaunchLabelFromText(
    `${option.hardwareId} ${option.hardwareLabel}`,
    walletRunBaseHardwareLaunchLabel(base.id, option.hardwareLabel),
  )
}

function walletRunProHardwareLaunchLabel(base: { id: string; hardwareLabel: string }, option?: DirectWalletHardwareOption | null) {
  const fallback = walletRunBaseHardwareLaunchLabel(base.id, base.hardwareLabel)
  if (!option) return fallback
  return walletRunHardwareLaunchLabelFromText(`${option.hardwareId} ${option.hardwareLabel}`, fallback)
}

function walletRunHardwareHourlyCredits(option?: DirectWalletHardwareOption | null) {
  if (!option) return 0
  const gpuCount = Math.max(1, Number(option.gpuCount || 1))
  const buyerHourly = Number(option.bestBuyerMicros || 0) > 0
    ? Number(option.bestBuyerMicros) / WALLET_CREDIT_MICROS
    : Number(option.bestPricePerHour || 0)
  return Number.isFinite(buyerHourly) && buyerHourly > 0 ? buyerHourly * gpuCount : 0
}

function formatWalletRunHourlyPrice(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "Live price unavailable"
  const maximumFractionDigits = value < 1 ? 3 : value < 10 ? 2 : 1
  return `${value.toLocaleString(undefined,{maximumFractionDigits})} UC/hr`
}

function WalletRunHourlyPrice({
  hourlyPriceCredits,
  originalHourlyPriceCredits,
  discountPercent,
  fromProAccount,
  fallback,
  dark = false,
  align = "left",
  size = "row",
  className = "",
}: {
  hourlyPriceCredits: number
  originalHourlyPriceCredits: number
  discountPercent: number
  fromProAccount: boolean
  fallback: string
  dark?: boolean
  align?: "left" | "right"
  size?: "card" | "row" | "summary"
  className?: string
}) {
  const hasHourlyPrice = Number.isFinite(hourlyPriceCredits) && hourlyPriceCredits > 0
  const discounted = hasHourlyPrice &&
    fromProAccount &&
    Number.isFinite(originalHourlyPriceCredits) &&
    originalHourlyPriceCredits > hourlyPriceCredits &&
    discountPercent > 0
  const priceClassName = size === "summary"
    ? "text-[13px]"
    : size === "card"
      ? "text-[10px]"
      : "text-[12px]"

  if (!discounted) {
    const label = hasHourlyPrice ? formatWalletRunHourlyPrice(hourlyPriceCredits) : fallback
    return <span className={`${className} ${priceClassName} font-semibold`}>{label}</span>
  }

  const currentPrice = formatWalletRunHourlyPrice(hourlyPriceCredits)
  const originalPrice = formatWalletRunHourlyPrice(originalHourlyPriceCredits)
  const accessiblePrice = `${currentPrice} after ${discountPercent}% discount from a Pro account; original ${originalPrice}`
  const alignClassName = align === "right" ? "items-end text-right" : "items-start text-left"
  return (
    <span className={`${className} flex flex-col ${alignClassName}`}>
      <span className="sr-only">{accessiblePrice}</span>
      <span aria-hidden="true">
        <span className={`flex flex-wrap items-center gap-1 ${align === "right" ? "justify-end" : "justify-start"}`}>
          <s
            className="text-[9px] font-medium"
            style={{color:dark?"rgba(255,255,255,0.52)":"rgba(0,0,0,0.4)"}}>
            {originalPrice}
          </s>
          <span
            className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.04em]"
            style={{
              background:dark?"rgba(255,255,255,0.14)":"rgba(22,101,52,0.1)",
              color:dark?"rgba(255,255,255,0.82)":"rgba(20,83,45,0.9)",
            }}>
            Pro account · {discountPercent}% off
          </span>
        </span>
        <span className={`mt-0.5 block ${priceClassName} font-semibold`}>{currentPrice}</span>
      </span>
    </span>
  )
}

function walletRunHardwareEstimatedMinutesForLabel(label: string) {
  const text = normalizeWalletRunHardwareText(label)
  return /\bh100\b|\bh200\b/.test(text) ? 4 : 8
}

function walletRunReserveCredits(hourlyCredits: number, estimatedMinutes: number) {
  if (!Number.isFinite(hourlyCredits) || hourlyCredits <= 0) return 0
  const estimated = hourlyCredits * Math.max(1, estimatedMinutes + WALLET_RUN_BILLING_OVERHEAD_MINUTES) / 60
  return Math.max(0.01, Math.ceil(estimated * WALLET_RUN_PRICE_BUFFER * 100) / 100)
}

function walletRunHardwareIconForText(value: string): typeof DollarSign {
  const text = normalizeWalletRunHardwareText(value)
  if (/\bh100\b|\bh200\b/.test(text)) return Star
  if (/\ba100\b|\bl40s?\b|\ba40\b/.test(text)) return Zap
  return DollarSign
}

function walletRunStaticHardwareChoice(base: typeof HW[number]): WalletRunHardwareChoice {
  const hardwareLabel = walletRunBaseHardwareLaunchLabel(base.id, base.label)
  const estimatedMinutes = walletRunHardwareEstimatedMinutesForLabel(hardwareLabel)
  return {
    ...base,
    costBand: "Mid cost",
    available: false,
    hardwareLabel,
    hourlyPriceCredits: 0,
    originalHourlyPriceCredits: 0,
    discountPercent: 0,
    fromProAccount: false,
    maxPriceCredits: 0,
    estimatedMinutes,
    gpuCount: 1,
    providerCount: 0,
    providers: [],
  }
}

function walletRunLiveHardwareChoice(option: DirectWalletHardwareOption, index: number): WalletRunHardwareChoice {
  const hourlyCredits = walletRunHardwareHourlyCredits(option)
  const gpuCount = Math.max(1, Number(option.gpuCount || 1))
  const hardwareLabel = walletRunHardwareLaunchLabelFromText(`${option.hardwareId} ${option.hardwareLabel}`, option.hardwareLabel || option.hardwareId || "GPU")
  const estimatedMinutes = walletRunHardwareEstimatedMinutesForLabel(`${option.hardwareId} ${option.hardwareLabel}`)
  const maxPriceCredits = walletRunReserveCredits(hourlyCredits, estimatedMinutes)
  const providerCount = Math.max(1, Number(option.providerCount || 0), option.providers.length)
  const providerSummary = option.providers.length > 0
    ? option.providers.slice(0, 3).join(", ")
    : `${providerCount} provider${providerCount === 1 ? "" : "s"}`
  const regionSummary = option.regions.slice(0, 2).join(", ")
  return {
    id: option.id || `${option.hardwareId || option.hardwareLabel || "hardware"}_${index}`,
    label: hardwareLabel || option.hardwareLabel || option.hardwareId || "GPU",
    Icon: walletRunHardwareIconForText(`${option.hardwareId} ${option.hardwareLabel}`),
    costBand: "Mid cost",
    spec: [providerSummary, regionSummary].filter(Boolean).join(" · "),
    available: walletHardwareOptionLaunchable(option) && hourlyCredits > 0,
    hardwareLabel,
    hourlyPriceCredits: hourlyCredits,
    originalHourlyPriceCredits: Number(option.originalBuyerMicros || 0) / WALLET_CREDIT_MICROS * gpuCount,
    discountPercent: option.discountPercent,
    fromProAccount: option.fromProAccount,
    maxPriceCredits,
    estimatedMinutes,
    gpuCount,
    providerCount,
    providers: option.providers,
  }
}

function walletRunHardwareChoiceRank(choice: WalletRunHardwareChoice) {
  const text = normalizeWalletRunHardwareText(`${choice.id} ${choice.label} ${choice.hardwareLabel}`)
  const catalogRank = PRO_HARDWARE_CATALOG.findIndex(item=>item.match.test(text))
  return catalogRank >= 0 ? catalogRank : PRO_HARDWARE_CATALOG.length
}

function walletRunHardwareChoices(options: DirectWalletHardwareOption[]): WalletRunHardwareChoice[] {
  const live = options
    .map(walletRunLiveHardwareChoice)
    .filter(choice=>choice.available)
    .sort((a,b)=>
      walletRunHardwareChoiceRank(a) - walletRunHardwareChoiceRank(b) ||
      a.hourlyPriceCredits - b.hourlyPriceCredits ||
      a.label.localeCompare(b.label)
    )
  if (live.length === 0) return HW.map(walletRunStaticHardwareChoice)
  const costBands = assignHardwareCostBands(live, choice=>choice.hourlyPriceCredits)
  return live.map((choice,index)=>({...choice,costBand:costBands[index] || "Mid cost"}))
}

function walletRunProviderChoices(choice: WalletRunHardwareChoice) {
  if (!choice.available) return [];
  return uniqueWalletProviderSlugs(choice.providers);
}

function walletRunHardwareMatchesLabel(choice: WalletRunHardwareChoice, label: string) {
  const target = normalizeWalletRunHardwareText(label)
  if (!target) return false
  const targetLaunchLabel = normalizeWalletRunHardwareText(walletRunHardwareLaunchLabelFromText(label, label))
  const choiceText = normalizeWalletRunHardwareText(`${choice.id} ${choice.label} ${choice.hardwareLabel} ${choice.spec}`)
  const choiceLaunchLabel = normalizeWalletRunHardwareText(walletRunHardwareLaunchLabelFromText(choiceText, choice.hardwareLabel))
  return choiceText.includes(target) ||
    target.includes(choiceLaunchLabel) ||
    choiceLaunchLabel.includes(target) ||
    Boolean(targetLaunchLabel && choiceText.includes(targetLaunchLabel))
}

function walletRunSnapshotHardwareChoice(options: DirectWalletHardwareOption[], providerSlug: WalletProviderSlug | "", hardwareLabel: string) {
  const choices = walletRunHardwareChoices(options).filter(choice=>choice.available)
  if (choices.length === 0) return null
  const providerChoices = providerSlug
    ? choices.filter(choice=>walletRunProviderChoices(choice).includes(providerSlug))
    : choices
  const scopedChoices = providerChoices.length > 0 ? providerChoices : choices
  const matched = scopedChoices.find(choice=>walletRunHardwareMatchesLabel(choice, hardwareLabel))
  if (matched) return matched
  return scopedChoices[0] || choices[0] || null
}

type WalletRunProHardwareChoice = {
  id: string
  hardwareLabel: string
  spec: string
  costBand: HardwareCostBand
  available: boolean
  hourlyPriceCredits: number
  originalHourlyPriceCredits: number
  discountPercent: number
  fromProAccount: boolean
  maxPriceCredits: number
  estimatedMinutes: number
  gpuCount: number
  providerCount: number
  providers: string[]
}

type DirectWalletHardwareOptionPayload = {
  hardware_id?: string
  hardware_label?: string
  gpu_count?: number
  stock_available?: number
  launchable?: boolean
  capacity_status?: string
  best_price_per_hour?: number
  best_buyer_price_micros?: number | string
  original_buyer_price_micros?: number | string
  discount_percent?: number
  from_pro_account?: boolean
  provider_count?: number
  providers?: string[]
  regions?: string[]
  runtime_classes?: string[]
  pro_access_modes?: string[]
  pro_access_options?: DirectWalletProAccessOptionPayload[]
  updated_at?: string
}

type DirectWalletProAccessOptionPayload = {
  mode?: string
  support_mode?: string
  providers?: string[]
  regions?: string[]
  cluster_types?: string[]
  required_parameters?: string[]
  resolved_parameters?: Record<string,unknown>
}

function normalizeDirectWalletProAccessMode(value: unknown): DirectWalletProAccessMode | "" {
  const mode = String(value || "").trim().toLowerCase().replace(/[\s-]+/g,"_")
  return mode === "vm" || mode === "container" || mode === "notebook" || mode === "api" || mode === "cluster" ? mode : ""
}

function normalizeDirectWalletClusterType(value: unknown): DirectWalletClusterType | "" {
  const profile = String(value || "").trim().toLowerCase().replace(/[\s-]+/g,"_")
  if (profile === "genrl" || profile === "genrl_managed" || profile === "managed" || profile === "head_node") return "genrl-managed"
  if (profile === "k8s" || profile === "kube" || profile === "kubernetes") return "kubernetes"
  if (profile === "slurm") return "slurm"
  if (profile === "ray") return "ray"
  return ""
}

function normalizeDirectWalletProAccessOptions(rows?: DirectWalletProAccessOptionPayload[]): DirectWalletProAccessOption[] {
  return (Array.isArray(rows) ? rows : []).map((row): DirectWalletProAccessOption | null=>{
    const mode = normalizeDirectWalletProAccessMode(row.mode)
    if (!mode) return null
    const resolvedParameters = Object.fromEntries(
      Object.entries(row.resolved_parameters || {})
        .map(([key,value])=>[String(key).trim(),String(value ?? "").trim()])
        .filter(([key,value])=>key && value),
    )
    return {
      mode,
      supportMode: String(row.support_mode || "").trim(),
      providers: Array.isArray(row.providers) ? row.providers.map(String).filter(Boolean) : [],
      regions: Array.isArray(row.regions) ? row.regions.map(String).filter(Boolean) : [],
      clusterTypes: Array.isArray(row.cluster_types)
        ? row.cluster_types.map(normalizeDirectWalletClusterType).filter((value): value is DirectWalletClusterType=>Boolean(value))
        : [],
      requiredParameters: Array.isArray(row.required_parameters) ? row.required_parameters.map(String).filter(Boolean) : [],
      resolvedParameters: Object.keys(resolvedParameters).length > 0 ? resolvedParameters : undefined,
    }
  }).filter((option): option is DirectWalletProAccessOption=>Boolean(option))
}

function normalizeDirectWalletHardwareOptions(rows?: DirectWalletHardwareOptionPayload[]) {
  return (Array.isArray(rows) ? rows : []).map((row,index): DirectWalletHardwareOption => {
    const hardwareLabel = String(row.hardware_label || row.hardware_id || "GPU").trim()
    const hardwareId = String(row.hardware_id || hardwareLabel).trim()
    const gpuCount = Math.max(1, Math.round(Number(row.gpu_count || 1)))
    const providers = Array.isArray(row.providers) ? row.providers.map(String).filter(Boolean) : []
    const providerSlug = providers.length === 1 ? walletProviderSlugFromLabel(providers[0]) : ""
    return {
      id: providerSlug
        ? `${providerSlug}:${hardwareId || hardwareLabel || "hardware"}:${gpuCount}`
        : `${hardwareId || hardwareLabel || "hardware"}_${index}`,
      hardwareId,
      hardwareLabel,
      gpuCount,
      stockAvailable: Math.max(0, Math.round(Number(row.stock_available || 0))),
      launchable: typeof row.launchable === "boolean"
        ? row.launchable
        : Number(row.stock_available || 0) > 0,
      capacityStatus: String(row.capacity_status || "").trim() || undefined,
      bestPricePerHour: Number(row.best_price_per_hour || 0),
      bestBuyerMicros: Number(row.best_buyer_price_micros || 0),
      originalBuyerMicros: Number(row.original_buyer_price_micros || 0),
      discountPercent: Math.max(0, Math.round(Number(row.discount_percent || 0))),
      fromProAccount: Boolean(row.from_pro_account),
      providerCount: Math.max(0, Math.round(Number(row.provider_count || 0))),
      providers,
      regions: Array.isArray(row.regions) ? row.regions.map(String).filter(Boolean) : [],
      runtimeClasses: Array.isArray(row.runtime_classes) ? row.runtime_classes.map(String).filter(Boolean) : [],
      proAccessModes: Array.isArray(row.pro_access_modes)
        ? row.pro_access_modes.map(normalizeDirectWalletProAccessMode).filter((value): value is DirectWalletProAccessMode => Boolean(value))
        : [],
      proAccessOptions: normalizeDirectWalletProAccessOptions(row.pro_access_options),
      updatedAt: String(row.updated_at || ""),
    }
  }).filter(option=>option.hardwareLabel)
}

async function readDirectWalletProHardwareOptions(
  mode: DirectWalletProAccessMode,
  providerSlug?: WalletProviderSlug | "",
  clusterType?: DirectWalletClusterType,
  refresh = true,
) {
  const payload = await apiRequest<{
    hardware_options?: DirectWalletHardwareOptionPayload[]
    refresh_error?: string
  }>(
    walletProProviderHardwareRequestPath({
      mode,
      providerSlug,
      clusterProfile: clusterType,
      refresh,
    }),
    { method: "GET", timeoutMs: 36000 },
  )
  return normalizeDirectWalletHardwareOptions(
    walletProviderHardwareOptionsFromPayload<DirectWalletHardwareOptionPayload>(payload),
  )
}

function walletRunProHardwareChoices(options: DirectWalletHardwareOption[]): WalletRunProHardwareChoice[] {
  const liveOptions = options.filter(option=>
    walletHardwareOptionLaunchable(option) && uniqueWalletProviderSlugs(option.providers).length === 1,
  )
  const choices = liveOptions.map(option=>{
    const text = normalizeWalletRunHardwareText(`${option.hardwareId} ${option.hardwareLabel}`)
    const base = PRO_HARDWARE_CATALOG.find(item=>item.match.test(text))
    const estimatedMinutes = walletRunHardwareEstimatedMinutesForLabel(base?.hardwareLabel || option.hardwareLabel)
    const hourlyPriceCredits = walletRunHardwareHourlyCredits(option)
    const originalHourlyPriceCredits = Number(option.originalBuyerMicros || 0) / WALLET_CREDIT_MICROS * Math.max(1, Number(option.gpuCount || 1))
    const providerSlugs = uniqueWalletProviderSlugs(option.providers)
    const providerCount = Math.max(1, providerSlugs.length, Number(option.providerCount || option.providers.length || 1))
    const providers = (providerSlugs.length > 0 ? providerSlugs.map(walletProviderLabel) : option.providers).slice(0,2)
    const regions = option.regions.slice(0,2).filter(Boolean)
    const fallbackLabel = walletRunHardwareLaunchLabelFromText(`${option.hardwareId} ${option.hardwareLabel}`, option.hardwareLabel)
    const hardwareLabel = base ? walletRunProHardwareLaunchLabel(base, option) : fallbackLabel
    const specParts = [base?.detail || `${Math.max(1, Number(option.gpuCount || 1))} GPU capacity`, regions.join(", ")].filter(Boolean)
    return {
      id: option.id,
      hardwareLabel,
      spec: specParts.join(" · "),
      costBand: "Mid cost" as HardwareCostBand,
      available: hourlyPriceCredits > 0,
      hourlyPriceCredits,
      originalHourlyPriceCredits,
      discountPercent: option.discountPercent,
      fromProAccount: option.fromProAccount,
      maxPriceCredits: walletRunReserveCredits(hourlyPriceCredits, estimatedMinutes),
      estimatedMinutes,
      gpuCount: Math.max(1, Number(option.gpuCount || 1)),
      providerCount,
      providers,
    }
  }).sort((a,b)=>{
    const aPrice = walletProviderHourlyPriceSortValue(a.hourlyPriceCredits)
    const bPrice = walletProviderHourlyPriceSortValue(b.hourlyPriceCredits)
    if (aPrice !== bPrice) return aPrice - bPrice
    return a.hardwareLabel.localeCompare(b.hardwareLabel) || a.providers.join(",").localeCompare(b.providers.join(","))
  })
  const costBands = assignHardwareCostBands(choices,choice=>choice.hourlyPriceCredits)
  return choices.map((choice,index)=>({...choice,costBand:costBands[index] || "Mid cost"}))
}

const DIRECT_WALLET_CLUSTER_PROFILE_LABELS: Record<DirectWalletClusterType,string> = {
  "genrl-managed": "GENRL managed",
  kubernetes: "Kubernetes",
  slurm: "Slurm",
  ray: "Ray",
}

function directWalletHardwareAccessOption(
  option: DirectWalletHardwareOption | null | undefined,
  mode: DirectWalletProAccessMode,
  clusterType?: DirectWalletClusterType,
  providerSlug?: WalletProviderSlug | "",
) {
  return option?.proAccessOptions?.find(access=>{
    if (access.mode !== mode) return false
    if (mode === "cluster" && clusterType && !access.clusterTypes?.includes(clusterType)) return false
    if (!providerSlug) return true
    return walletProHardwareSupportsProvider(
      { providers: option.providers, proAccessOptions: [access] },
      providerSlug,
      mode,
      clusterType,
    )
  }) || null
}

function directWalletHardwareSupportsAccess(option: DirectWalletHardwareOption, mode: DirectWalletProAccessMode, clusterType?: DirectWalletClusterType) {
  if (!walletHardwareOptionLaunchable(option)) return false
  const accessOption = directWalletHardwareAccessOption(option,mode,clusterType)
  if (mode === "cluster") return Boolean(accessOption && (!clusterType || accessOption.clusterTypes?.includes(clusterType)))
  return Boolean(accessOption || option.proAccessModes?.includes(mode))
}

function directWalletClusterProfileOptions(options: DirectWalletHardwareOption[]) {
  const seen = new Set<DirectWalletClusterType>()
  options.forEach(option=>{
    if (!walletHardwareOptionLaunchable(option)) return
    option.proAccessOptions?.forEach(access=>{
      if (access.mode !== "cluster") return
      access.clusterTypes?.forEach(profile=>seen.add(profile))
    })
  })
  return (Object.keys(DIRECT_WALLET_CLUSTER_PROFILE_LABELS) as DirectWalletClusterType[])
    .filter(profile=>seen.has(profile))
    .map(profile=>({ label: DIRECT_WALLET_CLUSTER_PROFILE_LABELS[profile], value: profile }))
}

function directWalletParameterLabel(key: string) {
  const label = key.replace(/_/g," ").trim()
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : "Parameter"
}

function directWalletParameterRows(access: DirectWalletProAccessOption | null) {
  const resolved = {...(access?.resolvedParameters || {})}
  if (access?.providers?.length) resolved.provider = access.providers.join(", ")
  if (access?.regions?.length) resolved.region = access.regions.join(", ")
  const required = access?.requiredParameters || []
  const hidden = new Set(["min_node_count","default_node_count","max_node_count","node_count_kind","billable_node_count","controller_node_count"])
  const orderedKeys = Array.from(new Set([...required, ...Object.keys(resolved)])).filter(key=>!hidden.has(key))
  return orderedKeys
    .map(key=>({ key, label: directWalletParameterLabel(key), value: String(resolved[key] || "").trim() || "chosen at launch" }))
    .filter(row=>row.key && row.value)
}

function directWalletPositiveInt(value: unknown) {
  const parsed = Math.round(Number(String(value ?? "").trim()))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function directWalletClusterNodeBounds(access: DirectWalletProAccessOption | null) {
  const resolved = access?.resolvedParameters || {}
  const managedSingleNode = directWalletPositiveInt(resolved.max_node_count) === 1 || resolved.node_count_kind === "gpu_nodes"
  const min = directWalletPositiveInt(resolved.min_node_count) || (managedSingleNode ? 1 : 2)
  const max = Math.max(min,directWalletPositiveInt(resolved.max_node_count) || (managedSingleNode ? 1 : 8))
  const preferred = directWalletPositiveInt(resolved.default_node_count) || directWalletPositiveInt(resolved.node_count) || min
  const defaultNodeCount = Math.min(max,Math.max(min,preferred))
  const controllerNodeCount = directWalletPositiveInt(resolved.controller_node_count)
  return { min, max, defaultNodeCount, controllerNodeCount }
}

function directWalletClampClusterNodeCount(value: number, bounds: ReturnType<typeof directWalletClusterNodeBounds>) {
  return Math.min(bounds.max,Math.max(bounds.min,Math.round(value || bounds.defaultNodeCount)))
}

function directWalletClusterAccessOption(access: DirectWalletProAccessOption | null, workerNodeCount: number) {
  if (!access) return null
  const bounds = directWalletClusterNodeBounds(access)
  const workers = directWalletClampClusterNodeCount(workerNodeCount,bounds)
  return {
    ...access,
    resolvedParameters: {
      ...(access.resolvedParameters || {}),
      node_count: String(workers),
      billable_node_count: String(workers + bounds.controllerNodeCount),
    },
  }
}

function directWalletProviderIncludes(providers: string[] | undefined, provider: string) {
  const needle = provider.toLowerCase()
  return (providers || []).some(item=>item.toLowerCase().includes(needle))
}

function directWalletSetupEstimate({
  gatewayMode,
  proMode,
  proAccessMode,
  clusterType,
  proHardwareLoading,
  selectedProHardware,
  selectedHardwareAvailable,
}: {
  gatewayMode: boolean
  proMode: boolean
  proAccessMode: DirectWalletProAccessMode
  clusterType?: DirectWalletClusterType
  proHardwareLoading: boolean
  selectedProHardware: WalletRunProHardwareChoice | null
  selectedHardwareAvailable: boolean
}) {
  if (gatewayMode) {
    return { label: "Usually under 10 sec", detail: "API route only" }
  }
  if (proMode && proHardwareLoading) {
    return { label: "Checking", detail: "Reading live provider setup path" }
  }
  if (proMode && !selectedProHardware) {
    return { label: "Unavailable", detail: "Choose launchable live hardware" }
  }
  if (proMode && proAccessMode === "cluster") {
    if (directWalletProviderIncludes(selectedProHardware?.providers,"runpod")) {
      return { label: "Usually 1-4 min", detail: `${DIRECT_WALLET_CLUSTER_PROFILE_LABELS[clusterType || "slurm"] || "Cluster"} scheduler readiness` }
    }
    if (directWalletProviderIncludes(selectedProHardware?.providers,"hyperstack")) {
      return { label: "Usually 8-15 min", detail: `${DIRECT_WALLET_CLUSTER_PROFILE_LABELS[clusterType || "kubernetes"] || "Cluster"} control plane readiness` }
    }
    return { label: "Usually 5-15 min", detail: `${DIRECT_WALLET_CLUSTER_PROFILE_LABELS[clusterType || "kubernetes"] || "Cluster"} control plane readiness` }
  }
  if (proMode && proAccessMode === "notebook") {
    return { label: "Usually 3-8 min", detail: "Notebook runtime readiness" }
  }
  if (proMode && proAccessMode === "api") {
    return { label: "Usually 2-6 min", detail: "API runtime readiness" }
  }
  if (proMode && proAccessMode === "container") {
    return { label: "Usually 2-6 min", detail: "Container runtime readiness" }
  }
  if (!selectedHardwareAvailable) {
    return { label: "Waiting", detail: "Waiting for live capacity" }
  }
  return { label: "Usually 2-8 min", detail: "VM boot and runtime readiness" }
}

function directWalletJobMatchesLaunch(job: DirectWalletJob, launch: DirectWalletLaunchResult) {
  const tokens = [launch.providerJobId,launch.clientLaunchId,launch.taskId]
    .map(value=>String(value || "").trim().toLowerCase())
    .filter(Boolean)
  if (tokens.length === 0) return false
  return [job.id,job.clientLaunchId]
    .map(value=>String(value || "").trim().toLowerCase())
    .some(value=>value && tokens.includes(value))
}

function directWalletHardwareSupportsWebTerminal(option: DirectWalletHardwareOption) {
  return option.providers.some(provider=>[
    "gcp",
    "google-cloud",
    "googlecloud",
    "hyperstack",
    "lambda",
  ].includes(String(provider || "").trim().toLowerCase().replace(/[_\s]+/g,"-")))
}

function WalletRunLaunchedSSHTerminal({
  jobs,
  launch,
  onRefresh,
  onStartAnother,
}: {
  jobs: DirectWalletJob[]
  launch: DirectWalletLaunchResult
  onRefresh?: DirectWalletDashboardProps["onRefresh"]
  onStartAnother: () => void
}) {
  const launchedJob = jobs.find(job=>directWalletJobMatchesLaunch(job,launch))
  const jobID = String(launch.providerJobId || launchedJob?.id || "").trim()

  useEffect(()=>{
    if (jobID || !onRefresh) return
    void onRefresh()
    const timer = window.setInterval(()=>void onRefresh(),5_000)
    return ()=>window.clearInterval(timer)
  },[jobID,onRefresh])

  if (!jobID) {
    return (
      <div className="flex min-h-[430px] items-center justify-center rounded-2xl p-6 text-center" style={G.subtle}>
        <div className="max-w-md">
          <LoaderCircle size={24} className="mx-auto mb-3 animate-spin" style={{color:"rgba(0,0,0,0.38)"}} />
          <p className="text-[14px] font-semibold">Preparing your SSH terminal</p>
          <p className="mt-1.5 text-[11px] leading-relaxed" style={{color:"rgba(0,0,0,0.46)"}}>
            The VM request is queued. This view will become the terminal automatically when the provider job is assigned.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 rounded-2xl p-3 sm:flex-row sm:items-center sm:justify-between" style={G.subtle}>
        <div className="min-w-0">
          <Label>Secure remote machine</Label>
          <p className="mt-1 truncate text-[12px] font-semibold">{launchedJob?.model || "SSH VM"}</p>
          <p className="truncate text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>{launchedJob ? `${launchedJob.provider} · ${launchedJob.hw} · ${launchedJob.status}` : "Provisioning through GENRL"}</p>
        </div>
        <button type="button" onClick={onStartAnother} className="rounded-xl px-3 py-2 text-[11px] font-semibold" style={G.inset}>Start another</button>
      </div>
      <Suspense fallback={<div className="flex min-h-[430px] items-center justify-center rounded-2xl" style={G.subtle}><LoaderCircle size={18} className="animate-spin" /></div>}>
        <LazyWebSSHTerminal key={jobID} jobId={jobID} />
      </Suspense>
    </div>
  )
}

function RunPanel({
  onClose,
  onLaunchWorkload,
  onLaunched,
  hardwareOptions,
  jobs,
  onRefresh,
  onNeedCredits,
  launchBusy,
  creditActionsDisabled,
}: {
  onClose:()=>void
  onLaunchWorkload?: DirectWalletDashboardProps["onLaunchWorkload"]
  onLaunched?: (result?: DirectWalletLaunchResult | void) => void
  hardwareOptions: DirectWalletHardwareOption[]
  jobs: DirectWalletJob[]
  onRefresh?: DirectWalletDashboardProps["onRefresh"]
  proStats: DirectWalletProStats
  onNeedCredits?: () => void
  launchBusy: boolean
  creditActionsDisabled: boolean
}) {
  const [model,setModel]=useState("deepseek"), [hw,setHw]=useState("")
  const [tab,setTab]=useState<DirectWalletRunPanelTab>("Gateway")
  const [terminalLaunch,setTerminalLaunch]=useState<DirectWalletLaunchResult|null>(null)
  const [vmProviderSlug,setVMProviderSlug]=useState<WalletProviderSlug | "">("")
  const [proAccessMode,setProAccessMode]=useState<DirectWalletProAccessMode>("vm")
  const [proHardwareID,setProHardwareID]=useState("")
  const [proHardwareByMode,setProHardwareByMode]=useState<Record<DirectWalletProAccessMode, DirectWalletHardwareOption[]>>({
    vm: [],
    container: [],
    notebook: [],
    api: [],
    cluster: [],
  })
  const [clusterType,setClusterType]=useState<DirectWalletClusterType>("kubernetes")
  const [clusterNodeCount,setClusterNodeCount]=useState(2)
  const [proHardwareLoading,setProHardwareLoading]=useState(false)
  const [proHardwareError,setProHardwareError]=useState("")
  const [snap,setSnap]=useState(false), [mode,setMode]=useState<"one-shot"|"session">("one-shot")
  const [idleMinutes,setIdleMinutes]=useState(String(DEFAULT_RUNTIME_IDLE_MINUTES))
  const [serviceName,setServiceName]=useState("")
  const [prompt,setPrompt]=useState(""), [files,setFiles]=useState<File[]>([])
  const [fileError,setFileError]=useState("")
  const [launchError,setLaunchError]=useState("")
  const [drag,setDrag]=useState(false)
  const fileInputRef=useRef<HTMLInputElement|null>(null)
  const launchIntentInFlightRef=useRef(false)
  const fileInputID = "wallet-run-task-files"
  const hardwareChoices = useMemo(()=>walletRunHardwareChoices(hardwareOptions),[hardwareOptions])
  const selectedHardware = hardwareChoices.find(item=>item.id===hw) ?? hardwareChoices[0]!
  const vmProviderChoices = useMemo(()=>walletRunProviderChoices(selectedHardware),[selectedHardware])
  const defaultVMProviderSlug = vmProviderChoices[0] || ""
  const selectedVMProviderSlug = vmProviderChoices.includes(vmProviderSlug as WalletProviderSlug) ? vmProviderSlug : ""
  const effectiveVMProviderSlug = selectedVMProviderSlug || defaultVMProviderSlug
  const gatewayMode = tab === "Gateway"
  const proMode = tab === "Direct"
  const terminalMode = tab === "SSH Terminal"
  const directLaunchMode = proMode || terminalMode
  const selectedAccessMode: DirectWalletProAccessMode = terminalMode ? "vm" : proAccessMode
  const modelChoices = gatewayMode ? GATEWAY_MODELS : MODELS
  const selectedModel = modelChoices.find(item=>item.id===model) || modelChoices[0]!
  const activeModelID = selectedModel?.id || model
  const selectedProAccess = PRO_ACCESS_MODES.find(option=>option.id===selectedAccessMode) || PRO_ACCESS_MODES[0]!
  const proAccessLabels = PRO_ACCESS_MODES.map(option=>option.label)
  const proModeHardwareOptions = proHardwareByMode[selectedAccessMode] || []
  const proModeSourceHardwareOptions = terminalMode
    ? proModeHardwareOptions.filter(directWalletHardwareSupportsWebTerminal)
    : proModeHardwareOptions
  const clusterProfileOptions = useMemo(()=>directWalletClusterProfileOptions(proModeSourceHardwareOptions),[proModeSourceHardwareOptions])
  const selectedClusterProfile = clusterProfileOptions.find(option=>option.value === clusterType) || clusterProfileOptions[0] || null
  const proAccessHardwareOptions = useMemo(()=>proModeSourceHardwareOptions.filter(option=>
    directWalletHardwareSupportsAccess(option,selectedAccessMode,selectedAccessMode === "cluster" ? selectedClusterProfile?.value : undefined),
  ),[proModeSourceHardwareOptions,selectedAccessMode,selectedClusterProfile?.value])
  const proProviderChoices = useMemo(()=>walletProProviderChoicesForAccess(
    proAccessHardwareOptions,
    selectedAccessMode,
    selectedAccessMode === "cluster" ? selectedClusterProfile?.value : undefined,
  ),[proAccessHardwareOptions,selectedAccessMode,selectedClusterProfile?.value])
  const proHardwareChoices = useMemo(()=>walletRunProHardwareChoices(proAccessHardwareOptions),[proAccessHardwareOptions])
  const selectedProHardware = proHardwareChoices.find(item=>item.id===proHardwareID) ?? proHardwareChoices[0] ?? null
  const selectedProHardwareOption = proAccessHardwareOptions.find(option=>option.id === selectedProHardware?.id) || null
  const effectiveProProviderSlug = selectedProHardwareOption
    ? walletProProviderChoicesForAccess(
        [selectedProHardwareOption],
        selectedAccessMode,
        selectedAccessMode === "cluster" ? selectedClusterProfile?.value : undefined,
      )[0] || ""
    : ""
  const advertisedSelectedProAccessOption = directWalletHardwareAccessOption(
    selectedProHardwareOption,
    selectedAccessMode,
    selectedAccessMode === "cluster" ? selectedClusterProfile?.value : undefined,
    effectiveProProviderSlug,
  )
  const selectedProAccessOption: DirectWalletProAccessOption | null = advertisedSelectedProAccessOption || (
    selectedProHardwareOption && effectiveProProviderSlug
      ? {
          mode: selectedAccessMode,
          supportMode: "legacy",
          providers: selectedProHardwareOption.providers,
          regions: selectedProHardwareOption.regions,
          clusterTypes: selectedAccessMode === "cluster" && selectedClusterProfile ? [selectedClusterProfile.value] : undefined,
        }
      : null
  )
  const selectedClusterNodeBounds = directWalletClusterNodeBounds(selectedAccessMode === "cluster" ? selectedProAccessOption : null)
  const selectedClusterNodeCount = selectedAccessMode === "cluster" ? directWalletClampClusterNodeCount(clusterNodeCount,selectedClusterNodeBounds) : 1
  const selectedProAccessOptionWithCluster = selectedAccessMode === "cluster"
    ? directWalletClusterAccessOption(selectedProAccessOption,selectedClusterNodeCount)
    : selectedProAccessOption
  const effectiveSelectedProAccessOption = walletPinProAccessProvider(
    selectedProAccessOptionWithCluster,
    effectiveProProviderSlug,
  )
  const selectedClusterBillableNodeCount = selectedAccessMode === "cluster"
    ? selectedClusterNodeCount + selectedClusterNodeBounds.controllerNodeCount
    : 1
  const selectedClusterNodeUnit = selectedProAccessOption?.resolvedParameters?.node_count_kind === "gpu_nodes" ? "GPU node" : "GPU worker"
  const selectedProHourlyPriceCredits = (selectedProHardware?.hourlyPriceCredits || 0) * selectedClusterBillableNodeCount
  const selectedProMaxPriceCredits = walletRunReserveCredits(selectedProHourlyPriceCredits,60)
  const proParameterRows = directWalletParameterRows(effectiveSelectedProAccessOption)
  const executionMode: DirectWalletExecutionMode = gatewayMode ? "gateway" : "vm_gpu"
  const proSupplyLoading = proHardwareLoading
  const gatewayBlockedByFiles = gatewayMode && files.length > 0
  const hasUnsupportedFiles = !gatewayMode && !directLaunchMode && Boolean(fileError)
  const hasPrompt = directLaunchMode || prompt.trim().length > 0
  const hasServiceName = gatewayMode || serviceName.trim().length > 0
  const hasLiveProHardware = Boolean(
    selectedProHardware?.available &&
    effectiveProProviderSlug &&
    effectiveSelectedProAccessOption &&
    (selectedAccessMode !== "cluster" || selectedClusterProfile),
  )
  const ready = hasServiceName && hasPrompt && !launchBusy && !creditActionsDisabled && !gatewayBlockedByFiles && !hasUnsupportedFiles && (gatewayMode || (directLaunchMode ? hasLiveProHardware && !proSupplyLoading : Boolean(selectedHardware?.available)))
  const launchReady = ready && Boolean(onLaunchWorkload)
  const proCostDetail = selectedProHardware && selectedProHourlyPriceCredits > 0
    ? "Relative band among the current eligible hardware"
    : proSupplyLoading
      ? "Checking live provider supply"
      : `${selectedProAccess.credential} provisioning · no live supply selected`
  const proCostLabel = selectedProHardware && selectedProHourlyPriceCredits > 0
    ? hardwareCostBandLabel(selectedProHardware.costBand)
    : proSupplyLoading
      ? "Checking"
      : "Unavailable"
  const proSupplySummary = proSupplyLoading
    ? `Checking live ${selectedProAccess.label} supply`
    : proHardwareChoices.length > 0
      ? `${proHardwareChoices.length} live hardware option${proHardwareChoices.length === 1 ? "" : "s"} across ${proProviderChoices.length} provider${proProviderChoices.length === 1 ? "" : "s"}`
      : selectedAccessMode === "cluster"
        ? "No supported cluster capacity"
      : `No live ${selectedProAccess.label} supply`
  const setupEstimate = directWalletSetupEstimate({
    gatewayMode,
    proMode: directLaunchMode,
    proAccessMode: selectedAccessMode,
    clusterType: selectedClusterProfile?.value,
    proHardwareLoading: proSupplyLoading,
    selectedProHardware,
    selectedHardwareAvailable: Boolean(selectedHardware?.available),
  })

  useEffect(()=>{
    if (selectedHardware?.available) return
    const firstLive = hardwareChoices.find(item=>item.available)
    if (firstLive && firstLive.id !== hw) setHw(firstLive.id)
  },[hardwareChoices,hw,selectedHardware])

  useEffect(()=>{
    if (!vmProviderSlug) return
    if (vmProviderChoices.includes(vmProviderSlug as WalletProviderSlug)) return
    setVMProviderSlug("")
  },[vmProviderChoices,vmProviderSlug])

  useEffect(()=>{
    if ((gatewayMode || directLaunchMode) && mode === "session") setMode("one-shot")
  },[directLaunchMode,gatewayMode,mode])

  useEffect(()=>{
    if (!directLaunchMode || selectedAccessMode !== "cluster" || clusterProfileOptions.length === 0) return
    if (clusterProfileOptions.some(option=>option.value === clusterType)) return
    setClusterType(clusterProfileOptions[0]!.value)
  },[clusterProfileOptions,clusterType,directLaunchMode,selectedAccessMode])

  useEffect(()=>{
    if (!directLaunchMode || selectedAccessMode !== "cluster") return
    setClusterNodeCount(current=>directWalletClampClusterNodeCount(current,selectedClusterNodeBounds))
  },[
    directLaunchMode,
    selectedAccessMode,
    selectedClusterNodeBounds.defaultNodeCount,
    selectedClusterNodeBounds.max,
    selectedClusterNodeBounds.min,
  ])

  useEffect(()=>{
    if (!directLaunchMode) return
    let active = true
    setProHardwareLoading(true)
    setProHardwareError("")
    readDirectWalletProHardwareOptions(selectedAccessMode)
      .then(options=>{
        if (!active) return
        setProHardwareByMode(current=>({ ...current, [selectedAccessMode]: options }))
      })
      .catch(error=>{
        if (!active) return
        setProHardwareError(error instanceof Error ? error.message : "Could not load live direct-provider supply.")
        setProHardwareByMode(current=>({ ...current, [selectedAccessMode]: [] }))
      })
      .finally(()=>{
        if (active) setProHardwareLoading(false)
      })
    return ()=>{ active = false }
  },[directLaunchMode,selectedAccessMode])

  useEffect(()=>{
    if (!directLaunchMode) return
    if (selectedProHardware?.available && selectedProHardware.id === proHardwareID) return
    const firstAvailable = proHardwareChoices.find(item=>item.available)
    setProHardwareID(firstAvailable?.id || proHardwareChoices[0]?.id || "")
  },[directLaunchMode,proHardwareChoices,proHardwareID,selectedProHardware])

  useEffect(()=>{
    if (modelChoices.some(item=>item.id===model)) return
    setModel(modelChoices[0]?.id || "deepseek")
  },[model,modelChoices])

  async function refreshProHardwareSupply() {
    setProHardwareError("")
    setProHardwareLoading(true)
    try {
      const options = await readDirectWalletProHardwareOptions(selectedAccessMode)
      setProHardwareByMode(current=>({ ...current, [selectedAccessMode]: options }))
    } catch (error) {
      setProHardwareError(error instanceof Error ? error.message : "Could not load live direct-provider supply.")
      setProHardwareByMode(current=>({ ...current, [selectedAccessMode]: [] }))
    } finally {
      setProHardwareLoading(false)
    }
  }

  function addFiles(incoming?: FileList | File[] | null) {
    const incomingFiles = incoming ? Array.from(incoming) : []
    if (incomingFiles.length === 0) return
    const accepted = incomingFiles.filter(walletRunFileSupported)
    const rejected = incomingFiles.filter(file=>!walletRunFileSupported(file))
    setFileError(rejected.length > 0 ? walletRunUnsupportedFilesMessage(rejected) : "")
    if (accepted.length > 0) setFiles(current=>appendUniqueWalletRunFiles(current,accepted))
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function togglePersistentMode() {
    setMode(current => {
      const next = current === "session" ? "one-shot" : "session"
      if (next === "session") setSnap(true)
      return next
    })
  }

  async function launch() {
    setLaunchError("")
    const launchTab: DirectWalletRunTab = terminalMode ? "Direct" : tab as DirectWalletRunTab
    if (creditActionsDisabled) {
      onNeedCredits?.()
      return
    }
    if (!onLaunchWorkload) {
      setLaunchError("Launch actions are unavailable in this wallet session.")
      return
    }
    if (!gatewayMode && !serviceName.trim()) {
      setLaunchError("Name this service before provisioning it.")
      return
    }
    if (directLaunchMode) {
      if (launchBusy) {
        setLaunchError("A launch is already starting.")
        return
      }
      if (proSupplyLoading) {
        setLaunchError("Live direct-provider supply is still loading.")
        return
      }
      if (!hasLiveProHardware) {
        setLaunchError(selectedAccessMode === "cluster" ? "Choose launchable cluster hardware." : `Choose launchable ${selectedProAccess.label} hardware.`)
        return
      }
    } else if (!ready) {
      setLaunchError("Choose the required launch inputs first.")
      return
    }
    if (directLaunchMode && !selectedProHardware) {
      setLaunchError("Choose live direct-provider hardware before launching.")
      return
    }
    const launchClusterType = directLaunchMode && selectedAccessMode === "cluster" ? selectedClusterProfile?.value : undefined
    if (directLaunchMode && selectedAccessMode === "cluster" && !launchClusterType) {
      setLaunchError("Choose a supported cluster profile before launching.")
      return
    }
    try {
      const launchHardware = directLaunchMode ? {
        ...selectedProHardware,
        hourlyPriceCredits: selectedProHourlyPriceCredits,
        maxPriceCredits: selectedProMaxPriceCredits,
      } : {
        ...selectedHardware,
        hardwareLabel: walletProviderLaunchHardwareLabel(
          effectiveVMProviderSlug,
          selectedHardware.hardwareLabel,
          selectedHardware.providers,
        ),
      }
      const launchPrompt = directLaunchMode ? "" : prompt.trim()
      const persistent = !directLaunchMode && mode === "session"
      const result = await onLaunchWorkload({
        executionMode,
        runTab: launchTab,
        sshTerminal: terminalMode,
        serviceName: gatewayMode ? undefined : serviceName.trim(),
        proAccessMode: directLaunchMode ? selectedAccessMode : undefined,
        proAccessOption: directLaunchMode ? effectiveSelectedProAccessOption || undefined : undefined,
        providerSlug: !gatewayMode && !directLaunchMode ? effectiveVMProviderSlug || undefined : undefined,
        clusterType: launchClusterType,
        clusterNodeCount: directLaunchMode && selectedAccessMode === "cluster" ? selectedClusterNodeCount : undefined,
        model: directLaunchMode ? "genrl-recommended" : activeModelID,
        hardware: directLaunchMode ? launchHardware.id : selectedHardware.id,
        hardwareLabel: launchHardware.hardwareLabel,
        hourlyPriceCredits: launchHardware.hourlyPriceCredits,
        maxPriceCredits: launchHardware.maxPriceCredits,
        estimatedMinutes: launchHardware.estimatedMinutes,
        gpuCount: launchHardware.gpuCount,
        prompt: launchPrompt,
        files: directLaunchMode ? [] : files,
        persistent,
        idleTimeoutSeconds: persistent ? normalizeRuntimeIdleMinutes(idleMinutes) * 60 : undefined,
        snapshots: directLaunchMode ? false : snap,
      })
      if (terminalMode && result) {
        setTerminalLaunch(result)
        void onRefresh?.()
      } else {
        onLaunched?.(result)
      }
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : "Could not start this launch.")
      // The route owns the toast; keep the panel open so the user can retry.
    }
  }

  function handleLaunchIntent() {
    if (!launchReady || launchIntentInFlightRef.current) return
    launchIntentInFlightRef.current = true
    void launch().finally(()=>{ launchIntentInFlightRef.current = false })
  }

  return (
    <Panel onClose={onClose} wide>
      <PanelHead title="Run Task" onClose={onClose} />
      <div className="flex-shrink-0 px-6 py-4 border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
        <SegCtrl
          options={["Gateway","VM/GPU","Direct","SSH Terminal"]}
          value={tab}
          onChange={value=>{
            if (value === "VM/GPU") return
            setTab(value as DirectWalletRunPanelTab)
          }}
          disabled={{"VM/GPU":"VM/GPU is coming soon"}}
          disabledLabels={{"VM/GPU":"Coming soon"}}
        />
        {proMode ? (
          <div className="mt-3">
            {proAccessLabels.length > 0 ? (
              <SegCtrl
                options={proAccessLabels}
                value={selectedProAccess.label}
                onChange={value=>{
                  const next = PRO_ACCESS_MODES.find(option=>option.label === value)
                  if (next) setProAccessMode(next.id)
                }}
              />
            ) : (
              <div className="rounded-xl px-3 py-2.5" style={G.inset}>
                <p className="text-[12px] font-semibold">No supported direct access available</p>
                <p className="mt-0.5 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>The live capacity table is not advertising any launchable direct-provider modes.</p>
              </div>
            )}
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5" style={G.inset}>
              <div className="min-w-0">
                <Label>Live supply</Label>
                <p className="mt-0.5 truncate text-[12px] font-semibold" style={{color:"rgba(0,0,0,0.68)"}}>{proSupplySummary}</p>
              </div>
              <button type="button" onClick={()=>void refreshProHardwareSupply()} disabled={proSupplyLoading} className="h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center disabled:opacity-45" style={G.subtle} aria-label="Refresh live supply" title="Refresh live supply">
                <RefreshCcw size={13} className={proSupplyLoading ? "animate-spin" : ""} />
              </button>
            </div>
            {proAccessMode === "cluster" && clusterProfileOptions.length > 0 ? (
              <div className="mt-2 rounded-xl px-3 py-2.5" style={G.inset}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <Label>Cluster profile</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>{selectedClusterNodeUnit}{selectedClusterNodeCount === 1 ? "" : "s"}</span>
                    <div className="flex h-8 items-center rounded-full px-1" style={G.subtle}>
                      <button
                        type="button"
                        className="flex h-6 w-6 items-center justify-center rounded-full disabled:opacity-35"
                        disabled={selectedClusterNodeCount <= selectedClusterNodeBounds.min}
                        onClick={()=>setClusterNodeCount(current=>directWalletClampClusterNodeCount(current-1,selectedClusterNodeBounds))}
                        aria-label={`Remove ${selectedClusterNodeUnit.toLowerCase()}`}
                        title={`Remove ${selectedClusterNodeUnit.toLowerCase()}`}
                      >
                        <Minus size={12} />
                      </button>
                      <output className="w-7 text-center text-[12px] font-bold" aria-label={`${selectedClusterNodeCount} ${selectedClusterNodeUnit.toLowerCase()}${selectedClusterNodeCount === 1 ? "" : "s"}`}>{selectedClusterNodeCount}</output>
                      <button
                        type="button"
                        className="flex h-6 w-6 items-center justify-center rounded-full disabled:opacity-35"
                        disabled={selectedClusterNodeCount >= selectedClusterNodeBounds.max}
                        onClick={()=>setClusterNodeCount(current=>directWalletClampClusterNodeCount(current+1,selectedClusterNodeBounds))}
                        aria-label={`Add ${selectedClusterNodeUnit.toLowerCase()}`}
                        title={`Add ${selectedClusterNodeUnit.toLowerCase()}`}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                </div>
                <SegCtrl
                  options={clusterProfileOptions.map(option=>option.label)}
                  value={selectedClusterProfile?.label || clusterProfileOptions[0]!.label}
                  onChange={value=>{
                    const next = clusterProfileOptions.find(option=>option.label === value)
                    if (next) setClusterType(next.value)
                  }}
                />
                {selectedClusterNodeBounds.controllerNodeCount > 0 ? (
                  <p className="mt-2 text-[10px] font-medium" style={{color:"rgba(0,0,0,0.42)"}}>
                    Includes {selectedClusterNodeBounds.controllerNodeCount} provider controller in the cost band.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {terminalMode && !terminalLaunch ? (
          <div className="mt-3">
            <div className="rounded-xl px-3 py-2.5" style={G.inset}>
              <p className="text-[12px] font-semibold">SSH VM</p>
              <p className="mt-0.5 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Launch from the same live hardware catalogue as Direct. The private key stays embedded in GENRL and is never displayed.</p>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl px-3 py-2.5" style={G.inset}>
              <div className="min-w-0">
                <Label>Live SSH supply</Label>
                <p className="mt-0.5 truncate text-[12px] font-semibold" style={{color:"rgba(0,0,0,0.68)"}}>{proSupplySummary}</p>
              </div>
              <button type="button" onClick={()=>void refreshProHardwareSupply()} disabled={proSupplyLoading} className="h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center disabled:opacity-45" style={G.subtle} aria-label="Refresh live SSH supply" title="Refresh live SSH supply">
                <RefreshCcw size={13} className={proSupplyLoading ? "animate-spin" : ""} />
              </button>
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto px-6 pt-5 pb-36 flex flex-col gap-5" style={{scrollbarWidth:"none"}}>
        {terminalMode && terminalLaunch ? (
          <WalletRunLaunchedSSHTerminal jobs={jobs} launch={terminalLaunch} onRefresh={onRefresh} onStartAnother={()=>setTerminalLaunch(null)} />
        ) : (
        <>
        {!gatewayMode ? (
          <div>
            <label htmlFor="wallet-run-service-name"><Label>Service name</Label></label>
            <input
              id="wallet-run-service-name"
              value={serviceName}
              onChange={event=>setServiceName(event.currentTarget.value)}
              maxLength={64}
              required
              aria-required="true"
              placeholder="e.g. research-vm"
              className="mt-2 h-11 w-full rounded-xl px-4 text-[13px] font-semibold outline-none"
              style={{...G.field,color:"rgba(0,0,0,0.78)"}}
            />
            <p className="mt-1.5 text-[10px]" style={{color:"rgba(0,0,0,0.38)"}}>
              Required · shown in your ledger and service details.
            </p>
          </div>
        ) : null}

        {/* Model */}
        {!directLaunchMode ? <div>
          <Label>Model</Label>
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1" style={{scrollbarWidth:"none"}}>
            {modelChoices.map(m=>{
              const on=activeModelID===m.id
              return (
                <motion.button key={m.id} onClick={()=>setModel(m.id)} whileTap={{scale:0.95}}
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl"
                  style={on?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.subtle,color:"rgba(0,0,0,0.7)"}}>
                  <span className="text-[11px] font-semibold">{m.name}</span>
                  {m.tag&&<span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={on?{background:"rgba(255,255,255,0.15)"}:{background:"rgba(0,0,0,0.07)",color:"rgba(0,0,0,0.5)"}}>{m.tag}</span>}
                </motion.button>
              )
            })}
          </div>
        </div> : null}

        {/* Prompt */}
        {!directLaunchMode ? <div>
          <Label>Prompt</Label>
          {!gatewayMode && (
            <input id={fileInputID} ref={fileInputRef} type="file" multiple accept={WALLET_RUN_FILE_ACCEPT} className="sr-only"
              onChange={e=>{addFiles(e.currentTarget.files); e.currentTarget.value=""}} />
          )}
          <div
            className="mt-2 overflow-hidden rounded-xl"
            onDragEnter={!gatewayMode ? e=>{e.preventDefault();setDrag(true)} : undefined}
            onDragOver={!gatewayMode ? e=>{e.preventDefault();setDrag(true)} : undefined}
            onDragLeave={!gatewayMode ? e=>{e.preventDefault();setDrag(false)} : undefined}
            onDrop={!gatewayMode ? e=>{e.preventDefault();setDrag(false);addFiles(e.dataTransfer.files)} : undefined}
            style={{
              ...G.field,
              background:!gatewayMode && drag ? "rgba(255,255,255,0.22)" : G.field.background,
              border:!gatewayMode && drag ? "1px solid rgba(14,12,8,0.24)" : G.field.border,
            }}>
            <textarea rows={5} value={prompt} onChange={e=>setPrompt(e.target.value)} placeholder="Describe the workload…"
              className="w-full bg-transparent px-4 pb-2 pt-3 text-[14px] resize-none outline-none" />
            {!gatewayMode && (
              <div className="border-t px-3 py-2.5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="flex flex-wrap items-center gap-2 text-[12px]" style={{color:"rgba(0,0,0,0.42)"}}>
                  <Upload size={13} />
                  <span>Drop files here or</span>
                  <button type="button" onClick={()=>fileInputRef.current?.click()}
                    className="font-semibold underline underline-offset-2" style={{color:"rgba(0,0,0,0.72)"}}>
                    browse
                  </button>
                  <span style={{color:"rgba(0,0,0,0.32)"}}>{WALLET_RUN_SUPPORTED_FILE_LABEL}</span>
                </div>
                {fileError ? (
                  <p className="mt-2 text-[11px] font-medium" style={{color:"#b91c1c"}} role="alert">
                    {fileError}
                  </p>
                ) : null}
                {files.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {files.map((f,i)=>(
                      <div key={walletRunFileKey(f)} className="flex max-w-full items-center gap-2 rounded-lg px-2.5 py-1.5" style={G.inset}>
                        <FileText size={12} style={{color:"rgba(0,0,0,0.4)"}} />
                        <span className="max-w-[180px] truncate text-[11px]">{f.name}</span>
                        <span className="text-[10px] flex-shrink-0" style={{color:"rgba(0,0,0,0.35)"}}>{formatWalletRunFileSize(f.size)}</span>
                        <button type="button" onClick={()=>setFiles(files.filter((_,j)=>j!==i))} aria-label={`Remove ${f.name}`}>
                          <X size={12} style={{color:"rgba(0,0,0,0.35)"}} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div> : null}

        {/* Hardware */}
        {gatewayMode ? (
          <div className="rounded-xl px-4 py-3.5 flex items-center gap-3" style={G.subtle}>
            <Server size={15} style={{color:"rgba(0,0,0,0.45)"}} />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold">Gateway route</p>
              <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>API model call · no VM/GPU</p>
            </div>
          </div>
        ) : (
          <div>
            <Label>Hardware</Label>
            {directLaunchMode ? (
              <>
                <div className="mt-2 overflow-hidden rounded-2xl" style={G.subtle}>
                  {proSupplyLoading ? (
                    <div className="flex items-center gap-3 px-4 py-4">
                      <LoaderCircle size={15} className="animate-spin" style={{color:"rgba(0,0,0,0.45)"}} />
                      <div>
                        <p className="text-[13px] font-semibold">Checking live {selectedProAccess.label} supply</p>
                        <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Reading the current provider capacity table</p>
                      </div>
                    </div>
                  ) : proHardwareChoices.length === 0 ? (
                    <div className="px-4 py-4">
                      <p className="text-[13px] font-semibold">{selectedAccessMode === "cluster" ? "No cluster hardware available" : `No live ${selectedProAccess.label} hardware available`}</p>
                      <p className="mt-0.5 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
                        {proHardwareError || (selectedAccessMode === "cluster" ? "Cluster mode only shows provider-reported cluster capacity that can be launched as a cluster." : terminalMode ? "SSH Terminal only shows providers with secure browser-terminal support." : "This mode will appear here as soon as a provider reports launchable stock.")}
                      </p>
                    </div>
                  ) : proHardwareChoices.map((item,index)=>{
                    const on = selectedProHardware?.id === item.id
                    const providerText = item.providerCount > 0
                      ? item.providers.length > 0
                        ? item.providers.join(", ")
                        : `${item.providerCount} live provider${item.providerCount === 1 ? "" : "s"}`
                      : "Live supply"
                    return (
                      <motion.button
                        key={item.id}
                        type="button"
                        onClick={()=>item.available&&setProHardwareID(item.id)}
                        whileTap={{scale:item.available?0.985:1}}
                        disabled={!item.available}
                        className="flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left"
                        style={{
                          borderTop:index === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                          background:on ? "rgba(14,12,8,0.88)" : "transparent",
                          color:on ? "white" : "rgba(0,0,0,0.74)",
                          opacity:item.available?1:0.58,
                        }}>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold">{item.hardwareLabel}</p>
                          <p className="mt-0.5 truncate text-[11px]" style={{color:on?"rgba(255,255,255,0.62)":"rgba(0,0,0,0.42)"}}>{item.spec}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <WalletRunHourlyPrice
                            hourlyPriceCredits={item.hourlyPriceCredits}
                            originalHourlyPriceCredits={item.originalHourlyPriceCredits}
                            discountPercent={item.discountPercent}
                            fromProAccount={item.fromProAccount}
                            fallback={hardwareCostBandLabel(item.costBand)}
                            dark={on}
                            align="right"
                          />
                          <p className="mt-0.5 text-[10px]" style={{color:on?"rgba(255,255,255,0.52)":"rgba(0,0,0,0.38)"}}>{item.gpuCount} GPU · {providerText}</p>
                        </div>
                      </motion.button>
                    )
                  })}
                </div>
                {proParameterRows.length > 0 ? (
                  <div className="mt-2 rounded-xl px-3 py-2.5" style={G.inset}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <Label>Parameters</Label>
                      <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>{selectedProAccessOption?.supportMode || "live"}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {proParameterRows.slice(0,8).map(row=>(
                        <div key={row.key} className="min-w-0 rounded-lg px-2.5 py-2" style={G.subtle}>
                          <p className="truncate text-[9px] font-bold uppercase tracking-[0.18em]" style={{color:"rgba(0,0,0,0.36)"}}>{row.label}</p>
                          <p className="mt-0.5 truncate text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.72)"}}>{row.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {hardwareChoices.map(({id,label,Icon,costBand,spec,available,providerCount,hourlyPriceCredits,originalHourlyPriceCredits,discountPercent,fromProAccount})=>{
                  const on=hw===id
                  return (
                    <motion.button key={id} onClick={()=>available&&setHw(id)} whileTap={{scale:available?0.96:1}} disabled={!available}
                      className="p-3.5 rounded-xl text-left disabled:cursor-not-allowed"
                      title={`${label} · ${spec || `${providerCount} provider${providerCount === 1 ? "" : "s"}`}`}
                      style={on?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.subtle,opacity:available?1:0.58}}>
                      <Icon size={14} className="mb-2" style={{color:on?"rgba(255,255,255,0.6)":"rgba(0,0,0,0.4)"}} />
                      <p className="text-[12px] font-semibold leading-tight break-words" style={{overflowWrap:"anywhere"}}>{label}</p>
                      <WalletRunHourlyPrice
                        hourlyPriceCredits={hourlyPriceCredits}
                        originalHourlyPriceCredits={originalHourlyPriceCredits}
                        discountPercent={discountPercent}
                        fromProAccount={fromProAccount}
                        fallback={hardwareCostBandLabel(costBand)}
                        dark={on}
                        size="card"
                        className="mt-1"
                      />
                      <p className="text-[9px] leading-snug break-words" style={{color:on?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.38)", overflowWrap:"anywhere"}}>{spec || `${providerCount} provider${providerCount === 1 ? "" : "s"}`}</p>
                    </motion.button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {!gatewayMode && !directLaunchMode && vmProviderChoices.length > 0 ? (
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <Label>Provider</Label>
              <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>
                {effectiveVMProviderSlug ? walletProviderLabel(effectiveVMProviderSlug) : "Choose provider"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {vmProviderChoices.map(providerSlug=>{
                const on = effectiveVMProviderSlug === providerSlug
                return (
                  <motion.button
                    key={providerSlug}
                    type="button"
                    onClick={()=>setVMProviderSlug(providerSlug)}
                    whileTap={{scale:0.96}}
                    className="rounded-xl px-3 py-2.5 text-left"
                    style={on?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.subtle,color:"rgba(0,0,0,0.68)"}}>
                    <p className="text-[12px] font-semibold">{walletProviderLabel(providerSlug)}</p>
                    <p className="text-[10px]" style={{color:on?"rgba(255,255,255,0.58)":"rgba(0,0,0,0.42)"}}>Pinned</p>
                  </motion.button>
                )
              })}
            </div>
          </div>
        ) : null}

        {!gatewayMode && !directLaunchMode && (
          <div className="rounded-xl" style={G.subtle}>
            <div className="flex items-center justify-between px-4 py-3.5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
              <div>
                <p className="text-[13px] font-semibold">Persistent session</p>
                <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>VM stays live across follow-up turns</p>
              </div>
              <Toggle value={mode==="session"} onChange={togglePersistentMode} label="Persistent session" />
            </div>
            {mode === "session" && (
              <div className="px-4 pb-3">
                <div className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5" style={G.inset}>
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold">Keep alive</p>
                    <p className="text-[10px]" style={{color:"rgba(0,0,0,0.38)"}}>after output with no input</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number"
                      min={MIN_RUNTIME_IDLE_MINUTES}
                      max={MAX_RUNTIME_IDLE_MINUTES}
                      step={5}
                      inputMode="numeric"
                      value={idleMinutes}
                      onChange={e=>setIdleMinutes(e.currentTarget.value.replace(/[^\d]/g,"").slice(0,4))}
                      onBlur={()=>setIdleMinutes(String(normalizeRuntimeIdleMinutes(idleMinutes)))}
                      aria-label="Persistent VM idle minutes"
                      className="w-16 rounded-lg px-2 py-1.5 text-right text-[12px] font-semibold outline-none"
                      style={{...G.field,color:"rgba(0,0,0,0.78)"}}
                    />
                    <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>min</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between px-4 py-3.5 border-t" style={{borderColor:"rgba(0,0,0,0.06)"}}>
              <div>
                <p className="text-[13px] font-semibold">Snapshots</p>
                <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Save run state for context recall</p>
              </div>
              <Toggle value={snap} onChange={()=>setSnap(!snap)} label="Snapshots" />
            </div>
          </div>
        )}

        {/* Hardware selection summary */}
        <div className="flex items-center justify-between rounded-xl px-4 py-3" style={G.inset}>
          <div>
            <p className="text-[12px] font-semibold">{directLaunchMode ? `${selectedProAccess.title} · ${selectedProHardware?.hardwareLabel || "No live hardware"}` : selectedModel?.name}</p>
            <p className="text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>
              {gatewayMode
                ? "API gateway · no VM/GPU reserve"
                : directLaunchMode
                  ? proCostDetail
                : selectedHardware?.available ? `${selectedHardware.estimatedMinutes} min estimate · relative to current eligible hardware` : "Waiting for live capacity"}
            </p>
          </div>
          {gatewayMode ? (
            <p className="text-[13px] font-semibold">Gateway</p>
          ) : (
            <WalletRunHourlyPrice
              hourlyPriceCredits={directLaunchMode ? selectedProHourlyPriceCredits : selectedHardware?.hourlyPriceCredits || 0}
              originalHourlyPriceCredits={directLaunchMode
                ? (selectedProHardware?.originalHourlyPriceCredits || 0) * selectedClusterBillableNodeCount
                : selectedHardware?.originalHourlyPriceCredits || 0}
              discountPercent={directLaunchMode ? selectedProHardware?.discountPercent || 0 : selectedHardware?.discountPercent || 0}
              fromProAccount={Boolean(directLaunchMode ? selectedProHardware?.fromProAccount : selectedHardware?.fromProAccount)}
              fallback={directLaunchMode ? proCostLabel : selectedHardware ? hardwareCostBandLabel(selectedHardware.costBand) : "Unavailable"}
              align="right"
              size="summary"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3" style={G.subtle}>
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full" style={G.inset}>
              {launchBusy ? <LoaderCircle size={14} className="animate-spin" style={{color:"rgba(0,0,0,0.46)"}} /> : <Clock size={14} style={{color:"rgba(0,0,0,0.46)"}} />}
            </span>
            <div className="min-w-0">
              <p className="text-[12px] font-semibold">{launchBusy ? "Setting up" : "Setup time"}</p>
              <p className="truncate text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>{setupEstimate.detail}</p>
            </div>
          </div>
          <p className="flex-shrink-0 text-right text-[12px] font-semibold" style={{color:"rgba(0,0,0,0.72)"}}>{setupEstimate.label}</p>
        </div>

        {gatewayBlockedByFiles ? (
          <div className="rounded-xl px-4 py-3" style={{...G.inset,border:"1px solid rgba(176,88,0,0.18)"}}>
            <p className="text-[12px] font-semibold" style={{color:"rgba(116,62,0,0.85)"}}>Files require VM/GPU mode</p>
          </div>
        ) : null}

        {creditActionsDisabled ? (
          <div className="rounded-2xl px-4 py-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" style={{...G.inset,border:"1px solid rgba(0,0,0,0.07)"}}>
            <Wallet size={15} style={{color:"rgba(0,0,0,0.45)"}} />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold">Purchase credits</p>
              <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Use a purchase link before running a task.</p>
            </div>
            <button type="button" onClick={onNeedCredits} className="rounded-xl px-3 py-2 text-[11px] font-semibold" style={{background:"rgba(14,12,8,0.86)",color:"#fff"}}>
              Open purchase link
            </button>
          </div>
        ) : null}

        {launchError ? (
          <div className="rounded-xl px-4 py-3" role="alert" style={{...G.inset,border:"1px solid rgba(185,28,28,0.18)"}}>
            <p className="text-[12px] font-semibold" style={{color:"#b91c1c"}}>{launchError}</p>
          </div>
        ) : null}

        <button
          type="button"
          disabled={!launchReady}
          className="w-full py-3.5 rounded-2xl text-[14px] font-semibold text-white flex items-center justify-center gap-2 select-none"
          style={{
            background:launchReady?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.28)",
            cursor:launchReady?"pointer":"not-allowed",
          }}
          onClick={handleLaunchIntent}>
          <Zap size={15} /> {creditActionsDisabled ? "Purchase credits" : launchBusy ? "Launching..." : gatewayMode ? "Launch Gateway Job" : terminalMode ? "Start SSH Terminal" : proMode ? selectedProAccess.action : "Launch VM/GPU Job"}
        </button>
        </>
        )}
      </div>
    </Panel>
  )
}

// ─── Storage panel ────────────────────────────────────────────────────────────

const WALLET_STORAGE_PREVIEW_FILES = ["dataset_train.parquet","dataset_val.parquet","metadata.json","tokenizer_config.json","README.md"]
type DirectWalletStorageTab = "Volumes" | "Snapshots"

function StoragePanel({
  onClose,
  volumes,
  snapshots,
  storageRoutes,
  creditActionsDisabled,
  onNeedCredits,
  onCreateStorageVolume,
  onDeleteStorageVolume,
  onDeleteRunSnapshot,
  onLoadStorageVolumeFiles,
  onUploadStorageVolumeFiles,
  onImportStorageVolumeHF,
}: {
  onClose:()=>void
  volumes: DirectWalletVolume[]
  snapshots: DirectWalletSnapshot[]
  storageRoutes: DirectWalletStorageRoute[]
  creditActionsDisabled: boolean
  onNeedCredits?: () => void
  onCreateStorageVolume?: DirectWalletDashboardProps["onCreateStorageVolume"]
  onDeleteStorageVolume?: DirectWalletDashboardProps["onDeleteStorageVolume"]
  onDeleteRunSnapshot?: DirectWalletDashboardProps["onDeleteRunSnapshot"]
  onLoadStorageVolumeFiles?: DirectWalletDashboardProps["onLoadStorageVolumeFiles"]
  onUploadStorageVolumeFiles?: DirectWalletDashboardProps["onUploadStorageVolumeFiles"]
  onImportStorageVolumeHF?: DirectWalletDashboardProps["onImportStorageVolumeHF"]
}) {
  const liveVolumes = useMemo(() => volumes.filter(isLiveWalletVolume), [volumes])
  const [tab,setTab]=useState<DirectWalletStorageTab>("Volumes")
  const [selVol,setSelVol]=useState(liveVolumes[0]?.id || "")
  const [createOpen,setCreateOpen]=useState(false)
  const [deleteTarget,setDeleteTarget]=useState<DirectWalletVolume|null>(null)
  const [confirmName,setConfirmName]=useState("")
  const [deletingID,setDeletingID]=useState("")
  const uploadInputRef = useRef<HTMLInputElement|null>(null)
  const [storageFiles,setStorageFiles]=useState<DirectWalletStorageFile[]>([])
  const [filesLoading,setFilesLoading]=useState(false)
  const [storageBusy,setStorageBusy]=useState<""|"upload"|"import">("")
  const [storageMessage,setStorageMessage]=useState("")
  const [importOpen,setImportOpen]=useState(false)
  const [hfRepo,setHFRepo]=useState("")
  const [hfFilename,setHFFilename]=useState("")
  const [hfToken,setHFToken]=useState("")
  const vol=liveVolumes.find(v=>v.id===selVol) || liveVolumes[0]
  const previewFiles: DirectWalletStorageFile[] = vol && vol.files > 0
    ? WALLET_STORAGE_PREVIEW_FILES.slice(0, Math.min(WALLET_STORAGE_PREVIEW_FILES.length, vol.files)).map((filename, index)=>({id:`preview-${vol.id}-${index}`, filename}))
    : []
  const visibleFiles = onLoadStorageVolumeFiles ? storageFiles : previewFiles

  useEffect(() => {
    if (liveVolumes.some(v=>v.id===selVol)) return
    setSelVol(liveVolumes[0]?.id || "")
  }, [liveVolumes, selVol])

  useEffect(() => {
    let cancelled = false
    async function loadFiles() {
      setStorageMessage("")
      if (!vol || !onLoadStorageVolumeFiles) {
        setStorageFiles([])
        setFilesLoading(false)
        return
      }
      setFilesLoading(true)
      try {
        const files = await onLoadStorageVolumeFiles(vol)
        if (!cancelled) setStorageFiles(files)
      } catch (error) {
        if (!cancelled) {
          setStorageFiles([])
          setStorageMessage(error instanceof Error ? error.message : "Could not load files")
        }
      } finally {
        if (!cancelled) setFilesLoading(false)
      }
    }
    void loadFiles()
    return () => { cancelled = true }
  }, [vol?.id, onLoadStorageVolumeFiles])

  async function refreshFiles(volume = vol) {
    if (!volume || !onLoadStorageVolumeFiles) return
    const files = await onLoadStorageVolumeFiles(volume)
    setStorageFiles(files)
  }

  async function uploadFiles(files: File[]) {
    if (!vol || !onUploadStorageVolumeFiles || files.length === 0 || storageBusy) return
    setStorageBusy("upload")
    setStorageMessage("")
    try {
      const result = await onUploadStorageVolumeFiles(vol, files)
      if (Array.isArray(result)) setStorageFiles(result)
      else await refreshFiles(vol)
    } catch (error) {
      setStorageMessage(error instanceof Error ? error.message : "Upload failed")
    } finally {
      setStorageBusy("")
    }
  }

  async function importHFFile() {
    if (!vol || !onImportStorageVolumeHF || storageBusy) return
    const repoID = hfRepo.trim()
    const filename = hfFilename.trim()
    if (!repoID || !filename) return
    setStorageBusy("import")
    setStorageMessage("")
    try {
      const result = await onImportStorageVolumeHF(vol, {repoID, filename, hfToken: hfToken.trim()})
      if (Array.isArray(result)) setStorageFiles(result)
      else await refreshFiles(vol)
      setImportOpen(false)
      setHFRepo("")
      setHFFilename("")
      setHFToken("")
    } catch (error) {
      setStorageMessage(error instanceof Error ? error.message : "Import failed")
    } finally {
      setStorageBusy("")
    }
  }

  function openStorageFile(file: DirectWalletStorageFile) {
    if (!file.downloadURL) return
    window.open(file.downloadURL, "_blank", "noopener,noreferrer")
  }

  async function confirmDelete() {
    if (!deleteTarget || !onDeleteStorageVolume || deletingID) return
    if (confirmName.trim() !== deleteTarget.name) return
    setDeletingID(deleteTarget.id)
    try {
      await onDeleteStorageVolume(deleteTarget)
      setSelVol(current => current === deleteTarget.id ? liveVolumes.find(v=>v.id!==deleteTarget.id)?.id || "" : current)
      setDeleteTarget(null)
      setConfirmName("")
    } finally {
      setDeletingID("")
    }
  }

  return (
    <>
      <Panel onClose={onClose} wide>
        <PanelHead title="Storage" onClose={onClose} />
        <div className="flex-shrink-0 px-6 py-4 border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
          <SegCtrl options={["Volumes","Snapshots"]} value={tab} onChange={value=>setTab(value as DirectWalletStorageTab)} />
        </div>
        {tab === "Volumes" ? (
        <div className="flex flex-1 flex-col overflow-hidden md:flex-row">
          {/* Volume list */}
          <div className="max-h-[38%] w-full flex-shrink-0 overflow-y-auto border-b md:max-h-none md:w-[38%] md:border-b-0 md:border-r" style={{borderColor:"rgba(0,0,0,0.06)"}}>
            <div className="px-4 pt-4 pb-2 flex items-center justify-between">
              <Label>Volumes</Label>
              <motion.button type="button" whileTap={{scale:0.95}} onClick={()=>setCreateOpen(true)} disabled={creditActionsDisabled}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
                style={{background:creditActionsDisabled?"rgba(14,12,8,0.26)":"rgba(14,12,8,0.85)",color:"white"}}>
                <Plus size={11}/> {creditActionsDisabled ? "Purchase" : "New"}
              </motion.button>
            </div>
            {creditActionsDisabled ? (
              <div className="mx-4 mb-3 rounded-2xl px-3 py-3 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" style={{...G.inset,border:"1px solid rgba(0,0,0,0.07)"}}>
                <Wallet size={14} style={{color:"rgba(0,0,0,0.45)"}} />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold">Purchase credits</p>
                  <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Use a purchase link before opening a new volume.</p>
                </div>
                <button type="button" onClick={onNeedCredits} className="rounded-xl px-3 py-2 text-[11px] font-semibold" style={{background:"rgba(14,12,8,0.86)",color:"#fff"}}>
                  Open link
                </button>
              </div>
            ) : null}
            {liveVolumes.length === 0 ? (
              <p className="px-4 py-8 text-center text-[12px]" style={{color:"rgba(0,0,0,0.38)"}}>No live storage volumes yet.</p>
            ) : liveVolumes.map(v=>(
              <motion.div key={v.id}
                className="w-full flex items-center gap-2 px-4 py-3.5 transition-colors"
                style={{background:selVol===v.id?"rgba(0,0,0,0.04)":"transparent",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
                <button type="button" onClick={()=>setSelVol(v.id)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  aria-label={`Open ${v.name}`}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={G.inset}>
                    <Folder size={16} style={{color:"rgba(0,0,0,0.45)"}} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium truncate">{v.name}</p>
                    <p className="text-[11px]" style={{color:"rgba(0,0,0,0.4)"}}>{v.size} · {v.provider}</p>
                  </div>
                </button>
                {selVol===v.id ? (
                  <motion.button type="button" whileTap={{scale:0.9}}
                    onClick={()=>{ setDeleteTarget(v); setConfirmName("") }}
                    disabled={!onDeleteStorageVolume || deletingID===v.id}
                    className="w-7 h-7 rounded-full flex flex-shrink-0 items-center justify-center"
                    style={{background:"rgba(220,38,38,0.07)", color:"#dc2626", opacity:onDeleteStorageVolume?1:0.45}}
                    aria-label={`Delete ${v.name}`}
                    title="Delete volume">
                    {deletingID===v.id ? <LoaderCircle size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </motion.button>
                ) : null}
              </motion.div>
            ))}
          </div>
          {/* File browser */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {vol ? (
              <>
            <div className="px-5 pt-4 pb-2 flex flex-wrap items-center justify-between gap-3">
              <div><p className="text-[14px] font-semibold">{vol.name}</p><p className="text-[11px]" style={{color:"rgba(0,0,0,0.4)"}}>{vol.region} · {vol.provider} · {vol.files.toLocaleString()} files</p></div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={uploadInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event)=>{
                    const files = Array.from(event.currentTarget.files || [])
                    event.currentTarget.value = ""
                    void uploadFiles(files)
                  }}
                />
                <motion.button
                  type="button"
                  whileTap={{scale:0.95}}
                  onClick={()=>uploadInputRef.current?.click()}
                  disabled={!onUploadStorageVolumeFiles || Boolean(storageBusy)}
                  className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg"
                  style={{background:!onUploadStorageVolumeFiles || storageBusy?"rgba(14,12,8,0.28)":"rgba(14,12,8,0.85)",color:"white"}}>
                  {storageBusy==="upload" ? <LoaderCircle size={11} className="animate-spin" /> : <Upload size={11}/>}Upload
                </motion.button>
                <motion.button
                  type="button"
                  whileTap={{scale:0.95}}
                  onClick={()=>setImportOpen(value=>!value)}
                  disabled={!onImportStorageVolumeHF || Boolean(storageBusy)}
                  className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg"
                  style={{...G.subtle, opacity:!onImportStorageVolumeHF || storageBusy ? 0.52 : 1}}>
                  {storageBusy==="import" ? <LoaderCircle size={11} className="animate-spin" /> : <Download size={11}/>}HF Import
                </motion.button>
              </div>
            </div>
            <div className="px-5 pb-5">
              {importOpen ? (
                <div className="mb-3 rounded-2xl p-3" style={{...G.inset,border:"1px solid rgba(0,0,0,0.06)"}}>
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr]">
                    <input value={hfRepo} onChange={e=>setHFRepo(e.target.value)} placeholder="owner/repo" className="rounded-xl border-0 px-3 py-2 text-[12px] outline-none" style={G.subtle} />
                    <input value={hfFilename} onChange={e=>setHFFilename(e.target.value)} placeholder="path/file" className="rounded-xl border-0 px-3 py-2 text-[12px] outline-none" style={G.subtle} />
                  </div>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <input value={hfToken} onChange={e=>setHFToken(e.target.value)} placeholder="HF token" type="password" className="min-w-0 flex-1 rounded-xl border-0 px-3 py-2 text-[12px] outline-none" style={G.subtle} />
                    <div className="flex gap-2">
                      <motion.button type="button" whileTap={{scale:0.95}} onClick={()=>setImportOpen(false)} className="rounded-xl px-3 py-2 text-[12px] font-semibold" style={G.subtle}>Cancel</motion.button>
                      <motion.button type="button" whileTap={{scale:0.95}} onClick={()=>void importHFFile()} disabled={!hfRepo.trim() || !hfFilename.trim() || storageBusy==="import"} className="rounded-xl px-3 py-2 text-[12px] font-semibold text-white" style={{background:!hfRepo.trim() || !hfFilename.trim() || storageBusy==="import"?"rgba(14,12,8,0.28)":"rgba(14,12,8,0.85)"}}>
                        {storageBusy==="import" ? "Importing..." : "Import"}
                      </motion.button>
                    </div>
                  </div>
                </div>
              ) : null}
              {storageMessage ? <p className="mb-3 rounded-xl px-3 py-2 text-[11px]" style={{...G.subtle,color:"rgba(0,0,0,0.56)"}}>{storageMessage}</p> : null}
              {filesLoading ? (
                <div className="rounded-2xl px-4 py-8 text-center" style={G.inset}>
                  <LoaderCircle size={17} className="mx-auto mb-3 animate-spin" style={{color:"rgba(0,0,0,0.36)"}} />
                  <p className="text-[13px] font-semibold">Loading files</p>
                </div>
              ) : visibleFiles.length > 0 ? visibleFiles.map((file,i)=>(
                <div key={file.id || `${file.filename}-${i}`} className="flex items-center gap-3 py-3 hover:bg-black/[0.02] rounded-xl px-2 -mx-2 group" style={i>0?{borderTop:"1px solid rgba(0,0,0,0.04)"}:{}}>
                  <FileText size={13} style={{color:"rgba(0,0,0,0.35)"}} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px]">{file.filename}</p>
                    <p className="mt-0.5 truncate text-[10px]" style={{color:"rgba(0,0,0,0.36)"}}>
                      {file.sizeBytes ? formatWalletRunFileSize(file.sizeBytes) : file.providerStatus || file.provider || "Stored"}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {file.downloadURL ? (
                      <motion.button type="button" whileTap={{scale:0.9}} onClick={()=>openStorageFile(file)} className="p-1.5 rounded-lg" style={G.inset} aria-label={`Download ${file.filename}`}>
                        <Download size={12} style={{color:"rgba(0,0,0,0.5)"}} />
                      </motion.button>
                    ) : null}
                  </div>
                </div>
              )) : (
                <div className="rounded-2xl px-4 py-8 text-center" style={G.inset}>
                  <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-2xl" style={G.subtle}>
                    <FileText size={15} style={{color:"rgba(0,0,0,0.36)"}} />
                  </div>
                  <p className="text-[13px] font-semibold">No files yet</p>
                  <p className="mx-auto mt-1 max-w-[280px] text-[11px] leading-snug" style={{color:"rgba(0,0,0,0.42)"}}>Upload files or import from Hugging Face to populate this volume.</p>
                </div>
              )}
            </div>
            </>
            ) : (
              <p className="px-5 py-10 text-center text-[13px]" style={{color:"rgba(0,0,0,0.38)"}}>Connect storage or create a volume to populate this panel.</p>
            )}
          </div>
        </div>
        ) : (
          <StorageSnapshotsTab snapshots={snapshots} onDeleteRunSnapshot={onDeleteRunSnapshot} />
        )}
      </Panel>
      <AnimatePresence>
        {createOpen ? (
          <StorageCreateDialog
            routes={storageRoutes}
            creditActionsDisabled={creditActionsDisabled}
            onNeedCredits={onNeedCredits}
            onCreate={onCreateStorageVolume}
            onCancel={() => setCreateOpen(false)}
          />
        ) : null}
        {deleteTarget ? (
          <StorageDeleteDialog
            volume={deleteTarget}
            confirmName={confirmName}
            deleting={deletingID===deleteTarget.id}
            onChange={setConfirmName}
            onCancel={() => {
              if (deletingID) return
              setDeleteTarget(null)
              setConfirmName("")
            }}
            onConfirm={() => void confirmDelete()}
          />
        ) : null}
      </AnimatePresence>
    </>
  )
}

type DirectWalletSnapshotFileKind = "output" | "manifest"

type DirectWalletSnapshotViewerState = {
  snapshot: DirectWalletSnapshot
  kind: DirectWalletSnapshotFileKind
  href: string
  loading: boolean
  text: string
  error?: string
}

function snapshotFileKindLabel(kind: DirectWalletSnapshotFileKind) {
  return kind === "manifest" ? "Manifest" : "Output"
}

function readSnapshotStoredUserID() {
  if (typeof window === "undefined") return ""
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY)
    if (!raw) return ""
    const parsed = JSON.parse(raw) as { id?: unknown }
    return String(parsed?.id || "").trim()
  } catch {
    return ""
  }
}

function formatSnapshotViewerText(raw: string, contentType: string) {
  if (!/application\/json/i.test(contentType)) return raw
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

function snapshotViewerErrorMessage(raw: string, status: number) {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return String(parsed.error_detail || parsed.detail || parsed.message || `HTTP ${status}`)
  } catch {
    return raw.trim() || `HTTP ${status}`
  }
}

async function fetchSnapshotViewerText(href: string) {
  const settings = readUISettings()
  const headers: Record<string,string> = {
    Accept: "text/plain, application/json;q=0.9, */*;q=0.8",
  }
  if (settings.api_key) headers["X-API-Key"] = settings.api_key
  if (settings.tenant_id) headers["X-Tenant-Id"] = settings.tenant_id
  else {
    const storedUserID = readSnapshotStoredUserID()
    if (storedUserID) headers["X-Tenant-Id"] = storedUserID
  }
  const session = readStoredAuthSession()
  if (session?.token) headers.Authorization = `Bearer ${session.token}`

  const controller = new AbortController()
  const timer = window.setTimeout(()=>controller.abort(), 30000)
  try {
    const res = await fetch(buildAPIURL(href), {
      method: "GET",
      headers,
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    })
    const raw = await res.text()
    if (!res.ok) throw new Error(snapshotViewerErrorMessage(raw, res.status))
    return formatSnapshotViewerText(raw, res.headers.get("content-type") || "")
  } finally {
    window.clearTimeout(timer)
  }
}

function snapshotViewerFileName(viewer: DirectWalletSnapshotViewerState) {
  const base = String(viewer.snapshot.model || viewer.snapshot.id || "snapshot")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 56) || "snapshot"
  return `genrl-${base}-${viewer.kind}.${viewer.kind === "manifest" ? "json" : "txt"}`
}

function StorageSnapshotsTab({ snapshots, onDeleteRunSnapshot }: { snapshots: DirectWalletSnapshot[]; onDeleteRunSnapshot?: DirectWalletDashboardProps["onDeleteRunSnapshot"] }) {
  const [deletingSnapshotID,setDeletingSnapshotID]=useState("")
  const [viewer,setViewer]=useState<DirectWalletSnapshotViewerState | null>(null)

  async function deleteSnapshot(snapshot: DirectWalletSnapshot) {
    if (!onDeleteRunSnapshot || deletingSnapshotID) return
    setDeletingSnapshotID(snapshot.id)
    try {
      await onDeleteRunSnapshot(snapshot)
    } finally {
      setDeletingSnapshotID("")
    }
  }

  async function openSnapshotViewer(snapshot: DirectWalletSnapshot, kind: DirectWalletSnapshotFileKind, href?: string) {
    if (!href) return
    setViewer({ snapshot, kind, href, loading:true, text:"" })
    try {
      const text = await fetchSnapshotViewerText(href)
      setViewer(current=>current?.snapshot.id===snapshot.id && current.kind===kind
        ? { ...current, loading:false, text }
        : current)
    } catch (error) {
      setViewer(current=>current?.snapshot.id===snapshot.id && current.kind===kind
        ? { ...current, loading:false, error:error instanceof Error ? error.message : "Could not load snapshot file.", text:"" }
        : current)
    }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <Label>Snapshots</Label>
          <span className="text-[11px] font-medium" style={{color:"rgba(0,0,0,0.42)"}}>
            {snapshots.length.toLocaleString()} saved
          </span>
        </div>
        {snapshots.length === 0 ? (
          <div className="rounded-2xl px-4 py-10 text-center" style={G.inset}>
            <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-2xl" style={G.subtle}>
              <Layers size={16} style={{color:"rgba(0,0,0,0.36)"}} />
            </div>
            <p className="text-[13px] font-semibold">No snapshots yet</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl" style={G.inset}>
            {snapshots.map((snapshot,index)=>(
              <div
                key={snapshot.id}
                className={`${index>0?"border-t":""} flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center sm:gap-4`}
                style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={G.subtle}>
                    <Layers size={14} style={{color:"rgba(0,0,0,0.46)"}} />
                  </div>
                  <span className="hidden w-32 flex-shrink-0 truncate font-mono text-[11px] sm:block" title={snapshot.id} style={{color:"rgba(0,0,0,0.34)"}}>
                    {snapshot.id}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold">{snapshot.model}</p>
                    <p className="mt-0.5 truncate text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
                      {snapshot.jobId ? `Job ${snapshot.jobId}` : "Run snapshot"}
                    </p>
                    <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:hidden" style={{color:"rgba(0,0,0,0.34)"}}>
                      <span className="max-w-[180px] truncate font-mono" title={snapshot.id}>{snapshot.id}</span>
                      <span>{snapshot.size}</span>
                      <span>{snapshot.created}</span>
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 sm:flex-shrink-0 sm:justify-end">
                  <span className="hidden text-right text-[11px] tabular-nums sm:block" style={{color:"rgba(0,0,0,0.38)"}}>
                    {snapshot.size} · {snapshot.created}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <StorageSnapshotFileAction href={snapshot.outputDownloadURL} label="Output" icon="output" onOpen={()=>void openSnapshotViewer(snapshot, "output", snapshot.outputDownloadURL)} />
                    <StorageSnapshotFileAction href={snapshot.manifestDownloadURL} label="Manifest" icon="manifest" onOpen={()=>void openSnapshotViewer(snapshot, "manifest", snapshot.manifestDownloadURL)} />
                    <motion.button
                      type="button"
                      whileTap={{scale:0.95}}
                      onClick={()=>void deleteSnapshot(snapshot)}
                      disabled={!onDeleteRunSnapshot || Boolean(deletingSnapshotID)}
                      className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold"
                      style={{background:"rgba(220,38,38,0.08)",color:"#dc2626",opacity:onDeleteRunSnapshot ? 1 : 0.45}}>
                      {deletingSnapshotID === snapshot.id ? <LoaderCircle size={11} className="animate-spin" /> : <Trash2 size={11} />}Delete
                    </motion.button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <AnimatePresence>
        {viewer ? <StorageSnapshotViewerDialog viewer={viewer} onClose={()=>setViewer(null)} /> : null}
      </AnimatePresence>
    </>
  )
}

function StorageSnapshotFileAction({ href, label, icon, onOpen }: { href?: string; label: string; icon: "output" | "manifest"; onOpen: () => void }) {
  const Icon = icon === "manifest" ? FileText : Download
  const className = "inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[11px] font-semibold"
  if (!href) {
    return (
      <button
        type="button"
        disabled
        className={`${className} cursor-not-allowed`}
        style={{...G.subtle, opacity:0.42}}>
        <Icon size={11} />{label}
      </button>
    )
  }
  return (
    <motion.button
      type="button"
      whileTap={{scale:0.95}}
      onClick={onOpen}
      className={className}
      style={{...G.subtle,color:"rgba(0,0,0,0.66)"}}>
      <Icon size={11} />{label}
    </motion.button>
  )
}

function StorageSnapshotViewerDialog({ viewer, onClose }: { viewer: DirectWalletSnapshotViewerState; onClose: () => void }) {
  const [copied,setCopied]=useState(false)
  const label = snapshotFileKindLabel(viewer.kind)
  const canUseText = !viewer.loading && !viewer.error && viewer.text.length > 0

  useEffect(()=>{
    setCopied(false)
  },[viewer.snapshot.id, viewer.kind])

  async function copyViewerText() {
    if (!canUseText) return
    const ok = await copyTextToClipboard(viewer.text)
    if (ok) {
      setCopied(true)
      window.setTimeout(()=>setCopied(false),1400)
    }
  }

  function downloadViewerText() {
    if (!canUseText) return
    downloadTextFile(snapshotViewerFileName(viewer), viewer.text)
  }

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-center px-3 sm:px-5"
      style={{background:"rgba(20,16,8,0.28)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)"}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={onClose}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={`Snapshot ${label}`}
        className="flex max-h-[calc(100dvh-24px)] w-full max-w-[760px] flex-col overflow-hidden rounded-[28px] sm:max-h-[86vh] sm:rounded-3xl"
        style={G.panel}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b p-4 sm:p-5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
          <div className="min-w-0">
            <Label>Snapshot {label}</Label>
            <h2 className="mt-1 truncate text-[20px] font-semibold tracking-normal">{viewer.snapshot.model}</h2>
            <p className="mt-1 truncate text-[12px]" title={viewer.snapshot.id} style={{color:"rgba(0,0,0,0.44)"}}>
              {viewer.snapshot.id} · {viewer.snapshot.size} · {viewer.snapshot.created}
            </p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button type="button" onClick={copyViewerText} disabled={!canUseText} className="h-8 rounded-full px-3 text-[12px] font-semibold disabled:opacity-40" style={G.inset}>
              {copied ? "Copied" : "Copy"}
            </button>
            <button type="button" onClick={downloadViewerText} disabled={!canUseText} className="h-8 rounded-full px-3 text-[12px] font-semibold disabled:opacity-40" style={G.inset}>
              Download
            </button>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset} aria-label="Close snapshot viewer">
              <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {viewer.loading ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl px-5 py-12 text-[13px]" style={{...G.inset,color:"rgba(0,0,0,0.42)"}}>
              <LoaderCircle size={14} className="animate-spin" /> Loading {label.toLowerCase()}
            </div>
          ) : viewer.error ? (
            <div className="rounded-2xl px-5 py-8 text-center text-[13px]" style={{...G.inset,color:"#9f1239"}}>
              {viewer.error}
            </div>
          ) : (
            <pre className="max-h-[62vh] overflow-auto whitespace-pre-wrap break-words rounded-2xl p-4 font-mono text-[12px] leading-relaxed" style={{...G.inset,color:"rgba(0,0,0,0.72)"}}>
              {viewer.text || "No content returned."}
            </pre>
          )}
        </div>
      </motion.section>
    </motion.div>
  )
}

function StorageCreateDialog({
  routes,
  creditActionsDisabled,
  onNeedCredits,
  onCreate,
  onCancel,
}: {
  routes: DirectWalletStorageRoute[]
  creditActionsDisabled: boolean
  onNeedCredits?: () => void
  onCreate?: DirectWalletDashboardProps["onCreateStorageVolume"]
  onCancel: () => void
}) {
  const route = routes[0]
  const type = route?.volumeTypes.find(item=>item.id==="object" || item.volumeType.toLowerCase()==="object") || route?.volumeTypes[0]
  const environment = route?.environments[0]
  const [name,setName]=useState("")
  const [sizeGB,setSizeGB]=useState("20")
  const [creating,setCreating]=useState(false)
  const numericSize = Number(sizeGB || 0)
  const cleanSize = Number.isFinite(numericSize) ? Math.max(1, Math.floor(numericSize)) : NaN
  const estimatedMonthly = type?.pricePerGBHour && Number.isFinite(cleanSize) ? cleanSize * type.pricePerGBHour * 24 * 30 : 0

  async function submit() {
    const cleanName = name.trim()
    if (creditActionsDisabled) {
      onNeedCredits?.()
      return
    }
    if (!onCreate || !route || !type || !cleanName || !Number.isFinite(cleanSize) || cleanSize < 1 || creating) return
    setCreating(true)
    try {
      await onCreate({
        name: cleanName,
        sizeGB: cleanSize,
        volumeType: type.volumeType,
        environmentName: environment?.name || "managed-global",
        routeID: route.routeID,
        sourceSlug: route.sourceSlug,
        providerSlug: route.providerSlug,
      })
      onCancel()
    } finally {
      setCreating(false)
    }
  }

  const canCreate = Boolean(onCreate && route && type && name.trim() && Number.isFinite(cleanSize) && cleanSize > 0 && !creating && !creditActionsDisabled)
  const dialog = (
    <motion.div
      className="fixed inset-0 z-[90] grid place-items-center px-3 sm:px-5"
      style={{background:"rgba(20,16,8,0.28)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)"}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={() => {
        if (!creating) onCancel()
      }}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label="Create storage volume"
        className="max-h-[calc(100dvh-24px)] w-full max-w-[480px] overflow-y-auto rounded-[28px] p-4 sm:rounded-3xl sm:p-5"
        style={G.panel}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={G.inset}>
            <HardDrive size={17} style={{color:"rgba(0,0,0,0.58)"}} />
          </div>
          <button type="button" onClick={onCancel} disabled={creating} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset} aria-label="Cancel create volume">
            <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
          </button>
        </div>
        <div className="mt-4">
          <Label>New volume</Label>
          <h2 className="mt-2 text-[22px] font-semibold tracking-normal">Connect S3 storage</h2>
        </div>

        {routes.length === 0 ? (
          <p className="mt-5 rounded-2xl px-4 py-4 text-[13px] leading-relaxed" style={{...G.inset, color:"rgba(0,0,0,0.48)"}}>
            GENRL S3 is not ready yet.
          </p>
        ) : creditActionsDisabled ? (
          <div className="mt-5 rounded-2xl px-4 py-4 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center" style={{...G.inset,border:"1px solid rgba(0,0,0,0.07)"}}>
            <Wallet size={15} style={{color:"rgba(0,0,0,0.45)"}} />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold">Purchase credits</p>
              <p className="mt-1 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Open a purchase link before creating storage.</p>
            </div>
            <button type="button" onClick={onNeedCredits} className="rounded-xl px-3 py-2 text-[11px] font-semibold" style={{background:"rgba(14,12,8,0.86)",color:"#fff"}}>
              Open link
            </button>
          </div>
        ) : (
          <div className="mt-5 grid gap-3">
            <label className="block">
              <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>Name</span>
              <input value={name} onChange={event=>setName(event.target.value)} disabled={creating}
                className="mt-2 w-full rounded-2xl px-4 py-3 text-[13px] font-medium outline-none"
                style={G.field}
                placeholder="training-data"
                autoFocus />
            </label>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_160px]">
              <div className="rounded-2xl px-4 py-3" style={G.inset}>
                <p className="text-[12px] font-semibold">{route?.label || "GENRL S3"}</p>
                <p className="mt-1 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>{route?.detail || "Object storage through the GENRL account"}</p>
              </div>
              <label className="block">
                <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>Size GB</span>
                <input type="number" min="1" step="1" value={sizeGB} onChange={event=>setSizeGB(event.target.value)} disabled={creating}
                  className="mt-2 w-full rounded-2xl px-4 py-3 text-[13px] font-medium outline-none"
                  style={G.field} />
              </label>
            </div>
            <div className="rounded-2xl px-4 py-3" style={G.inset}>
              <p className="text-[12px] font-semibold">{type?.label || "S3 object storage"}</p>
              <p className="mt-1 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
                {estimatedMonthly > 0 ? `Estimated ${estimatedMonthly.toFixed(2)} UC/month` : "GENRL managed storage pricing applies after creation"}
              </p>
            </div>
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onCancel} disabled={creating} className="rounded-xl px-4 py-2 text-[12px] font-semibold" style={G.subtle}>
            Cancel
          </button>
          <motion.button type="button" whileTap={{scale:0.96}} onClick={() => void submit()} disabled={!canCreate}
            className="rounded-xl px-4 py-2 text-[12px] font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{background:creditActionsDisabled?"rgba(14,12,8,0.35)":"rgba(14,12,8,0.9)"}}>
            {creating ? <LoaderCircle size={13} className="animate-spin" /> : <Plus size={13} />}
            {creditActionsDisabled ? "Purchase credits" : "Create S3 storage"}
          </motion.button>
        </div>
      </motion.section>
    </motion.div>
  )
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body)
}

function StorageDeleteDialog({
  volume,
  confirmName,
  deleting,
  onChange,
  onCancel,
  onConfirm,
}: {
  volume: DirectWalletVolume
  confirmName: string
  deleting: boolean
  onChange: (value: string) => void
  onCancel: () => void
  onConfirm: () => void
}) {
  const canDelete = confirmName.trim() === volume.name
  const dialog = (
    <motion.div
      className="fixed inset-0 z-[90] grid place-items-center px-3 sm:px-5"
      style={{background:"rgba(20,16,8,0.28)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)"}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={onCancel}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={`Delete ${volume.name}`}
        className="max-h-[calc(100dvh-24px)] w-full max-w-[420px] overflow-y-auto rounded-[28px] p-4 sm:rounded-3xl sm:p-5"
        style={G.panel}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background:"rgba(220,38,38,0.08)"}}>
            <Trash2 size={17} style={{color:"#dc2626"}} />
          </div>
          <button type="button" onClick={onCancel} disabled={deleting} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset} aria-label="Cancel delete">
            <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
          </button>
        </div>
        <div className="mt-4">
          <Label>Delete volume</Label>
          <h2 className="mt-2 text-[22px] font-semibold tracking-normal break-words">{volume.name}</h2>
          <p className="mt-2 text-[12px] leading-relaxed" style={{color:"rgba(0,0,0,0.48)"}}>
            This permanently deletes the provider-native volume and stops showing it in live storage.
          </p>
        </div>
        <label className="mt-4 block">
          <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.42)"}}>Type {volume.name} to confirm</span>
          <input
            value={confirmName}
            onChange={event=>onChange(event.target.value)}
            disabled={deleting}
            className="mt-2 w-full rounded-2xl border-0 px-4 py-3 text-[13px] font-medium outline-none"
            style={{...G.inset, color:"rgba(0,0,0,0.78)"}}
            autoFocus
          />
        </label>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onCancel} disabled={deleting} className="rounded-xl px-4 py-2 text-[12px] font-semibold" style={G.subtle}>
            Cancel
          </button>
          <motion.button type="button" whileTap={{scale:0.96}} onClick={onConfirm} disabled={!canDelete || deleting}
            className="rounded-xl px-4 py-2 text-[12px] font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{background:"#dc2626"}}>
            {deleting ? <LoaderCircle size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Delete volume
          </motion.button>
        </div>
      </motion.section>
    </motion.div>
  )
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body)
}

function taskStageTone(status: DirectWalletTaskStageStatus) {
  if (status === "done") return { bg:"rgba(34,197,94,0.12)", color:"#15803d", border:"rgba(34,197,94,0.18)" }
  if (status === "failed") return { bg:"rgba(220,38,38,0.11)", color:"#b91c1c", border:"rgba(220,38,38,0.18)" }
  if (status === "active") return { bg:"rgba(217,143,50,0.13)", color:"#b45309", border:"rgba(217,143,50,0.22)" }
  return { bg:"rgba(0,0,0,0.04)", color:"rgba(0,0,0,0.36)", border:"rgba(0,0,0,0.06)" }
}

function taskStatusLabel(value?: string) {
  const text = String(value || "").trim().replace(/[_-]+/g, " ")
  if (!text) return "Pending"
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function normalizedTaskState(value?: string) {
  return String(value || "").trim().toLowerCase().replace(/[_\s-]+/g, "_")
}

function isProcessingTaskState(value?: string) {
  const text = normalizedTaskState(value)
  return Boolean(text) && (
    text.includes("active") ||
    text.includes("pending") ||
    text.includes("queued") ||
    text.includes("running") ||
    text.includes("processing") ||
    text.includes("streaming") ||
    text.includes("provision") ||
    text.includes("runtime_ready") ||
    text.includes("starting") ||
    text.includes("loading")
  )
}

function isTerminalTaskState(value?: string) {
  const text = normalizedTaskState(value)
  return Boolean(text) && (
    text.includes("failed") ||
    text.includes("error") ||
    text.includes("complete") ||
    text.includes("completed") ||
    text.includes("done") ||
    text.includes("stopped") ||
    text.includes("expired") ||
    text.includes("terminated") ||
    text.includes("closed") ||
    text.includes("closing") ||
    text.includes("output_recorded")
  )
}

function taskIsWaitingForTextOutput(task?: DirectWalletTaskDetail) {
  if (!task || task.lastError || String(task.output || "").trim()) return false
  const states = [task.status, task.providerStatus, task.stageLabel, task.leaseStatus]
  if (states.some(isTerminalTaskState)) return false
  return states.some(isProcessingTaskState)
}

function textStreamChunks(text: string) {
  const tokens = String(text || "").match(/\S+\s*/g) || []
  if (tokens.length === 0) return [String(text || "")]
  const chunks: string[] = []
  for (let index = 0; index < tokens.length; index += TEXT_RESPONSE_STREAM_WORDS) {
    chunks.push(tokens.slice(index, index + TEXT_RESPONSE_STREAM_WORDS).join(""))
  }
  return chunks
}

function streamedTextKey(message: DirectWalletTaskMessage, text = message.text) {
  return `${message.id}:${String(text || "").length}:${String(text || "").slice(0, 48)}`
}

function markAssistantMessagesStreamed(messages: DirectWalletTaskMessage[]) {
  messages.forEach(message => {
    if (message.role !== "assistant" || !String(message.text || "").trim()) return
    streamedTextMessageKeys.add(streamedTextKey(message))
  })
}

function shouldStreamAssistantMessage(message: DirectWalletTaskMessage) {
  const text = String(message.text || "").trim()
  if (message.role !== "assistant" || !text) return false
  if (text.toLowerCase().startsWith("processing")) return false
  if (text.toLowerCase().startsWith("working on it")) return false
  if (text.toLowerCase().startsWith("loading session")) return false
  if (text.toLowerCase().startsWith("no output")) return false
  if (normalizedTaskState(message.status).includes("streaming")) return false
  if (isTerminalTaskState(message.status) && normalizedTaskState(message.status).includes("failed")) return false
  return true
}

function isProcessingAssistantMessage(message: DirectWalletTaskMessage) {
  const text = String(message.text || "").trim().toLowerCase()
  if (message.role !== "assistant") return false
  return text.startsWith("processing") || text.startsWith("working on it")
}

function ProcessingMessageText() {
  const [dotCount, setDotCount] = useState(3)
  useEffect(() => {
    const timer = setInterval(() => {
      setDotCount(current => current >= 3 ? 1 : current + 1)
    }, 420)
    return () => clearInterval(timer)
  }, [])
  return (
    <span>
      Processing<span className="inline-block w-4">{".".repeat(dotCount)}</span>
    </span>
  )
}

function StreamingMessageText({ message, enabled }: { message: DirectWalletTaskMessage; enabled: boolean }) {
  const text = String(message.text || "")
  const chunks = useMemo(() => textStreamChunks(text), [text])
  const streamKey = streamedTextKey(message, text)
  const shouldAnimate = enabled && chunks.length > 1 && !streamedTextMessageKeys.has(streamKey)
  const [visibleCount, setVisibleCount] = useState(shouldAnimate ? 1 : chunks.length)

  useEffect(() => {
    if (!shouldAnimate) {
      setVisibleCount(chunks.length)
      return
    }
    setVisibleCount(1)
    const timer = setInterval(() => {
      setVisibleCount(current => {
        const next = Math.min(chunks.length, current + 1)
        if (next >= chunks.length) {
          streamedTextMessageKeys.add(streamKey)
          clearInterval(timer)
        }
        return next
      })
    }, TEXT_RESPONSE_STREAM_MS)
    return () => clearInterval(timer)
  }, [chunks.length, shouldAnimate, streamKey])

  return (
    <>
      {chunks.slice(0, visibleCount).join("")}
      {shouldAnimate && visibleCount < chunks.length ? <span className="ml-0.5 animate-pulse" aria-hidden="true">|</span> : null}
    </>
  )
}

function TaskStat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "muted" }) {
  const color = tone === "ok" ? "#15803d" : tone === "warn" ? "#b45309" : "rgba(0,0,0,0.68)"
  return (
    <div className="rounded-2xl px-3 py-3" style={G.inset}>
      <Label>{label}</Label>
      <p className="mt-1.5 min-h-[34px] text-[13px] font-semibold leading-snug break-words" title={value} style={{color, overflowWrap:"anywhere"}}>{value}</p>
    </div>
  )
}

function TaskDetailLine({
  label,
  value,
  mono=false,
  good=false,
  bad=false,
  copyable=false,
  copyValue,
}: {
  label: string
  value?: string
  mono?: boolean
  good?: boolean
  bad?: boolean
  copyable?: boolean
  copyValue?: string
}) {
  const text = String(value || "").trim() || "—"
  const copyText = String(copyValue ?? value ?? "").trim()
  const color = good ? "#15803d" : bad ? "#b91c1c" : "rgba(0,0,0,0.72)"
  const [copied, setCopied] = useState(false)

  async function copyLine() {
    if (!copyText) return
    const ok = await copyTextToClipboard(copyText)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1200)
  }

  return (
    <div className="grid grid-cols-[minmax(94px,0.36fr)_minmax(0,1fr)] items-start gap-3 py-2.5 border-b last:border-b-0" style={{borderColor:"rgba(0,0,0,0.06)"}}>
      <span className="text-[11px] font-semibold leading-snug" style={{color:"rgba(0,0,0,0.38)"}}>{label}</span>
      <span className="flex min-w-0 items-start justify-end gap-2 text-right">
        <span
          className={`min-w-0 whitespace-pre-wrap text-[12px] leading-snug ${mono ? "font-mono" : "font-medium"}`}
          style={{color, overflowWrap:"anywhere", wordBreak:mono ? "break-all" : "normal"}}>
          {text}
        </span>
        {copyable && copyText ? (
          <button
            type="button"
            onClick={copyLine}
            className="mt-[-2px] flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
            style={{background:"rgba(255,255,255,0.38)", color:"rgba(0,0,0,0.48)"}}
            aria-label={`Copy ${label}`}
            title={`Copy ${label}`}>
            {copied ? <Check size={11} /> : <Copy size={11} />}
          </button>
        ) : null}
      </span>
    </div>
  )
}

function runtimeSessionCanAcceptInput(status?: string) {
  const text = String(status || "").trim().toLowerCase().replace(/[_\s]+/g, "-")
  if (!text) return true
  return !["failed", "stopped", "expired", "closed", "terminated", "terminating", "completed", "running-input"].includes(text)
}

function runtimeSessionCanResume(status?: string) {
  const text = String(status || "").trim().toLowerCase().replace(/[_\s]+/g, "-")
  if (!text) return true
  return ![
    "failed",
    "stopped",
    "expired",
    "closed",
    "terminated",
    "terminating",
    "completed",
    "closing-complete",
    "genrl-prompt-completed",
  ].includes(text)
}

function runtimeSessionCanRestart(status?: string) {
  const text = String(status || "").trim().toLowerCase().replace(/[_\s]+/g, "-")
  return ["stopped", "expired", "closed", "terminated", "completed"].includes(text)
}

function taskMessageIsExportableOutput(message: DirectWalletTaskMessage) {
  const text = String(message.text || "").trim()
  if (message.role !== "assistant" || !text || isProcessingAssistantMessage(message)) return false
  const normalized = text.toLowerCase()
  return !normalized.startsWith("no output recorded") && !normalized.startsWith("loading session turns")
}

function taskMessageIsExportableConversation(message: DirectWalletTaskMessage) {
  const text = String(message.text || "").trim()
  if (!text || isProcessingAssistantMessage(message)) return false
  const normalized = text.toLowerCase()
  return message.role === "user" || (!normalized.startsWith("no output recorded") && !normalized.startsWith("loading session turns"))
}

function taskOutputExportText(messages: DirectWalletTaskMessage[]) {
  return messages
    .filter(taskMessageIsExportableOutput)
    .map(message => String(message.text || "").trim())
    .filter(Boolean)
    .join("\n\n---\n\n")
    .trim()
}

function taskConversationExportText(messages: DirectWalletTaskMessage[]) {
  return messages
    .filter(taskMessageIsExportableConversation)
    .map(message => {
      const role = message.role === "assistant" ? "Assistant" : message.role === "system" ? "System" : "User"
      const time = String(message.time || "").trim()
      const heading = time ? `## ${role} · ${time}` : `## ${role}`
      return `${heading}\n\n${String(message.text || "").trim()}`
    })
    .filter(Boolean)
    .join("\n\n---\n\n")
    .trim()
}

function taskGatewayContinuationMessages(messages: DirectWalletTaskMessage[], nextText: string): DirectWalletGatewayMessage[] {
  const prior = messages
    .filter(taskMessageIsExportableConversation)
    .map((message): DirectWalletGatewayMessage => ({
      role: message.role,
      text: String(message.text || "").trim(),
    }))
    .filter(message =>
      Boolean(message.text) && (message.role === "user" || message.role === "assistant" || message.role === "system")
    )
  const nextMessage: DirectWalletGatewayMessage = { role:"user", text:nextText }
  return [...prior, nextMessage].slice(-24)
}

function taskOutputFileName(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  const base = String(task?.title || row.label || row.id || "task-output")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || "task-output"
  return `genrl-${base}-output.md`
}

function taskAccessFileName(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  return taskOutputFileName(row, task).replace(/-output\.md$/, "-access.txt")
}

function taskAccessString(...values: unknown[]) {
  for (const value of values) {
    const text = String(value || "").trim()
    if (text) return text
  }
  return ""
}

function taskAccessRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function taskAccessMode(access?: DirectWalletTaskAccess) {
  return taskAccessString(access?.mode).toLowerCase()
}

function taskAccessHasArtifact(access?: DirectWalletTaskAccess) {
  return Boolean(access && Object.keys(access).length > 0)
}

function taskAccessPrimary(access?: DirectWalletTaskAccess) {
  if (!access) return null
  const mode = taskAccessMode(access)
  const ssh = taskAccessRecord(access.ssh)
  const web = taskAccessRecord(access.web)
  const api = taskAccessRecord(access.api)
  const container = taskAccessRecord(access.container)
  const cluster = taskAccessRecord(access.cluster)
  const headNode = taskAccessRecord(cluster.head_node)
  const primaryValue = taskAccessString(access.primary_value)
  const safePrimaryValue = mode === "cluster" && primaryValue && !primaryValue.trim().startsWith("ssh ") ? "" : primaryValue
  const label = taskAccessString(safePrimaryValue ? access.primary_label : "") ||
    (mode === "vm" ? "SSH command" : mode === "cluster" ? "Cluster SSH" : mode === "notebook" ? "Notebook URL" : mode === "api" ? "API base URL" : "Service URL")
  const value = taskAccessString(
    safePrimaryValue,
    mode === "vm" || mode === "cluster" ? taskAccessString(headNode.ssh_command, ssh.command) : "",
    mode === "notebook" ? web.notebook_url : "",
    mode === "api" ? api.base_url : "",
    mode === "container" ? container.runtime : "",
    mode === "cluster" ? "" : web.runtime_url,
  )
  return value ? { label, value } : null
}

function taskAccessCopyText(access?: DirectWalletTaskAccess) {
  const primary = taskAccessPrimary(access)
  const api = taskAccessRecord(access?.api)
  return taskAccessString(access?.copy_text, api.sample_curl, primary?.value)
}

function taskAccessPrivateKey(access?: DirectWalletTaskAccess) {
  const ssh = taskAccessRecord(access?.ssh)
  return taskAccessString(ssh.private_key_download_path)
}

function taskAccessKubeconfig(access?: DirectWalletTaskAccess) {
  const cluster = taskAccessRecord(access?.cluster)
  const kubeconfig = taskAccessRecord(cluster.kubeconfig)
  return taskAccessString(kubeconfig.content)
}

function taskAccessKubeconfigURL(access?: DirectWalletTaskAccess) {
  const cluster = taskAccessRecord(access?.cluster)
  return taskAccessString(cluster.kubeconfig_url)
}

function taskAccessKubeconfigFileName(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  const cluster = taskAccessRecord(task?.access?.cluster)
  const kubeconfig = taskAccessRecord(cluster.kubeconfig)
  return taskAccessString(kubeconfig.file_name) || taskAccessFileName(row, task).replace(/-access\.txt$/, "-kubeconfig.yaml")
}

function taskAccessPrivateKeyFileName(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  const ssh = taskAccessRecord(task?.access?.ssh)
  return taskAccessString(ssh.private_key_file_name) || taskAccessFileName(row).replace(/-access\.txt$/, "-ssh-key.pem")
}

function taskAccessOpenURL(access?: DirectWalletTaskAccess) {
  const primary = taskAccessPrimary(access)
  const web = taskAccessRecord(access?.web)
  const api = taskAccessRecord(access?.api)
  const cluster = taskAccessRecord(access?.cluster)
  const candidates = [
    primary?.value,
    web.notebook_url,
    cluster.dashboard_url,
    cluster.genrl_proxy_url,
    web.dashboard_url,
    web.genrl_proxy_url,
    web.runtime_url,
    api.base_url,
  ]
  return candidates.map(value=>taskAccessString(value)).find(value=>/^https?:\/\//i.test(value) || value.startsWith("/api/")) || ""
}

function taskAccessExportText(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  const access = task?.access
  if (!access) return ""
  const primary = taskAccessPrimary(access)
  const api = taskAccessRecord(access.api)
  const ssh = taskAccessRecord(access.ssh)
  const container = taskAccessRecord(access.container)
  const web = taskAccessRecord(access.web)
  const resource = taskAccessRecord(access.resource)
  const cluster = taskAccessRecord(access.cluster)
  const headNode = taskAccessRecord(cluster.head_node)
  const lines = [
    `GENRL access for ${task?.title || row.label}`,
    taskAccessString(access.mode) ? `Mode: ${taskAccessString(access.mode)}` : "",
    taskAccessString(access.status) ? `Status: ${taskAccessString(access.status)}` : "",
    taskAccessString(access.provider) ? `Provider: ${taskAccessString(access.provider)}` : "",
    primary ? `${primary.label}: ${primary.value}` : "",
    taskAccessString(ssh.username) ? `SSH username: ${taskAccessString(ssh.username)}` : "",
    taskAccessString(ssh.host) ? `SSH host: ${taskAccessString(ssh.host)}` : "",
    taskAccessString(cluster.cluster_id) ? `Cluster ID: ${taskAccessString(cluster.cluster_id)}` : "",
    taskAccessString(cluster.cluster_type) ? `Cluster type: ${taskAccessString(cluster.cluster_type)}` : "",
    taskAccessString(cluster.node_count) ? `Cluster nodes: ${taskAccessString(cluster.node_count)}` : "",
    taskAccessString(headNode.ssh_command) ? `Head node SSH: ${taskAccessString(headNode.ssh_command)}` : "",
    taskAccessString(cluster.genrl_proxy_url, web.genrl_proxy_url) ? `GENRL proxy: ${taskAccessString(cluster.genrl_proxy_url, web.genrl_proxy_url)}` : "",
    taskAccessString(cluster.dashboard_url, web.dashboard_url) ? `Cluster dashboard: ${taskAccessString(cluster.dashboard_url, web.dashboard_url)}` : "",
    taskAccessString(cluster.kubeconfig_url) ? `Kubeconfig: ${taskAccessString(cluster.kubeconfig_url)}` : "",
    taskAccessString(cluster.api_server) ? `Kubernetes API: ${taskAccessString(cluster.api_server)}` : "",
    taskAccessString(container.image) ? `Container image: ${taskAccessString(container.image)}` : "",
    taskAccessString(container.start_command) ? `Start command: ${taskAccessString(container.start_command)}` : "",
    taskAccessString(container.port_spec) ? `Port: ${taskAccessString(container.port_spec)}` : "",
    taskAccessString(container.logs_status) ? `Logs: ${taskAccessString(container.logs_status)}` : "",
    taskAccessString(web.runtime_url) ? `Runtime URL: ${taskAccessString(web.runtime_url)}` : "",
    taskAccessString(api.workloads_url) ? `Workloads URL: ${taskAccessString(api.workloads_url)}` : "",
    taskAccessString(api.models_url) ? `Models URL: ${taskAccessString(api.models_url)}` : "",
    taskAccessString(api.chat_completions_url) ? `Chat completions URL: ${taskAccessString(api.chat_completions_url)}` : "",
    taskAccessString(api.model) ? `Model: ${taskAccessString(api.model)}` : "",
    taskAccessString(api.api_key) ? `API key: ${taskAccessString(api.api_key)}` : "",
    taskAccessString(resource.id) ? `Resource ID: ${taskAccessString(resource.id)}` : "",
    taskAccessString(resource.region) ? `Region: ${taskAccessString(resource.region)}` : "",
    taskAccessString(api.sample_curl) ? `\nSample curl:\n${taskAccessString(api.sample_curl)}` : "",
  ].filter(Boolean)
  return lines.join("\n")
}

function taskAccessDownloadText(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  return taskAccessKubeconfig(task?.access) || taskAccessExportText(row, task)
}

function taskAccessDownloadFileName(row: DirectWalletLedgerRow, task?: DirectWalletTaskDetail) {
  if (taskAccessKubeconfig(task?.access)) return taskAccessKubeconfigFileName(row, task)
  return taskAccessPrivateKey(task?.access) ? taskAccessPrivateKeyFileName(row, task) : taskAccessFileName(row, task)
}

function taskAccessTitle(access?: DirectWalletTaskAccess) {
  switch (taskAccessMode(access)) {
  case "vm":
    return "SSH access"
  case "cluster":
    return "Cluster access"
  case "notebook":
    return "Notebook access"
  case "api":
    return "API access"
  case "container":
    return "Container access"
  default:
    return "Access"
  }
}

function taskAccessStatus(access?: DirectWalletTaskAccess) {
  const status = taskAccessString(access?.status)
  if (status) return taskStatusLabel(status)
  if (taskAccessPrimary(access) || taskAccessPrivateKey(access)) return "Ready"
  return "Pending"
}

function taskAccessShellArg(value: string) {
  const text = String(value || "").trim()
  if (!text) return ""
  if (/^[A-Za-z0-9_./:@-]+$/.test(text)) return text
  return "'" + text.replace(/'/g, "'\\''") + "'"
}

function taskAccessSSHCommand(command: string, privateKeyFileName: string) {
  const cleanCommand = taskAccessString(command)
  const keyFile = taskAccessString(privateKeyFileName)
  if (!cleanCommand || !keyFile || /\s-i\s+\S+/.test(cleanCommand)) return cleanCommand
  if (!cleanCommand.startsWith("ssh ")) return cleanCommand
  return cleanCommand.replace(/^ssh\s+/, `ssh -i ${taskAccessShellArg(keyFile)} `)
}

function taskAccessPrivateKeyPEMLooksValid(value: string) {
  const text = String(value || "").trim()
  return /^-----BEGIN (?:OPENSSH |RSA |EC |DSA )?PRIVATE KEY-----[\s\S]+-----END (?:OPENSSH |RSA |EC |DSA )?PRIVATE KEY-----$/.test(text)
}

function taskProviderJobCancelable(task?: DirectWalletTaskDetail, options: { allowCompleted?: boolean } = {}) {
  const states = [task?.status, task?.providerStatus, task?.leaseStatus, task?.stageLabel].map(normalizedTaskState)
  if (states.some(state =>
    state.includes("terminated") ||
    state.includes("terminating") ||
    state.includes("closed") ||
    state.includes("closing-complete") ||
    state.includes("cleanup") ||
    state.includes("failed") ||
    (!options.allowCompleted && state.includes("completed"))
  )) return false
  return Boolean(task?.providerJobId)
}

function taskPersistentRuntimeLooksActive(task?: DirectWalletTaskDetail) {
  if (!task?.persistent || !task.runtimeSessionId) return false
  const states = [task.status, task.providerStatus, task.leaseStatus, task.stageLabel].map(normalizedTaskState)
  if (task.lastError || states.some(state =>
    state.includes("failed") ||
    state.includes("error") ||
    state.includes("terminating") ||
    state.includes("terminated") ||
    state.includes("closing_complete") ||
    state.includes("closed") ||
    state.includes("stopped") ||
    state.includes("expired")
  )) return false
  const leaseActive = states.some(state => state.includes("active") || state.includes("reserved") || state.includes("running"))
  const providerActive = states.some(state => state.includes("active") || state.includes("runtime_ready") || state.includes("idle"))
  return Boolean((leaseActive || providerActive) && (task.publicIp || task.runtimeUrl || task.remoteId || task.providerJobId))
}

function taskPersistentRuntimeLooksClosed(task?: DirectWalletTaskDetail, sessionStatus?: string, sessionBillingStatus?: string) {
  if (!task?.persistent || !task.runtimeSessionId) return false
  const states = [sessionStatus, sessionBillingStatus, task.status, task.providerStatus, task.leaseStatus, task.stageLabel].map(normalizedTaskState)
  if (states.some(state =>
    state.includes("closing_complete") ||
    state.includes("closed") ||
    state.includes("stopped") ||
    state.includes("expired") ||
    state.includes("terminated") ||
    state.includes("user_requested_stop")
  )) return true
  const billingSettled = states.some(state => state.includes("settled"))
  const resourceReleased = states.some(state => state.includes("terminated") || state.includes("released"))
  return billingSettled && resourceReleased
}

function taskAccessResourceClosable(task?: DirectWalletTaskDetail) {
  return taskProviderJobCancelable(task, { allowCompleted: true })
}

function runtimeSessionClosable(status?: string) {
  const text = String(status || "").trim().toLowerCase().replace(/[_\s]+/g, "-")
  if (!text) return true
  return ![
    "failed",
    "stopped",
    "expired",
    "closed",
    "terminated",
    "terminating",
    "completed",
    "closing",
    "stopping",
    "closing-complete",
    "genrl-prompt-completed",
  ].includes(text)
}

function taskLifecycleSettled(task?: DirectWalletTaskDetail, sessionStatus?: string) {
  if (taskPersistentRuntimeLooksActive(task)) return false
  const states = [sessionStatus, task?.status, task?.providerStatus, task?.leaseStatus, task?.stageLabel].map(normalizedTaskState)
  return states.some(state =>
    state.includes("closing-complete") ||
    state.includes("closed") ||
    state.includes("terminated") ||
    state.includes("expired") ||
    state.includes("completed")
  )
}

function taskAccessResourceCloseLabel(mode: string) {
  switch (mode) {
    case "gateway-job":
      return "Cancel Gateway"
    case "job":
      return "Cancel VM"
    case "session":
      return "Close Session"
    case "vm":
      return "Close SSH"
    case "cluster":
      return "Close Cluster"
    case "container":
      return "Close Container"
    case "notebook":
      return "Close Notebook"
    case "api":
      return "Close API"
    default:
      return ""
  }
}

function taskAccessResourceCloseNoun(mode: string) {
  switch (mode) {
    case "gateway-job":
      return "gateway job"
    case "job":
      return "VM"
    case "session":
      return "persistent session"
    case "vm":
      return "SSH VM"
    case "cluster":
      return "cluster"
    case "container":
      return "container"
    case "notebook":
      return "notebook"
    case "api":
      return "API endpoint"
    default:
      return "resource"
  }
}

async function copyTextToClipboard(text: string) {
  if (!text) return false
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Fall back to the selection API below for older or restricted browsers.
    }
  }
  if (typeof document === "undefined") return false
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "true")
  textarea.style.position = "fixed"
  textarea.style.left = "-9999px"
  textarea.style.top = "0"
  document.body.appendChild(textarea)
  textarea.focus()
  textarea.select()
  textarea.setSelectionRange(0, textarea.value.length)
  let copied = false
  try {
    copied = document.execCommand("copy")
  } catch {
    copied = false
  } finally {
    textarea.remove()
  }
  return copied
}

function downloadTextFile(fileName: string, text: string, type = "text/markdown;charset=utf-8") {
  if (!text || typeof document === "undefined" || typeof URL === "undefined") return
  const blob = new Blob([text.endsWith("\n") ? text : `${text}\n`], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

function TaskChatBubble({ message }: { message: DirectWalletTaskMessage }) {
  const user = message.role === "user"
  const system = message.role === "system"
  const processing = isProcessingAssistantMessage(message)
  const stream = shouldStreamAssistantMessage(message)
  const bubbleStyle = user
    ? { background:"rgba(14,12,8,0.88)", color:"#fff", border:"1px solid rgba(255,255,255,0.08)" }
    : system
      ? { background:"rgba(217,143,50,0.10)", color:"rgba(0,0,0,0.70)", border:"1px solid rgba(217,143,50,0.16)" }
      : { ...G.inset, color:"rgba(0,0,0,0.76)" }
  const metaColor = user ? "rgba(255,255,255,0.48)" : "rgba(0,0,0,0.36)"
  const showStatus = Boolean(!user && message.status)
  const hasMeta = Boolean(showStatus || message.time)
  return (
    <div className={`flex ${user ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[86%] rounded-2xl px-4 py-3" style={bubbleStyle}>
        {hasMeta ? (
          <div className={`mb-1.5 flex items-center gap-2 ${user ? "justify-end" : ""}`}>
            {showStatus ? (
              <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{background:user ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.05)", color:user ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.46)"}}>
                {taskStatusLabel(message.status)}
              </span>
            ) : null}
            {message.time ? <span className="text-[10px]" style={{color:metaColor}}>{message.time}</span> : null}
          </div>
        ) : null}
        <p className="whitespace-pre-wrap break-words text-[12px] leading-relaxed">
          {processing ? <ProcessingMessageText /> : <StreamingMessageText message={message} enabled={stream} />}
        </p>
      </div>
    </div>
  )
}

function taskConversationMessages(row: DirectWalletLedgerRow, task: DirectWalletTaskDetail | undefined, sessionDetail: DirectWalletRuntimeSessionDetail | null, outputFallback: string) {
  if (sessionDetail?.messages.length) return sessionDetail.messages
  const gatewayMessages = task?.gatewayMessages?.filter(message => String(message.text || "").trim()) || []
  if (gatewayMessages.length > 0) {
    const messages: DirectWalletTaskMessage[] = gatewayMessages.map((message, index) => ({
      id: `${row.id}:gateway:${index}`,
      role: message.role,
      text: String(message.text || "").trim(),
      time: row.time,
    }))
    const output = String(task?.output || "").trim()
    const last = messages[messages.length - 1]
    if (output && !(last?.role === "assistant" && String(last.text || "").trim() === output)) {
      messages.push({ id:`${row.id}:output`, role:"assistant", text:output, status:task?.status, time:row.time })
    } else if (!output && task) {
      const waiting = taskIsWaitingForTextOutput(task)
      messages.push({
        id:`${row.id}:output-pending`,
        role:"assistant",
        text:waiting ? PROCESSING_RESPONSE_TEXT : outputFallback,
        status:task.lastError ? "failed" : waiting ? "processing" : undefined,
        time:row.time,
      })
    }
    return messages
  }
  const input = String(task?.input || task?.prompt || "").trim()
  const output = String(task?.output || "").trim()
  const messages: DirectWalletTaskMessage[] = []
  if (input) {
    messages.push({ id:`${row.id}:input`, role:"user", text:input, time:row.time })
  }
  if (output) {
    messages.push({ id:`${row.id}:output`, role:"assistant", text:output, status:task?.status, time:row.time })
  } else if (task) {
    const waiting = taskIsWaitingForTextOutput(task)
    messages.push({
      id:`${row.id}:output-pending`,
      role:"assistant",
      text:waiting ? PROCESSING_RESPONSE_TEXT : outputFallback,
      status:task.lastError ? "failed" : waiting ? "processing" : undefined,
      time:row.time,
    })
  }
  return messages
}

function taskMessageNormalizedText(message: DirectWalletTaskMessage) {
  return String(message.text || "").trim().replace(/\s+/g, " ").toLowerCase()
}

function taskMessageIsActive(message: DirectWalletTaskMessage) {
  return isProcessingTaskState(message.status) && !isTerminalTaskState(message.status)
}

function taskMessageIsPendingAssistant(message: DirectWalletTaskMessage) {
  return message.role === "assistant" && isProcessingAssistantMessage(message) && taskMessageIsActive(message)
}

function dedupeTaskConversationMessages(messages: DirectWalletTaskMessage[]) {
  const visible: DirectWalletTaskMessage[] = []
  for (const message of messages) {
    const text = taskMessageNormalizedText(message)
    if (!text) continue
    if (message.role === "user" && taskMessageIsActive(message) && visible.some(existing =>
      existing.role === "user" &&
      taskMessageIsActive(existing) &&
      taskMessageNormalizedText(existing) === text
    )) {
      continue
    }
    if (taskMessageIsPendingAssistant(message)) {
      if (visible.some(taskMessageIsPendingAssistant)) continue
    } else if (message.role === "assistant" && taskMessageIsActive(message)) {
      for (let index = visible.length - 1; index >= 0; index -= 1) {
        if (taskMessageIsPendingAssistant(visible[index])) visible.splice(index, 1)
      }
    }
    visible.push(message)
  }
  return visible
}

function TaskConversation({
  messages,
  loading,
  error,
  showComposer,
  canSendTurn,
  sessionStatus,
  placeholder,
  allowFiles = true,
  turnInput,
  attachedFiles,
  turnBusy,
  actions,
  onInputChange,
  onAttachFiles,
  onRemoveFile,
  onSubmit,
}: {
  messages: DirectWalletTaskMessage[]
  loading?: boolean
  error?: string
  showComposer?: boolean
  canSendTurn?: boolean
  sessionStatus?: string
  placeholder?: string
  allowFiles?: boolean
  turnInput?: string
  attachedFiles?: File[]
  turnBusy?: boolean
  actions?: ReactNode
  onInputChange?: (value: string) => void
  onAttachFiles?: (files: File[]) => void
  onRemoveFile?: (index: number) => void
  onSubmit?: () => void | Promise<void>
}) {
  const visible = dedupeTaskConversationMessages(messages.filter(message => String(message.text || "").trim()))
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [dragging,setDragging]=useState(false)
  const files = attachedFiles || []
  const sendable = Boolean(canSendTurn && String(turnInput || "").trim() && !turnBusy)
  const optimisticInput = String(turnInput || "").trim()
  const hasMatchingPendingInput = Boolean(optimisticInput) && visible.some(message =>
    message.role === "user" &&
    String(message.text || "").trim() === optimisticInput &&
    isProcessingTaskState(message.status)
  )
  const hasPendingAssistant = visible.some(message => message.role === "assistant" && isProcessingTaskState(message.status))
  const showOptimisticTurn = Boolean(turnBusy && optimisticInput && !hasMatchingPendingInput)
  const showProcessingBubble = Boolean(turnBusy && !hasPendingAssistant)

  function addFileList(list: FileList | null) {
    const next = Array.from(list || [])
    if (next.length > 0) onAttachFiles?.(next)
  }

  return (
    <div
      className="rounded-2xl p-4"
      style={{...G.subtle, border:dragging ? "1px solid rgba(217,143,50,0.42)" : G.subtle.border}}
      onDragEnter={event=>{
        if (!showComposer || !canSendTurn || !allowFiles) return
        event.preventDefault()
        setDragging(true)
      }}
      onDragOver={event=>{
        if (!showComposer || !canSendTurn || !allowFiles) return
        event.preventDefault()
      }}
      onDragLeave={event=>{
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
      }}
      onDrop={event=>{
        if (!showComposer || !canSendTurn || !allowFiles) return
        event.preventDefault()
        setDragging(false)
        addFileList(event.dataTransfer.files)
      }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label>Conversation</Label>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {actions}
          {loading ? <LoaderCircle size={13} className="animate-spin" style={{color:"rgba(0,0,0,0.42)"}} /> : null}
        </div>
      </div>
      <div className="mt-3 max-h-[330px] overflow-y-auto space-y-3 pr-1" style={{scrollbarWidth:"none"}}>
        {visible.length > 0 ? (
          visible.map(message => <TaskChatBubble key={message.id} message={message} />)
        ) : loading && !turnBusy ? (
          null
        ) : (
          <TaskChatBubble message={{id:"empty", role:"system", text:"No task payload recorded for this ledger entry."}} />
        )}
        {showOptimisticTurn ? (
          <TaskChatBubble message={{id:"optimistic-input", role:"user", text:optimisticInput, status:"running", time:"just now"}} />
        ) : null}
        {showProcessingBubble ? (
          <TaskChatBubble message={{id:"optimistic-processing", role:"assistant", text:PROCESSING_RESPONSE_TEXT, status:"processing", time:"just now"}} />
        ) : loading && visible.length === 0 && !turnBusy ? (
          <TaskChatBubble message={{id:"loading", role:"assistant", text:"Syncing conversation.", status:"loading"}} />
        ) : null}
      </div>
      {error ? <p className="mt-3 text-[11px] leading-relaxed" style={{color:"#b91c1c"}}>{error}</p> : null}
      {showComposer ? (
        <form
          className="mt-4 rounded-2xl p-2"
          style={{...G.inset, outline:dragging ? "1px dashed rgba(217,143,50,0.56)" : "1px solid rgba(0,0,0,0.04)"}}
          onSubmit={event => {
            event.preventDefault()
            void onSubmit?.()
          }}>
          {files.length > 0 ? (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {files.map((file,index)=>(
                <span key={`${file.name}:${file.size}:${index}`} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium" style={{background:"rgba(255,255,255,0.34)", color:"rgba(0,0,0,0.58)"}}>
                  <FileText size={11} />
                  <span className="max-w-[160px] truncate">{file.name}</span>
                  <button type="button" onClick={()=>onRemoveFile?.(index)} className="w-4 h-4 rounded-full flex items-center justify-center" style={{background:"rgba(0,0,0,0.06)"}} aria-label={`Remove ${file.name}`}>
                    <X size={9} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            {allowFiles ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={event=>{
                    addFileList(event.target.files)
                    event.target.value = ""
                  }}
                />
                <button
                  type="button"
                  disabled={!canSendTurn || turnBusy}
                  onClick={()=>fileInputRef.current?.click()}
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40"
                  style={{background:"rgba(255,255,255,0.32)", color:"rgba(0,0,0,0.58)"}}
                  aria-label="Attach files"
                  title="Attach files">
                  <Upload size={14} />
                </button>
              </>
            ) : null}
            <textarea
              value={turnInput || ""}
              onChange={event=>onInputChange?.(event.target.value)}
              rows={2}
              disabled={!canSendTurn || turnBusy}
              placeholder={placeholder || (canSendTurn ? "Message this VM" : `Session ${taskStatusLabel(sessionStatus)}`)}
              className="min-h-[44px] flex-1 resize-none bg-transparent px-2 py-2 text-[12px] leading-relaxed outline-none placeholder:text-black/35 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!sendable}
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40"
              style={{background:"rgba(14,12,8,0.88)", color:"#fff"}}
              aria-label="Send input"
              title="Send input">
              {turnBusy ? <LoaderCircle size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  )
}

function persistentVMProviderJobId(row: DirectWalletLedgerRow, task: DirectWalletTaskDetail | undefined, sessionDetail: DirectWalletRuntimeSessionDetail | null) {
  return taskAccessString(task?.providerJobId, sessionDetail?.session.providerJobId, task?.id, row.id)
}

function persistentVMSameVMChain(row: DirectWalletLedgerRow, task: DirectWalletTaskDetail | undefined, sessionDetail: DirectWalletRuntimeSessionDetail | null) {
  const runtimeSessionId = taskAccessString(task?.runtimeSessionId, sessionDetail?.session.id)
  const providerJobId = persistentVMProviderJobId(row, task, sessionDetail)
  const backingVM = taskAccessString(task?.remoteId, task?.publicIp, task?.runtimeUrl)
  const parts = [
    runtimeSessionId ? `session ${runtimeSessionId}` : "",
    providerJobId ? `job ${providerJobId}` : "",
    backingVM ? `vm ${backingVM}` : "",
  ].filter(Boolean)
  return parts.join(" -> ") || "Waiting for runtime session binding"
}

function persistentVMEvidenceText(row: DirectWalletLedgerRow, task: DirectWalletTaskDetail | undefined, sessionDetail: DirectWalletRuntimeSessionDetail | null, sessionStatus?: string) {
  const session = sessionDetail?.session
  const runtimeSessionId = taskAccessString(task?.runtimeSessionId, session?.id)
  const providerJobId = persistentVMProviderJobId(row, task, sessionDetail)
  const lines = [
    ["Task", taskAccessString(task?.title, row.label)],
    ["Status", taskStatusLabel(sessionStatus)],
    ["Same VM chain", persistentVMSameVMChain(row, task, sessionDetail)],
    ["Runtime session", runtimeSessionId],
    ["Provider job", providerJobId],
    ["Provider", taskAccessString(session?.provider, task?.provider, row.meta)],
    ["Model", taskAccessString(session?.model, task?.model)],
    ["GPU", taskAccessString(task?.gpu, session?.hw)],
    ["Backing VM", taskAccessString(task?.remoteId)],
    ["Public IP", taskAccessString(task?.publicIp)],
    ["Runtime URL", taskAccessString(task?.runtimeUrl)],
    ["Turns", session?.turns != null ? String(session.turns) : ""],
    ["Last active", taskAccessString(session?.lastActive, session?.started)],
    ["Billing", taskAccessString(session?.billingStatus, task?.leaseStatus, hardwareCostBandLabel(session?.costBand || "Mid cost"))],
    ["Snapshot", task?.snapshotEnabled ? taskAccessString(task.snapshotId, "Snapshot pending") : ""],
  ]
  return ["GENRL persistent VM evidence", ...lines.map(([label, value]) => `${label}: ${value || "—"}`)].join("\n")
}

function PersistentVMEvidencePanel({
  row,
  task,
  sessionDetail,
  sessionStatus,
  copied,
  onCopy,
}: {
  row: DirectWalletLedgerRow
  task?: DirectWalletTaskDetail
  sessionDetail: DirectWalletRuntimeSessionDetail | null
  sessionStatus?: string
  copied: boolean
  onCopy: () => void | Promise<void>
}) {
  const session = sessionDetail?.session
  const runtimeSessionId = taskAccessString(task?.runtimeSessionId, session?.id)
  const providerJobId = persistentVMProviderJobId(row, task, sessionDetail)
  const sameVMChain = persistentVMSameVMChain(row, task, sessionDetail)
  const provider = taskAccessString(session?.provider, task?.provider, row.meta)
  const model = taskAccessString(session?.model, task?.model)
  const turns = session?.turns != null ? String(session.turns) : ""
  const billing = taskAccessString(
    session?.billingStatus,
    task?.leaseStatus,
    hardwareCostBandLabel(session?.costBand || "Mid cost"),
  )
  const snapshot = task?.snapshotEnabled ? taskAccessString(task.snapshotId, "Snapshot pending") : ""
  const sameVMReady = Boolean(runtimeSessionId && providerJobId && (task?.remoteId || task?.publicIp || task?.runtimeUrl))

  return (
    <div className="rounded-2xl p-4" style={G.subtle}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Label>Persistent VM evidence</Label>
          <p className="mt-1 text-[12px] font-medium leading-relaxed break-words" title={sameVMChain} style={{color:"rgba(0,0,0,0.58)", overflowWrap:"anywhere"}}>
            {sameVMChain}
          </p>
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex h-8 flex-shrink-0 items-center justify-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold"
          style={G.inset}
          aria-label="Copy persistent VM evidence"
          title="Copy persistent VM evidence">
          {copied ? <Check size={13} /> : <Copy size={13} />}
          <span>{copied ? "Copied" : "Copy evidence"}</span>
        </button>
      </div>
      <div className="mt-3 rounded-2xl px-3" style={G.inset}>
        <TaskDetailLine label="Status" value={taskStatusLabel(sessionStatus)} good={sameVMReady && runtimeSessionCanAcceptInput(sessionStatus)} />
        <TaskDetailLine label="Same VM chain" value={sameVMChain} mono good={sameVMReady} copyable copyValue={sameVMChain} />
        <TaskDetailLine label="Runtime session" value={runtimeSessionId} mono copyable />
        <TaskDetailLine label="Provider job" value={providerJobId} mono copyable />
        <TaskDetailLine label="Provider" value={provider} />
        <TaskDetailLine label="Model" value={model} mono />
        <TaskDetailLine label="GPU" value={taskAccessString(task?.gpu, session?.hw)} mono />
        <TaskDetailLine label="Backing VM" value={task?.remoteId} mono copyable />
        <TaskDetailLine label="Public IP" value={task?.publicIp} mono copyable />
        <TaskDetailLine label="Runtime URL" value={task?.runtimeUrl} mono copyable good={Boolean(task?.runtimeUrl)} />
        <TaskDetailLine label="Turns" value={turns} mono good={Boolean(turns)} />
        <TaskDetailLine label="Billing" value={billing} />
        <TaskDetailLine label="Last active" value={taskAccessString(session?.lastActive, session?.started)} />
        <TaskDetailLine label="Snapshot" value={snapshot} mono={Boolean(task?.snapshotId)} good={Boolean(task?.snapshotId)} />
      </div>
    </div>
  )
}

function TaskAccessPanel({
  row,
  task,
  accessPrimary,
  accessCopyText,
  accessCopied,
  accessDownloadText,
  accessDownloadFileName,
  accessOpenURL,
  canCloseResource,
  closeBusy,
  downloadBusy,
  downloadError,
  onCopy,
  onDownload,
  onOpen,
  onCloseResource,
}: {
  row: DirectWalletLedgerRow
  task?: DirectWalletTaskDetail
  accessPrimary: ReturnType<typeof taskAccessPrimary>
  accessCopyText: string
  accessCopied: boolean
  accessDownloadText: string
  accessDownloadFileName: string
  accessOpenURL: string
  canCloseResource?: boolean
  closeBusy?: boolean
  downloadBusy?: boolean
  downloadError?: string
  onCopy: (text?: string) => void | Promise<void>
  onDownload: () => void | Promise<void>
  onOpen: () => void
  onCloseResource?: () => void | Promise<void>
}) {
  const access = task?.access
  const mode = taskAccessMode(access) || taskAccessString(task?.accessMode).toLowerCase()
  const ssh = taskAccessRecord(access?.ssh)
  const container = taskAccessRecord(access?.container)
  const web = taskAccessRecord(access?.web)
  const api = taskAccessRecord(access?.api)
  const cluster = taskAccessRecord(access?.cluster)
  const headNode = taskAccessRecord(cluster.head_node)
  const resource = taskAccessRecord(access?.resource)
  const privateKey = taskAccessPrivateKey(access)
  const kubeconfig = taskAccessKubeconfig(access)
  const kubeconfigURL = taskAccessKubeconfigURL(access)
  const primaryKind = taskAccessString(access?.primary_kind)
  const command = accessPrimary?.value || taskAccessString(headNode.ssh_command, ssh.command)
  const keyFileName = privateKey ? accessDownloadFileName : ""
  const sshCommand = mode === "vm" || mode === "cluster" ? taskAccessSSHCommand(command, keyFileName) : command
  const copyText = sshCommand || accessCopyText
  const host = taskAccessString(ssh.host, headNode.host, resource.public_ip, task?.publicIp)
  const username = taskAccessString(ssh.username, headNode.ssh_username)
  const port = taskAccessString(ssh.port, headNode.ssh_port)
  const fingerprint = taskAccessString(ssh.fingerprint)
  const provider = taskAccessString(access?.provider, task?.provider, row.meta)
  const clusterID = taskAccessString(cluster.cluster_id)
  const clusterType = taskAccessString(cluster.cluster_type, cluster.scheduler)
  const clusterSupport = taskAccessString(cluster.support_mode)
  const clusterNodes = taskAccessString(cluster.node_count)
  const clusterGPUsPerNode = taskAccessString(cluster.gpus_per_node)
  const clusterProxyURL = taskAccessString(cluster.genrl_proxy_url, web.genrl_proxy_url)
  const clusterDashboardURL = taskAccessString(cluster.dashboard_url, web.dashboard_url)
  const clusterScheduler = taskAccessString(cluster.scheduler)
  const clusterAPIServer = taskAccessString(cluster.api_server)
  const clusterProviderStatus = taskAccessString(cluster.provider_status)
  const clusterNodesList = Array.isArray(cluster.nodes) ? cluster.nodes : []
  const isKubernetesCluster = clusterScheduler === "kubernetes" || clusterType === "kubernetes"
  const containerServiceURL = taskAccessString(container.service_url, container.runtime, web.runtime_url, command)
  const containerImage = taskAccessString(container.image, resource.image)
  const containerPort = taskAccessString(container.port_spec, container.port)
  const containerCommand = taskAccessString(container.start_command)
  const containerReachability = taskAccessString(container.reachability_status)
  const containerError = taskAccessString(container.last_error)
  const containerLogs = taskAccessString(container.logs_status)
  const containerExec = taskAccessString(container.exec_status)
  const containerDisk = taskAccessString(container.container_disk_gb)
  const containerVolume = taskAccessString(container.volume_gb)
  const apiBaseURL = taskAccessString(api.base_url, command)
  const apiKey = taskAccessString(api.api_key)
  const apiKeyStatus = taskAccessString(api.key_status)
  const apiWorkloadsURL = taskAccessString(api.workloads_url)
  const apiModelsURL = taskAccessString(api.models_url)
  const apiChatURL = taskAccessString(api.chat_completions_url)
  const apiModel = taskAccessString(api.model)
  const apiSampleCurl = taskAccessString(api.sample_curl)
  const notebookURL = taskAccessString(web.notebook_url, command)
  const notebookReachability = taskAccessString(web.reachability_status)
  const notebookError = taskAccessString(web.last_error)
  const status = taskAccessStatus(access)
  const accessStatus = status.toLowerCase()
  const requiresVerifiedAccess = mode === "vm" || mode === "cluster" || mode === "notebook" || mode === "container" || mode === "api"
  const resourceClosed = taskLifecycleSettled(task)
  const usableAccessArtifact = Boolean(command || privateKey || kubeconfig || accessOpenURL || host)
  const accessFailed = resourceClosed && Boolean(task?.lastError) && !usableAccessArtifact
  const ready = !resourceClosed && (accessStatus === "ready" || (!requiresVerifiedAccess && Boolean(command || privateKey || accessOpenURL)))
  const title = taskAccessTitle(access || (mode ? { mode } : undefined))
  const closeLabel = taskAccessResourceCloseLabel(mode)
  const accessHeadline = accessFailed
    ? (mode === "api" ? "API launch failed" : mode === "cluster" ? "Cluster launch failed" : mode === "container" ? "Container launch failed" : mode === "notebook" ? "Notebook launch failed" : mode === "vm" ? "VM launch failed" : "Provider launch failed")
    : resourceClosed
      ? (mode === "api" ? "API endpoint closed" : mode === "cluster" ? "Cluster access closed" : mode === "container" ? "Container service closed" : mode === "notebook" ? "Notebook closed" : mode === "vm" ? "VM access closed" : "Provider access closed")
    : ready
      ? (mode === "vm" ? "VM access ready" : mode === "cluster" ? "Cluster access ready" : mode === "container" ? "Container service ready" : mode === "notebook" ? "Notebook ready" : "Provider access ready")
      : "Access pending"
  const accessDescription = accessFailed
    ? "The provider did not create a usable resource. No connection details were issued."
    : resourceClosed
      ? "This provider resource is closed. Connection details remain for audit only."
    : mode === "vm"
      ? "Download the PEM key and use the SSH command below."
      : mode === "cluster"
        ? clusterScheduler === "kubernetes" || clusterType === "kubernetes"
          ? clusterSupport === "native"
            ? "Download the provider kubeconfig and use the Kubernetes API endpoint."
            : "Download the kubeconfig through GENRL and use the SSH tunnel for the Kubernetes API."
          : "Download the cluster key and use the verified head-node SSH command."
      : mode === "container"
        ? "Open the service URL after the container health check is reachable."
        : mode === "notebook"
          ? "Open the notebook URL after the provider runtime is reachable."
          : mode === "api"
            ? "Use the Bearer key from curl, Python, JavaScript, or a VS Code project to submit tasks to the remote compute."
            : "Use the provider access details below."
  const missingText = accessFailed
    ? taskAccessString(task?.lastError, "Provider launch failed before access was ready.")
    : mode === "cluster"
    ? "Waiting for the provider to return head-node connection details. Refresh this task if the cluster was just created."
    : mode === "vm"
      ? "Waiting for the provider to return SSH connection details. Refresh this task if the VM was just created."
      : "Waiting for provider access details. Refresh this task if the resource was just created."
  const canDownload = Boolean(accessDownloadText || privateKey)

  return (
    <div className="rounded-2xl p-4" style={G.subtle}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Label>{title}</Label>
          <p className="mt-1 text-[13px] font-semibold" style={{color:"rgba(0,0,0,0.78)"}}>
            {accessHeadline}
          </p>
          <p className="mt-1 text-[11px] leading-relaxed" style={{color:"rgba(0,0,0,0.46)"}}>
            {accessDescription}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold" style={{
            background:accessFailed ? "rgba(220,38,38,0.10)" : resourceClosed ? "rgba(15,23,42,0.08)" : ready ? "rgba(34,197,94,0.12)" : "rgba(217,143,50,0.14)",
            color:accessFailed ? "#b91c1c" : resourceClosed ? "rgba(15,23,42,0.56)" : ready ? "#15803d" : "#b45309",
          }}>
            {accessFailed ? "Failed" : status}
          </span>
          {accessOpenURL ? (
            <button type="button" onClick={onOpen} className="h-8 w-8 rounded-full flex items-center justify-center" style={G.inset} aria-label="Open access URL" title="Open">
              <ArrowUpRight size={13} />
            </button>
          ) : null}
          <button type="button" onClick={()=>void onCopy(copyText)} disabled={!copyText} className="h-8 rounded-full px-3 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-40" style={G.inset} aria-label="Copy access output" title="Copy">
            {accessCopied ? <Check size={13} style={{color:"#15803d"}} /> : <Copy size={13} />}
            {accessCopied ? "Copied" : primaryKind === "kubeconfig" ? "Copy kubectl" : mode === "vm" || mode === "cluster" ? "Copy SSH" : "Copy"}
          </button>
          <button type="button" onClick={()=>void onDownload()} disabled={!canDownload || downloadBusy} className="h-8 rounded-full px-3 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-40" style={G.inset} aria-label="Download access output" title="Download">
            {downloadBusy ? <LoaderCircle size={13} className="animate-spin" /> : <Download size={13} />}
            {downloadBusy ? "Downloading" : kubeconfig ? "Download kubeconfig" : privateKey ? "Download PEM" : "Download"}
          </button>
          {mode === "cluster" && kubeconfigURL && !kubeconfig ? (
            <a href={kubeconfigURL} className="h-8 rounded-full px-3 flex items-center gap-1.5 text-[11px] font-semibold" style={G.inset} aria-label="Download kubeconfig" title="Download kubeconfig">
              <Download size={13} />
              Kubeconfig
            </a>
          ) : null}
          {closeLabel && canCloseResource ? (
            <button type="button" onClick={()=>void onCloseResource?.()} disabled={!canCloseResource || closeBusy} className="h-8 rounded-full px-3 flex items-center gap-1.5 text-[11px] font-semibold disabled:opacity-40" style={{...G.inset,color:"#b91c1c"}} aria-label={closeLabel} title={closeLabel}>
              {closeBusy ? <LoaderCircle size={13} className="animate-spin" /> : <Trash2 size={13} />}
              {closeBusy ? "Closing" : closeLabel}
            </button>
          ) : null}
        </div>
      </div>
      {downloadError ? (
        <p className="mt-3 rounded-xl px-3 py-2 text-[11px] font-medium" style={{background:"rgba(220,38,38,0.08)",color:"#b91c1c"}}>
          {downloadError}
        </p>
      ) : null}

      {command ? (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <Label>{accessPrimary?.label || "Access command"}</Label>
          <p className="mt-1.5 break-all font-mono text-[12px] leading-relaxed" style={{color:"rgba(0,0,0,0.76)"}}>{mode === "vm" || (mode === "cluster" && primaryKind !== "kubeconfig") ? sshCommand : command}</p>
          {privateKey ? (
            <p className="mt-2 text-[11px] leading-relaxed" style={{color:"rgba(0,0,0,0.46)"}}>
              Run <span className="font-mono">chmod 600 {keyFileName}</span> once before connecting.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <p className="text-[12px] font-medium leading-relaxed" style={{color:"rgba(0,0,0,0.56)"}}>{missingText}</p>
        </div>
      )}

      {mode === "container" ? (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="Service URL" value={containerServiceURL} mono good={Boolean(containerServiceURL)} />
          <TaskDetailLine label="Image" value={containerImage} mono />
          <TaskDetailLine label="Port" value={containerPort} mono good={Boolean(containerPort)} />
          <TaskDetailLine label="Start command" value={containerCommand} mono />
          <TaskDetailLine label="Health" value={containerReachability ? containerReachability.replace(/_/g," ") : ""} good={containerReachability === "ready"} />
          <TaskDetailLine label="Last check" value={containerError} />
          <TaskDetailLine label="Host" value={host || taskAccessString(container.public_ip)} mono good={Boolean(host || taskAccessString(container.public_ip))} />
          <TaskDetailLine label="Logs" value={containerLogs ? containerLogs.replace(/_/g," ") : "Provider console"} />
          <TaskDetailLine label="Exec" value={containerExec ? containerExec.replace(/_/g," ") : "Provider console"} />
          <TaskDetailLine label="Disk" value={containerDisk ? `${containerDisk} GB` : ""} mono />
          <TaskDetailLine label="Volume" value={containerVolume ? `${containerVolume} GB` : ""} mono />
          <TaskDetailLine label="Resource ID" value={taskAccessString(container.id, resource.id)} mono />
          <TaskDetailLine label="Region" value={taskAccessString(container.region, resource.region)} />
        </div>
      ) : mode === "cluster" ? (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="Cluster ID" value={clusterID} mono />
          <TaskDetailLine label="Profile" value={clusterType} />
          <TaskDetailLine label="Support" value={clusterSupport ? clusterSupport.replace(/_/g," ") : "managed"} />
          <TaskDetailLine label="Scheduler" value={clusterScheduler} />
          <TaskDetailLine label="Nodes" value={clusterNodes} mono good={Boolean(clusterNodes)} />
          <TaskDetailLine label="GPUs per node" value={clusterGPUsPerNode} mono />
          <TaskDetailLine label="Provider status" value={clusterProviderStatus ? clusterProviderStatus.replace(/_/g," ") : ""} good={accessStatus === "ready"} />
          {isKubernetesCluster ? <TaskDetailLine label="Kubernetes API" value={clusterAPIServer} mono copyable good={Boolean(clusterAPIServer)} /> : null}
          {isKubernetesCluster ? <TaskDetailLine label="Kubeconfig" value={kubeconfig ? taskAccessKubeconfigFileName(row, task) : kubeconfigURL ? "Ready to download" : "Waiting for provider"} mono good={Boolean(kubeconfig || kubeconfigURL)} /> : null}
          {clusterProxyURL ? <TaskDetailLine label="GENRL proxy" value={clusterProxyURL} mono good /> : null}
          {clusterDashboardURL ? <TaskDetailLine label="Dashboard" value={clusterDashboardURL} mono good /> : null}
          {clusterType !== "kubernetes" || privateKey || host ? <TaskDetailLine label={privateKey ? "Private key" : "SSH key"} value={privateKey ? keyFileName : "Provider managed"} mono good={Boolean(privateKey)} /> : null}
          {clusterType !== "kubernetes" || host ? <TaskDetailLine label="Head host" value={host || "Waiting for public IP"} mono good={Boolean(host)} /> : null}
          {username ? <TaskDetailLine label="Username" value={username} mono /> : null}
          {port ? <TaskDetailLine label="Port" value={port} mono /> : null}
          {fingerprint ? <TaskDetailLine label="Fingerprint" value={fingerprint} mono /> : null}
          <TaskDetailLine label="Node inventory" value={clusterNodesList.length ? `${clusterNodesList.length} returned` : "Waiting for provider"} good={clusterNodesList.length > 0} />
          <TaskDetailLine label="Resource ID" value={taskAccessString(headNode.remote_id, resource.id)} mono />
          <TaskDetailLine label="Region" value={taskAccessString(resource.region)} />
        </div>
      ) : mode === "api" ? (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="API base URL" value={apiBaseURL} mono good={accessStatus === "ready" && Boolean(apiBaseURL)} bad={accessStatus === "not_ready"} />
          <TaskDetailLine label="Workloads URL" value={apiWorkloadsURL} mono copyable good={accessStatus === "ready" && Boolean(apiWorkloadsURL)} />
          <TaskDetailLine label="Models URL" value={apiModelsURL} mono copyable good={accessStatus === "ready" && Boolean(apiModelsURL)} />
          <TaskDetailLine label="Chat URL" value={apiChatURL} mono copyable good={accessStatus === "ready" && Boolean(apiChatURL)} />
          <TaskDetailLine label="Model" value={apiModel} mono good={accessStatus === "ready" && Boolean(apiModel)} />
          <TaskDetailLine label="API key" value={apiKey} mono copyable good={Boolean(apiKey)} />
          <TaskDetailLine label="Key status" value={apiKeyStatus ? apiKeyStatus.replace(/_/g," ") : "Not configured"} good={Boolean(apiKey)} bad={!apiKey && accessStatus === "ready"} />
          <TaskDetailLine label="Sample request" value={apiSampleCurl} mono copyable />
          <TaskDetailLine label="Host" value={host || taskAccessString(resource.public_ip)} mono />
          <TaskDetailLine label="Resource ID" value={taskAccessString(resource.id)} mono />
          <TaskDetailLine label="Region" value={taskAccessString(resource.region)} />
        </div>
      ) : mode === "notebook" ? (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="Notebook URL" value={notebookURL} mono copyable good={Boolean(notebookURL)} />
          <TaskDetailLine label="Health" value={notebookReachability ? notebookReachability.replace(/_/g," ") : "Waiting"} good={notebookReachability === "ready"} />
          <TaskDetailLine label="Last check" value={notebookError} bad={Boolean(notebookError)} />
          <TaskDetailLine label="Host" value={host || taskAccessString(resource.public_ip)} mono />
          <TaskDetailLine label="Resource ID" value={taskAccessString(resource.id)} mono />
          <TaskDetailLine label="Region" value={taskAccessString(resource.region)} />
        </div>
      ) : (
        <div className="mt-3 rounded-xl p-3" style={G.inset}>
          <TaskDetailLine label={privateKey ? "Private key" : "SSH key"} value={privateKey ? keyFileName : "Not returned yet"} mono good={Boolean(privateKey)} bad={mode === "vm" && !privateKey && task?.stageLabel?.toLowerCase().includes("complete")} />
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="Host" value={host || (mode === "vm" ? "Waiting for public IP" : "")} mono good={Boolean(host)} />
          <TaskDetailLine label="Username" value={username} mono />
          <TaskDetailLine label="Port" value={port || (mode === "vm" ? "22" : "")} mono />
          <TaskDetailLine label="Fingerprint" value={fingerprint} mono />
        </div>
      )}
    </div>
  )
}

function TaskAccessCloseConfirmDialog({
  mode,
  title,
  provider,
  remoteId,
  busy,
  onCancel,
  onConfirm,
}: {
  mode: string
  title: string
  provider?: string
  remoteId?: string
  busy: boolean
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}) {
  const closeLabel = taskAccessResourceCloseLabel(mode) || "Close Resource"
  const noun = taskAccessResourceCloseNoun(mode)
  const question =
    mode === "gateway-job" ? "Cancel this gateway job?" :
    mode === "job" ? "Cancel this VM?" :
    mode === "session" ? "Close this persistent session?" :
    mode === "vm" ? "Close this SSH VM?" :
    `Close this ${noun}?`
  const body =
    mode === "session"
      ? "This stops the persistent session, terminates its backing provider resource, and releases the running lease."
      : mode === "job" || mode === "gateway-job"
        ? `This cancels the running ${noun} and releases the running lease. Any active work on this resource will stop.`
        : "This terminates the provider resource and releases the running lease. Any active notebook, container service, or SSH session on this resource will stop."
  const isCancel = mode === "job" || mode === "gateway-job"
  const confirmLabel = isCancel ? "Confirm cancel" : "Confirm close"
  const busyLabel = isCancel ? "Cancelling" : "Closing"
  useEffect(() => {
    if (busy || typeof window === "undefined") return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [busy, onCancel])
  const dialog = (
    <motion.div
      className="fixed inset-0 z-[90] grid place-items-center px-3 sm:px-5"
      style={{background:"rgba(16,14,10,0.34)", backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)"}}
      initial={{opacity:0}}
      animate={{opacity:1}}
      exit={{opacity:0}}
      onClick={busy ? undefined : onCancel}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={closeLabel}
        className="w-full max-w-[430px] rounded-[28px] p-4 sm:rounded-3xl sm:p-5"
        style={G.panel}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{background:"rgba(185,28,28,0.10)"}}>
            <Trash2 size={17} style={{color:"#b91c1c"}} />
          </div>
          <button type="button" onClick={onCancel} disabled={busy} className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40" style={G.inset} aria-label="Cancel close">
            <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
          </button>
        </div>
        <div className="mt-4">
          <Label>{confirmLabel}</Label>
          <h2 className="mt-2 text-[22px] font-semibold tracking-normal break-words">{question}</h2>
          <p className="mt-2 text-[12px] leading-relaxed" style={{color:"rgba(0,0,0,0.52)"}}>
            {body}
          </p>
        </div>
        <div className="mt-4 rounded-2xl p-3" style={G.inset}>
          <TaskDetailLine label="Task" value={title} />
          <TaskDetailLine label="Provider" value={provider} />
          <TaskDetailLine label="Remote ID" value={remoteId} mono />
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onCancel} disabled={busy} className="rounded-xl px-4 py-2 text-[12px] font-semibold disabled:opacity-40" style={G.subtle}>
            Keep running
          </button>
          <motion.button
            type="button"
            whileTap={{scale:0.96}}
            onClick={()=>void onConfirm()}
            disabled={busy}
            className="rounded-xl px-4 py-2 text-[12px] font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
            style={{background:"#b91c1c"}}>
            {busy ? <LoaderCircle size={13} className="animate-spin" /> : <Trash2 size={13} />}
            {busy ? busyLabel : closeLabel}
          </motion.button>
        </div>
      </motion.section>
    </motion.div>
  )
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body)
}

function LedgerTaskDialog({
  row,
  initialView = "task",
  hardwareOptions = [],
  onClose,
  onLaunchWorkload,
  onContinuationLaunched,
  onNeedCredits,
  onLoadRuntimeSession,
  onSendRuntimeSessionTurn,
  onCloseRuntimeSession,
  onTerminateProviderJob,
  onRefresh,
  onViewChange,
  launchBusy = false,
  creditActionsDisabled = false,
}: {
  row: DirectWalletLedgerRow
  initialView?: DirectWalletTaskView
  hardwareOptions?: DirectWalletHardwareOption[]
  onClose: () => void
  onLaunchWorkload?: DirectWalletDashboardProps["onLaunchWorkload"]
  onContinuationLaunched?: (result?: DirectWalletLaunchResult | void, options?: DirectWalletLaunchFocusOptions) => void
  onNeedCredits?: () => void
  onLoadRuntimeSession?: DirectWalletDashboardProps["onLoadRuntimeSession"]
  onSendRuntimeSessionTurn?: DirectWalletDashboardProps["onSendRuntimeSessionTurn"]
  onCloseRuntimeSession?: DirectWalletDashboardProps["onCloseRuntimeSession"]
  onTerminateProviderJob?: DirectWalletDashboardProps["onTerminateProviderJob"]
  onRefresh?: DirectWalletDashboardProps["onRefresh"]
  onViewChange?: (view: DirectWalletTaskView) => void
  launchBusy?: boolean
  creditActionsDisabled?: boolean
}) {
  const [showDetails, setShowDetails] = useState(false)
  const [view, setView] = useState<DirectWalletTaskView>(initialView)
  const [sessionDetail, setSessionDetail] = useState<DirectWalletRuntimeSessionDetail | null>(null)
  const [sessionLoading, setSessionLoading] = useState(false)
  const [sessionError, setSessionError] = useState("")
  const [turnInput, setTurnInput] = useState("")
  const [turnFiles, setTurnFiles] = useState<File[]>([])
  const [turnBusy, setTurnBusy] = useState(false)
  const [continuing, setContinuing] = useState(false)
  const [accessCloseBusy, setAccessCloseBusy] = useState(false)
  const [accessCloseConfirmOpen, setAccessCloseConfirmOpen] = useState(false)
  const [accessDownloadBusy, setAccessDownloadBusy] = useState(false)
  const [accessDownloadError, setAccessDownloadError] = useState("")
  const [jobCancelBusy, setJobCancelBusy] = useState(false)
  const [jobCancelConfirmOpen, setJobCancelConfirmOpen] = useState(false)
  const [sessionCloseBusy, setSessionCloseBusy] = useState(false)
  const [sessionCloseConfirmOpen, setSessionCloseConfirmOpen] = useState(false)
  const [outputCopied, setOutputCopied] = useState(false)
  const [sessionEvidenceCopied, setSessionEvidenceCopied] = useState(false)
  const [accessCopied, setAccessCopied] = useState(false)
  const [terminalOpen, setTerminalOpen] = useState(false)
  const [liveTurnMessages, setLiveTurnMessages] = useState<DirectWalletTaskMessage[]>([])
  const snapshotActionGateRef = useRef(0)
  const task = row.task
  const stages = task?.stages || []
  const stageLabel = task?.stageLabel || taskStatusLabel(task?.status)
  const isVerified = Boolean(task?.gpuVerified)
  const verification = task?.verificationStatus ? taskStatusLabel(task.verificationStatus) : isVerified ? "Hardware verified" : "Pending"
  const outputFallback = task?.lastError ? "No output recorded before the task failed." : task ? "No output recorded yet." : "No output recorded for this ledger entry."
  const runtimeSessionId = task?.runtimeSessionId
  const rawSessionStatus = sessionDetail?.session.status || task?.status
  const sessionStatus = taskPersistentRuntimeLooksClosed(task, rawSessionStatus, sessionDetail?.session.billingStatus)
    ? "stopped"
    : rawSessionStatus && !runtimeSessionCanAcceptInput(rawSessionStatus)
    ? rawSessionStatus
    : taskPersistentRuntimeLooksActive(task) ? "idle" : rawSessionStatus
  const gatewayConversation = Boolean(task?.gateway && !runtimeSessionId)
  const canSendRuntimeTurn = Boolean(runtimeSessionId && onSendRuntimeSessionTurn && runtimeSessionCanAcceptInput(sessionStatus))
  const canResumeRuntimeSession = Boolean(runtimeSessionId && runtimeSessionCanResume(sessionStatus))
  const canRestartRuntimeSession = Boolean(runtimeSessionId && onLaunchWorkload && task?.persistent && task?.snapshotId && runtimeSessionCanRestart(sessionStatus))
  const canSendGatewayTurn = Boolean(gatewayConversation && onLaunchWorkload && !creditActionsDisabled && !launchBusy)
  const canSendTurn = canSendRuntimeTurn || canSendGatewayTurn
  const canCloseRuntimeSession = Boolean(runtimeSessionId && onCloseRuntimeSession && runtimeSessionClosable(sessionStatus))
  const showConversationComposer = Boolean(runtimeSessionId || gatewayConversation)
  const conversationPlaceholder = gatewayConversation
    ? creditActionsDisabled
      ? "Purchase credits to continue"
      : launchBusy
        ? "Gateway is starting..."
        : "Continue this gateway chat"
    : undefined
  const messages = useMemo(
    () => [...taskConversationMessages(row, task, sessionDetail, outputFallback), ...liveTurnMessages],
    [row, task, sessionDetail, outputFallback, liveTurnMessages],
  )
  const outputText = useMemo(() => taskOutputExportText(messages), [messages])
  const conversationText = useMemo(() => taskConversationExportText(messages), [messages])
  const exportText = conversationText || outputText
  const hasExportText = exportText.length > 0
  const persistentEvidenceText = useMemo(
    () => persistentVMEvidenceText(row, task, sessionDetail, sessionStatus),
    [row, task, sessionDetail, sessionStatus],
  )
  const accessPrimary = taskAccessPrimary(task?.access)
  const accessCopyText = taskAccessCopyText(task?.access)
  const accessDownloadText = taskAccessDownloadText(row, task)
  const accessDownloadFileName = taskAccessDownloadFileName(row, task)
  const accessOpenURL = taskAccessOpenURL(task?.access)
  const accessMode = taskAccessMode(task?.access) || taskAccessString(task?.accessMode).toLowerCase()
  const hasAccessArtifact = taskAccessHasArtifact(task?.access)
  const showAccessAsTask = ["vm","container","notebook","api","cluster"].includes(accessMode)
  const showPersistentVMEvidence = Boolean(task && !task.gateway && !showAccessAsTask && (task.persistent || runtimeSessionId))
  const canCloseResource = ["vm","container","notebook","api","cluster"].includes(accessMode) && Boolean(onTerminateProviderJob) && taskAccessResourceClosable(task)
  const terminalProviderJobId = taskAccessString(task?.providerJobId, task?.id)
  const canOpenSSHTerminal = Boolean(task?.sshTerminal && terminalProviderJobId && !taskLifecycleSettled(task, sessionStatus))
  const cancelProviderJobMode = task?.gateway ? "gateway-job" : "job"
  const cancelProviderJobLabel = taskAccessResourceCloseLabel(cancelProviderJobMode)
  const canCancelProviderJob = Boolean(!runtimeSessionId && !showAccessAsTask && onTerminateProviderJob && taskProviderJobCancelable(task))
  const showClosedLifecycle = Boolean((runtimeSessionId || task?.providerJobId) && taskLifecycleSettled(task, sessionStatus) && !canCloseRuntimeSession && !canCloseResource && !canCancelProviderJob)
  const hasSnapshotMetadata = Boolean(task?.snapshotEnabled || task?.snapshotId)
  const canStartSnapshotContinuation = Boolean(task?.snapshotId && !continuing && !launchBusy && (onLaunchWorkload || canResumeRuntimeSession))

  useEffect(() => {
    setView(initialView)
    setAccessCloseConfirmOpen(false)
    setJobCancelConfirmOpen(false)
    setSessionCloseConfirmOpen(false)
    setTerminalOpen(false)
  }, [initialView, row.id])

  function selectView(nextView: DirectWalletTaskView) {
    setView(nextView)
    onViewChange?.(nextView)
  }

  function toggleSSHTerminal() {
    const nextOpen = !terminalOpen
    setTerminalOpen(nextOpen)
    if (nextOpen) selectView("task")
  }

  useEffect(() => {
    if (view !== "task" || !runtimeSessionId || !onLoadRuntimeSession) return
    let cancelled = false
    setSessionLoading(true)
    setSessionError("")
    onLoadRuntimeSession(runtimeSessionId)
      .then(detail => {
        if (!cancelled) setSessionDetail(detail)
      })
      .catch(error => {
        if (!cancelled) setSessionError(error instanceof Error ? error.message : "Could not load session turns.")
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [view, runtimeSessionId, onLoadRuntimeSession])

  async function copyOutput() {
    if (!hasExportText) return
    const copied = await copyTextToClipboard(exportText)
    if (!copied) return
    setOutputCopied(true)
    window.setTimeout(() => setOutputCopied(false), 1400)
  }

  async function copyPersistentEvidence() {
    if (!persistentEvidenceText) return
    const copied = await copyTextToClipboard(persistentEvidenceText)
    if (!copied) return
    setSessionEvidenceCopied(true)
    window.setTimeout(() => setSessionEvidenceCopied(false), 1400)
  }

  function downloadOutput() {
    if (!hasExportText) return
    downloadTextFile(taskOutputFileName(row, task), exportText)
  }

  async function copyAccess(textOverride?: string) {
    const text = taskAccessString(textOverride, accessCopyText)
    if (!text) return
    const copied = await copyTextToClipboard(text)
    if (!copied) return
    setAccessCopied(true)
    window.setTimeout(() => setAccessCopied(false), 1400)
  }

  async function downloadAccess() {
    const privateKeyDownloadPath = taskAccessPrivateKey(task?.access)
    setAccessDownloadError("")
    if (privateKeyDownloadPath) {
      if (accessDownloadBusy) return
      setAccessDownloadBusy(true)
      try {
        const privateKeyPEM = await apiRequest<string>(privateKeyDownloadPath, {
          cache:"no-store",
          timeoutMs:30000,
        })
        if (!taskAccessPrivateKeyPEMLooksValid(privateKeyPEM)) {
          throw new Error("The server did not return a valid SSH private key. Refresh the instance and try again.")
        }
        downloadTextFile(accessDownloadFileName, privateKeyPEM, "application/x-pem-file")
      } catch (error) {
        setAccessDownloadError(error instanceof Error ? error.message : "Could not download this instance's SSH key.")
      } finally {
        setAccessDownloadBusy(false)
      }
      return
    }
    if (!accessDownloadText) return
    downloadTextFile(accessDownloadFileName, accessDownloadText, "text/plain;charset=utf-8")
  }

  function openAccessURL() {
    if (!accessOpenURL || typeof window === "undefined") return
    const url = accessOpenURL.startsWith("/api/") ? buildAPIURL(accessOpenURL) : accessOpenURL
    window.open(url, "_blank", "noopener,noreferrer")
  }

  function requestCloseAccessResource() {
    if (!canCloseResource || accessCloseBusy) return
    setAccessCloseConfirmOpen(true)
  }

  function closeAccessResource() {
    const providerJobId = taskAccessString(task?.providerJobId, task?.id, row.id)
    if (!providerJobId || !onTerminateProviderJob || accessCloseBusy) return
    setAccessCloseBusy(true)
    setAccessCloseConfirmOpen(false)
    void Promise.resolve(onTerminateProviderJob(providerJobId))
      .catch(() => undefined)
      .finally(() => setAccessCloseBusy(false))
  }

  function requestCancelProviderJob() {
    if (!canCancelProviderJob || jobCancelBusy) return
    setJobCancelConfirmOpen(true)
  }

  function cancelProviderJob() {
    const providerJobId = taskAccessString(task?.providerJobId, task?.id, row.id)
    if (!providerJobId || !onTerminateProviderJob || jobCancelBusy) return
    setJobCancelBusy(true)
    setJobCancelConfirmOpen(false)
    void Promise.resolve(onTerminateProviderJob(providerJobId))
      .catch(() => undefined)
      .finally(() => setJobCancelBusy(false))
  }

  function requestCloseRuntimeSession() {
    if (!canCloseRuntimeSession || sessionCloseBusy) return
    setSessionCloseConfirmOpen(true)
  }

  function closeRuntimeSession() {
    if (!runtimeSessionId || !onCloseRuntimeSession || sessionCloseBusy) return
    setSessionCloseBusy(true)
    setSessionError("")
    setSessionDetail(current => current ? {
      ...current,
      session: {
        ...current.session,
        status: "stopping",
        rate: "Closing",
        billingStatus: "closing",
      },
    } : current)
    setSessionCloseConfirmOpen(false)
    void Promise.resolve(onCloseRuntimeSession(runtimeSessionId))
      .then(detail => {
        if (detail?.session) {
          setSessionDetail(detail)
        }
      })
      .catch(error => {
        setSessionError(error instanceof Error ? error.message : "Could not close this session.")
      })
      .finally(() => setSessionCloseBusy(false))
  }

  async function restartRuntimeSession() {
    if (!canRestartRuntimeSession) return
    return continueFromSnapshot()
  }

  function activateRuntimeSessionRestart(event?: { preventDefault?: () => void; stopPropagation?: () => void }) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    void restartRuntimeSession()
  }

  function activateSnapshotContinuation(event?: { preventDefault?: () => void; stopPropagation?: () => void }) {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    void continueFromSnapshot()
  }

  function activateRuntimeSessionRestartKey(event: { key?: string; preventDefault?: () => void; stopPropagation?: () => void }) {
    if (event.key !== "Enter" && event.key !== " ") return
    activateRuntimeSessionRestart(event)
  }

  function activateSnapshotContinuationKey(event: { key?: string; preventDefault?: () => void; stopPropagation?: () => void }) {
    if (event.key !== "Enter" && event.key !== " ") return
    activateSnapshotContinuation(event)
  }

  async function continueFromSnapshot() {
    const actionStartedAt = Date.now()
    if (actionStartedAt - snapshotActionGateRef.current < 650) return
    snapshotActionGateRef.current = actionStartedAt
    if (!task) return
    if (!task.snapshotId) {
      setSessionError("Snapshot is still being saved. Refresh before continuing.")
      return
    }
    if (continuing || launchBusy) return
    if (creditActionsDisabled && !runtimeSessionId) {
      onNeedCredits?.()
      return
    }
    const promptContextOnly = shouldUsePromptContextSnapshotContinuation({
      contextText: exportText,
      gateway: gatewayConversation,
      persistent: Boolean(task.persistent),
      runtimeSessionId,
    })
    const prompt = buildSnapshotContinuationPrompt({
      title: task.title,
      label: row.label,
      sourceText: task.prompt || task.input || task.title || row.label,
      contextText: promptContextOnly ? exportText : "",
    })
    const shouldLaunchRestoredRuntime = Boolean(!promptContextOnly && (canRestartRuntimeSession || taskLifecycleSettled(task, sessionStatus) || runtimeSessionCanRestart(sessionStatus)))
    const useExistingRuntime = Boolean(runtimeSessionId && canResumeRuntimeSession && !promptContextOnly && !shouldLaunchRestoredRuntime)
    const providerSlug = walletProviderSlugFromLabel(task.provider || row.meta)
    const restoredHardware = task.gpu && task.gpu !== "Model gateway" && task.gpu !== "GPU" ? task.gpu : ""
    const restoredHardwareChoice = !promptContextOnly
      ? walletRunSnapshotHardwareChoice(hardwareOptions, providerSlug, restoredHardware || task.model || "")
      : null
    const launchHardwareLabel = restoredHardwareChoice?.hardwareLabel || restoredHardware || "GPU"
    setContinuing(true)
    setSessionError("")
    try {
      if (useExistingRuntime) {
        selectView("task")
        setSessionError("")
        if (onLoadRuntimeSession) {
          const detail = await onLoadRuntimeSession(runtimeSessionId!)
          markAssistantMessagesStreamed(detail.messages)
          setSessionDetail(detail)
        }
        await onRefresh?.()
        onContinuationLaunched?.({
          taskId: task.id,
          providerJobId: task.providerJobId,
          clientLaunchId: task.clientLaunchId,
          runtimeSessionId,
        }, { view:"task" })
        return
      }
      if (!onLaunchWorkload) {
        setSessionError("This session cannot be continued from the FE because launch actions are unavailable.")
        return
      }
      const result = await onLaunchWorkload({
        executionMode: promptContextOnly ? "gateway" : "vm_gpu",
        runTab: promptContextOnly ? "Gateway" : "VM/GPU",
        serviceName: promptContextOnly ? undefined : task.title || row.label,
        providerSlug: !promptContextOnly ? providerSlug || undefined : undefined,
        model: promptContextOnly ? walletGatewayModelIDFromTask(task.model) : task.model || "deepseek",
        hardware: promptContextOnly ? "gateway" : restoredHardwareChoice?.id || launchHardwareLabel,
        hardwareLabel: promptContextOnly ? undefined : walletProviderLaunchHardwareLabel(
          providerSlug,
          launchHardwareLabel,
          restoredHardwareChoice?.providers || [],
        ),
        hourlyPriceCredits: restoredHardwareChoice?.hourlyPriceCredits,
        maxPriceCredits: restoredHardwareChoice?.maxPriceCredits,
        estimatedMinutes: restoredHardwareChoice?.estimatedMinutes,
        gpuCount: restoredHardwareChoice?.gpuCount,
        prompt,
        files: [],
        persistent: promptContextOnly ? false : Boolean(task.persistent),
        snapshots: true,
        continueFromSnapshot: {
          taskId: task.id,
          snapshotId: task.snapshotId,
          providerJobId: task.providerJobId,
          clientLaunchId: task.clientLaunchId,
          runtimeSessionId: task.runtimeSessionId,
          title: task.title,
          promptContextOnly,
        },
      })
      onContinuationLaunched?.(result, { view: promptContextOnly ? "task" : "progress" })
      if (!promptContextOnly) onClose()
    } catch (error) {
      // The route owns the toast; keep the dialog open so the user can retry.
      setSessionError(error instanceof Error ? error.message : "Could not restart this session from its snapshot.")
      setLiveTurnMessages([])
    } finally {
      setContinuing(false)
    }
  }

  async function submitTurn() {
    const text = turnInput.trim()
    if (!text || turnBusy) return
    selectView("task")
    if (gatewayConversation) {
      if (creditActionsDisabled) {
        onNeedCredits?.()
        return
      }
      if (!onLaunchWorkload || launchBusy) return
      const liveID = `gateway:${task?.id || row.id}:${Date.now()}`
      setTurnBusy(true)
      setSessionError("")
      setLiveTurnMessages([
        { id:`${liveID}:input`, role:"user", text, status:"running", time:"just now" },
        { id:`${liveID}:output`, role:"assistant", text:PROCESSING_RESPONSE_TEXT, status:"processing", time:"just now" },
      ])
      try {
        const gatewayMessages = taskGatewayContinuationMessages(messages, text)
        const result = await onLaunchWorkload({
          executionMode: "gateway",
          runTab: "Gateway",
          model: walletGatewayModelIDFromTask(task?.model),
          hardware: "gateway",
          prompt: text,
          files: [],
          persistent: false,
          snapshots: false,
          gatewayMessages,
          gatewayParentProviderJobId: taskAccessString(task?.providerJobId, task?.id, row.id),
        })
        setTurnInput("")
        setTurnFiles([])
        onContinuationLaunched?.(result, { view:"task" })
        await onRefresh?.()
        setLiveTurnMessages([])
      } catch (error) {
        setLiveTurnMessages([])
        setSessionError(error instanceof Error ? error.message : "Could not continue this gateway chat.")
      } finally {
        setTurnBusy(false)
      }
      return
    }
    if (!runtimeSessionId || !onSendRuntimeSessionTurn || !canSendRuntimeTurn) return
    const liveID = `live:${runtimeSessionId}:${Date.now()}`
    setTurnBusy(true)
    setSessionError("")
    setLiveTurnMessages([
      { id:`${liveID}:input`, role:"user", text, status:"running", time:"just now" },
      { id:`${liveID}:output`, role:"assistant", text:PROCESSING_RESPONSE_TEXT, status:"processing", time:"just now" },
    ])
    try {
      const restoreContext = task?.snapshotId ? {
        snapshot_id: task.snapshotId,
        include_roles: ["output_text", "log", "generated_artifact"],
        target_path: "/workspace/.genrl/snapshots",
      } : undefined
      const detail = await onSendRuntimeSessionTurn(runtimeSessionId, { text, files: turnFiles, restoreContext }, {
        onPhase:event => {
          const stage = normalizedTaskState(event.stage)
          setLiveTurnMessages(current => current.map(message => {
            if (message.id !== `${liveID}:output`) return message
            if (stage.includes("streaming")) return { ...message, status:"streaming" }
            if (stage.includes("completed")) return { ...message, status:"completed" }
            return {
              ...message,
              status:"processing",
              text:String(message.text || "").trim() ? message.text : PROCESSING_RESPONSE_TEXT,
            }
          }))
        },
        onToken:event => {
          const delta = String(event.delta || "")
          if (!delta) return
          setLiveTurnMessages(current => current.map(message => {
            if (message.id !== `${liveID}:output`) return message
            const currentText = String(message.text || "")
            const nextText = currentText.trim().toLowerCase().startsWith("processing") ? delta : `${currentText}${delta}`
            return { ...message, text:nextText, status:"streaming", time:"just now" }
          }))
        },
        onDetail:detail => {
          markAssistantMessagesStreamed(detail.messages)
          setSessionDetail(detail)
        },
      })
      markAssistantMessagesStreamed(detail.messages)
      setSessionDetail(detail)
      setLiveTurnMessages([])
      setTurnInput("")
      setTurnFiles([])
      await onRefresh?.()
    } catch (error) {
      setLiveTurnMessages([])
      setSessionError(error instanceof Error ? error.message : "Could not send input to this session.")
    } finally {
      setTurnBusy(false)
    }
  }

  return (
    <motion.div
      className="fixed inset-0 z-[70] grid place-items-center px-3 sm:px-5"
      style={{background:"rgba(20,16,8,0.28)", backdropFilter:"blur(16px)", WebkitBackdropFilter:"blur(16px)"}}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={onClose}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={task ? `Task details for ${task.title}` : `Ledger details for ${row.label}`}
        className="flex max-h-[calc(100dvh-24px)] w-full max-w-[820px] flex-col overflow-hidden rounded-[28px] sm:max-h-[86vh] sm:rounded-3xl"
        style={G.panel}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b p-4 sm:p-5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{background:"rgba(217,143,50,0.12)"}}>
              {task ? <Activity size={17} style={{color:"#b45309"}} /> : <Wallet size={17} style={{color:"#b45309"}} />}
            </div>
            <div className="min-w-0">
              <Label>{task ? "Ledger task" : "Ledger entry"}</Label>
              <h2 className="mt-1 text-[20px] font-semibold leading-tight tracking-normal break-words" title={task?.title || row.label} style={{overflowWrap:"anywhere"}}>{task?.title || row.label}</h2>
              <p className="mt-1 text-[12px] break-words" title={row.id} style={{color:"rgba(0,0,0,0.44)", overflowWrap:"anywhere"}}>{row.displayId || row.id} · {row.time}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={G.inset} aria-label="Close task details">
            <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <TaskStat label="Stage" value={stageLabel} tone={task?.lastError ? "warn" : isVerified ? "ok" : "muted"} />
            <TaskStat label="GPU" value={task?.gpu || "—"} tone={isVerified ? "ok" : "muted"} />
            <TaskStat label="Amount" value={`${row.amt > 0 ? "+" : ""}${row.amt.toFixed(2)} UC`} tone={row.amt > 0 ? "ok" : "muted"} />
          </div>

          {(canOpenSSHTerminal || canRestartRuntimeSession || canCloseRuntimeSession || canCloseResource || canCancelProviderJob || showClosedLifecycle) ? (
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              {canOpenSSHTerminal ? (
                <button
                  type="button"
                  onClick={toggleSSHTerminal}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold"
                  style={{...G.inset, color:"#14532d"}}
                  aria-label={terminalOpen ? "Hide SSH terminal" : "Open SSH terminal"}
                  title={terminalOpen ? "Hide SSH terminal" : "Open SSH terminal"}>
                  <TerminalSquare size={14} />
                  {terminalOpen ? "Hide Terminal" : "Open Terminal"}
                </button>
              ) : null}
              {showClosedLifecycle ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold disabled:opacity-70"
                  style={{...G.inset, color:"#15803d"}}
                  aria-label="Resource closed"
                  title="Resource closed">
                  <Check size={14} />
                  Closed
                </button>
              ) : null}
              {canRestartRuntimeSession ? (
                <div
                  role="button"
                  tabIndex={continuing || launchBusy ? -1 : 0}
                  aria-disabled={continuing || Boolean(launchBusy)}
                  onPointerUp={activateRuntimeSessionRestart}
                  onClick={activateRuntimeSessionRestart}
                  onKeyDown={activateRuntimeSessionRestartKey}
                  className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold ${(continuing || launchBusy) ? "pointer-events-none opacity-45" : "cursor-pointer"}`}
                  style={{...G.inset, color:"#14532d"}}
                  aria-label="Restart persistent session"
                  title="Restart persistent session">
                  {continuing || launchBusy ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                  {continuing || launchBusy ? "Restarting" : "Restart Session"}
                </div>
              ) : null}
              {canCloseRuntimeSession ? (
                <button
                  type="button"
                  onClick={requestCloseRuntimeSession}
                  disabled={sessionCloseBusy}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold disabled:opacity-45"
                  style={{...G.inset, color:"#b91c1c"}}
                  aria-label="Close persistent session"
                  title="Close persistent session">
                  {sessionCloseBusy ? <LoaderCircle size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {sessionCloseBusy ? "Closing" : "Close Session"}
                </button>
              ) : null}
              {canCloseResource ? (
                <button
                  type="button"
                  onClick={requestCloseAccessResource}
                  disabled={accessCloseBusy}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold disabled:opacity-45"
                  style={{...G.inset, color:"#b91c1c"}}
                  aria-label={taskAccessResourceCloseLabel(accessMode)}
                  title={taskAccessResourceCloseLabel(accessMode)}>
                  {accessCloseBusy ? <LoaderCircle size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {accessCloseBusy ? "Closing" : taskAccessResourceCloseLabel(accessMode)}
                </button>
              ) : null}
              {canCancelProviderJob ? (
                <button
                  type="button"
                  onClick={requestCancelProviderJob}
                  disabled={jobCancelBusy}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3 text-[12px] font-semibold disabled:opacity-45"
                  style={{...G.inset, color:"#b91c1c"}}
                  aria-label={cancelProviderJobLabel}
                  title={cancelProviderJobLabel}>
                  {jobCancelBusy ? <LoaderCircle size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {jobCancelBusy ? "Cancelling" : cancelProviderJobLabel}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="mt-5 grid grid-cols-2 rounded-full p-1 gap-1" style={G.inset}>
            {(["task", "progress"] as const).map(option => {
              const active = view === option
              return (
                <button key={option} type="button" onClick={()=>selectView(option)}
                  className="relative rounded-full px-4 py-2.5 text-[13px] font-semibold transition-colors"
                  style={{color:active ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0.42)"}}>
                  {active ? <motion.span layoutId="ledger-task-tab" className="absolute inset-0 rounded-full bg-white" style={{boxShadow:"0 1px 8px rgba(0,0,0,0.08)"}} transition={{type:"spring", damping:30, stiffness:420}} /> : null}
                  <span className="relative z-10">{option === "progress" ? "Progress" : "Task"}</span>
                </button>
              )
            })}
          </div>

          <AnimatePresence mode="wait" initial={false}>
            {view === "progress" ? (
              <motion.div key="progress" initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:0.16}}>
                {stages.length > 0 ? (
                  <div className="mt-4 rounded-2xl p-4" style={G.subtle}>
                    <div className="space-y-3">
                      {stages.map((stage, index) => {
                        const tone = taskStageTone(stage.status)
                        return (
                          <div key={stage.id} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className="w-7 h-7 rounded-full flex items-center justify-center border" style={{background:tone.bg, color:tone.color, borderColor:tone.border}}>
                                {stage.status === "done" ? <Check size={13} /> : stage.status === "failed" ? <X size={12} /> : stage.status === "active" ? <LoaderCircle size={13} className="animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full" style={{background:tone.color}} />}
                              </div>
                              {index < stages.length - 1 ? <div className="w-px flex-1 mt-1" style={{background:"rgba(0,0,0,0.08)", minHeight:18}} /> : null}
                            </div>
                            <div className="min-w-0 pb-1 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-[13px] font-semibold" style={{color:"rgba(0,0,0,0.78)"}}>{stage.label}</p>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{background:tone.bg, color:tone.color, borderColor:tone.border}}>{taskStatusLabel(stage.status)}</span>
                              </div>
                              {stage.detail ? <p className="mt-0.5 text-[11px] leading-relaxed" style={{color:"rgba(0,0,0,0.44)"}}>{stage.detail}</p> : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl p-4" style={G.subtle}>
                    <TaskDetailLine label="Type" value={row.meta} />
                    <TaskDetailLine label="Amount" value={`${row.amt > 0 ? "+" : ""}${row.amt.toFixed(2)} UC`} />
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div key="task" initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:0.16}}>
                <div className="mt-4 grid grid-cols-1 gap-3">
                  {terminalOpen && terminalProviderJobId ? (
                    <div className="overflow-hidden rounded-2xl">
                      <Suspense fallback={<div className="flex min-h-[430px] items-center justify-center rounded-2xl" style={G.subtle}><LoaderCircle size={18} className="animate-spin" /></div>}>
                        <LazyWebSSHTerminal key={terminalProviderJobId} jobId={terminalProviderJobId} />
                      </Suspense>
                    </div>
                  ) : null}
                  {showAccessAsTask ? (
                    <TaskAccessPanel
                      row={row}
                      task={task}
                      accessPrimary={accessPrimary}
                      accessCopyText={accessCopyText}
                      accessCopied={accessCopied}
                      accessDownloadText={accessDownloadText}
                      accessDownloadFileName={accessDownloadFileName}
                      accessOpenURL={accessOpenURL}
                      canCloseResource={canCloseResource}
                      closeBusy={accessCloseBusy}
                      downloadBusy={accessDownloadBusy}
                      downloadError={accessDownloadError}
                      onCopy={copyAccess}
                      onDownload={downloadAccess}
                      onOpen={openAccessURL}
                      onCloseResource={requestCloseAccessResource}
                    />
                  ) : (
                    <TaskConversation
                      messages={messages}
                      loading={sessionLoading || turnBusy}
                      error={sessionError || sessionDetail?.lastError}
                      actions={
                        <>
                          <button
                            type="button"
                            onClick={copyOutput}
                            disabled={!hasExportText}
                            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold disabled:opacity-40"
                            style={G.inset}
                            aria-label="Copy conversation"
                            title="Copy conversation">
                            {outputCopied ? <Check size={13} /> : <Copy size={13} />}
                            <span className="hidden sm:inline">{outputCopied ? "Copied" : "Copy"}</span>
                          </button>
                          <button
                            type="button"
                            onClick={downloadOutput}
                            disabled={!hasExportText}
                            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold disabled:opacity-40"
                            style={G.inset}
                            aria-label="Download conversation"
                            title="Download conversation">
                            <Download size={13} />
                            <span className="hidden sm:inline">Download</span>
                          </button>
                        </>
                      }
                      showComposer={showConversationComposer}
                      canSendTurn={canSendTurn}
                      sessionStatus={sessionStatus}
                      placeholder={conversationPlaceholder}
                      allowFiles={Boolean(runtimeSessionId)}
                      turnInput={turnInput}
                      attachedFiles={turnFiles}
                      turnBusy={turnBusy}
                      onInputChange={setTurnInput}
                      onAttachFiles={files=>setTurnFiles(current=>[...current, ...files])}
                      onRemoveFile={index=>setTurnFiles(current=>current.filter((_, fileIndex)=>fileIndex !== index))}
                      onSubmit={submitTurn}
                    />
                  )}
                  {hasSnapshotMetadata ? (
                    <div className="rounded-2xl p-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" style={G.inset}>
                      <div className="min-w-0">
                        <Label>Snapshot</Label>
                        <p className="mt-1 text-[12px] font-medium" style={{color:"rgba(0,0,0,0.58)"}}>
                          {task?.snapshotId ? `Saved context ${task.snapshotId}` : "Snapshot is still being saved. Refresh before continuing."}
                        </p>
                      </div>
                      <div
                        role="button"
                        tabIndex={canStartSnapshotContinuation ? 0 : -1}
                        aria-disabled={!canStartSnapshotContinuation}
                        onPointerUp={activateSnapshotContinuation}
                        onClick={activateSnapshotContinuation}
                        onKeyDown={activateSnapshotContinuationKey}
                        className={`inline-flex h-10 items-center justify-center gap-2 rounded-full px-4 text-[12px] font-semibold text-white ${canStartSnapshotContinuation ? "cursor-pointer" : "pointer-events-none opacity-45"}`}
                        style={{background:"rgba(14,12,8,0.88)"}}
                        aria-label="Continue from snapshot">
                        {continuing || launchBusy ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                        {!task?.snapshotId ? "Waiting for snapshot" : creditActionsDisabled && !runtimeSessionId ? "Purchase credits" : continuing || launchBusy ? "Continuing..." : "Continue"}
                      </div>
                    </div>
                  ) : null}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl p-3" style={G.inset}>
                      <Label>Model</Label>
                      <p className="mt-1.5 text-[12px] font-mono break-all" style={{color:"rgba(0,0,0,0.68)"}}>{task?.model || "—"}</p>
                    </div>
                    <div className="rounded-2xl p-3" style={G.inset}>
                      <Label>Provider</Label>
                      <p className="mt-1.5 text-[12px] font-semibold break-words" title={task?.provider || row.meta} style={{color:"rgba(0,0,0,0.68)", overflowWrap:"anywhere"}}>{task?.provider || row.meta}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {accessCloseConfirmOpen ? (
              <TaskAccessCloseConfirmDialog
                mode={accessMode}
                title={task?.title || row.label}
                provider={task?.provider || row.meta}
                remoteId={task?.remoteId}
                busy={accessCloseBusy}
                onCancel={()=>setAccessCloseConfirmOpen(false)}
                onConfirm={closeAccessResource}
              />
            ) : null}
            {jobCancelConfirmOpen ? (
              <TaskAccessCloseConfirmDialog
                mode={cancelProviderJobMode}
                title={task?.title || row.label}
                provider={task?.provider || row.meta}
                remoteId={task?.remoteId || task?.providerJobId}
                busy={jobCancelBusy}
                onCancel={()=>setJobCancelConfirmOpen(false)}
                onConfirm={cancelProviderJob}
              />
            ) : null}
            {sessionCloseConfirmOpen ? (
              <TaskAccessCloseConfirmDialog
                mode="session"
                title={task?.title || row.label}
                provider={task?.provider || row.meta}
                remoteId={runtimeSessionId}
                busy={sessionCloseBusy}
                onCancel={()=>setSessionCloseConfirmOpen(false)}
                onConfirm={closeRuntimeSession}
              />
            ) : null}
          </AnimatePresence>

          <button type="button" onClick={()=>setShowDetails(value=>!value)} className="mt-4 w-full rounded-2xl px-4 py-3 flex items-center justify-between text-[12px] font-semibold" style={G.inset}>
            <span>More details</span>
            <ChevronDown size={14} style={{transform:showDetails ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 160ms ease"}} />
          </button>

          <AnimatePresence initial={false}>
            {showDetails ? (
              <motion.div
                initial={{height:0, opacity:0}}
                animate={{height:"auto", opacity:1}}
                exit={{height:0, opacity:0}}
                transition={{duration:0.2, ease:"easeOut"}}
                className="overflow-hidden">
                {showPersistentVMEvidence ? (
                  <div className="mt-3">
                    <PersistentVMEvidencePanel
                      row={row}
                      task={task}
                      sessionDetail={sessionDetail}
                      sessionStatus={sessionStatus}
                      copied={sessionEvidenceCopied}
                      onCopy={copyPersistentEvidence}
                    />
                  </div>
                ) : null}
                <div className="mt-3 rounded-2xl p-4" style={G.subtle}>
                  <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2">
                    <div className="rounded-xl p-3 flex items-center gap-2" style={G.inset}>
                      <Server size={14} style={{color:"rgba(0,0,0,0.48)"}} />
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{color:"rgba(0,0,0,0.34)"}}>Provider</p>
                        <p className="text-[12px] font-semibold break-words" style={{overflowWrap:"anywhere"}}>{task?.provider || row.meta}</p>
                      </div>
                    </div>
                    <div className="rounded-xl p-3 flex items-center gap-2" style={G.inset}>
                      <Shield size={14} style={{color:isVerified ? "#15803d" : "rgba(0,0,0,0.48)"}} />
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{color:"rgba(0,0,0,0.34)"}}>Verification</p>
                        <p className="text-[12px] font-semibold break-words" style={{color:isVerified ? "#15803d" : "rgba(0,0,0,0.72)", overflowWrap:"anywhere"}}>{verification}</p>
                      </div>
                    </div>
                  </div>
                  {hasAccessArtifact && !showAccessAsTask ? (
                    <div className="mb-3">
                      <TaskAccessPanel
                        row={row}
                        task={task}
                        accessPrimary={accessPrimary}
                        accessCopyText={accessCopyText}
                        accessCopied={accessCopied}
                        accessDownloadText={accessDownloadText}
                        accessDownloadFileName={accessDownloadFileName}
                        accessOpenURL={accessOpenURL}
                        canCloseResource={canCloseResource}
                        closeBusy={accessCloseBusy}
                        downloadBusy={accessDownloadBusy}
                        downloadError={accessDownloadError}
                        onCopy={copyAccess}
                        onDownload={downloadAccess}
                        onOpen={openAccessURL}
                        onCloseResource={requestCloseAccessResource}
                      />
                    </div>
                  ) : null}
                  <TaskDetailLine label="Task ID" value={task?.id || row.id} mono copyable />
                  <TaskDetailLine label="Runtime session" value={task?.runtimeSessionId} mono copyable />
                  <TaskDetailLine label="Provider job" value={task?.providerJobId} mono copyable />
                  <TaskDetailLine label="Client launch" value={task?.clientLaunchId} mono copyable />
                  <TaskDetailLine label="Model" value={task?.model} mono />
                  <TaskDetailLine label="GPU used" value={task?.gpu} mono good={isVerified} />
                  <TaskDetailLine label="Hardware proof" value={isVerified ? "Verified" : "Pending"} good={isVerified} />
                  <TaskDetailLine label="IP address" value={task?.publicIp} mono copyable />
                  <TaskDetailLine label="Remote ID" value={task?.remoteId} mono copyable />
                  <TaskDetailLine label="Runtime URL" value={task?.runtimeUrl} mono copyable />
                  <TaskDetailLine label="Region" value={task?.region} />
                  <TaskDetailLine label="Image" value={task?.image} mono />
                  <TaskDetailLine label="Provider status" value={task?.providerStatus || task?.status} />
                  <TaskDetailLine label="Snapshot" value={task?.snapshotEnabled ? task.snapshotId || "Snapshot pending" : undefined} mono={Boolean(task?.snapshotId)} good={Boolean(task?.snapshotId)} copyable={Boolean(task?.snapshotId)} />
                  <TaskDetailLine label="Lease" value={task?.leaseStatus} />
                  <TaskDetailLine label="Attestation" value={task?.attestationStatus} good={String(task?.attestationStatus || "").toLowerCase().includes("verified")} />
                  <TaskDetailLine label="Evidence hash" value={task?.attestationHash} mono copyable />
                  <TaskDetailLine label="Last error" value={task?.lastError || task?.attestationError} bad={Boolean(task?.lastError || task?.attestationError)} />
                  {task?.verificationSummary ? (
                    <p className="mt-3 text-[11px] leading-relaxed" style={{color:"rgba(0,0,0,0.46)"}}>{task.verificationSummary}</p>
                  ) : null}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.section>
    </motion.div>
  )
}

function isLiveWalletVolume(volume: DirectWalletVolume) {
  const token = String(volume.status || "available").trim().toLowerCase().replace(/[_\s]+/g, "-")
  return !["deleted", "deleting", "terminated", "terminating", "failed", "error", "unavailable", "offline"].includes(token)
}

const WALLET_MICRO = 1_000_000

type DirectWalletCapacityDraft = {
  displayName: string
  status: string
  visibility: DirectWalletProVisibility
  broadcastMode: DirectWalletProBroadcastMode
  pricingMode: DirectWalletProPricingMode
  adjustmentPercent: string
  fixedPrice: string
  minPrice: string
  maxPrice: string
  reserveFloor: string
  stockLimit: string
  allowedRegions: string
  allowedHardware: string
}

function walletCleanID(value?: string) {
  return String(value || "").trim().toLowerCase()
}

function walletStatusLabel(value?: string) {
  return String(value || "unknown").trim().toLowerCase().replace(/[_-]+/g, " ")
}

function walletMoneyMicros(value?: number | string | null) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0
}

function walletMicrosToCreditsInput(value?: number | string | null, fractionDigits = 4) {
  const micros = walletMoneyMicros(value)
  if (micros <= 0) return ""
  return (micros / WALLET_MICRO).toFixed(fractionDigits).replace(/\.?0+$/, "")
}

function walletCreditsToMicrosInput(value: string) {
  const parsed = Number(value || 0)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return Math.round(parsed * WALLET_MICRO)
}

function walletParseTokenList(value: string) {
  const seen = new Set<string>()
  return value
    .split(",")
    .map(part => part.trim())
    .filter(part => {
      if (!part) return false
      const key = part.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

function walletFormatMicros(value?: number | string | null, suffix = "UC/hr") {
  const micros = walletMoneyMicros(value)
  if (micros <= 0) return "—"
  const credits = micros / WALLET_MICRO
  const formatted = credits.toLocaleString(undefined, { maximumFractionDigits: 4 })
  return suffix ? `${formatted} ${suffix}` : `${formatted} UC`
}

function walletParseCreditsText(value?: string | null) {
  const match = String(value || "").replace(/,/g, "").match(/\d+(?:\.\d+)?/)
  const parsed = match ? Number(match[0]) : 0
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

function walletCapacityAdjustmentLabel(value?: number) {
  const bps = Number(value || 0)
  if (!Number.isFinite(bps) || bps === 0) return "pass-through"
  const percent = Math.abs(bps / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })
  return bps < 0 ? `${percent}% discount` : `${percent}% premium`
}

function walletNormalizeProVisibility(value?: string): DirectWalletProVisibility {
  return walletCleanID(value) === "network" || walletCleanID(value) === "marketplace" ? "network" : "private"
}

function walletNormalizeProBroadcastMode(value?: string, visibility?: string): DirectWalletProBroadcastMode {
  const upper = String(value || "").trim().toUpperCase()
  const normalizedVisibility = walletNormalizeProVisibility(visibility)
  if (upper === "BROADCAST_ONLY" || upper === "BOTH" || upper === "INTERNAL_ONLY") {
    if (normalizedVisibility === "private") return "INTERNAL_ONLY"
    return upper === "INTERNAL_ONLY" ? "BOTH" : upper
  }
  return normalizedVisibility === "network" ? "BOTH" : "INTERNAL_ONLY"
}

function walletNormalizeProPricingMode(value?: string): DirectWalletProPricingMode {
  const upper = String(value || "").trim().toUpperCase()
  if (upper === "PREMIUM_OR_DISCOUNT" || upper === "FIXED" || upper === "FLOOR_ADJUSTED") return upper
  return "PASSTHROUGH"
}

function walletCapacityDraftFromProvider(provider: DirectWalletProProvider): DirectWalletCapacityDraft {
  const visibility = walletNormalizeProVisibility(provider.visibility || provider.m)
  return {
    displayName: String(provider.n || provider.providerName || provider.slug || "").trim(),
    status: String(provider.status || "active").trim().toLowerCase(),
    visibility,
    broadcastMode: walletNormalizeProBroadcastMode(provider.broadcastMode, visibility),
    pricingMode: walletNormalizeProPricingMode(provider.pricingMode),
    adjustmentPercent: ((Number(provider.providerAdjustmentBps || 0) || 0) / 100).toString(),
    fixedPrice: walletMicrosToCreditsInput(provider.fixedUnitPriceMicros),
    minPrice: walletMicrosToCreditsInput(provider.minUnitPriceMicros),
    maxPrice: walletMicrosToCreditsInput(provider.maxUnitPriceMicros),
    reserveFloor: walletMicrosToCreditsInput(provider.reserveBalanceThresholdMicros, 2),
    stockLimit: Number(provider.broadcastStockLimit || 0) > 0 ? String(provider.broadcastStockLimit) : "",
    allowedRegions: Array.isArray(provider.allowedRegions) ? provider.allowedRegions.join(", ") : "",
    allowedHardware: Array.isArray(provider.allowedHardware) ? provider.allowedHardware.join(", ") : "",
  }
}

function walletCapacityPatchFromDraft(draft: DirectWalletCapacityDraft): DirectWalletCapacityUpdate {
  const visibility = walletNormalizeProVisibility(draft.visibility)
  return {
    display_name: draft.displayName.trim(),
    status: draft.status || "active",
    visibility,
    broadcast_mode: walletNormalizeProBroadcastMode(draft.broadcastMode, visibility),
    pricing_mode: walletNormalizeProPricingMode(draft.pricingMode),
    provider_adjustment_bps: Math.round((Number(draft.adjustmentPercent || 0) || 0) * 100),
    fixed_unit_price_micros: walletCreditsToMicrosInput(draft.fixedPrice),
    min_unit_price_micros: walletCreditsToMicrosInput(draft.minPrice),
    max_unit_price_micros: walletCreditsToMicrosInput(draft.maxPrice),
    reserve_balance_threshold_micros: walletCreditsToMicrosInput(draft.reserveFloor),
    broadcast_stock_limit: Math.max(0, Math.floor(Number(draft.stockLimit || 0) || 0)),
    allowed_regions: walletParseTokenList(draft.allowedRegions),
    allowed_hardware: walletParseTokenList(draft.allowedHardware),
  }
}

function walletCapacityPricingText(provider: DirectWalletProProvider) {
  const mode = walletNormalizeProPricingMode(provider.pricingMode)
  if (mode === "FIXED") {
    const fixed = walletFormatMicros(provider.fixedUnitPriceMicros)
    return fixed === "—" ? "fixed pricing" : `${fixed} fixed`
  }
  if (mode === "PREMIUM_OR_DISCOUNT") return walletCapacityAdjustmentLabel(provider.providerAdjustmentBps)
  if (mode === "FLOOR_ADJUSTED") {
    const floor = walletFormatMicros(provider.minUnitPriceMicros)
    return floor === "—" ? "floor adjusted" : `${floor} floor`
  }
  return "pass-through"
}

function WalletCapacityControl({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="block min-w-0">
      <span className="mb-1 block text-[9px] font-semibold uppercase tracking-[0.14em]" style={{color:"rgba(0,0,0,0.38)"}}>{label}</span>
      {children}
    </div>
  )
}

function WalletCapacityMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl px-3 py-2" style={G.inset}>
      <p className="text-[9px] font-semibold uppercase tracking-[0.14em]" style={{color:"rgba(0,0,0,0.34)"}}>{label}</p>
      <p className="mt-1 text-[12px] font-semibold truncate">{value}</p>
    </div>
  )
}

function WalletCapacitySegment<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (value: T) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-xl p-1" style={G.inset}>
      {options.map(option=>(
        <button
          key={option.value}
          type="button"
          onClick={()=>onChange(option.value)}
          disabled={disabled}
          className="flex-1 min-w-[92px] rounded-lg px-3 py-2 text-[11px] font-semibold disabled:opacity-50"
          style={value===option.value?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{color:"rgba(0,0,0,0.6)"}}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function WalletCapacityProviderRow({
  provider,
  index,
  editing,
  saving,
  canManage,
  networkVisibilityAvailable = true,
  actions,
  children,
  onToggle,
  onCancel,
  onSave,
}: {
  provider: DirectWalletProProvider
  index: number
  editing: boolean
  saving: boolean
  canManage: boolean
  networkVisibilityAvailable?: boolean
  actions?: ReactNode
  children?: ReactNode
  onToggle: () => void
  onCancel: () => void
  onSave: (providerId: string, input: DirectWalletCapacityUpdate) => Promise<void>
}) {
  const [draft,setDraft]=useState<DirectWalletCapacityDraft>(()=>walletCapacityDraftFromProvider(provider))
  const [error,setError]=useState("")
  const visibility = walletNormalizeProVisibility(provider.visibility || provider.m)
  const broadcastMode = walletStatusLabel(provider.broadcastMode || (visibility === "network" ? "both" : "internal_only"))
  const stock = Number(provider.broadcastStockLimit || 0) > 0 ? String(provider.broadcastStockLimit) : "No cap"
  const allowedRegions = Array.isArray(provider.allowedRegions) && provider.allowedRegions.length ? provider.allowedRegions.slice(0, 3).join(", ") : "All regions"
  const allowedHardware = Array.isArray(provider.allowedHardware) && provider.allowedHardware.length ? provider.allowedHardware.slice(0, 3).join(", ") : provider.g
  const pricingMode = walletNormalizeProPricingMode(draft.pricingMode)
  const pricingSummary = pricingMode === "PREMIUM_OR_DISCOUNT"
    ? walletCapacityAdjustmentLabel(Math.round((Number(draft.adjustmentPercent || 0) || 0) * 100))
    : pricingMode === "FIXED"
      ? (draft.fixedPrice ? `${draft.fixedPrice} UC/hr fixed` : "fixed pricing")
      : pricingMode === "FLOOR_ADJUSTED"
        ? (draft.minPrice ? `${draft.minPrice} UC/hr floor` : "floor adjusted")
        : "pass-through"
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "degraded", label: "Degraded" },
    { value: "suspended", label: "Paused" },
    { value: "revoked", label: "Revoked" },
  ]
  const visibilityOptions = [
    { value: "private" as DirectWalletProVisibility, label: "Private" },
    ...(networkVisibilityAvailable
      ? [{ value: "network" as DirectWalletProVisibility, label: "Network" }]
      : []),
  ]
  const broadcastOptions = draft.visibility === "network" ? [
    { value: "BOTH" as DirectWalletProBroadcastMode, label: "Internal + network" },
    { value: "BROADCAST_ONLY" as DirectWalletProBroadcastMode, label: "Network only" },
  ] : [
    { value: "INTERNAL_ONLY" as DirectWalletProBroadcastMode, label: "Internal only" },
  ]
  const pricingOptions = [
    { value: "PASSTHROUGH" as DirectWalletProPricingMode, label: "Provider cost" },
    { value: "PREMIUM_OR_DISCOUNT" as DirectWalletProPricingMode, label: "Discount / markup" },
    { value: "FIXED" as DirectWalletProPricingMode, label: "Fixed" },
    { value: "FLOOR_ADJUSTED" as DirectWalletProPricingMode, label: "Floor" },
  ]

  useEffect(()=>{
    if (editing) {
      const next = walletCapacityDraftFromProvider(provider)
      if (!networkVisibilityAvailable) {
        next.visibility = "private"
        next.broadcastMode = "INTERNAL_ONLY"
      }
      setDraft(next)
      setError("")
    }
  }, [editing, networkVisibilityAvailable, provider.id, provider.updatedAt])

  function updateDraft(patch: Partial<DirectWalletCapacityDraft>) {
    setDraft(current=>{
      const next = { ...current, ...patch }
      if (patch.visibility === "private") next.broadcastMode = "INTERNAL_ONLY"
      if (patch.visibility === "network" && next.broadcastMode === "INTERNAL_ONLY") next.broadcastMode = "BOTH"
      return next
    })
  }

  async function saveDraft() {
    const patch = walletCapacityPatchFromDraft(draft)
    if (!patch.display_name) {
      setError("Name is required.")
      return
    }
    if (patch.pricing_mode === "FIXED" && Number(patch.fixed_unit_price_micros || 0) <= 0) {
      setError("Fixed pricing needs a seller price above 0.")
      return
    }
    setError("")
    try {
      await onSave(provider.id, patch)
    } catch {
      setError("Could not save capacity settings.")
    }
  }

  return (
    <div className={`${index>0?"border-t":""}`} style={{borderColor:"rgba(0,0,0,0.06)"}}>
      <div className="flex flex-wrap items-center gap-4 px-5 py-4">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{background:provider.active?"#22c55e":"rgba(0,0,0,0.2)"}} />
        <div className="flex-1 min-w-[160px]">
          <p className="text-[13px] font-semibold truncate">{provider.n}</p>
          <p className="text-[11px] truncate" style={{color:"rgba(0,0,0,0.42)"}}>
            {provider.slug} · {walletStatusLabel(provider.status)} · {broadcastMode}
          </p>
        </div>
        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={visibility==="network"?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.inset,color:"rgba(0,0,0,0.6)"}}>
          {visibility === "network" ? "Network" : "Private"}
        </span>
        <span className="text-[12px] font-semibold tabular-nums min-w-[120px] text-right">{walletCapacityPricingText(provider)}</span>
        <span className="text-[11px] min-w-[72px] text-right" style={{color:"rgba(0,0,0,0.42)"}}>{stock}</span>
        {actions}
        <button
          type="button"
          onClick={onToggle}
          disabled={!canManage || saving}
          className="px-3 py-2 rounded-xl text-[11px] font-semibold flex items-center gap-2"
          style={editing?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{...G.inset,color:"rgba(0,0,0,0.62)"}}
        >
          {saving?<LoaderCircle size={13} className="animate-spin" />:<Settings size={13} />}
          {editing ? "Close" : "Manage"}
        </button>
      </div>
      {provider.creditStatus ? (
        <div className="px-5 pb-4">
          <ProviderCreditStatusCard
            providerName={provider.n || provider.providerName || "Google Cloud"}
            providerID={provider.id}
            status={provider.creditStatus}
            compact
          />
        </div>
      ) : null}
      {children}
      {editing&&(
        <div className="px-5 pb-5">
          <div className="overflow-hidden rounded-2xl" style={{...G.inset,border:"1px solid rgba(0,0,0,0.06)"}}>
            <div className="border-b p-4" style={{borderColor:"rgba(0,0,0,0.06)"}}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label>Capacity controls</Label>
                  <p className="mt-1 truncate text-[16px] font-semibold">{draft.displayName || provider.n}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold" style={{...G.subtle,color:"rgba(0,0,0,0.62)"}}>{walletStatusLabel(draft.status)}</span>
                  <span className="rounded-full px-3 py-1.5 text-[11px] font-semibold" style={{background:"rgba(14,12,8,0.88)",color:"#fff"}}>{pricingSummary}</span>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <WalletCapacityMetric label="Earned" value={walletFormatMicros(provider.earnedCreditMicros, "UC")} />
                <WalletCapacityMetric label="Pending" value={walletFormatMicros(provider.pendingPayoutMicros, "UC")} />
                <WalletCapacityMetric label="Regions" value={allowedRegions} />
                <WalletCapacityMetric label="Hardware" value={allowedHardware} />
              </div>
            </div>
            <div className="grid gap-4 p-4 xl:grid-cols-[0.95fr_1.05fr]">
              <div className="space-y-4">
                <section className="rounded-xl p-3" style={G.subtle}>
                  <Label>Identity</Label>
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1.2fr]">
                    <WalletCapacityControl label="Name">
                      <input value={draft.displayName} onChange={event=>updateDraft({displayName:event.target.value})} className="w-full rounded-xl px-3 py-2.5 text-[12px] outline-none" style={G.field} />
                    </WalletCapacityControl>
                    <WalletCapacityControl label="Status">
                      <WalletCapacitySegment value={draft.status} options={statusOptions} onChange={value=>updateDraft({status:value})} />
                    </WalletCapacityControl>
                  </div>
                </section>

                <section className="rounded-xl p-3" style={G.subtle}>
                  <Label>Availability</Label>
                  {!networkVisibilityAvailable&&(
                    <p className="mt-1 text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>This provider remains private until marketplace publishing is available.</p>
                  )}
                  <div className="mt-3 grid gap-3">
                    <WalletCapacityControl label="Visibility">
                      <WalletCapacitySegment value={draft.visibility} options={visibilityOptions} onChange={value=>updateDraft({visibility:value})} />
                    </WalletCapacityControl>
                    <WalletCapacityControl label="Use mode">
                      <WalletCapacitySegment value={draft.broadcastMode} options={broadcastOptions} onChange={value=>updateDraft({broadcastMode:value})} disabled={draft.visibility==="private"} />
                    </WalletCapacityControl>
                  </div>
                </section>
              </div>

              <div className="space-y-4">
                <section className="rounded-xl p-3" style={G.subtle}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Label>Pricing</Label>
                    <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.5)"}}>{pricingSummary}</span>
                  </div>
                  <div className="mt-3">
                    <WalletCapacitySegment value={pricingMode} options={pricingOptions} onChange={value=>updateDraft({pricingMode:value})} />
                  </div>
                  {pricingMode==="PREMIUM_OR_DISCOUNT"&&(
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <WalletCapacityControl label="Adjustment %">
                        <div className="relative">
                          <input type="number" step="0.1" value={draft.adjustmentPercent} onChange={event=>updateDraft({adjustmentPercent:event.target.value})} className="w-full rounded-xl px-3 py-2.5 pr-8 text-[12px] font-mono outline-none" style={G.field} />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px]" style={{color:"rgba(0,0,0,0.4)"}}>%</span>
                        </div>
                      </WalletCapacityControl>
                      <WalletCapacityControl label="Min UC / h">
                        <input type="number" min="0" step="0.0001" value={draft.minPrice} onChange={event=>updateDraft({minPrice:event.target.value})} placeholder="optional" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                      </WalletCapacityControl>
                      <WalletCapacityControl label="Max UC / h">
                        <input type="number" min="0" step="0.0001" value={draft.maxPrice} onChange={event=>updateDraft({maxPrice:event.target.value})} placeholder="optional" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                      </WalletCapacityControl>
                      <div className="md:col-span-3 flex flex-wrap gap-1">
                        {["-90","-50","-25","0","10"].map(preset=>(
                          <button key={preset} type="button" onClick={()=>updateDraft({adjustmentPercent:preset})} className="rounded-lg px-3 py-1.5 text-[11px] font-semibold" style={draft.adjustmentPercent===preset?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{...G.inset,color:"rgba(0,0,0,0.56)"}}>
                            {Number(preset)>0?`+${preset}%`:`${preset}%`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {pricingMode==="FIXED"&&(
                    <div className="mt-3 max-w-sm">
                      <WalletCapacityControl label="Fixed UC / h">
                        <input type="number" min="0" step="0.0001" value={draft.fixedPrice} onChange={event=>updateDraft({fixedPrice:event.target.value})} placeholder="credits" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                      </WalletCapacityControl>
                    </div>
                  )}
                  {pricingMode==="FLOOR_ADJUSTED"&&(
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <WalletCapacityControl label="Min UC / h">
                        <input type="number" min="0" step="0.0001" value={draft.minPrice} onChange={event=>updateDraft({minPrice:event.target.value})} placeholder="floor" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                      </WalletCapacityControl>
                      <WalletCapacityControl label="Max UC / h">
                        <input type="number" min="0" step="0.0001" value={draft.maxPrice} onChange={event=>updateDraft({maxPrice:event.target.value})} placeholder="optional" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                      </WalletCapacityControl>
                    </div>
                  )}
                </section>

                <section className="rounded-xl p-3" style={G.subtle}>
                  <Label>Guardrails</Label>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <WalletCapacityControl label="Stock cap">
                      <input type="number" min="0" step="1" value={draft.stockLimit} onChange={event=>updateDraft({stockLimit:event.target.value})} placeholder="all" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                    </WalletCapacityControl>
                    <WalletCapacityControl label="Reserve floor">
                      <input type="number" min="0" step="0.01" value={draft.reserveFloor} onChange={event=>updateDraft({reserveFloor:event.target.value})} placeholder="credits" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                    </WalletCapacityControl>
                    <WalletCapacityControl label="Allowed regions">
                      <input value={draft.allowedRegions} onChange={event=>updateDraft({allowedRegions:event.target.value})} placeholder="all" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                    </WalletCapacityControl>
                    <WalletCapacityControl label="Allowed hardware">
                      <input value={draft.allowedHardware} onChange={event=>updateDraft({allowedHardware:event.target.value})} placeholder="all" className="w-full rounded-xl px-3 py-2.5 text-[12px] font-mono outline-none" style={G.field} />
                    </WalletCapacityControl>
                  </div>
                </section>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t p-4" style={{borderColor:"rgba(0,0,0,0.06)"}}>
              <p className="text-[11px]" style={{color:error?"#b91c1c":"rgba(0,0,0,0.42)"}}>{error || (provider.lastError ? provider.lastError : " ")}</p>
              <div className="flex gap-2">
                <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-xl text-[12px] font-semibold" style={{...G.inset,color:"rgba(0,0,0,0.62)"}}>
                  Cancel
                </button>
                <button type="button" onClick={()=>void saveDraft()} disabled={saving || !canManage} className="px-4 py-2 rounded-xl text-[12px] font-semibold flex items-center gap-2" style={{background:!saving&&canManage?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.35)",color:"white"}}>
                  {saving?<LoaderCircle size={14} className="animate-spin" />:<Check size={14} />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function WalletCapacityProviderSummaryRow({ provider, index, onClick }: { provider: DirectWalletProProvider; index: number; onClick?: () => void }) {
  const visibility = walletNormalizeProVisibility(provider.visibility || provider.m)
  const broadcastMode = walletStatusLabel(provider.broadcastMode || (visibility === "network" ? "both" : "internal_only"))
  const stock = Number(provider.broadcastStockLimit || 0) > 0 ? String(provider.broadcastStockLimit) : "No cap"
  const balance = walletProviderBalanceLabel(provider)
  const content = (
    <div className="flex flex-wrap items-center gap-4 px-5 py-4">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{background:provider.active?"#22c55e":"rgba(0,0,0,0.2)"}} />
      <div className="flex-1 min-w-[160px] text-left">
        <p className="text-[13px] font-semibold truncate">{provider.n}</p>
        <p className="text-[11px] truncate" style={{color:"rgba(0,0,0,0.42)"}}>
          {provider.slug} · {walletStatusLabel(provider.status)} · {broadcastMode}
        </p>
      </div>
      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={visibility==="network"?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.inset,color:"rgba(0,0,0,0.6)"}}>
        {visibility === "network" ? "Network" : "Private"}
      </span>
      <span className="min-w-[132px] text-right text-[11px] font-semibold" style={{color:provider.providerBalanceStatus==="billing_enabled"?"rgba(4,116,72,0.92)":"rgba(0,0,0,0.48)"}}>{balance}</span>
      <span className="text-[12px] font-semibold tabular-nums min-w-[120px] text-right">{walletCapacityPricingText(provider)}</span>
      <span className="text-[11px] min-w-[72px] text-right" style={{color:"rgba(0,0,0,0.42)"}}>{stock}</span>
      {onClick ? <ChevronRight size={15} style={{color:"rgba(0,0,0,0.36)"}} /> : null}
    </div>
  )

  return (
    <div className={`${index>0?"border-t":""}`} style={{borderColor:"rgba(0,0,0,0.06)"}}>
      {onClick ? (
        <button type="button" onClick={onClick} className="block w-full transition hover:bg-white/20">
          {content}
        </button>
      ) : content}
    </div>
  )
}

function walletProviderLedgerRouteLabel(routeType: string) {
  return String(routeType || "").toLowerCase() === "external" ? "External" : "Internal"
}

function walletProviderLedgerCSVCell(value: unknown) {
  const text = String(value ?? "")
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function walletProviderLedgerCSV(provider: DirectWalletProProvider, rows: DirectWalletProProviderLedgerRow[]) {
  const header = ["provider", "provider_slug", "route", "hardware", "region", "status", "earned_credits", "earned_micros", "time", "id"]
  const body = rows.map(row => [
    provider.n || provider.providerName || "",
    provider.slug || "",
    walletProviderLedgerRouteLabel(row.routeType),
    row.hardware || "Provider GPU",
    row.region || "",
    walletStatusLabel(row.status),
    walletMicrosToCreditsInput(row.earnedMicros) || "0",
    walletMoneyMicros(row.earnedMicros),
    row.time || "",
    row.id || "",
  ])
  return [header, ...body].map(row => row.map(walletProviderLedgerCSVCell).join(",")).join("\n")
}

function walletProviderLedgerCSVFileName(provider: DirectWalletProProvider) {
  const base = String(provider.n || provider.slug || "provider")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "provider"
  return `genrl-provider-ledger-${base}-${new Date().toISOString().slice(0, 10)}.csv`
}

function WalletProviderLedgerDialog({
  provider,
  state,
  onClose,
  onRefresh,
}: {
  provider: DirectWalletProProvider
  state: { loading:boolean; error?:string; rows:DirectWalletProProviderLedgerRow[] }
  onClose: () => void
  onRefresh: () => void
}) {
  const totalEarned = state.rows.reduce((sum,row)=>sum + walletMoneyMicros(row.earnedMicros),0)
  const scrimStyle = {
    background:"rgba(18,16,12,0.42)",
    backdropFilter:"blur(28px) saturate(1.08)",
    WebkitBackdropFilter:"blur(28px) saturate(1.08)",
  }
  const cardStyle = {
    background:"linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(248,250,249,0.90) 100%)",
    backdropFilter:"blur(64px) saturate(1.15)",
    WebkitBackdropFilter:"blur(64px) saturate(1.15)",
    border:"1px solid rgba(255,255,255,0.86)",
    boxShadow:"0 28px 90px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.96)",
  }
  const glassCell = {
    background:"rgba(255,255,255,0.58)",
    backdropFilter:"blur(28px) saturate(1.12)",
    WebkitBackdropFilter:"blur(28px) saturate(1.12)",
    border:"1px solid rgba(255,255,255,0.72)",
  }
  const ledgerSurface = {
    background:"rgba(255,255,255,0.64)",
    backdropFilter:"blur(32px) saturate(1.08)",
    WebkitBackdropFilter:"blur(32px) saturate(1.08)",
    border:"1px solid rgba(255,255,255,0.76)",
  }
  function downloadCSV() {
    downloadTextFile(walletProviderLedgerCSVFileName(provider), walletProviderLedgerCSV(provider, state.rows), "text/csv;charset=utf-8")
  }
  const dialog = (
    <motion.div
      className="fixed inset-0 z-[120] grid place-items-center px-3 sm:px-5"
      style={scrimStyle}
      initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      onClick={onClose}>
      <motion.section
        role="dialog"
        aria-modal="true"
        aria-label={`${provider.n} provider ledger`}
        className="max-h-[calc(100dvh-24px)] w-full max-w-[720px] overflow-y-auto rounded-[28px] p-4 sm:rounded-3xl sm:p-5"
        style={cardStyle}
        initial={{scale:0.96, y:12, opacity:0}}
        animate={{scale:1, y:0, opacity:1}}
        exit={{scale:0.96, y:12, opacity:0}}
        transition={{type:"spring", damping:30, stiffness:380}}
        onClick={event=>event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label>Provider account</Label>
            <h2 className="mt-2 truncate text-[24px] font-semibold tracking-normal">{provider.n}</h2>
            <p className="mt-1 text-[12px]" style={{color:"rgba(0,0,0,0.42)"}}>
              {provider.slug} · {walletStatusLabel(provider.status)} · {walletCapacityPricingText(provider)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={downloadCSV} className="h-8 rounded-full px-3 flex items-center justify-center gap-1.5 text-[11px] font-semibold" style={glassCell} aria-label="Download provider ledger CSV" title="Download CSV">
              <Download size={13} style={{color:"rgba(0,0,0,0.5)"}} />CSV
            </button>
            <button type="button" onClick={onRefresh} disabled={state.loading} className="w-8 h-8 rounded-full flex items-center justify-center" style={glassCell} aria-label="Refresh provider ledger">
              {state.loading ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCcw size={14} style={{color:"rgba(0,0,0,0.5)"}} />}
            </button>
            <button type="button" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={glassCell} aria-label="Close provider ledger">
              <X size={14} style={{color:"rgba(0,0,0,0.5)"}} />
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl p-4" style={glassCell}>
            <Label>Jobs</Label>
            <p className="mt-2 text-[24px] tabular-nums" style={{fontWeight:300}}>{state.rows.length}</p>
          </div>
          <div className="rounded-2xl p-4" style={glassCell}>
            <Label>Earned</Label>
            <p className="mt-2 text-[24px] tabular-nums" style={{fontWeight:300}}>{walletFormatMicros(totalEarned, "UC")}</p>
          </div>
          <div className="rounded-2xl p-4" style={glassCell}>
            <Label>Visibility</Label>
            <p className="mt-2 text-[24px]" style={{fontWeight:300}}>{walletNormalizeProVisibility(provider.visibility || provider.m)==="network" ? "Network" : "Private"}</p>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl" style={ledgerSurface}>
          {state.loading && state.rows.length===0 ? (
            <div className="flex items-center justify-center gap-2 px-5 py-10 text-[13px]" style={{color:"rgba(0,0,0,0.42)"}}>
              <LoaderCircle size={14} className="animate-spin" /> Loading provider ledger
            </div>
          ) : state.error ? (
            <p className="px-5 py-8 text-center text-[13px]" style={{color:"#9f1239"}}>{state.error}</p>
          ) : state.rows.length ? (
            state.rows.map((row,index)=>(
              <div key={row.id} className={`${index>0?"border-t":""} grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-[180px_minmax(0,1fr)_128px] sm:items-center sm:gap-6`} style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[13px] font-semibold">Task ran</p>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={String(row.routeType).toLowerCase()==="external"?{background:"rgba(14,12,8,0.86)",color:"#fff"}:{...G.inset,color:"rgba(0,0,0,0.58)"}}>
                      {walletProviderLedgerRouteLabel(row.routeType)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>{row.time}</p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-semibold">{row.hardware || "Provider GPU"}</p>
                  <p className="mt-1 truncate text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
                    {[row.region, walletStatusLabel(row.status)].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <p className="text-left text-[14px] font-semibold tabular-nums sm:justify-self-end sm:text-right" style={{color:"rgba(4,88,64,0.95)"}}>
                  +{walletFormatMicros(row.earnedMicros, "UC")}
                </p>
              </div>
            ))
          ) : (
            <p className="px-5 py-10 text-center text-[13px]" style={{color:"rgba(0,0,0,0.38)"}}>No jobs have run on this provider account yet.</p>
          )}
        </div>
      </motion.section>
    </motion.div>
  )
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body)
}

const WALLET_PRO_BALANCE_COLORS = ["#0f766e", "#2563eb", "#ca8a04", "#be123c", "#7c3aed", "#16a34a"]

function walletProviderBalanceLabel(provider: DirectWalletProProvider) {
  if (String(provider.providerBalanceKind || "").toLowerCase() === "postpaid") {
    switch (String(provider.providerBalanceStatus || "").toLowerCase()) {
      case "billing_enabled": return "Postpaid · Billing enabled"
      case "billing_disabled": return "Postpaid · Billing disabled"
      case "unavailable": return "Postpaid · Check unavailable"
      default: return "Postpaid · Checking billing"
    }
  }
  const micros = walletMoneyMicros(provider.providerBalanceMicros)
  if (micros > 0 || provider.providerBalanceLastCheckedAt) return walletFormatMicros(micros, "UC")
  return "Balance pending"
}

function WalletProBalanceBanner({ proStats }: { proStats: DirectWalletProStats }) {
  const providerBalances = proStats.providers
    .map((provider, index) => ({
      id: provider.id,
      name: String(provider.n || provider.providerName || provider.slug || "Provider"),
      micros: walletMoneyMicros(provider.providerBalanceMicros),
      kind: String(provider.providerBalanceKind || "credit").toLowerCase(),
      status: String(provider.providerBalanceStatus || "").toLowerCase(),
      label: walletProviderBalanceLabel(provider),
      color: WALLET_PRO_BALANCE_COLORS[index % WALLET_PRO_BALANCE_COLORS.length],
    }))
    .sort((a, b) => b.micros - a.micros || Number(a.kind === "postpaid") - Number(b.kind === "postpaid"))
  const totalMicros = providerBalances.reduce((sum, provider) => sum + provider.micros, 0)
  const positiveBalances = providerBalances.filter(provider => provider.micros > 0)
  const postpaidProviders = providerBalances.filter(provider => provider.kind === "postpaid")
  const creditStatuses = proStats.providers.filter(provider => provider.creditStatus)
  const percentLabel = (micros: number) => {
    if (totalMicros <= 0) return "0%"
    const percent = (micros / totalMicros) * 100
    return `${percent >= 10 ? percent.toFixed(0) : percent.toFixed(1)}%`
  }
  const valueLabel = (provider: typeof providerBalances[number]) => provider.kind === "postpaid"
    ? provider.label
    : provider.micros > 0 ? walletFormatMicros(provider.micros, "UC") : "0 UC"
  const providerSummary = [
    positiveBalances.length ? `${positiveBalances.length} funded ${positiveBalances.length === 1 ? "provider" : "providers"}` : "",
    postpaidProviders.length ? `${postpaidProviders.length} postpaid` : "",
  ].filter(Boolean).join(" · ") || "No provider balances"
  const stats = [
    { label:"Earnings", value:proStats.earnings, hint:proStats.earningsHint },
    { label:"Active nodes", value:proStats.activeNodes, hint:proStats.activeNodesHint },
    { label:"Uptime", value:proStats.uptime, hint:proStats.uptimeHint },
  ]

  return (
    <div className="mb-5">
      <div className="px-2 pt-2 pb-5 text-center">
        <Label>Pro credits</Label>
        <p className="mx-auto mt-3 max-w-full break-words text-[56px] leading-none tabular-nums sm:text-[64px]" style={{fontWeight:300,letterSpacing:0,color:"rgba(4,34,24,0.96)"}}>
          {proStats.proCredits}
        </p>
        <p className="mt-3 text-[13px]" style={{color:"rgba(0,0,0,0.42)"}}>{proStats.proCreditsHint}</p>
      </div>

      <div className="mx-auto max-w-[760px] pb-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{color:"rgba(0,0,0,0.42)"}}>Provider mix</p>
          <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
            {providerSummary}
          </p>
        </div>
        <div className="flex h-3 w-full overflow-hidden rounded-full" style={{...G.inset,border:"1px solid rgba(0,0,0,0.04)"}}>
          {positiveBalances.length ? positiveBalances.map(provider => (
            <div
              key={provider.id}
              className="h-full"
              title={`${provider.name} ${percentLabel(provider.micros)}`}
              style={{width:`${(provider.micros / totalMicros) * 100}%`,minWidth:positiveBalances.length>1?4:undefined,background:provider.color}}
            />
          )) : (
            <div className="h-full w-full" style={{background:"rgba(0,0,0,0.08)"}} />
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
          {providerBalances.length ? providerBalances.map(provider => (
            <div key={provider.id} className="flex min-w-0 items-center gap-2 text-[11px]" style={{color:"rgba(0,0,0,0.5)"}}>
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{background:provider.micros>0?provider.color:provider.status==="billing_enabled"?"#22c55e":"rgba(0,0,0,0.18)"}} />
              <span className="max-w-[150px] truncate font-semibold" style={{color:"rgba(0,0,0,0.72)"}}>{provider.name}</span>
              {provider.kind !== "postpaid" ? <span className="tabular-nums">{percentLabel(provider.micros)}</span> : null}
              <span className={provider.kind === "postpaid" ? "" : "font-mono tabular-nums"}>{valueLabel(provider)}</span>
            </div>
          )) : (
            <p className="text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>Connect a provider to see the balance mix.</p>
          )}
        </div>
      </div>

      {creditStatuses.length > 0 ? (
        <div className="mb-5 grid gap-3 md:grid-cols-2">
          {creditStatuses.map(provider => (
            <ProviderCreditStatusCard
              key={`credit-${provider.id}`}
              providerName={provider.n || provider.providerName || "Google Cloud"}
              providerID={provider.id}
              status={provider.creditStatus!}
            />
          ))}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        {stats.map(stat => (
          <div key={stat.label} className="min-w-0 rounded-2xl p-4" style={G.subtle}>
            <Label>{stat.label}</Label>
            <p className="mt-2 truncate text-[24px] tabular-nums" style={{fontWeight:300,letterSpacing:0}}>{stat.value}</p>
            <p className="mt-1 truncate text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>{stat.hint}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Market panel ─────────────────────────────────────────────────────────────

function MarketProviderSetupField({
  field,
  value,
  onChange,
}: {
  field: ProviderRegistryInputField
  value: ProviderInputValue | undefined
  onChange: (value: ProviderInputValue) => void
}) {
  const [revealSecret,setRevealSecret]=useState(false)
  const [credentialFileDragActive,setCredentialFileDragActive]=useState(false)
  const [credentialFileName,setCredentialFileName]=useState("")
  const [credentialFileError,setCredentialFileError]=useState("")
  const credentialFileInputRef=useRef<HTMLInputElement|null>(null)
  const inputID=useId()
  const label = `${field.label || field.key}${field.required ? " *" : ""}`
  const displayValue = providerInputDisplayValue(value)
  const type = String(field.type || "").trim().toLowerCase()
  const multilineSecret = providerInputUsesMultilineSecret(field)
  const acceptsCredentialJSONFile = field.key === "credential_json" && providerInputUsesMultilineEditor(field)

  useEffect(()=>{
    if (!displayValue) {
      setRevealSecret(false)
      setCredentialFileName("")
      setCredentialFileError("")
    }
  },[displayValue])

  async function loadCredentialJSONFile(files: FileList | File[] | null) {
    if (!acceptsCredentialJSONFile) return
    const incoming = Array.from(files || [])
    setCredentialFileDragActive(false)
    setCredentialFileName("")
    setCredentialFileError("")
    try {
      if (incoming.length !== 1) {
        throw new Error(incoming.length > 1
          ? "Drop one JSON credential file at a time."
          : "Choose a JSON credential file.")
      }
      const contents = await readProviderCredentialJSONFile(incoming[0]!)
      onChange(providerInputValue(field,contents))
      setCredentialFileName(incoming[0]!.name)
    } catch (error) {
      setCredentialFileError(error instanceof Error ? error.message : "Could not read that credential file.")
    } finally {
      if (credentialFileInputRef.current) credentialFileInputRef.current.value = ""
    }
  }

  if (type === "boolean") {
    return (
      <label className="flex min-h-[46px] items-center justify-between gap-3 rounded-xl px-3 py-2" style={G.field}>
        <span className="text-[11px] font-semibold" style={{color:"rgba(0,0,0,0.58)"}}>{label}</span>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={event=>onChange(event.target.checked)}
          className="h-4 w-4 accent-black"
        />
      </label>
    )
  }

  const placeholder = field.key === "credential_json"
    ? "Paste the Google Cloud service-account JSON"
    : field.default !== undefined && field.default !== null
      ? String(field.default)
      : field.label || field.key

  return (
    <div className="block min-w-0">
      <label htmlFor={inputID} className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.12em]" style={{color:"rgba(0,0,0,0.42)"}}>{label}</label>
      {providerInputUsesMultilineEditor(field) ? (
        acceptsCredentialJSONFile ? (
          <>
            <input
              ref={credentialFileInputRef}
              type="file"
              accept={PROVIDER_CREDENTIAL_JSON_ACCEPT}
              className="sr-only"
              tabIndex={-1}
              aria-label="Choose Google Cloud credential JSON"
              onChange={event=>void loadCredentialJSONFile(event.currentTarget.files)}
            />
            <div
              className="overflow-hidden rounded-xl"
              onDragEnter={event=>{
                event.preventDefault()
                setCredentialFileDragActive(true)
              }}
              onDragOver={event=>{
                event.preventDefault()
                event.dataTransfer.dropEffect = "copy"
                setCredentialFileDragActive(true)
              }}
              onDragLeave={event=>{
                event.preventDefault()
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setCredentialFileDragActive(false)
              }}
              onDrop={event=>{
                event.preventDefault()
                void loadCredentialJSONFile(event.dataTransfer.files)
              }}
              style={{
                ...G.field,
                background:credentialFileDragActive ? "rgba(255,255,255,0.24)" : G.field.background,
                border:credentialFileDragActive ? "1px dashed rgba(14,12,8,0.38)" : G.field.border,
              }}
            >
              <div className="relative">
                <textarea
                  id={inputID}
                  value={displayValue}
                  onChange={event=>{
                    setCredentialFileName("")
                    setCredentialFileError("")
                    onChange(providerInputValue(field,event.target.value))
                  }}
                  placeholder={placeholder}
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={credentialFileError ? true : undefined}
                  aria-describedby={credentialFileError ? `${inputID}-file-error` : undefined}
                  className={`min-h-[104px] w-full resize-y bg-transparent px-4 py-3 text-[12px] outline-none font-mono ${multilineSecret?"pr-12":""}`}
                  style={multilineSecret&&!revealSecret?({WebkitTextSecurity:"disc"} as CSSProperties):undefined}
                />
                {multilineSecret&&(
                  <button
                    type="button"
                    onClick={()=>setRevealSecret(current=>!current)}
                    title={revealSecret?"Hide secret":"Reveal secret"}
                    aria-label={revealSecret?"Hide secret":"Reveal secret"}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg"
                    style={G.subtle}
                  >
                    {revealSecret?<EyeOff size={14} />:<Eye size={14} />}
                  </button>
                )}
              </div>
              <div className="border-t px-3 py-2.5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="flex flex-wrap items-center gap-2 text-[11px]" style={{color:"rgba(0,0,0,0.46)"}}>
                  {credentialFileName ? <FileText size={13} /> : <Upload size={13} />}
                  <span className={credentialFileName ? "max-w-[260px] truncate font-medium" : undefined}>
                    {credentialFileName ? `${credentialFileName} loaded` : "Drop a JSON file here or"}
                  </span>
                  {!credentialFileName&&(
                    <button
                      type="button"
                      onClick={()=>credentialFileInputRef.current?.click()}
                      className="font-semibold underline underline-offset-2"
                      style={{color:"rgba(0,0,0,0.72)"}}
                    >
                      browse
                    </button>
                  )}
                  {credentialFileName&&(
                    <button
                      type="button"
                      onClick={()=>credentialFileInputRef.current?.click()}
                      className="font-semibold underline underline-offset-2"
                      style={{color:"rgba(0,0,0,0.72)"}}
                    >
                      Replace
                    </button>
                  )}
                </div>
                {credentialFileError&&(
                  <p id={`${inputID}-file-error`} role="alert" className="mt-2 text-[11px] font-medium" style={{color:"#9f2f24"}}>
                    {credentialFileError}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="relative">
            <textarea
              id={inputID}
              value={displayValue}
              onChange={event=>onChange(providerInputValue(field,event.target.value))}
              placeholder={placeholder}
              autoComplete="off"
              spellCheck={false}
              className={`min-h-[104px] w-full resize-y rounded-xl px-4 py-3 text-[12px] outline-none font-mono ${multilineSecret?"pr-12":""}`}
              style={{...G.field,...(multilineSecret&&!revealSecret?({WebkitTextSecurity:"disc"} as CSSProperties):{})}}
            />
            {multilineSecret&&(
              <button
                type="button"
                onClick={()=>setRevealSecret(current=>!current)}
                title={revealSecret?"Hide secret":"Reveal secret"}
                aria-label={revealSecret?"Hide secret":"Reveal secret"}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-lg"
                style={G.subtle}
              >
                {revealSecret?<EyeOff size={14} />:<Eye size={14} />}
              </button>
            )}
          </div>
        )
      ) : (
        <input
          id={inputID}
          value={displayValue}
          onChange={event=>onChange(providerInputValue(field,event.target.value))}
          placeholder={placeholder}
          type={type === "integer" ? "number" : field.secret ? "password" : "text"}
          autoComplete="off"
          className="w-full rounded-xl px-4 py-3 text-[12px] outline-none"
          style={G.field}
        />
      )}
    </div>
  )
}

function MarketPanel({
  onClose,
  marketOffers,
  proStats,
  onCreateProProvider,
  onUpdateProProvider,
  onValidateProProvider,
  onRotateProProviderKey,
  onDeleteProProvider,
  onLoadProProviderLedger,
}: {
  onClose:()=>void
  marketOffers: DirectWalletMarketOffer[]
  proStats: DirectWalletProStats
  onCreateProProvider?: DirectWalletDashboardProps["onCreateProProvider"]
  onUpdateProProvider?: DirectWalletDashboardProps["onUpdateProProvider"]
  onValidateProProvider?: DirectWalletDashboardProps["onValidateProProvider"]
  onRotateProProviderKey?: DirectWalletDashboardProps["onRotateProProviderKey"]
  onDeleteProProvider?: DirectWalletDashboardProps["onDeleteProProvider"]
  onLoadProProviderLedger?: DirectWalletDashboardProps["onLoadProProviderLedger"]
}) {
  const [tab,setTab]=useState("Provider capacity")
  const marketProviderOptions = WALLET_MARKET_PROVIDER_OPTIONS.map(option=>({
    slug: option.value,
    label: option.value === "lambda" ? "Lambda" : option.label,
  }))
  const visibilityOptions = [
    { id: "private", label: "Private" },
    { id: "network", label: "Network" },
  ] as const
  const [providerSlug,setProviderSlug]=useState<DirectWalletProviderInput["providerSlug"]>("runpod")
  const [displayName,setDisplayName]=useState("RunPod")
  const [apiKey,setApiKey]=useState("")
  const [providerAuthMode,setProviderAuthMode]=useState("")
  const [providerAuthValues,setProviderAuthValues]=useState<ProviderInputValues>({})
  const [providerAccountValues,setProviderAccountValues]=useState<ProviderInputValues>({})
  const [visibility,setVisibility]=useState<DirectWalletProviderInput["visibility"]>("private")
  const [providerDescriptors,setProviderDescriptors]=useState<ProviderRegistryDescriptor[]>([])
  const [providerRegistryState,setProviderRegistryState]=useState<"loading"|"ready"|"error">("loading")
  const [busy,setBusy]=useState<string | null>(null)
  const [rotateId,setRotateId]=useState<string | null>(null)
  const [rotateKey,setRotateKey]=useState("")
  const [editingProviderId,setEditingProviderId]=useState<string | null>(null)
  const [ledgerProvider,setLedgerProvider]=useState<DirectWalletProProvider | null>(null)
  const [providerLedger,setProviderLedger]=useState<Record<string,{loading:boolean;error?:string;rows:DirectWalletProProviderLedgerRow[]}>>({})
  const providerOptions = marketProviderOptions.map(option=>{
    const descriptor = providerDescriptorForSlug(providerDescriptors, option.slug)
    const legacySetup = providerSupportsLegacyAPIKeySetup(descriptor)
    const typedSetup = Boolean(
      descriptor &&
      providerProductAvailable(descriptor.products.pro) &&
      descriptor.credentials.modes.length > 0,
    )
    const enabled = providerRegistryState === "ready" && (legacySetup || typedSetup)
    const hint = providerRegistryState === "loading"
      ? "Checking the provider registry…"
      : providerRegistryState === "error"
        ? "Provider setup is unavailable here. Use Settings > Connected providers."
        : !descriptor
          ? "This provider is not available in the live registry."
          : descriptor.products.pro.blockers?.[0] || "This provider requires typed credentials in Settings > Connected providers."
    return {
      ...option,
      descriptor,
      setupMode: legacySetup ? "api_key" as const : typedSetup ? "typed" as const : "unavailable" as const,
      disabled:!enabled,
      hint:enabled?undefined:hint,
    }
  })
  const selectedProviderOption = providerOptions.find(option=>option.slug===providerSlug)
  const selectedProviderDescriptor = selectedProviderOption?.descriptor
  const selectedProviderUsesTypedCredentials = selectedProviderOption?.setupMode === "typed"
  const selectedProviderIsGoogle = isGoogleProviderSlug(
    selectedProviderDescriptor?.slug || providerSlug,
  )
  const selectedCredentialFields = selectedProviderUsesTypedCredentials
    ? providerCredentialFieldsForMode(selectedProviderDescriptor, providerAuthMode).filter(field=>field.required)
    : []
  const selectedSetupAccountFields = selectedProviderUsesTypedCredentials
    ? (selectedProviderDescriptor?.account_inputs || []).filter(
        field =>
          field.required ||
          (selectedProviderIsGoogle &&
            ["project_id", "billing_account_id"].includes(field.key)),
      )
    : []
  const typedProviderValidationError = (()=>{
    if (!selectedProviderUsesTypedCredentials || !selectedProviderDescriptor) return ""
    if (!providerAuthMode) return "Choose an authentication mode."
    const missing = [
      ...providerMissingRequiredInputs(selectedCredentialFields,providerAuthValues),
      ...providerMissingRequiredInputs(selectedSetupAccountFields,providerAccountValues),
    ]
    if (missing.length > 0) return `Complete the required fields: ${missing.join(", ")}.`
    const invalidJSON = providerInvalidJSONInputs(
      [...selectedCredentialFields,...selectedSetupAccountFields],
      {...providerAuthValues,...providerAccountValues},
    )
    if (invalidJSON.length > 0) return `Enter valid JSON for: ${invalidJSON.join(", ")}.`
    const literalAllRegions = providerLiteralAllRegionInputs(
      selectedProviderDescriptor.account_inputs,
      providerAccountValues,
    )
    if (literalAllRegions.length > 0) return `Use canonical region IDs for: ${literalAllRegions.join(", ")}.`
    const scopeErrors = providerAccountScopeErrors(providerAccountValues)
    return scopeErrors.length > 0 ? scopeErrors.join(". ") : ""
  })()
  const networkVisibilityAvailable = !selectedProviderUsesTypedCredentials ||
    providerSupportsProNetworkVisibility(selectedProviderDescriptor)
  const canCreate = Boolean(
    onCreateProProvider &&
    !selectedProviderOption?.disabled &&
    displayName.trim().length > 0 &&
    (selectedProviderUsesTypedCredentials ? !typedProviderValidationError : apiKey.trim().length > 0) &&
    !busy,
  )

  useEffect(()=>{
    let cancelled = false
    setProviderRegistryState("loading")
    readProviderRegistry("pro")
      .then(payload=>{
        if (cancelled) return
        setProviderDescriptors(Array.isArray(payload.providers)?payload.providers:[])
        setProviderRegistryState("ready")
      })
      .catch(()=>{
        if (!cancelled) setProviderRegistryState("error")
      })
    return ()=>{ cancelled = true }
  },[])

  function selectProvider(slug: DirectWalletProviderInput["providerSlug"]) {
    const next = providerOptions.find(p=>p.slug===slug)
    if (!next || next.disabled) return
    setProviderSlug(slug)
    setDisplayName(next.label)
    setApiKey("")
    if (next.setupMode === "typed" && next.descriptor) {
      const mode = next.descriptor.credentials.default_mode || next.descriptor.credentials.modes[0] || ""
      setProviderAuthMode(mode)
      setProviderAuthValues(
        providerInputInitialValues(providerCredentialFieldsForMode(next.descriptor,mode)),
      )
      setProviderAccountValues(providerInputInitialValues(next.descriptor.account_inputs))
      if (!providerSupportsProNetworkVisibility(next.descriptor)) setVisibility("private")
    } else {
      setProviderAuthMode("")
      setProviderAuthValues({})
      setProviderAccountValues({})
    }
  }

  function setProviderCredentialValue(field: ProviderRegistryInputField, value: ProviderInputValue) {
    setProviderAuthValues(current=>({...current,[field.key]:value}))
  }

  async function addProvider() {
    if (!canCreate) return
    setBusy("create")
    try {
      if (selectedProviderUsesTypedCredentials && selectedProviderDescriptor) {
        const payload = providerTypedProSetupPayload({
          provider:selectedProviderDescriptor,
          mode:providerAuthMode,
          credentialValues:providerAuthValues,
          accountValues:providerAccountValues,
          providerName:selectedProviderDescriptor.display_name || selectedProviderOption?.label,
          displayName:displayName.trim(),
        })
        await onCreateProProvider?.({
          providerSlug:payload.provider_slug,
          providerName:payload.provider_name,
          displayName:payload.display_name,
          auth:payload.auth,
          account:payload.account,
          visibility:networkVisibilityAvailable ? visibility : "private",
        })
      } else {
        await onCreateProProvider?.({
          providerSlug,
          providerName:selectedProviderOption?.descriptor?.display_name || selectedProviderOption?.label,
          displayName:displayName.trim(),
          apiKey:apiKey.trim(),
          visibility,
        })
      }
      setApiKey("")
      setRotateId(null)
      setRotateKey("")
      setTab("Provider capacity")
    } catch {
      // Route-level callbacks own user-facing errors.
    } finally {
      if (selectedProviderUsesTypedCredentials) {
        setProviderAuthValues(current=>providerClearSecretInputValues(selectedCredentialFields,current))
      }
      setBusy(null)
    }
  }

  async function validateProvider(providerId: string) {
    if (!onValidateProProvider || busy) return
    setBusy(`validate:${providerId}`)
    try { await onValidateProProvider(providerId) } catch {
      // Route-level callbacks own user-facing errors.
    } finally { setBusy(null) }
  }

  async function rotateProviderKey(providerId: string) {
    if (!onRotateProProviderKey || !rotateKey.trim() || busy) return
    setBusy(`rotate:${providerId}`)
    try {
      await onRotateProProviderKey(providerId, rotateKey.trim())
      setRotateId(null)
      setRotateKey("")
    } catch {
      // Route-level callbacks own user-facing errors.
    } finally {
      setBusy(null)
    }
  }

  async function deleteProvider(providerId: string) {
    if (!onDeleteProProvider || busy) return
    if (!window.confirm("Remove this provider connection?")) return
    setBusy(`delete:${providerId}`)
    try { await onDeleteProProvider(providerId) } catch {
      // Route-level callbacks own user-facing errors.
    } finally { setBusy(null) }
  }

  async function saveCapacityProvider(providerId: string, input: DirectWalletCapacityUpdate) {
    if (!onUpdateProProvider || busy) return
    setBusy(`capacity:${providerId}`)
    try {
      await onUpdateProProvider(providerId, input)
      setEditingProviderId(null)
    } finally {
      setBusy(null)
    }
  }

  async function openProviderLedger(provider: DirectWalletProProvider, force = false) {
    setLedgerProvider(provider)
    if (!onLoadProProviderLedger) {
      setProviderLedger(current=>({
        ...current,
        [provider.id]: { loading:false, error:"Provider ledger is not available yet.", rows:[] },
      }))
      return
    }
    const current = providerLedger[provider.id]
    if (!force && current && !current.error && current.rows.length > 0) return
    setProviderLedger(previous=>({
      ...previous,
      [provider.id]: { loading:true, rows:previous[provider.id]?.rows || [] },
    }))
    try {
      const rows = await onLoadProProviderLedger(provider.id)
      setProviderLedger(previous=>({
        ...previous,
        [provider.id]: { loading:false, rows },
      }))
    } catch {
      setProviderLedger(previous=>({
        ...previous,
        [provider.id]: { loading:false, error:"Could not load provider ledger.", rows:previous[provider.id]?.rows || [] },
      }))
    }
  }

  return (
    <Panel onClose={onClose} wide>
      <PanelHead title="Market" onClose={onClose} />
      <div className="flex-shrink-0 px-6 py-4 border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
        <SegCtrl options={["Provider capacity","Settings"]} value={tab} onChange={setTab} />
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <AnimatePresence mode="wait">
          {tab==="Settings"&&(
            <motion.div key="settings" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <div className="mb-5 rounded-2xl p-4 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center" style={G.subtle}>
                <div>
                  <Label>Provider access</Label>
                  <p className="text-[15px] font-semibold mt-1">Connected providers are available for credit-funded compute</p>
                  <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.42)"}}>
                    Connect typed credentials in <a className="underline underline-offset-2" href="/settings?section=providers">Settings &gt; Connected providers</a>.
                    Network routing and selling are shown when the provider advertises live support.
                  </p>
                </div>
              </div>
              <div className="rounded-2xl p-4 mb-5" style={G.subtle}>
                <div className="flex items-center justify-between mb-4">
                  <div><Label>Add provider</Label><p className="text-[13px] font-semibold mt-1">Connect capacity</p></div>
                  <Shield size={18} style={{color:"rgba(0,0,0,0.35)"}} />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3 sm:grid-cols-4">
                  {providerOptions.map(p=>(
                    <button key={p.slug} onClick={()=>selectProvider(p.slug)} disabled={p.disabled} title={p.hint} className="px-3 py-2 rounded-xl text-[12px] font-semibold disabled:cursor-not-allowed disabled:opacity-45"
                      style={providerSlug===p.slug&&!p.disabled?{background:"rgba(14,12,8,0.88)",color:"#fff"}:{...G.inset,color:"rgba(0,0,0,0.62)"}}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {selectedProviderOption?.disabled&&(
                  <p className="mb-3 rounded-xl px-3 py-2 text-[11px]" style={{...G.inset,color:"rgba(0,0,0,0.5)"}}>
                    {selectedProviderOption.hint}
                  </p>
                )}
                {!selectedProviderUsesTypedCredentials&&(
                  <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1.4fr]">
                    <input className="px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder="Display name" />
                    <input className="px-4 py-3 rounded-xl text-[13px] outline-none font-mono" style={G.field} value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="API key" type="password" />
                  </div>
                )}
                {selectedProviderUsesTypedCredentials&&selectedProviderDescriptor&&(
                  <div className="mb-4 space-y-4 rounded-2xl p-4" style={G.inset}>
                    <div>
                      <div className="grid gap-3 md:grid-cols-2">
                        {selectedCredentialFields.map(field=>(
                          <div key={field.key} className={providerInputUsesMultilineEditor(field)?"md:col-span-2":""}>
                            <MarketProviderSetupField
                              field={field}
                              value={providerAuthValues[field.key]}
                              onChange={value=>setProviderCredentialValue(field,value)}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="mt-2 text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>Encrypted after submission. Configure networking in Settings &gt; Pro before launching.</p>
                    </div>
                    {selectedSetupAccountFields.length > 0&&(
                      <div className="border-t pt-4" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                        <Label>{selectedProviderIsGoogle ? "Google account and billing scope" : "Required account scope"}</Label>
                        <p className="mt-1 text-[10px]" style={{color:"rgba(0,0,0,0.42)"}}>
                          {selectedProviderIsGoogle
                            ? "Add the project and billing account when known so GENRL can immediately look for an existing Standard billing export. Leave an unknown value blank and finish it in Settings."
                            : "Project is filled from valid credential JSON when available. Confirm the required account details."}
                        </p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          {selectedSetupAccountFields.map(field=>(
                            <div key={field.key} className={providerInputUsesMultilineEditor(field)?"md:col-span-2":""}>
                              <MarketProviderSetupField
                                field={field}
                                value={providerAccountValues[field.key]}
                                onChange={value=>setProviderAccountValues(current=>({...current,[field.key]:value}))}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {typedProviderValidationError&&(
                      <p className="rounded-xl px-3 py-2 text-[11px]" style={{background:"rgba(120,70,20,0.08)",color:"rgba(76,42,10,0.74)"}}>{typedProviderValidationError}</p>
                    )}
                  </div>
                )}
                <div className={`flex flex-col items-stretch gap-3 sm:flex-row sm:items-center ${networkVisibilityAvailable?"justify-between":"justify-end"}`}>
                  {networkVisibilityAvailable&&<div>
                    <div className="flex gap-2">
                      {visibilityOptions.map(v=>(
                        <button key={v.id} onClick={()=>setVisibility(v.id)} className="px-3 py-1.5 rounded-xl text-[11px] font-semibold"
                          style={visibility===v.id?{background:"rgba(14,12,8,0.82)",color:"#fff"}:{...G.inset,color:"rgba(0,0,0,0.55)"}}>
                          {v.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] mt-2" style={{color:"rgba(0,0,0,0.4)"}}>Private keeps capacity on your account. Network publishes capacity into GENRL routing.</p>
                  </div>}
                  <motion.button whileTap={{scale:0.96}} onClick={addProvider} disabled={!canCreate}
                    className="px-4 py-2.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2"
                    style={{background:canCreate?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.35)",color:"white"}}>
                    {busy==="create"?<LoaderCircle size={14} className="animate-spin" />:<Plus size={14} />} {selectedProviderUsesTypedCredentials?"Connect":"Add provider"}
                  </motion.button>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden mb-4" style={G.subtle}>
                {proStats.providers.length ? proStats.providers.map((nd,i)=>(
                  <WalletCapacityProviderRow
                    key={nd.id}
                    provider={nd}
                    index={i}
                    editing={editingProviderId===nd.id}
                    saving={busy===`capacity:${nd.id}`}
                    canManage={!!onUpdateProProvider && !busy}
                    networkVisibilityAvailable={(()=>{
                      const descriptor = providerDescriptorForSlug(providerDescriptors,nd.slug)
                      const normalizedSlug = String(nd.slug || "").trim().toLowerCase().replace(/[^a-z0-9]+/g,"_")
                      if (["gcp","google_cloud","googlecloud"].includes(normalizedSlug)) {
                        return providerSupportsProNetworkVisibility(descriptor)
                      }
                      return !descriptor || providerSupportsLegacyAPIKeySetup(descriptor) || providerSupportsProNetworkVisibility(descriptor)
                    })()}
                    onToggle={()=>{
                      setRotateId(null)
                      setRotateKey("")
                      setEditingProviderId(editingProviderId===nd.id?null:nd.id)
                    }}
                    onCancel={()=>setEditingProviderId(null)}
                    onSave={saveCapacityProvider}
                    actions={(
                      <div className="flex items-center gap-2">
                        <button onClick={()=>validateProvider(nd.id)} disabled={!onValidateProProvider || !!busy} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset} title="Validate provider">
                          {busy===`validate:${nd.id}`?<LoaderCircle size={14} className="animate-spin" />:<RefreshCcw size={14} />}
                        </button>
                        <button onClick={()=>{
                          setEditingProviderId(null)
                          setRotateId(rotateId===nd.id?null:nd.id)
                          setRotateKey("")
                        }} disabled={!onRotateProProviderKey || !directWalletCanRotateLegacyKey(nd) || !!busy} className="w-8 h-8 rounded-full flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-45" style={G.inset} title={directWalletCanRotateLegacyKey(nd)?"Rotate API key":"Manage typed credentials in Settings > Connected providers"}>
                          <Shield size={14} />
                        </button>
                        <button onClick={()=>deleteProvider(nd.id)} disabled={!onDeleteProProvider || !!busy} className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset} title="Remove provider">
                          {busy===`delete:${nd.id}`?<LoaderCircle size={14} className="animate-spin" />:<Trash2 size={14} />}
                        </button>
                      </div>
                    )}
                  >
                    {rotateId===nd.id&&directWalletCanRotateLegacyKey(nd)&&(
                      <div className="px-5 pb-4 flex flex-col gap-2 sm:flex-row">
                        <input autoFocus type="password" className="flex-1 px-4 py-2.5 rounded-xl text-[13px] outline-none font-mono" style={G.field} placeholder="New API key" value={rotateKey} onChange={e=>setRotateKey(e.target.value)} />
                        <button onClick={()=>rotateProviderKey(nd.id)} disabled={!rotateKey.trim() || !!busy} className="px-4 py-2 rounded-xl text-[12px] font-semibold" style={{background:rotateKey.trim()&&!busy?"rgba(14,12,8,0.88)":"rgba(14,12,8,0.32)",color:"white"}}>
                          {busy===`rotate:${nd.id}`?"Saving":"Save key"}
                        </button>
                      </div>
                    )}
                  </WalletCapacityProviderRow>
                )) : (
                  <p className="px-5 py-10 text-center text-[13px]" style={{color:"rgba(0,0,0,0.38)"}}>No providers connected yet.</p>
                )}
              </div>
            </motion.div>
          )}
          {tab==="Provider capacity"&&(
            <motion.div key="pro" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
              <WalletProBalanceBanner proStats={proStats} />
              <div className="rounded-2xl overflow-hidden mb-4" style={G.subtle}>
                {proStats.providers.length ? proStats.providers.map((nd,i)=>(
                  <WalletCapacityProviderSummaryRow
                    key={nd.id}
                    provider={nd}
                    index={i}
                    onClick={()=>void openProviderLedger(nd)}
                  />
                )) : (
                  <p className="px-5 py-10 text-center text-[13px]" style={{color:"rgba(0,0,0,0.38)"}}>No providers connected yet.</p>
                )}
              </div>
              <div className="mt-4 rounded-2xl p-4 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center" style={G.subtle}>
                <div><Label>Payout destination</Label><p className="text-[13px] font-medium mt-1">{proStats.payoutDestination}</p></div>
                <motion.button whileTap={{scale:0.96}} className="text-[13px] font-semibold px-4 py-2 rounded-xl" style={{background:"rgba(14,12,8,0.9)",color:"white"}}>Withdraw</motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {ledgerProvider ? (
          <WalletProviderLedgerDialog
            provider={ledgerProvider}
            state={providerLedger[ledgerProvider.id] || { loading:true, rows:[] }}
            onClose={()=>setLedgerProvider(null)}
            onRefresh={()=>void openProviderLedger(ledgerProvider, true)}
          />
        ) : null}
      </AnimatePresence>
    </Panel>
  )
}

// ─── Workspace panel ──────────────────────────────────────────────────────────

const GENRL_WORKSPACE_PROVIDERS: DirectWalletMarketOffer[] = [
  {id:"genrl-runpod",name:"RunPod",region:"GENRL",gpu:"GPU capacity",vram:"workspace credits",rate:0,up:true,uptime:"live"},
  {id:"genrl-hyperstack",name:"Hyperstack",region:"GENRL",gpu:"GPU capacity",vram:"workspace credits",rate:0,up:true,uptime:"live"},
  {id:"genrl-lambda",name:"Lambda",region:"GENRL",gpu:"GPU capacity",vram:"workspace credits",rate:0,up:true,uptime:"live"},
]

function workspaceProviderAllowlistKey(workspaceName: string) {
  return `genrl.workspace.provider-allowlist.${workspaceName || "workspace"}`
}

function readWorkspaceProviderAllowlist(workspaceName: string, providers: DirectWalletMarketOffer[]) {
  const defaults = Object.fromEntries(providers.map((provider) => [provider.id, true])) as Record<string, boolean>
  if (typeof window === "undefined") return defaults
  try {
    const raw = window.localStorage.getItem(workspaceProviderAllowlistKey(workspaceName))
    return raw ? {...defaults, ...(JSON.parse(raw) as Record<string, boolean>)} : defaults
  } catch {
    return defaults
  }
}

function WorkspacePanel({
  onClose,
  members,
  workspaceActivity,
  workspaceInfo,
  workspaceInvites,
  onSendCredits,
  onUpdateWorkspace,
  onInviteWorkspaceMember,
  onAcceptWorkspaceInvite,
  onDeclineWorkspaceInvite,
  onSetWorkspaceBudget,
  onSetMemberBudget,
}: {
  onClose:()=>void
  members: DirectWalletMember[]
  workspaceActivity: DirectWalletWorkspaceActivity[]
  workspaceInfo: DirectWalletWorkspaceInfo
  workspaceInvites: DirectWalletWorkspaceInvite[]
  onSendCredits?: DirectWalletDashboardProps["onSendCredits"]
  onUpdateWorkspace?: DirectWalletDashboardProps["onUpdateWorkspace"]
  onInviteWorkspaceMember?: DirectWalletDashboardProps["onInviteWorkspaceMember"]
  onAcceptWorkspaceInvite?: DirectWalletDashboardProps["onAcceptWorkspaceInvite"]
  onDeclineWorkspaceInvite?: DirectWalletDashboardProps["onDeclineWorkspaceInvite"]
  onSetWorkspaceBudget?: DirectWalletDashboardProps["onSetWorkspaceBudget"]
  onSetMemberBudget?: DirectWalletDashboardProps["onSetMemberBudget"]
}) {
  const [tab, setTab] = useState("Members")
  const [selectedMember, setSelectedMember] = useState<string|null>(null)
  const [transferBusy,setTransferBusy] = useState(false)
  const [profileOpen,setProfileOpen] = useState(false)
  const [inviteOpen,setInviteOpen] = useState(false)
  const [workspaceNameDraft,setWorkspaceNameDraft] = useState(workspaceInfo.name)
  const [billingEmailDraft,setBillingEmailDraft] = useState(workspaceInfo.billingEmail)
  const [inviteName,setInviteName] = useState("")
  const [inviteEmail,setInviteEmail] = useState("")
  const [inviteRole,setInviteRole] = useState<DirectWalletWorkspaceRole>("viewer")
  const [workspaceBudgetDraft,setWorkspaceBudgetDraft] = useState(() => String(workspaceInfo.budgetLimit || ""))
  const [memberBudgetDrafts,setMemberBudgetDrafts] = useState<Record<string,string>>({})
  const workspaceProviders = GENRL_WORKSPACE_PROVIDERS
  const [providerAllowlist,setProviderAllowlist] = useState<Record<string,boolean>>(() => readWorkspaceProviderAllowlist(workspaceInfo.name, workspaceProviders))
  const [workspaceBusy,setWorkspaceBusy] = useState<"profile"|"invite"|"workspaceBudget"|string>("")
  const total = members.reduce((s,m)=>s+m.spend,0)
  const totalBudget = Math.max(1, workspaceInfo.budgetLimit || members.reduce((s,m)=>s+m.budget,0) || 5000)
  const pct=(total/totalBudget)*100
  const roleLabel = workspaceInfo.currentRole === "owner" ? "Owner" : workspaceInfo.currentRole === "admin" ? "Admin" : "Viewer"
  const workspaceTabs = ["Members","Activity","Providers"]

  const member = members.find(m=>m.name===selectedMember)
  const memberActivity = workspaceActivity.filter(a=>a.name===selectedMember)

  async function transferToMember(target: DirectWalletMember) {
    if (!onSendCredits || transferBusy) return
    const raw = window.prompt(`Credits to send to ${target.name}`, "25")
    const amountCredits = Number(raw || 0)
    if (!Number.isFinite(amountCredits) || amountCredits <= 0) return
    setTransferBusy(true)
    try {
      await onSendCredits({
        recipient: target.email || target.name,
        amountCredits,
        note: `Workspace transfer to ${target.name}`,
      })
    } finally {
      setTransferBusy(false)
    }
  }

  async function saveWorkspaceProfile() {
    if (!onUpdateWorkspace || !workspaceInfo.canManage || workspaceBusy) return
    setWorkspaceBusy("profile")
    try {
      await onUpdateWorkspace({
        companyName: workspaceNameDraft.trim() || workspaceInfo.name,
        billingEmail: billingEmailDraft.trim(),
      })
      setProfileOpen(false)
    } finally {
      setWorkspaceBusy("")
    }
  }

  async function inviteMember() {
    if (!onInviteWorkspaceMember || !workspaceInfo.canInvite || workspaceBusy) return
    const email = inviteEmail.trim().toLowerCase()
    if (!email) return
    setWorkspaceBusy("invite")
    try {
      await onInviteWorkspaceMember({
        name: inviteName.trim() || email.split("@")[0] || "Invited user",
        email,
        role: inviteRole,
      })
      setInviteName("")
      setInviteEmail("")
      setInviteRole("viewer")
      setInviteOpen(false)
      setTab("Members")
    } finally {
      setWorkspaceBusy("")
    }
  }

  async function acceptInvite(invite: DirectWalletWorkspaceInvite) {
    if (!onAcceptWorkspaceInvite || workspaceBusy) return
    setWorkspaceBusy(`inviteAccept:${invite.id}`)
    try {
      await onAcceptWorkspaceInvite(invite.id)
      setTab("Members")
    } finally {
      setWorkspaceBusy("")
    }
  }

  async function declineInvite(invite: DirectWalletWorkspaceInvite) {
    if (!onDeclineWorkspaceInvite || workspaceBusy) return
    setWorkspaceBusy(`inviteDecline:${invite.id}`)
    try {
      await onDeclineWorkspaceInvite(invite.id)
    } finally {
      setWorkspaceBusy("")
    }
  }

  async function saveWorkspaceBudget() {
    if (!onSetWorkspaceBudget || !workspaceInfo.canBudget || workspaceBusy) return
    const amountCredits = Number(workspaceBudgetDraft)
    if (!Number.isFinite(amountCredits) || amountCredits <= 0) return
    setWorkspaceBusy("workspaceBudget")
    try {
      await onSetWorkspaceBudget({amountCredits})
    } finally {
      setWorkspaceBusy("")
    }
  }

  async function saveMemberBudget(target: DirectWalletMember) {
    if (!target.id || !target.canBudget || !onSetMemberBudget || workspaceBusy) return
    const amountCredits = Number(memberBudgetDrafts[target.id] ?? target.budget)
    if (!Number.isFinite(amountCredits) || amountCredits <= 0) return
    setWorkspaceBusy(`memberBudget:${target.id}`)
    try {
      await onSetMemberBudget({memberId: target.id, amountCredits})
    } finally {
      setWorkspaceBusy("")
    }
  }

  function setProviderAllowed(providerId: string, allowed: boolean) {
    const next = {...providerAllowlist, [providerId]: allowed}
    setProviderAllowlist(next)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(workspaceProviderAllowlistKey(workspaceInfo.name), JSON.stringify(next))
    }
  }

  return (
    <Panel onClose={onClose} wide>
      <AnimatePresence mode="wait">
        {/* ── Member detail view ── */}
        {selectedMember && member ? (
          <motion.div key="detail" className="flex flex-col h-full"
            initial={{opacity:0,x:32}} animate={{opacity:1,x:0}} exit={{opacity:0,x:32}}
            transition={{duration:0.22,ease:EASE}}>
            {/* Header */}
            <div className="flex-shrink-0 flex flex-wrap items-center gap-3 px-4 py-4 border-b sm:px-6 sm:py-5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
              <motion.button whileTap={{scale:0.9}} onClick={()=>setSelectedMember(null)}
                className="flex items-center gap-1.5 text-[13px] font-medium mr-1" style={{color:"rgba(0,0,0,0.5)"}}>
                <ChevronRight size={16} style={{transform:"rotate(180deg)"}} /> Back
              </motion.button>
              <Avatar initials={member.initials} active={member.active} size={36} />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <p className="truncate text-[16px] font-semibold">{member.name}</p>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                    style={member.role==="Owner"?{background:"rgba(14,12,8,0.85)",color:"white"}:{background:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.55)"}}>
                    {member.role}
                  </span>
                  {member.status==="invited" && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{background:"rgba(234,179,8,0.18)",color:"rgba(120,90,0,0.85)"}}>Invited</span>
                  )}
                </div>
                <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.4)"}}>{member.status==="invited" ? member.email : `${member.tasks} workloads this month`}</p>
              </div>
              <motion.button whileTap={{scale:0.88}} onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset}>
                <X size={15} style={{color:"rgba(0,0,0,0.5)"}} />
              </motion.button>
            </div>

            {/* Stats row */}
            <div className="flex-shrink-0 grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-3 sm:px-6">
              {[
                { label:"Spent this month", value:`${member.spend.toFixed(2)} UC` },
                { label:"Budget limit",     value:`${member.budget.toLocaleString()} UC` },
                { label:"Utilisation",      value:`${((member.spend/Math.max(1,member.budget))*100).toFixed(0)}%` },
              ].map(({label,value})=>(
                <div key={label} className="rounded-2xl p-4" style={G.subtle}>
                  <Label>{label}</Label>
                  <p className="text-[20px] mt-1.5" style={{fontWeight:300,letterSpacing:"-0.02em"}}>{value}</p>
                </div>
              ))}
            </div>

            {/* Budget bar */}
            <div className="flex-shrink-0 px-6 pb-4">
              <div className="h-[3px] rounded-full overflow-hidden" style={{background:"rgba(0,0,0,0.07)"}}>
                <motion.div className="h-full rounded-full" style={{background:"rgba(14,12,8,0.5)"}}
                  initial={{width:0}} animate={{width:`${Math.min(100,(member.spend/Math.max(1,member.budget))*100)}%`}}
                  transition={{duration:0.7}} />
              </div>
            </div>

            {/* Activity feed */}
            <div className="flex-shrink-0 px-6 pb-2">
              <p className="text-[13px] font-semibold">Recent activity</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {memberActivity.length === 0 ? (
                <p className="text-[13px] py-6 text-center" style={{color:"rgba(0,0,0,0.35)"}}>No recent activity</p>
              ) : memberActivity.map((a,i)=>(
                <div key={i} className={`flex items-start gap-3 py-3.5 ${i<memberActivity.length-1?"border-b":""}`}
                  style={{borderColor:"rgba(0,0,0,0.05)"}}>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={G.inset}>
                    <Zap size={13} style={{color:"rgba(0,0,0,0.45)"}} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px]" style={{color:"rgba(0,0,0,0.75)"}}>{a.act}</p>
                    {a.jobId && <p className="text-[10px] font-mono mt-0.5" style={{color:"rgba(0,0,0,0.35)"}}>{a.jobId}</p>}
                    <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.38)"}}>{a.t}</p>
                  </div>
                  {a.cost && <p className="text-[13px] font-semibold flex-shrink-0" style={{color:"rgba(0,0,0,0.65)"}}>−{a.cost} UC</p>}
                </div>
              ))}

              {/* Transfer credits to this member */}
              <div className="mt-5 pt-5 border-t" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                {member.canBudget && member.id && (
                  <div className="mb-3 flex flex-col items-stretch gap-2 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <Label>Member budget</Label>
                      <input type="number" min="0" step="0.01"
                        value={memberBudgetDrafts[member.id] ?? String(member.budget || "")}
                        onChange={e=>setMemberBudgetDrafts({...memberBudgetDrafts,[member.id!]:e.target.value})}
                        className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                    </div>
                    <motion.button whileTap={{scale:0.97}} onClick={()=>void saveMemberBudget(member)} disabled={workspaceBusy===`memberBudget:${member.id}`}
                      className="h-[42px] px-5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2"
                      style={{background:"rgba(14,12,8,0.9)",color:"white",opacity:workspaceBusy===`memberBudget:${member.id}`?0.72:1}}>
                      {workspaceBusy===`memberBudget:${member.id}` ? <><LoaderCircle size={14} className="animate-spin"/> Saving</> : "Save budget"}
                    </motion.button>
                  </div>
                )}
                <motion.button whileTap={{scale:0.97}} onClick={()=>transferToMember(member)} disabled={!onSendCredits || transferBusy}
                  className="w-full py-3 rounded-2xl text-[14px] font-semibold flex items-center justify-center gap-2"
                  style={{background:onSendCredits && !transferBusy ? "rgba(14,12,8,0.88)" : "rgba(14,12,8,0.35)",color:"white"}}>
                  <Send size={14}/> {transferBusy ? "Sending..." : `Transfer credits to ${member.name.split(" ")[0]}`}
                </motion.button>
              </div>
            </div>
          </motion.div>

        ) : (
        /* ── Main workspace view ── */
        <motion.div key="main" className="flex flex-col h-full"
          initial={{opacity:0,x:-16}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-16}}
          transition={{duration:0.22,ease:EASE}}>
          {/* Header */}
          <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-b sm:px-6 sm:py-5" style={{borderColor:"rgba(0,0,0,0.06)"}}>
            <div className="min-w-0">
              <h2 className="text-[17px] font-semibold">{workspaceInfo.name}</h2>
              <p className="text-[12px] mt-0.5" style={{color:"rgba(0,0,0,0.42)"}}>{members.length} members · {roleLabel}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {workspaceInfo.canManage && (
                <motion.button whileTap={{scale:0.95}} onClick={()=>{ setProfileOpen(!profileOpen); setInviteOpen(false); setWorkspaceNameDraft(workspaceInfo.name); setBillingEmailDraft(workspaceInfo.billingEmail); setWorkspaceBudgetDraft(String(workspaceInfo.budgetLimit || totalBudget)) }}
                  className="h-9 w-9 rounded-xl flex items-center justify-center"
                  style={profileOpen?{background:"rgba(14,12,8,0.9)",color:"white"}:G.subtle}>
                  <Settings size={14}/>
                </motion.button>
              )}
              {workspaceInfo.canInvite && (
                <motion.button whileTap={{scale:0.95}} onClick={()=>{ setInviteOpen(!inviteOpen); setProfileOpen(false) }} className="h-9 flex items-center gap-1.5 text-[13px] font-semibold px-4 rounded-xl"
                style={{background:"rgba(14,12,8,0.9)",color:"white"}}>
                  <Plus size={13}/> Invite
                </motion.button>
              )}
              <motion.button whileTap={{scale:0.88}} onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center" style={G.inset}>
                <X size={15} style={{color:"rgba(0,0,0,0.5)"}} />
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {profileOpen && (
              <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}} exit={{height:0,opacity:0}}
                className="flex-shrink-0 overflow-hidden border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="px-6 py-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <div>
                      <Label>Workspace name</Label>
                      <input value={workspaceNameDraft} onChange={e=>setWorkspaceNameDraft(e.target.value)}
                        className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                    </div>
                    <div>
                      <Label>Billing email</Label>
                      <input value={billingEmailDraft} onChange={e=>setBillingEmailDraft(e.target.value)}
                        className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                    </div>
                    <motion.button whileTap={{scale:0.97}} onClick={()=>void saveWorkspaceProfile()} disabled={workspaceBusy==="profile"}
                      className="h-[42px] px-5 rounded-xl text-[13px] font-semibold flex items-center gap-2"
                      style={{background:"rgba(14,12,8,0.9)",color:"white",opacity:workspaceBusy==="profile"?0.72:1}}>
                      {workspaceBusy==="profile" ? <><LoaderCircle size={14} className="animate-spin"/> Saving</> : "Save"}
                    </motion.button>
                  </div>
                  {workspaceInfo.canBudget && (
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end pt-3 border-t" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                      <div>
                        <Label>Monthly budget</Label>
                        <div className="mt-2 flex items-center gap-2">
                          <input type="number" min="0" step="0.01" value={workspaceBudgetDraft}
                            onChange={e=>setWorkspaceBudgetDraft(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                          <span className="text-[12px] flex-shrink-0" style={{color:"rgba(0,0,0,0.42)"}}>UC</span>
                        </div>
                      </div>
                      <motion.button whileTap={{scale:0.97}} onClick={()=>void saveWorkspaceBudget()} disabled={workspaceBusy==="workspaceBudget"}
                        className="h-[42px] px-5 rounded-xl text-[13px] font-semibold flex items-center gap-2"
                        style={{background:"rgba(14,12,8,0.9)",color:"white",opacity:workspaceBusy==="workspaceBudget"?0.72:1}}>
                        {workspaceBusy==="workspaceBudget" ? <><LoaderCircle size={14} className="animate-spin"/> Saving</> : "Save budget"}
                      </motion.button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
            {inviteOpen && (
              <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}} exit={{height:0,opacity:0}}
                className="flex-shrink-0 overflow-hidden border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_150px_auto] gap-3 items-end">
                  <div>
                    <Label>Name</Label>
                    <input value={inviteName} onChange={e=>setInviteName(e.target.value)} placeholder="Justin"
                      className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="person@company.com"
                      className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field} />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <select value={inviteRole} onChange={e=>setInviteRole(e.target.value as DirectWalletWorkspaceRole)}
                      className="w-full mt-2 px-4 py-3 rounded-xl text-[13px] outline-none" style={G.field}>
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                  <motion.button whileTap={{scale:0.97}} onClick={()=>void inviteMember()} disabled={workspaceBusy==="invite" || !inviteEmail.trim()}
                    className="h-[42px] px-5 rounded-xl text-[13px] font-semibold flex items-center gap-2"
                    style={{background:inviteEmail.trim()?"rgba(14,12,8,0.9)":"rgba(14,12,8,0.35)",color:"white",opacity:workspaceBusy==="invite"?0.72:1}}>
                    {workspaceBusy==="invite" ? <><LoaderCircle size={14} className="animate-spin"/> Sending</> : "Send invite"}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {workspaceInvites.length > 0 && (
            <div className="flex-shrink-0 px-6 pt-4">
              <div className="rounded-2xl overflow-hidden" style={{...G.subtle,border:"1px solid rgba(14,12,8,0.10)"}}>
                <div className="px-4 py-3 border-b" style={{borderColor:"rgba(0,0,0,0.06)"}}>
                  <Label>Workspace invitations</Label>
                  <p className="mt-1 text-[12px]" style={{color:"rgba(0,0,0,0.48)"}}>Review invites before joining another workspace.</p>
                </div>
                {workspaceInvites.map((invite,index)=>{
                  const accepting = workspaceBusy === `inviteAccept:${invite.id}`
                  const declining = workspaceBusy === `inviteDecline:${invite.id}`
                  const busy = accepting || declining
                  return (
                    <div key={invite.id} className={`flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-center ${index>0?"border-t":""}`} style={{borderColor:"rgba(0,0,0,0.06)"}}>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-[13px] font-semibold">{invite.companyName || invite.workspaceName}</p>
                        <p className="mt-0.5 text-[11px]" style={{color:"rgba(0,0,0,0.42)"}}>
                          {invite.role === "owner" ? "Workspace owner" : invite.role === "admin" ? "Admin" : "Viewer"} access
                        </p>
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <motion.button whileTap={{scale:0.96}} type="button" onClick={()=>void declineInvite(invite)} disabled={busy || !onDeclineWorkspaceInvite}
                          className="h-9 rounded-xl px-3 text-[12px] font-semibold"
                          style={{...G.inset,color:"rgba(0,0,0,0.62)",opacity:busy?0.7:1}}>
                          {declining ? "Rejecting" : "Reject"}
                        </motion.button>
                        <motion.button whileTap={{scale:0.96}} type="button" onClick={()=>void acceptInvite(invite)} disabled={busy || !onAcceptWorkspaceInvite}
                          className="h-9 rounded-xl px-3 text-[12px] font-semibold"
                          style={{background:"rgba(14,12,8,0.9)",color:"white",opacity:busy?0.72:1}}>
                          {accepting ? "Accepting" : "Accept"}
                        </motion.button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Budget bar */}
          <div className="flex-shrink-0 mx-6 my-4 rounded-2xl p-4" style={G.subtle}>
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
              <div>
                <Label>Monthly budget</Label>
                <p className="text-[22px] mt-1.5" style={{fontWeight:300,letterSpacing:"-0.02em"}}>
                  {total.toFixed(2)} <span className="text-[14px]" style={{color:"rgba(0,0,0,0.38)",fontWeight:400}}>/ {totalBudget.toLocaleString()} UC</span>
                </p>
              </div>
              <p className="text-[11px]" style={{color:"rgba(0,0,0,0.38)"}}>Resets Aug 1</p>
            </div>
            <div className="h-[2px] rounded-full overflow-hidden" style={{background:"rgba(0,0,0,0.08)"}}>
              <motion.div className="h-full rounded-full" style={{background:"rgba(14,12,8,0.5)"}}
                initial={{width:0}} animate={{width:`${Math.min(100,pct)}%`}} transition={{duration:0.7}} />
            </div>
            <p className="text-[11px] mt-1.5" style={{color:"rgba(0,0,0,0.38)"}}>{pct.toFixed(1)}% consumed</p>
          </div>

          <div className="flex-shrink-0 px-6 pb-4">
            <SegCtrl options={workspaceTabs} value={tab} onChange={setTab} />
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <AnimatePresence mode="wait">
              {tab==="Members" && (
                <motion.div key="mem" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
                  <div className="rounded-2xl overflow-hidden" style={G.subtle}>
                    {members.map((m,i)=>(
                      <motion.button key={m.name} whileTap={{scale:0.99}} onClick={()=>setSelectedMember(m.name)}
                        className={`w-full flex items-start gap-3.5 px-4 py-4 text-left hover:bg-black/[0.03] transition-colors sm:items-center ${i>0?"border-t":""}`}
                        style={{borderColor:"rgba(0,0,0,0.06)"}}>
                        <Avatar initials={m.initials} active={m.active} size={36} />
                        <div className="flex-1 min-w-0">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <p className="truncate text-[14px] font-semibold">{m.name}</p>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                              style={m.role==="Owner"?{background:"rgba(14,12,8,0.85)",color:"white"}:{background:"rgba(0,0,0,0.06)",color:"rgba(0,0,0,0.55)"}}>
                              {m.role}
                            </span>
                            {m.status==="invited" && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{background:"rgba(234,179,8,0.18)",color:"rgba(120,90,0,0.85)"}}>Invited</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1 rounded-full overflow-hidden" style={{background:"rgba(0,0,0,0.07)"}}>
                              <motion.div className="h-full rounded-full" style={{background:"rgba(0,0,0,0.35)"}}
                                initial={{width:0}} animate={{width:`${Math.min(100,(m.spend/Math.max(1,m.budget))*100)}%`}} transition={{duration:0.6}} />
                            </div>
                            <p className="text-[10px] flex-shrink-0" style={{color:"rgba(0,0,0,0.38)"}}>{m.spend.toFixed(0)} / {m.budget} UC</p>
                          </div>
                        </div>
                        <div className="hidden text-right flex-shrink-0 mr-1 sm:block">
                          <p className="text-[13px] font-semibold">{m.spend.toFixed(2)} UC</p>
                          <p className="text-[10px]" style={{color:"rgba(0,0,0,0.38)"}}>{m.tasks} workloads</p>
                        </div>
                        <ChevronRight size={14} style={{color:"rgba(0,0,0,0.2)"}} />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
              {tab==="Activity" && (
                <motion.div key="act" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
                  {workspaceActivity.map((a,i)=>(
                    <div key={i} className={`flex items-start gap-3 py-3.5 ${i<workspaceActivity.length-1?"border-b":""}`}
                      style={{borderColor:"rgba(0,0,0,0.05)"}}>
                      <Avatar initials={a.u} active={false} size={30} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px]">
                          <span className="font-semibold">{a.name}</span>
                          <span style={{color:"rgba(0,0,0,0.55)"}}> {a.act}</span>
                        </p>
                        {a.jobId && <p className="text-[10px] font-mono mt-0.5" style={{color:"rgba(0,0,0,0.38)"}}>{a.jobId}</p>}
                        <p className="text-[11px] mt-0.5" style={{color:"rgba(0,0,0,0.38)"}}>{a.t}</p>
                      </div>
                      {a.cost && <p className="text-[13px] font-semibold flex-shrink-0">−{a.cost} UC</p>}
                    </div>
                  ))}
                </motion.div>
              )}
              {tab==="Providers" && (
                <motion.div key="prov" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
                  <div className="mb-3 text-[12px]" style={{color:"rgba(0,0,0,0.45)"}}>
                    <p>Connected providers can route private or network workloads according to their live capabilities.</p>
                    <p className="mt-1.5 flex flex-wrap gap-3">
                      <a className="font-semibold underline underline-offset-2" href="/settings?section=providers">Connected providers</a>
                    </p>
                  </div>
                  <p className="text-[12px] mb-3 font-semibold" style={{color:"rgba(0,0,0,0.55)"}}>Network routing preferences</p>
                  <div className="rounded-2xl overflow-hidden" style={G.subtle}>
                    {workspaceProviders.map((p,i)=>(
                      <div key={p.id} className={`flex items-center gap-3 px-4 py-4 ${i>0?"border-t":""}`} style={{borderColor:"rgba(0,0,0,0.06)"}}>
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={G.inset}>
                          <Server size={14} style={{color:"rgba(0,0,0,0.48)"}} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold">{p.name}</p>
                          <p className="text-[11px] truncate" style={{color:"rgba(0,0,0,0.42)"}}>{p.region || "GENRL"} · {p.gpu || "Provider capacity"}</p>
                        </div>
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={providerAllowlist[p.id]?{background:"rgba(14,12,8,0.85)",color:"white"}:{...G.inset,color:"rgba(0,0,0,0.46)"}}>
                          {providerAllowlist[p.id] ? "Allowed" : "Blocked"}
                        </span>
                        <Toggle value={providerAllowlist[p.id] !== false} onChange={next=>setProviderAllowed(p.id,next)} />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </Panel>
  )
}

// ─── Top Nav ──────────────────────────────────────────────────────────────────

const FEATURE_PANELS = ["run","storage","market","workspace"] as const

function WalletNotificationsMenu({
  items,
  unread,
  markAllRead,
  markRead,
  clear,
}: {
  items: WalletNotification[]
  unread: number
  markAllRead: () => void
  markRead: (id: string) => void
  clear: () => void
}) {
  const hasItems = items.length > 0
  const panelStyle = {
    background:"linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,252,252,0.80) 100%)",
    backdropFilter:"blur(72px) saturate(1.18) brightness(1.08)",
    WebkitBackdropFilter:"blur(72px) saturate(1.18) brightness(1.08)",
    border:"1px solid rgba(255,255,255,0.82)",
    boxShadow:"0 20px 64px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.92)"
  }
  const tx = "rgba(14,12,8,0.88)"
  const txMeta = "rgba(14,12,8,0.45)"
  const txFaint = "rgba(14,12,8,0.28)"
  const line = "rgba(14,12,8,0.07)"
  const iconShell = G.subtle
  const control = { ...G.subtle, color:txMeta }
  return (
    <motion.div
      initial={{opacity:0,y:-8,scale:0.98}}
      animate={{opacity:1,y:0,scale:1}}
      exit={{opacity:0,y:-8,scale:0.98}}
      transition={{duration:0.18,ease:EASE}}
      className="absolute right-0 top-[54px] z-50 w-[calc(100vw-24px)] max-w-[340px] overflow-hidden rounded-[28px] sm:top-[64px]"
      style={{...panelStyle, transformOrigin:"top right"}}>
      <div className="flex items-center justify-between gap-3 px-5 py-4" style={hasItems?{borderBottom:`1px solid ${line}`}:{}}>
        <div>
          <p className="text-[14px] font-semibold tracking-tight" style={{color:tx}}>Notifications</p>
          <p className="text-[11px] mt-0.5" style={{color:txMeta}}>{unread} unread · {items.length} total</p>
        </div>
        <div className="flex items-center gap-1">
          {unread > 0 ? (
            <button type="button" onClick={markAllRead} className="w-8 h-8 rounded-full flex items-center justify-center" style={control} title="Mark all read" aria-label="Mark all read">
              <Check size={13} />
            </button>
          ) : null}
          {hasItems ? (
            <button type="button" onClick={clear} className="w-8 h-8 rounded-full flex items-center justify-center" style={control} title="Clear notifications" aria-label="Clear notifications">
              <Trash2 size={13} />
            </button>
          ) : null}
        </div>
      </div>
      <div className="max-h-[min(390px,calc(100dvh-132px))] overflow-y-auto" style={{scrollbarWidth:"none"}}>
        {!hasItems ? (
          <div className="px-6 pb-10 pt-5 text-center">
            <div className="mx-auto mb-3 w-11 h-11 rounded-2xl flex items-center justify-center" style={iconShell}>
              <Bell size={15} style={{color:txMeta}} />
            </div>
            <p className="text-[13px] font-semibold" style={{color:tx}}>All caught up</p>
            <p className="text-[11px] mt-1 leading-snug" style={{color:txMeta}}>New workload and wallet updates will appear here.</p>
          </div>
        ) : (
          items.slice(0, 8).map((item,index)=>{
            const Icon = WALLET_NOTIFICATION_ICONS[item.kind] || Bell
            return (
              <button
                key={item.id}
                type="button"
                onClick={()=>markRead(item.id)}
                className="w-full flex items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-black/[0.03]"
                style={index<Math.min(items.length,8)-1?{borderBottom:`1px solid ${line}`}:{}}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={item.read?iconShell:{background:"rgba(14,12,8,0.86)",border:"1px solid rgba(14,12,8,0.10)"}}>
                  <Icon size={14} style={{color:item.read?txMeta:"#fff"}} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[12px] font-semibold truncate" style={{color:tx}}>{item.title}</p>
                    <span className="text-[10px] flex-shrink-0" style={{color:txFaint}}>{timeAgo(item.createdAt)}</span>
                  </div>
                  <p className="text-[11px] mt-1 leading-snug" style={{color:txMeta}}>{item.body}</p>
                </div>
                {!item.read ? <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background:"rgba(14,12,8,0.86)"}} /> : null}
              </button>
            )
          })
        )}
      </div>
    </motion.div>
  )
}

function TopNav({
  panel,
  onOpen,
  user,
  lightText,
}: {
  panel:Panel
  onOpen:(p:Panel)=>void
  user: DirectWalletUser
  lightText: boolean
}) {
  const { items, unread, markAllRead, markRead, clear } = useNotifications()
  const { bootstrap: instanceBootstrap } = useInstanceBranding()
  const [notificationsOpen,setNotificationsOpen]=useState(false)
  const notificationsRef = useRef<HTMLDivElement | null>(null)
  const navItems=[
    {id:"run" as Panel,label:"Run"},
    {id:"storage" as Panel,label:"Storage"},
    {id:"market" as Panel,label:"Market"},
    {id:"workspace" as Panel,label:"Workspace"},
  ]
  const navText = lightText ? "rgba(255,255,255,0.92)" : "rgba(14,12,8,0.88)"
  const navMuted = lightText ? "rgba(255,255,255,0.56)" : "rgba(0,0,0,0.42)"
  const navFaint = lightText ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.09)"
  const navChrome = lightText
    ? { background:"rgba(14,12,8,0.16)", backdropFilter:"blur(40px)", WebkitBackdropFilter:"blur(40px)", borderBottom:"1px solid rgba(255,255,255,0.18)" }
    : G.nav
  const navInset = lightText ? { background:"rgba(255,255,255,0.10)" } : G.inset
  const navControl = lightText
    ? { background:"rgba(255,255,255,0.11)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", border:"1px solid rgba(255,255,255,0.20)" }
    : G.subtle

  useEffect(()=>{
    if (!notificationsOpen) return
    function onDocumentPointerDown(event: PointerEvent) {
      if (!notificationsRef.current?.contains(event.target as Node)) setNotificationsOpen(false)
    }
    function onDocumentKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setNotificationsOpen(false)
    }
    document.addEventListener("pointerdown", onDocumentPointerDown)
    document.addEventListener("keydown", onDocumentKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onDocumentPointerDown)
      document.removeEventListener("keydown", onDocumentKeyDown)
    }
  },[notificationsOpen])

  return (
    <div className="sticky top-0 z-30 flex flex-shrink-0 flex-wrap items-center justify-between gap-2 px-3 py-2 sm:h-[60px] sm:flex-nowrap sm:px-8 sm:py-0" style={navChrome}>
      <div className="flex w-auto items-center gap-2.5 sm:w-[250px]">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-[8px]" style={{background:"rgba(255,255,255,0.92)"}}>
          {instanceBootstrap?.branding.logo_url ? (
            <img
              src={instanceBootstrap.branding.logo_url}
              alt={`${instanceBootstrap.display_name} logo`}
              className="h-full w-full object-contain p-0.5"
            />
          ) : (
            <div className="h-2.5 w-2.5 rounded-full" style={{background:lightText?"rgba(14,12,8,0.86)":"rgba(14,12,8,0.9)"}} />
          )}
        </div>
        <span className="hidden whitespace-nowrap text-[14px] font-bold tracking-tight sm:inline" style={{color:navText}}>{instanceBootstrap?.display_name || "GENRL"}</span>
        <span
          className="rounded-full px-2 py-1 text-[8px] font-bold uppercase leading-none tracking-[0.16em]"
          style={{
            color:navText,
            background:lightText?"rgba(255,255,255,0.13)":"rgba(14,12,8,0.07)",
            border:`1px solid ${lightText?"rgba(255,255,255,0.24)":"rgba(14,12,8,0.12)"}`,
          }}>
          Beta
        </span>
      </div>

      <div className="order-3 flex w-full min-w-0 items-center gap-0.5 overflow-x-auto rounded-2xl p-1 sm:order-none sm:w-auto sm:overflow-visible" style={{...navInset, scrollbarWidth:"none"}}>
        {navItems.map(({id,label})=>(
          <button key={id} onClick={()=>onOpen(panel===id?null:id)} className="relative flex-shrink-0 px-3 py-1.5 rounded-xl text-[13px] font-medium transition-colors sm:px-5"
            style={{color:panel===id?navText:navMuted}}>
            {panel===id&&<motion.div layoutId="nav-pill" className="absolute inset-0 rounded-xl" style={{background:lightText?"rgba(255,255,255,0.16)":"#fff",boxShadow:`0 1px 5px ${navFaint}`}} transition={{type:"spring",stiffness:500,damping:38}} />}
            <span className="relative z-10">{label}</span>
          </button>
        ))}
      </div>

      <div className="flex w-auto items-center justify-end gap-2.5 sm:w-[250px]">
        <div ref={notificationsRef} className="relative">
          <motion.button
            whileTap={{scale:0.9}}
            onClick={()=>setNotificationsOpen(open=>!open)}
            className="relative w-8 h-8 rounded-full flex items-center justify-center"
            style={navControl}
            title="Notifications"
            aria-label="Notifications"
            aria-haspopup="dialog"
            aria-expanded={notificationsOpen}>
            <Bell size={14} style={{color:notificationsOpen?navText:navMuted}} />
            {unread > 0 ? (
              <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full text-[9px] leading-[15px] text-center font-bold" style={{background:lightText?"rgba(255,255,255,0.92)":"rgba(14,12,8,0.90)",color:lightText?"rgba(14,12,8,0.86)":"#fff"}}>
                {unread > 9 ? "9+" : unread}
              </span>
            ) : (
              <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{background:lightText?"rgba(255,255,255,0.72)":"rgba(14,12,8,0.55)"}} />
            )}
          </motion.button>
          <AnimatePresence>
            {notificationsOpen ? (
              <WalletNotificationsMenu
                items={items}
                unread={unread}
                markAllRead={markAllRead}
                markRead={markRead}
                clear={clear}
              />
            ) : null}
          </AnimatePresence>
        </div>
        <motion.button whileTap={{scale:0.95}} onClick={()=>onOpen(panel==="more"?null:"more")} className="flex items-center gap-2">
          <Avatar initials={user.initials} active={true} size={32} />
          <span className="text-[12px] font-semibold hidden lg:block" style={{color:navText}}>{user.name}</span>
        </motion.button>
      </div>
    </div>
  )
}

// ─── Wallet home ──────────────────────────────────────────────────────────────

type LedgerStatusTab = "History" | "Active"
type LedgerJobTypeTab = "Gateway" | "VM" | "Direct" | "SSH Terminal"

const LEDGER_STATUS_TABS: LedgerStatusTab[] = ["History", "Active"]
const LEDGER_JOB_TYPE_TABS: LedgerJobTypeTab[] = ["Gateway", "VM", "Direct", "SSH Terminal"]

function ledgerRowJobType(row: DirectWalletLedgerRow): LedgerJobTypeTab | null {
  const task = row.task
  if (!task) return null
  if (task.gateway) return "Gateway"
  if (task.sshTerminal) return "SSH Terminal"
  const accessMode = normalizedTaskState(task.accessMode || String(task.access?.mode || ""))
  if (accessMode) return "Direct"
  return "VM"
}

function ledgerRowIsActive(row: DirectWalletLedgerRow) {
  const task = row.task
  if (!task) return false
  if (taskPersistentRuntimeLooksActive(task)) return true
  const states = [task.status, task.providerStatus, task.leaseStatus, task.stageLabel].map(normalizedTaskState)
  if (task.lastError || states.some(state => state.includes("failed") || state.includes("error"))) return false
  if (states.some(state =>
    state.includes("closing_complete") ||
    state.includes("closed") ||
    state.includes("terminated") ||
    state.includes("expired") ||
    state.includes("stopped") ||
    state.includes("completed") ||
    state.includes("complete") ||
    state.includes("done") ||
    state.includes("output_recorded")
  )) return false
  return states.some(state =>
    state.includes("active") ||
    state.includes("idle") ||
    state.includes("pending") ||
    state.includes("queued") ||
    state.includes("running") ||
    state.includes("processing") ||
    state.includes("streaming") ||
    state.includes("provision") ||
    state.includes("starting") ||
    state.includes("loading") ||
    state.includes("booting") ||
    state.includes("cleanup")
  )
}

function ledgerRowMatchesTabs(row: DirectWalletLedgerRow, statusTab: LedgerStatusTab, jobTypeTab: LedgerJobTypeTab) {
  if (ledgerRowJobType(row) !== jobTypeTab) return false
  return statusTab === "Active" ? ledgerRowIsActive(row) : !ledgerRowIsActive(row)
}

function launchLookupTokens(launch?: DirectWalletLaunchResult | null) {
  const tokens = new Set<string>()
  if (!launch) return tokens
  for (const value of [launch.taskId, launch.providerJobId, launch.clientLaunchId, launch.runtimeSessionId]) {
    const token = String(value || "").trim().toLowerCase()
    if (token) tokens.add(token)
  }
  return tokens
}

function ledgerRowMatchesLaunch(row: DirectWalletLedgerRow, tokens: Set<string>) {
  if (tokens.size === 0) return false
  for (const value of [
    row.id,
    row.displayId,
    row.task?.id,
    row.task?.providerJobId,
    row.task?.clientLaunchId,
    row.task?.runtimeSessionId,
    row.task?.remoteId,
  ]) {
    const token = String(value || "").trim().toLowerCase()
    if (token && tokens.has(token)) return true
  }
  return false
}

function WalletHome({
  onOpen,
  lightText,
  data,
  onLaunchWorkload,
  onContinuationLaunched,
  onNeedCredits,
  onLoadRuntimeSession,
  onSendRuntimeSessionTurn,
  onCloseRuntimeSession,
  onTerminateProviderJob,
  onRefresh,
  launchBusy,
  creditActionsDisabled,
  pendingLaunch,
  onPendingLaunchHandled,
}: {
  onOpen:(p:Panel)=>void
  lightText:boolean
  data: DirectWalletResolvedData
  onLaunchWorkload?: DirectWalletDashboardProps["onLaunchWorkload"]
  onContinuationLaunched?: (result?: DirectWalletLaunchResult | void, options?: DirectWalletLaunchFocusOptions) => void
  onNeedCredits?: () => void
  onLoadRuntimeSession?: DirectWalletDashboardProps["onLoadRuntimeSession"]
  onSendRuntimeSessionTurn?: DirectWalletDashboardProps["onSendRuntimeSessionTurn"]
  onCloseRuntimeSession?: DirectWalletDashboardProps["onCloseRuntimeSession"]
  onTerminateProviderJob?: DirectWalletDashboardProps["onTerminateProviderJob"]
  onRefresh?: DirectWalletDashboardProps["onRefresh"]
  launchBusy: boolean
  creditActionsDisabled: boolean
  pendingLaunch?: DirectWalletPendingLaunch | null
  onPendingLaunchHandled?: () => void
}) {
  const [hidden,setHidden]=useState(false)
  const [showProCreditBalance,setShowProCreditBalance]=useState(false)
  const [selectedLedger,setSelectedLedger]=useState<DirectWalletLedgerRow | null>(null)
  const [selectedLedgerView,setSelectedLedgerView]=useState<DirectWalletTaskView>("task")
  const [ledgerPage,setLedgerPage]=useState(0)
  const [ledgerStatusTab,setLedgerStatusTab]=useState<LedgerStatusTab>("History")
  const [ledgerJobTypeTab,setLedgerJobTypeTab]=useState<LedgerJobTypeTab>("VM")
  const providerBalanceMicros = data.proStats.providers.reduce((sum, provider) => sum + walletMoneyMicros(provider.providerBalanceMicros), 0)
  const proCreditBalance = providerBalanceMicros > 0 ? providerBalanceMicros / WALLET_MICRO : walletParseCreditsText(data.proStats.proCredits)
  const displayedBalance = showProCreditBalance ? proCreditBalance : data.balance
  const displayedBalanceLabel = showProCreditBalance ? "Pro Credit Balance" : "Universal Credits"
  const proCreditButtonLabel = showProCreditBalance ? "Hide Pro Credit Balance" : "See Pro Credit Balance"
  const chartData = data.chartData.map(point => {
    const value = Number(point.v)
    return { d: point.d, v: Number.isFinite(value) && value > 0 ? value : 0 }
  })
  const chartMaxValue = Math.max(1, ...chartData.map(point => point.v))
  const totalSpend=chartData.reduce((s,d)=>s+d.v,0)
  const totalSpendLabel = walletSpendDisplayValue(totalSpend)
  const chartRangeLabel = walletChartRangeLabel(chartData)
  const storageVolumeLabel = `${data.volumes.length} volume${data.volumes.length === 1 ? "" : "s"}`
  const workspaceSpend = data.members.reduce((sum,m)=>sum+m.spend,0)
  const workspaceInviteLabel = data.workspaceInvites.length > 0
    ? `${data.workspaceInvites.length} pending invite${data.workspaceInvites.length === 1 ? "" : "s"}`
    : `${data.workspaceName} · ${data.members.length} member${data.members.length === 1 ? "" : "s"}`
  const filteredLedger = data.ledger.filter(row=>ledgerRowMatchesTabs(row, ledgerStatusTab, ledgerJobTypeTab))
  const ledgerPageCount = Math.max(1, Math.ceil(filteredLedger.length / LEDGER_PAGE_SIZE))
  const activeLedgerPage = Math.min(ledgerPage, ledgerPageCount - 1)
  const ledgerStart = activeLedgerPage * LEDGER_PAGE_SIZE
  const ledgerRows = filteredLedger.slice(ledgerStart, ledgerStart + LEDGER_PAGE_SIZE)
  const ledgerRangeStart = filteredLedger.length === 0 ? 0 : ledgerStart + 1
  const ledgerRangeEnd = Math.min(filteredLedger.length, ledgerStart + ledgerRows.length)

  useEffect(()=>{
    setLedgerPage(0)
  },[ledgerStatusTab,ledgerJobTypeTab])

  useEffect(()=>{
    setLedgerPage(page=>Math.min(page, Math.max(0, ledgerPageCount - 1)))
  },[ledgerPageCount])

  useEffect(()=>{
    const tokens = launchLookupTokens(pendingLaunch?.result)
    if (tokens.size === 0) return
    const match = data.ledger.find(row=>ledgerRowMatchesLaunch(row,tokens))
    if (!match) return
    setSelectedLedgerView(pendingLaunch?.view || "task")
    setSelectedLedger(match)
    onPendingLaunchHandled?.()
  },[data.ledger,pendingLaunch,onPendingLaunchHandled])

  useEffect(()=>{
    if (!selectedLedger) return
    const selectedTokens = launchLookupTokens({
      taskId: selectedLedger.task?.id || selectedLedger.id,
      providerJobId: selectedLedger.task?.providerJobId,
      clientLaunchId: selectedLedger.task?.clientLaunchId,
      runtimeSessionId: selectedLedger.task?.runtimeSessionId,
    })
    const match = data.ledger.find(row=>row.id === selectedLedger.id) ||
      data.ledger.find(row=>ledgerRowMatchesLaunch(row, selectedTokens))
    if (!match) return
    setSelectedLedger(current=>{
      if (!current) return current
      const currentTokens = launchLookupTokens({
        taskId: current.task?.id || current.id,
        providerJobId: current.task?.providerJobId,
        clientLaunchId: current.task?.clientLaunchId,
        runtimeSessionId: current.task?.runtimeSessionId,
      })
      return current.id === selectedLedger.id || ledgerRowMatchesLaunch(match, currentTokens) ? match : current
    })
  },[
    data.ledger,
    selectedLedger?.id,
    selectedLedger?.task?.id,
    selectedLedger?.task?.providerJobId,
    selectedLedger?.task?.clientLaunchId,
    selectedLedger?.task?.runtimeSessionId,
  ])

  // Colour tokens that flip with the background toggle
  const tx      = lightText ? "rgba(255,255,255,0.92)" : "rgba(14,12,8,0.88)"
  const txMuted = lightText ? "rgba(255,255,255,0.62)" : "rgba(14,12,8,0.32)"
  const txSub   = lightText ? "rgba(255,255,255,0.52)" : "rgba(14,12,8,0.30)"
  const txMeta  = lightText ? "rgba(255,255,255,0.68)" : "rgba(14,12,8,0.42)"
  const txFaint = lightText ? "rgba(255,255,255,0.34)" : "rgba(14,12,8,0.28)"
  const txLine  = lightText ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.06)"
  const chartStroke = lightText ? "rgba(255,255,255,0.35)" : "rgba(14,12,8,0.18)"
  const chartFill   = lightText ? "rgba(255,255,255,0.10)" : "rgba(14,12,8,0.10)"
  const homeCard = lightText
    ? { background:"rgba(14,12,8,0.18)", backdropFilter:"blur(40px)", WebkitBackdropFilter:"blur(40px)", border:"1px solid rgba(255,255,255,0.22)", boxShadow:"0 8px 30px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.14)" }
    : G.card
  const homeSubtle = lightText
    ? { background:"rgba(255,255,255,0.10)", backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)", border:"1px solid rgba(255,255,255,0.20)" }
    : G.subtle
  const homeInset = lightText ? { background:"rgba(255,255,255,0.10)" } : G.inset
  const runningSessionCount = data.sessions.filter(session=>session.status === "running").length
  const idleSessionCount = data.sessions.filter(session=>session.status === "idle").length
  const activeSessionCount = runningSessionCount + idleSessionCount
  const protectedSnapshotCount = data.snapshots.filter(snapshot=>!["deleted","deleting","failed","error","unavailable","offline","inactive","closed","archived","expired"].includes(String(snapshot.status || "available").trim().toLowerCase())).length
  const protectedVolumeCount = data.volumes.filter(isLiveWalletVolume).length
  const reserveHint = activeSessionCount > 0
    ? "Held against active sessions"
    : protectedSnapshotCount + protectedVolumeCount > 0
      ? "Protecting stored snapshots and volumes"
      : "No active holds"
  const reserveRows = activeSessionCount > 0
    ? [
        { c:lightText?"rgba(255,255,255,0.72)":"#b45309", l:`${runningSessionCount} running` },
        { c:txFaint, l:`${idleSessionCount} idle` },
      ]
    : [
        { c:lightText?"rgba(255,255,255,0.72)":"#15803d", l:`${protectedSnapshotCount} snapshot${protectedSnapshotCount === 1 ? "" : "s"}` },
        { c:txFaint, l:`${protectedVolumeCount} volume${protectedVolumeCount === 1 ? "" : "s"}` },
      ]

  const CIRCLES=[
    {label:"Send",    Icon:ArrowUpRight,  action:"send"    as Panel},
    {label:"Add",     Icon:Plus,           action:"add"     as Panel},
    {label:"Receive", Icon:ArrowDownLeft,  action:"receive" as Panel},
    {label:"More",    Icon:MoreHorizontal, action:"more"    as Panel},
  ]
  const TILES=[
    {Icon:Zap,        title:"Run Task",  sub:"Any model, any hardware",   foot:data.jobs[0]?.time ? `Last run ${data.jobs[0].time}` : `${data.sessions.length} active session${data.sessions.length === 1 ? "" : "s"}`, pct:null, badge:null,  action:"run"       as Panel},
    {Icon:HardDrive,  title:"Storage",   sub:storageVolumeLabel, foot:null, pct:data.storageUsagePct,   badge:null,  action:"storage"   as Panel},
    {Icon:Users,      title:"Workspace", sub:workspaceInviteLabel, foot:`${workspaceSpend.toLocaleString(undefined,{maximumFractionDigits:2})} UC this month`,pct:null, badge:data.workspaceInvites.length > 0 ? "INVITE" : null,  action:"workspace" as Panel},
    {Icon:TrendingUp, title:"Market",    sub:`${data.marketOffers.length} providers · seller capacity`,  foot:null,                    pct:null, badge:data.user.plan.toUpperCase(), action:"market"    as Panel},
  ]

  return (
    <>
    <div className="min-h-full overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+48px)]" style={{scrollbarWidth:"none"}}>
      <div className="mx-auto max-w-[1000px] px-4 pt-5 sm:px-8 sm:pt-8">

        {/* ── Hero: balance over ghost chart, no card ── */}
        <motion.div initial={false} animate={{opacity:1,y:0}} transition={{duration:0.4,ease:EASE}}
          className="relative mb-7 h-[250px] sm:h-[220px]">

          {/* 30-day chart — ghost background, colour-aware */}
          <div className="absolute inset-0 pointer-events-none">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{top:24,right:0,left:0,bottom:0}}>
                <defs>
                  <linearGradient id="heroBg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={chartFill} />
                    <stop offset="95%" stopColor="rgba(0,0,0,0)" />
                  </linearGradient>
                </defs>
                <YAxis hide domain={[0, chartMaxValue]} />
                <Area type="monotone" dataKey="v" stroke={chartStroke} strokeWidth={1.5}
                  fill="url(#heroBg)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Balance — centred */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-3"
              style={{color:txMuted}}>
              {displayedBalanceLabel}
            </span>
            <div className="flex items-end gap-2.5 mb-1.5">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span key={hidden?"h":"v"}
                  initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}}
                  transition={{duration:0.22}}
                  className="text-[48px] leading-none sm:text-[82px]"
                  style={{fontWeight:300,letterSpacing:0,color:tx}}>
                  {hidden ? "·  ·  ·  ·" : displayedBalance.toLocaleString("en-US",{minimumFractionDigits:2})}
                </motion.span>
              </AnimatePresence>
              {!hidden && (
                <span className="text-[22px] mb-2" style={{fontWeight:300,color:txSub}}>UC</span>
              )}
            </div>
            {!hidden && (
              <p className="text-[13px]" style={{color:txSub}}>
                ≈ ${displayedBalance.toLocaleString("en-US",{minimumFractionDigits:2})} USD
              </p>
            )}
          </div>

          {/* Bottom row: user info · actions */}
          <div className="absolute bottom-0 left-0 right-0 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div className="flex items-center gap-2.5">
              <Avatar initials={data.user.initials} active={true} size={28} />
              <div>
                <p className="text-[13px] font-semibold leading-tight" style={{color:tx}}>{data.user.name}</p>
                <p className="text-[10px] flex items-center gap-1.5 mt-0.5" style={{color:txMeta}}>
                  {data.user.handle}
                  <span className="inline-block w-px h-2.5" style={{background:txMeta}} />
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />{data.user.plan.toUpperCase()}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <motion.button whileTap={{scale:0.9}} onClick={()=>setHidden(!hidden)}
                className="h-9 px-3 rounded-full text-[13px] leading-none font-semibold flex items-center justify-center whitespace-nowrap sm:px-4" style={{...homeSubtle,color:txMeta}}>
                {hidden ? "Show" : "Hide"}
              </motion.button>
              <motion.button whileTap={{scale:0.95}} onClick={()=>setShowProCreditBalance(value=>!value)}
                className="h-9 px-3 rounded-full text-[13px] leading-none font-semibold flex items-center justify-center gap-1.5 whitespace-nowrap sm:px-4"
                style={showProCreditBalance
                  ? (lightText?{...homeSubtle,color:tx}:{background:tx,color:"#fff"})
                  : {...homeSubtle,color:txMeta}}>
                <Wallet size={13} /> {proCreditButtonLabel}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* ── 4 circles ── */}
        <motion.div initial={false} animate={{opacity:1,y:0}} transition={{duration:0.38,ease:EASE,delay:0.08}}
          className="mb-8 grid grid-cols-4 gap-3 sm:flex sm:justify-center sm:gap-10">
          {CIRCLES.map(({label,Icon,action},i)=>(
            <motion.button key={label} onClick={()=>onOpen(action)}
              initial={false} animate={{opacity:1,y:0}} transition={{delay:0.12+i*0.05,duration:0.3,ease:EASE}}
              className="flex flex-col items-center gap-2.5">
              <motion.div whileHover={{scale:1.08,y:-2}} whileTap={{scale:0.91}}
                className="w-[54px] h-[54px] rounded-full flex items-center justify-center sm:h-[64px] sm:w-[64px]"
                style={{
                  background:lightText?"rgba(255,255,255,0.14)":"rgba(14,12,8,0.85)",
                  boxShadow:lightText?"0 8px 28px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.16)":"0 8px 28px rgba(0,0,0,0.22),0 2px 6px rgba(0,0,0,0.15)"
                }}>
                <Icon size={22} style={{color:lightText?"rgba(14,12,8,0.82)":"#fff"}} strokeWidth={1.8} />
              </motion.div>
              <span className="text-[12px] font-medium" style={{color:txMeta}}>{label}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* ── 4 tiles ── */}
        <div className="grid grid-cols-2 gap-3 mb-6 sm:grid-cols-4 sm:gap-4">
          {TILES.map(({Icon,title,sub,foot,pct,badge,action},i)=>(
            <motion.button key={title} onClick={()=>onOpen(action)}
              initial={false} animate={{opacity:1,y:0}} transition={{delay:0.18+i*0.06,duration:0.34,ease:EASE}}
              whileHover={{y:-3,scale:1.02,transition:{duration:0.2}}} whileTap={{scale:0.97}}
              className="min-h-[148px] rounded-2xl p-4 text-left sm:p-5" style={homeCard}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={homeInset}>
                  <Icon size={16} style={{color:txMeta}} strokeWidth={1.8} />
                </div>
                <ChevronRight size={13} style={{color:txFaint}} />
              </div>
              <p className="text-[14px] font-semibold tracking-tight mb-0.5" style={{color:tx}}>{title}</p>
              <p className="text-[11px] mb-3" style={{color:txMeta}}>{sub}</p>
              {pct!==null?(
                <div>
                  <div className="h-[2px] rounded-full overflow-hidden mb-1.5" style={{background:txLine}}>
                    <motion.div className="h-full rounded-full" style={{background:lightText?"rgba(255,255,255,0.56)":"rgba(0,0,0,0.32)"}} initial={{width:0}} animate={{width:`${pct}%`}} transition={{delay:0.4+i*0.06,duration:0.7,ease:EASE}} />
                  </div>
                  <p className="text-[10px]" style={{color:txMuted}}>{pct}% used</p>
                </div>
              ):badge?(
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={lightText?{background:"rgba(255,255,255,0.10)",color:tx,border:"1px solid rgba(255,255,255,0.18)"}:{background:"rgba(217,143,50,0.12)",color:"#b45309",border:"1px solid rgba(217,143,50,0.18)"}}>{badge}</span>
              ):(
                <p className="text-[10px]" style={{color:txMuted}}>{foot}</p>
              )}
            </motion.button>
          ))}
        </div>

        {/* ── Chart + Reserves ── */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-[3fr_1fr]">
          <motion.div initial={false} animate={{opacity:1}} transition={{delay:0.4,duration:0.35}} className="rounded-2xl p-5" style={homeCard}>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
              <div><Label color={txMuted}>30-day spend</Label><p className="mt-1.5 text-[28px]" style={{fontWeight:300,letterSpacing:"-0.02em",color:tx}}>{totalSpendLabel} <span className="text-[16px]" style={{color:txSub,fontWeight:400}}>UC</span></p></div>
              <p className="text-[10px]" style={{color:txFaint}}>{chartRangeLabel}</p>
            </div>
            <ResponsiveContainer width="100%" height={96}>
              <AreaChart data={chartData} margin={{top:0,right:0,left:0,bottom:0}}>
                <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={chartFill} /><stop offset="95%" stopColor="rgba(0,0,0,0)" /></linearGradient></defs>
                <XAxis dataKey="d" hide />
                <YAxis hide domain={[0, chartMaxValue]} />
                <Tooltip contentStyle={{background:lightText?"rgba(14,12,8,0.86)":"rgba(255,255,255,0.96)",border:`1px solid ${txLine}`,borderRadius:10,fontSize:11,color:tx}} labelStyle={{color:tx}} itemStyle={{color:tx}} formatter={(v:number)=>[`${walletSpendDisplayValue(Number(v || 0))} UC`,"Spend"]} />
                <Area type="monotone" dataKey="v" stroke={lightText?"rgba(255,255,255,0.46)":"rgba(14,12,8,0.45)"} strokeWidth={1.5} fill="url(#sg)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
          <motion.div initial={false} animate={{opacity:1}} transition={{delay:0.44,duration:0.35}} className="rounded-2xl p-5 flex flex-col" style={homeCard}>
            <Label color={txMuted}>Open reserves</Label>
            <p className="mt-2 text-[28px]" style={{fontWeight:300,letterSpacing:"-0.02em",color:tx}}>{data.reserved.toFixed(2)}<span className="text-[14px] ml-1" style={{color:txSub,fontWeight:400}}>UC</span></p>
            <p className="text-[11px] mt-1" style={{color:txMeta}}>{reserveHint}</p>
            <div className="mt-auto pt-5 border-t" style={{borderColor:txLine}}>
              {reserveRows.map(({c,l})=>(
                <div key={l} className="flex items-center gap-1.5 mb-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{background:c}} /><span className="text-[11px]" style={{color:txMeta}}>{l}</span></div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Ledger ── */}
        <motion.div initial={false} animate={{opacity:1}} transition={{delay:0.48,duration:0.35}} className="rounded-2xl p-4 sm:p-6" style={homeCard}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="text-[15px] font-semibold" style={{color:tx}}>Ledger</h2>
            <div className="flex items-center gap-2">
              <span className="text-[11px] tabular-nums" style={{color:txMeta}}>
                {ledgerRangeStart}-{ledgerRangeEnd} of {filteredLedger.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={activeLedgerPage === 0}
                  onClick={()=>setLedgerPage(page=>Math.max(0, page - 1))}
                  className="w-7 h-7 rounded-full flex items-center justify-center disabled:opacity-35 disabled:cursor-not-allowed"
                  style={homeSubtle}
                  title="Previous ledger page"
                  aria-label="Previous ledger page">
                  <ChevronRight size={13} style={{color:txMeta,transform:"rotate(180deg)"}} />
                </button>
                <button
                  type="button"
                  disabled={activeLedgerPage >= ledgerPageCount - 1}
                  onClick={()=>setLedgerPage(page=>Math.min(ledgerPageCount - 1, page + 1))}
                  className="w-7 h-7 rounded-full flex items-center justify-center disabled:opacity-35 disabled:cursor-not-allowed"
                  style={homeSubtle}
                  title="Next ledger page"
                  aria-label="Next ledger page">
                  <ChevronRight size={13} style={{color:txMeta}} />
                </button>
              </div>
            </div>
          </div>
          <div className="mb-4 space-y-2">
            <SegCtrl
              options={LEDGER_STATUS_TABS}
              value={ledgerStatusTab}
              onChange={value=>setLedgerStatusTab(value as LedgerStatusTab)}
              layoutId="ledger-status-seg"
            />
            <SegCtrl
              options={LEDGER_JOB_TYPE_TABS}
              value={ledgerJobTypeTab}
              onChange={value=>setLedgerJobTypeTab(value as LedgerJobTypeTab)}
              layoutId="ledger-type-seg"
            />
          </div>
          <div>
            {ledgerRows.length > 0 ? ledgerRows.map((row,i)=>(
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={()=>{
                  setSelectedLedgerView("task")
                  setSelectedLedger(row)
                }}
                onKeyDown={event=>{
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    setSelectedLedgerView("task")
                    setSelectedLedger(row)
                  }
                }}
                className={`flex min-w-0 items-start gap-3 py-3.5 sm:items-center sm:gap-4 ${lightText?"hover:bg-white/[0.04] focus-visible:bg-white/[0.06]":"hover:bg-black/[0.02] focus-visible:bg-black/[0.03]"} transition-colors cursor-pointer rounded-xl px-2 outline-none`}
                style={i<ledgerRows.length-1?{borderBottom:`1px solid ${txLine}`}:{}}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={homeInset}>
                  {row.kind==="run"&&<Zap size={13} style={{color:lightText?txMeta:"#b45309"}} />}
                  {row.kind==="send"&&<ArrowUpRight size={13} style={{color:lightText?txMeta:"#b91c1c"}} />}
                  {row.kind==="topup"&&<ArrowDownLeft size={13} style={{color:lightText?txMeta:"#15803d"}} />}
                  {row.kind==="store"&&<HardDrive size={13} style={{color:txMeta}} />}
                </div>
                <span className="hidden font-mono text-[11px] w-24 flex-shrink-0 truncate sm:block" title={row.id} style={{color:txFaint}}>{row.displayId || row.id}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium" style={{color:tx}}>{row.label}</p>
                  <p className="truncate text-[11px]" title={row.meta} style={{color:txMeta}}>{row.meta}</p>
                  <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] sm:hidden" style={{color:txFaint}}>
                    <span className="max-w-[150px] truncate font-mono" title={row.id}>{row.displayId || row.id}</span>
                    <span>{row.time}</span>
                  </p>
                </div>
                <span className="ml-auto flex-shrink-0 text-right text-[13px] font-semibold tabular-nums sm:ml-0" style={{color:lightText?tx:(row.amt>0?"#15803d":"rgba(0,0,0,0.72)")}}>{row.amt>0?"+":""}{row.amt.toFixed(2)} UC</span>
                <span className="hidden text-[11px] w-20 text-right flex-shrink-0 sm:block" style={{color:txFaint}}>{row.time}</span>
              </div>
            )) : (
              <div className="rounded-xl px-4 py-8 text-center" style={homeInset}>
                <p className="text-[13px] font-semibold" style={{color:tx}}>No {ledgerStatusTab.toLowerCase()} {ledgerJobTypeTab} jobs</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
    <AnimatePresence>
      {selectedLedger ? (
        <LedgerTaskDialog
          key={selectedLedger.id}
          row={selectedLedger}
          initialView={selectedLedgerView}
          hardwareOptions={data.hardwareOptions}
          onClose={()=>setSelectedLedger(null)}
          onLaunchWorkload={onLaunchWorkload}
          onContinuationLaunched={onContinuationLaunched}
          onNeedCredits={onNeedCredits}
          onLoadRuntimeSession={onLoadRuntimeSession}
          onSendRuntimeSessionTurn={onSendRuntimeSessionTurn}
          onCloseRuntimeSession={onCloseRuntimeSession}
          onTerminateProviderJob={onTerminateProviderJob}
          onRefresh={onRefresh}
          onViewChange={setSelectedLedgerView}
          launchBusy={launchBusy}
          creditActionsDisabled={creditActionsDisabled}
        />
      ) : null}
    </AnimatePresence>
    </>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App(props: DirectWalletDashboardProps) {
  const data = resolveDashboardData(props)
  const { bootstrap: instanceBootstrap } = useInstanceBranding()
  const instanceBackground = instanceBootstrap
    ? instanceBackgroundValue(instanceBootstrap.branding)
    : "transparent"
  const initialAppearance = readWalletAppearance(
    walletAppearanceFallback(instanceBackground),
    data.user.id,
  )
  const [panel, setPanel] = useState<Panel>(() =>
    typeof window === "undefined" ? null : walletPanelFromSearch(window.location.search)
  )
  const [bg,        setBgState]   = useState<string>(initialAppearance.background)
  const [lightText, setLightTextState] = useState(initialAppearance.lightText)
  const [pendingLaunch, setPendingLaunch] = useState<DirectWalletPendingLaunch | null>(null)
  const creditActionsDisabled = Boolean(props.creditActionsDisabled)

  useEffect(() => {
    const next = readWalletAppearance(walletAppearanceFallback(instanceBackground), data.user.id)
    setBgState(next.background)
    setLightTextState(next.lightText)
  }, [data.user.id, instanceBackground])

  function setBg(next: string) {
    setBgState(next)
    writeWalletAppearance({ background: next, lightText }, data.user.id)
  }

  function setLightText(next: boolean) {
    setLightTextState(next)
    writeWalletAppearance({ background: bg, lightText: next }, data.user.id)
  }

  function handleRunLaunched(result?: DirectWalletLaunchResult | void, options?: DirectWalletLaunchFocusOptions) {
    setPanel(null)
    if (result) setPendingLaunch({ result, view: options?.view || "progress" })
  }

  function openCreditPurchase() {
    setPanel("add")
  }

  const isImageBg = bg.startsWith("url(")

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] flex-col overflow-hidden"
      style={isImageBg
        ? { backgroundImage: bg, backgroundSize:"cover", backgroundPosition:"center" }
        : { background: bg }}>
      <TopNav panel={panel} onOpen={setPanel} user={data.user} lightText={lightText} />
      <WalletEntryPrompts userID={data.user.id} />

      <main className="flex-1 overflow-y-auto">
        <WalletHome
          onOpen={setPanel}
          lightText={lightText}
          data={data}
          onLaunchWorkload={props.onLaunchWorkload}
          onContinuationLaunched={handleRunLaunched}
          onNeedCredits={openCreditPurchase}
          onLoadRuntimeSession={props.onLoadRuntimeSession}
          onSendRuntimeSessionTurn={props.onSendRuntimeSessionTurn}
          onCloseRuntimeSession={props.onCloseRuntimeSession}
          onTerminateProviderJob={props.onTerminateProviderJob}
          onRefresh={props.onRefresh}
          launchBusy={data.launchBusy}
          creditActionsDisabled={creditActionsDisabled}
          pendingLaunch={pendingLaunch}
          onPendingLaunchHandled={()=>setPendingLaunch(null)}
        />
      </main>

      <AnimatePresence>
        {panel === "send"      && <SendPanel      key="send"      onClose={() => setPanel(null)} balance={data.balance} onSendCredits={props.onSendCredits} />}
        {panel === "add"       && <AddPanel       key="add"       onClose={() => setPanel(null)} balance={data.balance} onAddCredits={props.onAddCredits} />}
        {panel === "receive"   && <ReceivePanel   key="receive"   onClose={() => setPanel(null)} user={data.user} />}
        {panel === "more"      && <MorePanel      key="more"      onClose={() => setPanel(null)} user={data.user} onExportLedger={props.onExportLedger} onSignOut={props.onSignOut} onNavigate={p=>{ setPanel(null); setTimeout(()=>setPanel(p),80) }} />}
        {panel === "settings"  && <SettingsPanel  key="settings"  onClose={() => setPanel(null)} bg={bg} setBg={setBg} lightText={lightText} setLightText={setLightText} user={data.user} onUpdateUser={props.onUpdateUser} accountSessions={props.accountSessions} accountSessionsLoading={props.accountSessionsLoading} accountSessionsError={props.accountSessionsError} revokingAccountSessionId={props.revokingAccountSessionId} onRefreshAccountSessions={props.onRefreshAccountSessions} onRevokeAccountSession={props.onRevokeAccountSession} onRevokeOtherAccountSessions={props.onRevokeOtherAccountSessions} />}
        {panel === "subscriptions" && <SubscriptionsPanel key="subscriptions" onClose={() => setPanel(null)} workspaceInfo={data.workspaceInfo} />}
        {panel === "run"       && <RunPanel       key="run"       onClose={() => setPanel(null)} onLaunchWorkload={props.onLaunchWorkload} onLaunched={handleRunLaunched} hardwareOptions={data.hardwareOptions} jobs={data.jobs} onRefresh={props.onRefresh} proStats={data.proStats} onNeedCredits={openCreditPurchase} launchBusy={data.launchBusy} creditActionsDisabled={creditActionsDisabled} />}
        {panel === "storage"   && <StoragePanel   key="storage"   onClose={() => setPanel(null)} volumes={data.volumes} snapshots={data.snapshots} storageRoutes={data.storageRoutes} creditActionsDisabled={creditActionsDisabled} onNeedCredits={openCreditPurchase} onCreateStorageVolume={props.onCreateStorageVolume} onDeleteStorageVolume={props.onDeleteStorageVolume} onDeleteRunSnapshot={props.onDeleteRunSnapshot} onLoadStorageVolumeFiles={props.onLoadStorageVolumeFiles} onUploadStorageVolumeFiles={props.onUploadStorageVolumeFiles} onImportStorageVolumeHF={props.onImportStorageVolumeHF} />}
        {panel === "market"    && <MarketPanel    key="market"    onClose={() => setPanel(null)} marketOffers={data.marketOffers} proStats={data.proStats} onCreateProProvider={props.onCreateProProvider} onUpdateProProvider={props.onUpdateProProvider} onValidateProProvider={props.onValidateProProvider} onRotateProProviderKey={props.onRotateProProviderKey} onDeleteProProvider={props.onDeleteProProvider} onLoadProProviderLedger={props.onLoadProProviderLedger} />}
        {panel === "workspace" && <WorkspacePanel key="workspace" onClose={() => setPanel(null)} members={data.members} workspaceActivity={data.workspaceActivity} workspaceInfo={data.workspaceInfo} workspaceInvites={data.workspaceInvites} onSendCredits={props.onSendCredits} onUpdateWorkspace={props.onUpdateWorkspace} onInviteWorkspaceMember={props.onInviteWorkspaceMember} onAcceptWorkspaceInvite={props.onAcceptWorkspaceInvite} onDeclineWorkspaceInvite={props.onDeclineWorkspaceInvite} onSetWorkspaceBudget={props.onSetWorkspaceBudget} onSetMemberBudget={props.onSetMemberBudget} />}
      </AnimatePresence>
    </div>
  )
}
